import React from "react";
import { toast } from "sonner";
import {
  Trash2,
  AlertCircle,
  Plus,
  Copy,
  FileText,
  ChevronDown,
  Monitor,
  CheckCircle,
  ChevronLeft,
  CalendarClock,
  MessageSquareText,
  PlusCircle,
  PenTool,
  XCircle,
  Receipt,
  Download,
  Printer,
  Mail,
  Loader2,
} from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip2,
  Legend as RechartsLegend,
  BarChart,
  Bar,
} from "recharts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { peekReturnContext, consumeReturnContext } from "../utils/clientNavContext";
// Sub-fase 5.3.2-B · router de guardado · conectado a handleGlobalSave para
// derivar las operaciones críticas de Clases Fijas a sus endpoints atómicos
// (cambiar-forma-cobro / cambiar-cuota / cambiar-horario) en lugar de pasar
// por PUT update_client. Los datos personales siguen por PUT light con
// whitelist conservadora.
import {
  serializarTurno,
  decidirRoutingGuardado,
  construirPayloadPutLight,
  ejecutarSecuenciaDeAcciones,
  TIPOS_ACCION,
  MOTIVOS_BLOQUEO,
  PUT_LIGHT_WHITELIST_CONSERVADORA,
  PUT_LIGHT_EXCLUIDOS_PENDIENTES,
  ENDPOINTS as ROUTING_ENDPOINTS,
} from "../utils/clientNewProfileRouting";
// Sub-fase 7 · helper de validación cruzada SEPA para forma_pago=domiciliacion
import { mensajeFaltaSepaParaInscripcion } from "../utils/mensajesInscripcionClasesFijas";
import { extractApiErrorMessage } from "../lib/api-errors";
import axios from "axios";
import { API } from "../api";
import DerivadorSelector, { esComoNosConocioMedico } from "../components/admin/DerivadorSelector";
import useMotivoAnulacionFactura from "../components/facturacion/useMotivoAnulacionFactura";
import useCodigoPostal from "../hooks/useCodigoPostal";
import DateDropdownSelector from "../components/ui/date-dropdown-selector";
import CustomColorSelect from "../components/admin/CustomColorSelect";
import MembershipSection from "../components/admin/MembershipSection";
import RejillaAnualPagos from "../components/client/RejillaAnualPagos";
import ModalInscripcionClasesFijas from "../components/client/ModalInscripcionClasesFijas";
import ModalEditarFechasIS from "../components/admin/ModalEditarFechasIS";
import BajaClinicaModal from "../components/admin/BajaClinicaModal";
import ReactivarClinicaModal from "../components/admin/ReactivarClinicaModal";
import { useAuth } from "../context/AuthContext";
import "./client-new-profile.css";
import "./empleado-ficha.css";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];

const SEDES = ["MUNTANER", "MEFIR", "CONSULTAS"];

// Cuadrante: colores de casilla (mismos que el modal de Profesionales).
const COLORES_CUADRANTE = [
  { value: '#3B82F6', label: 'Azul' },
  { value: '#10B981', label: 'Verde' },
  { value: '#8B5CF6', label: 'Púrpura' },
  { value: '#F59E0B', label: 'Naranja' },
  { value: '#EF4444', label: 'Rojo' },
  { value: '#EC4899', label: 'Rosa' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#6366F1', label: 'Índigo' },
];

// F2 · Clasificación laboral (catálogos cerrados · idénticos al backend y al
// modal de Profesionales). tipo_relacion es el clasificador maestro; es_autonomo
// se DERIVA de él (autonomo → true). funcion_contable manda el coste a su línea
// de la P&L en el Cuadro de Mando (B0).
const TIPO_RELACION_OPTS_FICHA = [
  { value: 'empleado', label: 'Empleado (nómina)' },
  { value: 'autonomo', label: 'Autónomo (factura)' },
  { value: 'colaborador', label: 'Colaborador' },
  { value: 'proveedor_externo', label: 'Proveedor externo' },
  { value: 'direccion_sin_nomina', label: 'Dirección sin nómina' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'excluir', label: 'Excluir' },
];
const FUNCION_CONTABLE_OPTS_FICHA = [
  { value: '', label: '— Sin clasificar —' },
  { value: 'tecnico_clinico', label: 'Técnico clínico' },
  { value: 'tecnico_mpd', label: 'Técnico MPD' },
  { value: 'fisioterapeuta', label: 'Fisioterapeuta' },
  { value: 'recepcion', label: 'Recepción' },
  { value: 'administracion', label: 'Administración' },
  { value: 'limpieza', label: 'Limpieza' },
  { value: 'back_office', label: 'Back office' },
  { value: 'direccion', label: 'Dirección' },
  { value: 'medico', label: 'Médico' },
  { value: 'otro', label: 'Otro' },
];
const ESTADO_REVISION_OPTS_FICHA = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'falta_dni', label: 'Falta DNI' },
  { value: 'falta_imputacion', label: 'Falta imputación' },
  { value: 'revisar_identidad', label: 'Revisar identidad' },
  { value: 'revisar_relacion', label: 'Revisar relación' },
  { value: 'completo', label: 'Completo' },
];

const CIUDADES_PROVINCIA = {
  "barcelona": { provincia: "Barcelona", pais: "ES" },
  "hospitalet de llobregat": { provincia: "Barcelona", pais: "ES" },
  "l'hospitalet de llobregat": { provincia: "Barcelona", pais: "ES" },
  "badalona": { provincia: "Barcelona", pais: "ES" },
  "terrassa": { provincia: "Barcelona", pais: "ES" },
  "sabadell": { provincia: "Barcelona", pais: "ES" },
  "mataro": { provincia: "Barcelona", pais: "ES" },
  "santa coloma de gramenet": { provincia: "Barcelona", pais: "ES" },
  "cornella de llobregat": { provincia: "Barcelona", pais: "ES" },
  "sant boi de llobregat": { provincia: "Barcelona", pais: "ES" },
  "sant cugat del valles": { provincia: "Barcelona", pais: "ES" },
  "manresa": { provincia: "Barcelona", pais: "ES" },
  "rubí": { provincia: "Barcelona", pais: "ES" },
  "rubi": { provincia: "Barcelona", pais: "ES" },
  "vilanova i la geltru": { provincia: "Barcelona", pais: "ES" },
  "viladecans": { provincia: "Barcelona", pais: "ES" },
  "granollers": { provincia: "Barcelona", pais: "ES" },
  "cerdanyola del valles": { provincia: "Barcelona", pais: "ES" },
  "esplugues de llobregat": { provincia: "Barcelona", pais: "ES" },
  "gavà": { provincia: "Barcelona", pais: "ES" },
  "gava": { provincia: "Barcelona", pais: "ES" },
  "castelldefels": { provincia: "Barcelona", pais: "ES" },
  "sant feliu de llobregat": { provincia: "Barcelona", pais: "ES" },
  "sitges": { provincia: "Barcelona", pais: "ES" },
  "mollet del valles": { provincia: "Barcelona", pais: "ES" },
  "sant adria de besos": { provincia: "Barcelona", pais: "ES" },
  "igualada": { provincia: "Barcelona", pais: "ES" },
  "vic": { provincia: "Barcelona", pais: "ES" },
  "girona": { provincia: "Girona", pais: "ES" },
  "figueres": { provincia: "Girona", pais: "ES" },
  "lloret de mar": { provincia: "Girona", pais: "ES" },
  "blanes": { provincia: "Girona", pais: "ES" },
  "olot": { provincia: "Girona", pais: "ES" },
  "tarragona": { provincia: "Tarragona", pais: "ES" },
  "reus": { provincia: "Tarragona", pais: "ES" },
  "tortosa": { provincia: "Tarragona", pais: "ES" },
  "lleida": { provincia: "Lleida", pais: "ES" },
  "madrid": { provincia: "Madrid", pais: "ES" },
  "valencia": { provincia: "Valencia", pais: "ES" },
  "sevilla": { provincia: "Sevilla", pais: "ES" },
  "zaragoza": { provincia: "Zaragoza", pais: "ES" },
  "malaga": { provincia: "Malaga", pais: "ES" },
  "bilbao": { provincia: "Vizcaya", pais: "ES" },
  "san sebastian": { provincia: "Guipuzcoa", pais: "ES" },
  "palma de mallorca": { provincia: "Baleares", pais: "ES" },
  "palma": { provincia: "Baleares", pais: "ES" },
};

// Codigo postal Barcelona -> barrio
const CP_BARRIO_BCN = {
  "08001": "Raval", "08002": "Barri Gotic", "08003": "Barceloneta / Born",
  "08004": "Poble Sec", "08005": "Vila Olimpica / Poblenou", "08006": "Sant Gervasi / Gracia",
  "08007": "Dreta Eixample", "08008": "Dreta Eixample", "08009": "Dreta Eixample",
  "08010": "Dreta Eixample", "08011": "Esquerra Eixample", "08012": "Gracia",
  "08013": "Sagrada Familia", "08014": "Sants", "08015": "Esquerra Eixample / Sant Antoni",
  "08016": "Horta-Guinardo", "08017": "Sarria-Sant Gervasi", "08018": "Sant Marti / Poblenou",
  "08019": "Sant Marti", "08020": "Sant Marti / Besos", "08021": "Sant Gervasi-Galvany",
  "08022": "Sant Gervasi-Bonanova", "08023": "Gracia / Vallcarca", "08024": "Gracia / Coll",
  "08025": "Sagrada Familia / Camp d'en Grassot", "08026": "Horta-Guinardo / Baix Guinardo",
  "08027": "Horta-Guinardo", "08028": "Les Corts", "08029": "Les Corts / Sarria",
  "08030": "Nou Barris", "08031": "Nou Barris / Roquetes", "08032": "Horta",
  "08033": "Nou Barris / Trinitat", "08034": "Pedralbes / Sarria", "08035": "Horta-Guinardo / Carmel",
  "08036": "Esquerra Eixample", "08037": "Sagrada Familia / Industria",
  "08038": "Sants-Montjuic / Zona Franca", "08039": "Barceloneta / Port",
  "08040": "Sants-Montjuic / Montjuic", "08041": "Nou Barris / Canyelles",
  "08042": "Nou Barris / Torre Baro",
};

// Codigo postal -> ciudad, provincia, pais (primeros 2 digitos = provincia)
const CP_PROVINCIA = {
  "08": { provincia: "Barcelona", pais: "ES", ciudad: "Barcelona" },
  "17": { provincia: "Girona", pais: "ES", ciudad: "Girona" },
  "25": { provincia: "Lleida", pais: "ES", ciudad: "Lleida" },
  "43": { provincia: "Tarragona", pais: "ES", ciudad: "Tarragona" },
  "01": { provincia: "Alava", pais: "ES" },
  "02": { provincia: "Albacete", pais: "ES" },
  "03": { provincia: "Alicante", pais: "ES" },
  "04": { provincia: "Almeria", pais: "ES" },
  "05": { provincia: "Avila", pais: "ES" },
  "06": { provincia: "Badajoz", pais: "ES" },
  "07": { provincia: "Baleares", pais: "ES" },
  "09": { provincia: "Burgos", pais: "ES" },
  "10": { provincia: "Caceres", pais: "ES" },
  "11": { provincia: "Cadiz", pais: "ES" },
  "12": { provincia: "Castellon", pais: "ES" },
  "13": { provincia: "Ciudad Real", pais: "ES" },
  "14": { provincia: "Cordoba", pais: "ES" },
  "15": { provincia: "A Coruña", pais: "ES" },
  "16": { provincia: "Cuenca", pais: "ES" },
  "18": { provincia: "Granada", pais: "ES" },
  "19": { provincia: "Guadalajara", pais: "ES" },
  "20": { provincia: "Guipuzcoa", pais: "ES" },
  "21": { provincia: "Huelva", pais: "ES" },
  "22": { provincia: "Huesca", pais: "ES" },
  "23": { provincia: "Jaen", pais: "ES" },
  "24": { provincia: "Leon", pais: "ES" },
  "26": { provincia: "La Rioja", pais: "ES" },
  "27": { provincia: "Lugo", pais: "ES" },
  "28": { provincia: "Madrid", pais: "ES", ciudad: "Madrid" },
  "29": { provincia: "Malaga", pais: "ES" },
  "30": { provincia: "Murcia", pais: "ES" },
  "31": { provincia: "Navarra", pais: "ES" },
  "32": { provincia: "Ourense", pais: "ES" },
  "33": { provincia: "Asturias", pais: "ES" },
  "34": { provincia: "Palencia", pais: "ES" },
  "35": { provincia: "Las Palmas", pais: "ES" },
  "36": { provincia: "Pontevedra", pais: "ES" },
  "37": { provincia: "Salamanca", pais: "ES" },
  "38": { provincia: "Santa Cruz de Tenerife", pais: "ES" },
  "39": { provincia: "Cantabria", pais: "ES" },
  "40": { provincia: "Segovia", pais: "ES" },
  "41": { provincia: "Sevilla", pais: "ES" },
  "42": { provincia: "Soria", pais: "ES" },
  "44": { provincia: "Teruel", pais: "ES" },
  "45": { provincia: "Toledo", pais: "ES" },
  "46": { provincia: "Valencia", pais: "ES" },
  "47": { provincia: "Valladolid", pais: "ES" },
  "48": { provincia: "Vizcaya", pais: "ES" },
  "49": { provincia: "Zamora", pais: "ES" },
  "50": { provincia: "Zaragoza", pais: "ES" },
  "51": { provincia: "Ceuta", pais: "ES" },
  "52": { provincia: "Melilla", pais: "ES" },
};

// ─── Componente lazy de taquilla (solo carga datos al montar) ───
const TaquillaPanel = ({ clienteId }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    axios.get(`${API}/taquillas/cliente/${clienteId}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [clienteId]);

  const fmtDate = (s) => {
    if (!s) return "—";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).substring(0,10);
  };

  const estadoBadge = (estado) => {
    const map = {
      confirmado: "cn-badge--success",
      registrado: "cn-badge--warning",
      pendiente:  "cn-badge--warning",
      hold:       "cn-badge--neutral",
      devuelto:   "cn-badge--danger",
      anulado:    "cn-badge--neutral",
    };
    const labels = {
      confirmado: "Cobrado",
      registrado: "Pendiente",
      pendiente:  "Pend. SEPA",
      hold:       "Previsto",
      devuelto:   "Devuelto",
      anulado:    "Anulado",
    };
    const principal = <span className={`cn-badge ${map[estado] || "cn-badge--neutral"}`}>{labels[estado] || estado}</span>;
    if (estado === "hold") {
      return (
        <>
          {principal}
          <span
            title="No entra en la próxima remesa SEPA"
            style={{ marginLeft: 4, padding: "1px 5px", fontSize: 9, fontWeight: 500, color: "#6b7280", backgroundColor: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 3, display: "inline-block", verticalAlign: "middle", lineHeight: "14px" }}
          >
            fuera de remesa
          </span>
        </>
      );
    }
    return principal;
  };

  const accionBadge = (accion) => {
    const map = { ALQUILER: "cn-badge--success", RENOVAR: "cn-badge--warning", BAJA: "cn-badge--danger" };
    return <span className={`cn-badge ${map[accion] || "cn-badge--neutral"}`}>{accion}</span>;
  };

  if (loading) return <div style={{ padding: 24, color: "#94a3b8" }}>Cargando taquilla...</div>;
  if (!data?.tiene_taquilla) return (
    <div className="cn-empty-state cn-empty-state--compact" style={{ border: "none", background: "transparent" }}>
      <p className="cn-empty-state-desc" style={{ fontStyle: "italic", textAlign: "center", width: "100%" }}>
        Este cliente no tiene taquilla asignada.
      </p>
    </div>
  );

  const t = data.taquilla || {};
  const cobros = data.cobros || [];
  const historial = data.historial || [];

  const mesesVencida = (() => {
    if (!t.fecha_fin || !t.vencida) return 0;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(t.fecha_fin));
    if (!m) return 0;
    const ff = new Date(+m[1], +m[2]-1, +m[3]);
    const hoy = new Date();
    return (hoy.getFullYear() - ff.getFullYear()) * 12 + (hoy.getMonth() - ff.getMonth());
  })();

  return (
    <>
      {/* Resumen */}
      <div className="cn-eco-summary">
        <div className="cn-eco-card">
          <span className="cn-eco-label">Nº de Taquilla</span>
          <span className="cn-eco-value" style={{ fontSize: 18, fontWeight: 700, color: "var(--cn-primary)" }}>{t.numero || "—"}</span>
        </div>
        <div className="cn-eco-card">
          <span className="cn-eco-label">Vestuario</span>
          <span className="cn-eco-value">{t.vestuario_nombre || "—"}</span>
        </div>
        <div className="cn-eco-card">
          <span className="cn-eco-label">Sede</span>
          <span className="cn-eco-value">{t.sede_nombre || "—"}</span>
        </div>
        <div className={`cn-eco-card ${t.vencida ? "cn-eco-card--danger" : "cn-eco-card--success"}`}>
          <span className="cn-eco-label">Vencimiento</span>
          <span className="cn-eco-value" style={{ color: t.vencida ? "var(--cn-danger)" : "var(--cn-success)" }}>
            {fmtDate(t.fecha_fin)}{t.vencida ? ` — Venció hace ${mesesVencida} mes${mesesVencida !== 1 ? "es" : ""}` : ""}
          </span>
        </div>
        <div className="cn-eco-card">
          <span className="cn-eco-label">Precio / Depósito</span>
          <span className="cn-eco-value">{t.precio ?? "—"}€ / {t.deposito ?? "—"}€</span>
        </div>
      </div>

      <div className="cn-twin-panels">
        {/* Detalles */}
        <div className="cn-panel" style={{ flex: 1, borderTop: "3px solid var(--cn-primary)" }}>
          <div className="cn-panel-header">
            <h3 className="cn-panel-title">Detalle de la taquilla</h3>
          </div>
          <div className="cn-panel-content cn-data-grid cn-data-grid--compact">
            <div className="cn-data-item"><span className="cn-data-label">Número</span><span className="cn-data-value" style={{ fontSize: 16, fontWeight: 600 }}>{t.numero || "—"}</span></div>
            <div className="cn-data-item"><span className="cn-data-label">Forma de pago</span><span className="cn-data-value">{t.forma_pago || "—"}</span></div>
            <div className="cn-data-item"><span className="cn-data-label">Fecha inicio</span><span className="cn-data-value">{fmtDate(t.fecha_inicio)}</span></div>
            <div className="cn-data-item"><span className="cn-data-label">Fecha fin</span><span className="cn-data-value" style={{ color: t.vencida ? "var(--cn-danger)" : undefined, fontWeight: t.vencida ? 700 : undefined }}>{fmtDate(t.fecha_fin)}{t.vencida ? ` — Venció hace ${mesesVencida} mes${mesesVencida !== 1 ? "es" : ""}` : ""}</span></div>
            {t.notas && <div className="cn-data-item" style={{ gridColumn: "span 2" }}><span className="cn-data-label">Notas</span><span className="cn-data-value" style={{ color: "var(--cn-text-muted)", fontStyle: "italic" }}>{t.notas}</span></div>}
          </div>
        </div>

        {/* Cobros y historial */}
        <div className="cn-panel" style={{ flex: 1, borderTop: "3px solid var(--cn-success)" }}>
          <div className="cn-panel-header">
            <h3 className="cn-panel-title">Cobros de taquilla ({cobros.length})</h3>
          </div>
          <div className="cn-panel-content" style={{ fontSize: 12 }}>
            {cobros.length === 0 ? <p style={{ color: "#94a3b8", fontStyle: "italic" }}>Sin cobros registrados</p> : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid #e2e8f0", textAlign: "left" }}>
                  <th style={{ padding: "4px 6px" }}>Tipo</th><th style={{ padding: "4px 6px" }}>Importe</th><th style={{ padding: "4px 6px" }}>Estado</th><th style={{ padding: "4px 6px" }}>Fecha</th>
                </tr></thead>
                <tbody>{cobros.map((c, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 6px" }}>{c.tipo === "taquilla_deposito" ? "Depósito" : "Cuota"}</td>
                    <td style={{ padding: "4px 6px" }}>{c.importe}€</td>
                    <td style={{ padding: "4px 6px" }}>{estadoBadge(c.estado)}</td>
                    <td style={{ padding: "4px 6px" }}>{fmtDate(c.created_at)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            {historial.length > 0 && (
              <>
                <h4 style={{ fontSize: 12, fontWeight: 600, marginTop: 12, marginBottom: 4 }}>Historial</h4>
                {historial.map((h, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, padding: "3px 0", fontSize: 11, color: "#64748b", alignItems: "center" }}>
                    {accionBadge(h.accion)}
                    <span>Taq. {h.numero_taquilla}</span>
                    <span>{fmtDate(h.created_at)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Tarjeta Taquilla para tab Inscripciones (con modales funcionales) ───
const TaquillaInscripcionCard = ({ clienteId, sede }) => {
  const [taq, setTaq] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showContratar, setShowContratar] = React.useState(false);
  const [taquillasLibres, setTaquillasLibres] = React.useState([]);
  const [formAlquiler, setFormAlquiler] = React.useState({ taquilla_id: "", precio: 39, deposito: 10, forma_pago: "efectivo" });
  const [processing, setProcessing] = React.useState(false);
  const [confirmBaja, setConfirmBaja] = React.useState(false);

  const fetchTaq = React.useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/taquillas/cliente/${clienteId}`);
      setTaq(r.data?.tiene_taquilla ? r.data : null);
    } catch { setTaq(null); }
    finally { setLoading(false); }
  }, [clienteId]);
  React.useEffect(() => { fetchTaq(); }, [fetchTaq]);

  const fmtD = (s) => { if (!s) return "—"; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).substring(0,10); };

  const tiene = taq?.tiene_taquilla;
  const t = taq?.taquilla || {};

  // Contratar: cargar taquillas libres
  const openContratar = async () => {
    try {
      const r = await axios.get(`${API}/taquillas/taquillas`, { params: { sede_id: sede } });
      const libres = (r.data || []).filter(t => !t.alquilada);
      setTaquillasLibres(libres);
    } catch { setTaquillasLibres([]); }
    setShowContratar(true);
  };

  const handleAlquilar = async () => {
    if (!formAlquiler.taquilla_id) { toast.error("Selecciona una taquilla"); return; }
    setProcessing(true);
    try {
      // Obtener nombre del cliente
      let clienteNombre = "";
      try { const r = await axios.get(`${API}/admin/clients/${clienteId}`); clienteNombre = `${r.data?.nombre || ""} ${r.data?.primer_apellido || ""}`.trim(); } catch {}
      await axios.post(`${API}/taquillas/taquillas/${formAlquiler.taquilla_id}/alquilar`, {
        cliente_id: clienteId, cliente_nombre: clienteNombre,
        precio: formAlquiler.precio, deposito: formAlquiler.deposito,
        forma_pago: formAlquiler.forma_pago,
      });
      toast.success("Taquilla alquilada");
      setShowContratar(false);
      fetchTaq();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al alquilar"); }
    finally { setProcessing(false); }
  };

  const handleRenovar = async () => {
    setProcessing(true);
    try {
      let clienteNombre = t.cliente_nombre || "";
      await axios.post(`${API}/taquillas/taquillas/${t._id}/renovar`, {
        cliente_id: clienteId, cliente_nombre: clienteNombre,
        precio: t.precio || 39, deposito: t.deposito || 0,
        forma_pago: t.forma_pago || "efectivo",
      });
      toast.success("Taquilla renovada");
      fetchTaq();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al renovar"); }
    finally { setProcessing(false); }
  };

  const handleBaja = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/taquillas/taquillas/${t._id}/dar-baja`, {
        motivo: "voluntaria", devolver_deposito: true,
      });
      toast.success("Taquilla dada de baja");
      setConfirmBaja(false);
      fetchTaq();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al dar de baja"); }
    finally { setProcessing(false); }
  };

  if (loading) return <div className="cn-panel" style={{ margin: 0 }}><div className="cn-panel-header"><h3 className="cn-panel-title" style={{ margin: 0, color: "#94a3b8" }}>Taquilla...</h3></div></div>;

  return (
    <>
      <div className="cn-panel" style={{ margin: 0, backgroundColor: tiene ? "" : "" }}>
        <div className="cn-panel-header" style={{ backgroundColor: tiene ? "#f0fdf4" : "" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input type="checkbox" checked={!!tiene} readOnly style={{ width: "16px", height: "16px", accentColor: "var(--cn-primary)", cursor: "default" }} />
              <h3 className="cn-panel-title" style={{ margin: 0 }}>Taquilla</h3>
            </div>
            {tiene && (
              <span className={`cn-badge ${t.vencida ? "cn-badge--danger" : "cn-badge--success"}`}>
                {t.vencida ? "Vencida" : "Activa"}
              </span>
            )}
          </div>
          <p className="cn-panel-subtitle" style={{ margin: 0 }}>Alquiler de taquilla personal</p>
        </div>

        {tiene ? (
          <div className="cn-panel-content" style={{ fontSize: "13px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div className="cn-data-item"><span className="cn-data-label">Número</span><span className="cn-data-value" style={{ fontWeight: 600 }}>{t.numero}</span></div>
              <div className="cn-data-item"><span className="cn-data-label">Vestuario</span><span className="cn-data-value">{t.vestuario_nombre || "—"}</span></div>
              <div className="cn-data-item"><span className="cn-data-label">Sede</span><span className="cn-data-value">{t.sede_nombre || "—"}</span></div>
              <div className="cn-data-item">
                <span className="cn-data-label">Vencimiento</span>
                <span className="cn-data-value" style={{ color: t.vencida ? "var(--cn-danger)" : undefined, fontWeight: t.vencida ? 700 : undefined }}>
                  {fmtD(t.fecha_fin)}
                  {t.vencida && (() => { const m=/^(\d{4})-(\d{2})-(\d{2})/.exec(String(t.fecha_fin)); if(!m) return ""; const ff=new Date(+m[1],+m[2]-1,+m[3]); const h=new Date(); const d=(h.getFullYear()-ff.getFullYear())*12+(h.getMonth()-ff.getMonth()); return ` — Venció hace ${d} mes${d!==1?"es":""}`; })()}
                </span>
              </div>
              <div className="cn-data-item"><span className="cn-data-label">Precio / Depósito</span><span className="cn-data-value">{t.precio}€ / {t.deposito}€</span></div>
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "12px", paddingTop: "10px", borderTop: "1px solid var(--cn-border)" }}>
              <button onClick={handleRenovar} disabled={processing} className="cn-btn cn-btn--sm cn-btn--outline">{processing ? "..." : "Renovar"}</button>
              <button onClick={() => setConfirmBaja(true)} disabled={processing} className="cn-btn cn-btn--sm cn-btn--outline" style={{ color: "var(--cn-danger)", borderColor: "var(--cn-danger)" }}>Dar de baja</button>
            </div>
          </div>
        ) : (
          <div className="cn-panel-content" style={{ fontSize: "13px" }}>
            <p style={{ color: "var(--cn-text-muted)", fontStyle: "italic", margin: "0 0 10px" }}>Sin taquilla asignada</p>
            <button onClick={openContratar} className="cn-btn cn-btn--sm cn-btn--primary">Contratar taquilla</button>
          </div>
        )}
      </div>

      {/* Modal contratar */}
      {showContratar && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowContratar(false); }}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "400px", maxWidth: "90vw", boxShadow: "0 10px 40px rgba(0,0,0,0.15)" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "16px", fontWeight: 700 }}>Contratar taquilla</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "4px" }}>Taquilla disponible</label>
                <select className="cn-input-editable" value={formAlquiler.taquilla_id}
                  onChange={(e) => setFormAlquiler({ ...formAlquiler, taquilla_id: e.target.value })}>
                  <option value="">Seleccionar...</option>
                  {taquillasLibres.map(tl => <option key={tl._id} value={tl._id}>{tl.numero} {tl.vestuario_nombre ? `(${tl.vestuario_nombre})` : ""}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "4px" }}>Precio (€)</label>
                  <input type="number" className="cn-input-editable" value={formAlquiler.precio} onChange={(e) => setFormAlquiler({ ...formAlquiler, precio: +e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "4px" }}>Depósito (€)</label>
                  <input type="number" className="cn-input-editable" value={formAlquiler.deposito} onChange={(e) => setFormAlquiler({ ...formAlquiler, deposito: +e.target.value })} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "4px" }}>Forma de pago</label>
                <select className="cn-input-editable" value={formAlquiler.forma_pago}
                  onChange={(e) => setFormAlquiler({ ...formAlquiler, forma_pago: e.target.value })}>
                  <option value="efectivo">Efectivo</option>
                  <option value="tpv">Tarjeta</option>
                  <option value="domiciliacion">Domiciliación</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
              <button onClick={() => setShowContratar(false)} className="cn-btn cn-btn--sm cn-btn--outline">Cancelar</button>
              <button onClick={handleAlquilar} disabled={processing} className="cn-btn cn-btn--sm cn-btn--primary">{processing ? "Procesando..." : "Confirmar alquiler"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar baja */}
      {confirmBaja && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmBaja(false); }}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "380px", maxWidth: "90vw", boxShadow: "0 10px 40px rgba(0,0,0,0.15)" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 700, color: "#dc2626" }}>Dar de baja taquilla {t.numero}</h3>
            <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 16px" }}>Se liberará la taquilla y se generará la devolución del depósito ({t.deposito}€) si corresponde.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button onClick={() => setConfirmBaja(false)} className="cn-btn cn-btn--sm cn-btn--outline">Cancelar</button>
              <button onClick={handleBaja} disabled={processing} className="cn-btn cn-btn--sm cn-btn--outline" style={{ color: "#fff", backgroundColor: "#dc2626", borderColor: "#dc2626" }}>{processing ? "Procesando..." : "Confirmar baja"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// FASE D1 · Predeploy Bloque 4 (Ann 2026-05-06):
// Convierte el `detail` de un AxiosError en un string legible para toast.
// El backend del Bloque 4 devuelve detail como objeto (LEGACY_PATH_BLOCKED_*,
// ESTADO_INCOHERENTE_BLOQUEA_BAJA, IS_*) con campos estructurados. Antes el
// toast mostraba [object Object]; ahora se prioriza el primer campo legible.
// No hace rediseño UI; solo evita basura en el toast.
const parseErrorDetail = (err, fallback = "Error inesperado") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object") {
    const order = [
      "message",
      "mensaje",
      "error",
      "error_code",
      "code",
      "motivo_revision",
      "motivos_revision_criticos",
      "endpoint_recomendado",
    ];
    for (const k of order) {
      const v = detail[k];
      if (v == null) continue;
      if (typeof v === "string" && v.trim()) return v;
      if (Array.isArray(v) && v.length > 0) return v.join("; ");
      if (typeof v === "object") {
        try { return JSON.stringify(v); } catch { /* ignore */ }
      }
    }
    try { return JSON.stringify(detail); } catch { /* ignore */ }
  }
  return fallback;
};

// EMPLEADO: tipos de documento para la pestaña Documentación.
const TIPOS_DOC_EMPLEADO = [
  "Seguridad social",
  "DNI",
  "Titulación",
  "Colegiatura",
  "Currículum",
  "Contrato",
  "Anexo - pacto no competencia",
  "Anexo - derechos de imagen",
  "Anexo - horas complementarias",
  "Anexo - cámaras de seguridad",
  "Anexo - buenas prácticas",
  "Anexo - normativa",
  "Cesión de derechos",
];

// EMPLEADO · GRUPOS DE DOCUMENTOS OBLIGATORIOS según lo que hace el trabajador.
// Se marca la casilla del grupo y quedan a la vista los documentos que hay que
// subir, con su estado (subido / falta). Los archivos van al mismo almacén
// cifrado que el resto (laboral_documentos · tipo="empleado_doc").
const GRUPOS_DOC_EMPLEADO = [
  { campo: "requiere_docs_clases_online", label: "Clases online",
    docs: ["Anexo - derechos de imagen", "Cesión de derechos"] },
  { campo: "requiere_docs_fisioterapia", label: "Fisioterapia",
    docs: ["Colegiatura", "Titulación"] },
];

// EMPLEADO · tareas del checklist de Incorporación (onboarding).
const CHECKLIST_INCORP = [
  { id: "docs", label: "Recopilar documentación obligatoria (DNI, NAF, titulación, IBAN)" },
  { id: "contrato", label: "Firma del contrato" },
  { id: "anexos", label: "Firma de anexos (no competencia, imagen, horas, cámaras, buenas prácticas, normativa)" },
  { id: "alta_ss", label: "Alta en la Seguridad Social" },
  { id: "credenciales", label: "Entrega de credenciales / accesos" },
  { id: "material", label: "Entrega de material / EPIs (uniforme, llaves, taquilla, tarjeta)" },
  { id: "formacion_prl", label: "Formación inicial / PRL" },
  { id: "reconocimiento", label: "Reconocimiento médico inicial" },
  { id: "cuadrante", label: "Asignación en cuadrante / horario" },
  { id: "presentacion", label: "Presentación al equipo" },
];

// EMPLEADO · catálogos del contrato.
const FUNCIONES_EMP = [
  ["fisioterapeuta", "Fisioterapeuta"],
  ["tecnico", "Técnico"],
  ["limpieza", "Limpieza"],
  ["administrativo_centro", "Administrativo de centro"],
  ["back_office_administrativo", "Back office administrativo"],
  ["informatica", "Informática"],
  ["gerencia", "Gerencia"],
  ["otros_conceptos_sociales", "Otros conceptos sociales"],
];
const TIPOS_CONTRATO_EMP = ["indefinido", "temporal", "prácticas", "formación", "obra/servicio", "otro"];

// EMPLEADO · jornada DERIVADA de Horas/semana (40h = jornada legal completa).
// El % de jornada NO es editable: se calcula desde las horas semanales.
const HORAS_JORNADA_COMPLETA = 40;
const calcPctJornada = (horasSemana) => {
  const h = Number(horasSemana) || 0;
  return Math.round((h / HORAS_JORNADA_COMPLETA) * 100);
};

// EMPLEADO · jornada (días + franjas horarias) editable desde la ficha.
const DIAS_SEMANA_EMP = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const HORAS_EMP = Array.from({ length: 30 }, (_, i) => `${String(Math.floor(i / 2) + 7).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);

// EMPLEADO · placeholder de pestaña pendiente de desarrollar (mismo estilo cn-).
const PlaceholderEmpleado = ({ titulo, desc }) => (
  <div style={{ padding: 16 }}>
    <div className="cn-panel">
      <div className="cn-panel-header"><h3 className="cn-panel-title">{titulo}</h3></div>
      <div className="cn-panel-content">
        <p style={{ color: "var(--cn-text-muted)", fontStyle: "italic", margin: "0 0 10px" }}>{desc}</p>
        <span style={{ display: "inline-block", background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 12 }}>PENDIENTE DE DESARROLLAR</span>
      </div>
    </div>
  </div>
);

const EmpleadoFicha = () => {
  const { pedirMotivo, modalMotivo } = useMotivoAnulacionFactura();   // [Ann 30-10-2026] desplegable único de motivos
  const navigate = useNavigate();
  const { isAdmin, sedeActiva } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnKey = searchParams.get("returnKey");
  const returnCtx = returnKey ? peekReturnContext(returnKey) : null;
  const handleVolver = () => {
    if (!returnKey) return;
    const ctx = consumeReturnContext(returnKey);
    if (ctx?.path) navigate(ctx.path);
    else navigate(-1);
  };
  const [id, setId] = React.useState(searchParams.get("id"));
  const isNuevo = searchParams.get("nuevo") === "true";
  const [client, setClient] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const fotoInputRef = React.useRef(null);
  const [error, setError] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("datos");
  const [historyFilter, setHistoryFilter] = React.useState("Todo");
  const [showEventoModal, setShowEventoModal] = React.useState(false);
  const [eventoForm, setEventoForm] = React.useState({
    tipo_evento: 'observacion_interna',
    fecha_evento: new Date().toISOString().split('T')[0],
    descripcion: '',
    comentario_interno: '',
    titulo_queja: '',
    comentarios_terapeutas: '',
  });
  const [isAlertsOpen, setIsAlertsOpen] = React.useState(false);
  const [comoNosConocio, setComoNosConocio] = React.useState("");

  // EMPLEADO · pestaña Documentación
  const [docsEmpleado, setDocsEmpleado] = React.useState([]);
  const [docTipo, setDocTipo] = React.useState("");
  const [docFile, setDocFile] = React.useState(null);
  const [subiendoDoc, setSubiendoDoc] = React.useState(false);

  // EMPLEADO · pestaña Incorporación (estado local; persistirá con el backend)
  const [incorp, setIncorp] = React.useState({
    fecha_incorporacion: "", fecha_alta_ss: "", prueba_inicio: "", prueba_fin: "",
    responsable: "", checklist: {},
    // Un trabajador puede incorporarse en VARIAS sedes a la vez → lista, no texto.
    sedes: [],
  });
  const toggleIncorp = (tid) => setIncorp((p) => ({ ...p, checklist: { ...p.checklist, [tid]: !p.checklist[tid] } }));

  // EMPLEADO · pestaña Contrato (estado local; persistirá con el backend / Laboral v2)
  const [contrato, setContrato] = React.useState({
    tipo_contrato: "", convenio: "", categoria_profesional: "", grupo_cotizacion: "",
    funcion: "", puesto: "", departamento: "", fecha_alta: "", fecha_fin: "",
    jornada: "completa", porcentaje_jornada: 100, horas_semanales: 40, num_pagas: 12, irpf_porcentaje: 0,
  });
  const setContr = (k, v) => setContrato((p) => {
    const next = { ...p, [k]: v };
    // % jornada CALCULADO desde Horas/semana (no editable); jornada se deriva.
    if (k === "horas_semanales") {
      const pct = calcPctJornada(v);
      next.porcentaje_jornada = pct;
      next.jornada = pct >= 100 ? "completa" : "parcial";
    }
    return next;
  });

  // EMPLEADO · pestaña Salario (estado local; persistirá con el backend / Laboral v2)
  const [salario, setSalario] = React.useState({
    bruto_anual: 0, num_pagas: 12, prorratea_pagas: true,
    complementos: [], pluses: [],
    irpf_porcentaje: 0, situacion_familiar: "", num_descendientes: 0,
    reparto: [],
  });
  const setSal = (k, v) => setSalario((p) => ({ ...p, [k]: v }));
  const addLineaSal = (campo) => setSalario((p) => ({ ...p, [campo]: [...p[campo], { concepto: "", importe: 0 }] }));
  const setLineaSal = (campo, i, k, v) => setSalario((p) => { const arr = [...p[campo]]; arr[i] = { ...arr[i], [k]: v }; return { ...p, [campo]: arr }; });
  const delLineaSal = (campo, i) => setSalario((p) => ({ ...p, [campo]: p[campo].filter((_, idx) => idx !== i) }));
  const addReparto = () => setSalario((p) => ({ ...p, reparto: [...p.reparto, { funcion: "", sede: "", porcentaje: 0, desde: "", hasta: "" }] }));
  const setRepartoF = (i, k, v) => setSalario((p) => { const arr = [...p.reparto]; arr[i] = { ...arr[i], [k]: v }; return { ...p, reparto: arr }; });
  const delReparto = (i) => setSalario((p) => ({ ...p, reparto: p.reparto.filter((_, idx) => idx !== i) }));

  // EMPLEADO · sedes reales del CRM (para desplegables · nada de texto libre en sede)
  const [sedesCrm, setSedesCrm] = React.useState([]);
  React.useEffect(() => {
    (async () => { try { setSedesCrm((await axios.get(`${API}/admin/sedes`)).data || []); } catch { setSedesCrm([]); } })();
  }, []);

  // EMPLEADO · pestaña Nóminas (solo lectura: las sube el agente de IA → laboral_nominas)
  const [nominas, setNominas] = React.useState([]);
  const [nominaSel, setNominaSel] = React.useState(null);
  const [anioNom, setAnioNom] = React.useState(String(new Date().getFullYear()));
  const cargarNominas = React.useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/laboral/v2/nominas`, { params: { profesional_id: id } });
      setNominas(Array.isArray(r.data) ? r.data : (r.data?.nominas || []));
    } catch { setNominas([]); }
  }, [id]);
  React.useEffect(() => { cargarNominas(); }, [cargarNominas]);

  // EMPLEADO · pestaña Jornada y horas (DATOS REALES: jornada de config_profesionales
  // ya viene en `client`; horas complementarias y resumen tienen endpoints vivos).
  const [resumenHoras, setResumenHoras] = React.useState(null);
  const [horasComp, setHorasComp] = React.useState([]);
  const cargarHorasEmpleado = React.useCallback(async () => {
    if (!id) return;
    try { setResumenHoras((await axios.get(`${API}/config/profesionales/${id}/resumen-horas`)).data || null); } catch { setResumenHoras(null); }
    try { setHorasComp((await axios.get(`${API}/config/profesionales/${id}/horas-complementarias`)).data || []); } catch { setHorasComp([]); }
  }, [id]);
  React.useEffect(() => { cargarHorasEmpleado(); }, [cargarHorasEmpleado]);
  const fmtMinEmp = (m) => { const v = Number(m || 0); const h = Math.floor(Math.abs(v) / 60); const r = Math.abs(v) % 60; return `${v < 0 ? "-" : ""}${h}h ${r}m`; };

  // EMPLEADO · jornada EDITABLE desde la ficha (la fuente es esta ficha, no el modal)
  const [jornadaEdit, setJornadaEdit] = React.useState(null);
  const [guardandoJornada, setGuardandoJornada] = React.useState(false);
  // Ann 2026-09-08: la jornada nueva rige DESDE una fecha (por defecto hoy); el pasado no se mueve.
  const [jornadaVigenteDesde, setJornadaVigenteDesde] = React.useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; });
  // «Hasta» OPCIONAL (Ann, 21-oct: «de tal fecha a tal fecha este horario y de tal
  // fecha a tal fecha este otro»). No se guarda como campo: al ponerlo se registran
  // DOS tramos — el nuevo desde «desde», y el que había ANTES vuelve a partir del día
  // siguiente a «hasta». Así el «hasta» siempre es coherente y no hay huecos.
  const [jornadaVigenteHasta, setJornadaVigenteHasta] = React.useState("");
  React.useEffect(() => {
    if (jornadaEdit === null && client && (client.id || client._id)) {
      setJornadaEdit(DIAS_SEMANA_EMP.map((dia) => {
        const ex = (client.jornada || []).find((j) => j.dia === dia);
        return { dia, activo: ex ? !!ex.activo : (dia !== "Sábado" && dia !== "Domingo"), franjas: ex?.franjas ? ex.franjas.map((f) => ({ ...f })) : [] };
      }));
    }
  }, [client, jornadaEdit]);
  const toggleDiaJ = (i) => setJornadaEdit((p) => p.map((d, idx) => idx === i ? { ...d, activo: !d.activo } : d));
  // Ann 29-10-2026: «es tedioso volver a hacerlo todo por un pequeño error, mejor que me permitas editar una franja».
  // EDITAR: carga ese tramo en el editor con su «Rige desde»; al guardar, el CRM SUSTITUYE el tramo de esa fecha.
  const editarTramoJ = (t) => {
    setJornadaEdit(DIAS_SEMANA_EMP.map((dia) => {
      const ex = (Array.isArray(t.jornada) ? t.jornada : []).find((j) => j && j.dia === dia);
      return { dia, activo: ex ? !!ex.activo : false, franjas: ex && Array.isArray(ex.franjas) ? ex.franjas.map((f) => ({ ...f })) : [] };
    }));
    if (t.desde) setJornadaVigenteDesde(t.desde);
    setJornadaVigenteHasta("");
    toast.info(t.desde ? `Editando el tramo que rige desde ${fechaEs(t.desde)}: corrige y pulsa «Guardar jornada» (sustituye ese tramo)` : "Editando la jornada: corrige y pulsa «Guardar jornada»");
    try { document.getElementById("jornada-editor")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) { /* nada */ }
  };
  // ELIMINAR: tramos de hoy o futuros; el pasado no se reescribe (Ann 30-10-2026: el de hoy también, caso Marina).
  const eliminarTramoJ = async (t) => {
    if (!t.desde || !t.eliminable) return;
    if (!window.confirm(`¿Eliminar el tramo programado desde ${fechaEs(t.desde)}? Volverá a regir el horario anterior a esa fecha.`)) return;
    try {
      const r = await axios.delete(`${API}/config/profesionales/${id}/jornada-historial/${t.desde}`);
      setClient((prev) => ({ ...prev, jornada_historial: r.data?.jornada_historial || [], jornada: r.data?.jornada || prev.jornada }));
      toast.success("Tramo eliminado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se ha podido eliminar el tramo");
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // JORNADAS POR FECHAS · «de tal fecha a tal fecha este horario» (Ann, 21-oct)
  // ════════════════════════════════════════════════════════════════════════
  // 🛡️ Nada de esto itera la jornada EN CRUDO: hay empleados antiguos cuyo
  // `jornada` no es un array o cuyas `franjas` vienen en otro formato, y eso
  // tumbaba el render entero (incidente del 20-oct).
  const diasDeJornada = (j) => (Array.isArray(j) ? j : []);
  const franjasDeDia = (d) => (d && Array.isArray(d.franjas) ? d.franjas : []);
  const minutosFranja = (f) => {
    const aMin = (h) => {
      const [hh, mm] = String(h || "").split(":").map(Number);
      return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
    };
    const ini = aMin(f && f.inicio), fin = aMin(f && f.fin);
    if (ini === null || fin === null) return null;
    return fin > ini ? fin - ini : null;
  };
  const horasTexto = (min) => {
    const h = Math.floor(min / 60), m = min % 60;
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")} min`;
  };
  const minutosDeJornada = (j) => {
    let total = 0;
    diasDeJornada(j).forEach((d) => {
      if (!d || d.activo === false) return;
      franjasDeDia(d).forEach((f) => { const m = minutosFranja(f); if (m !== null) total += m; });
    });
    return total;
  };
  const DIAS_ORDEN_J = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
  const DIAS_ABREV_J = { Lunes: "L", Martes: "M", "Miércoles": "X", Jueves: "J", Viernes: "V", "Sábado": "S", Domingo: "D" };
  const textoJornada = (j) => {
    const porDia = {};
    diasDeJornada(j).forEach((d) => {
      if (!d || d.activo === false) return;
      const fr = franjasDeDia(d).filter((f) => minutosFranja(f) !== null)
        .map((f) => `${f.inicio}–${f.fin}`).join(", ");
      if (fr) porDia[d.dia] = fr;
    });
    const grupos = [];
    DIAS_ORDEN_J.forEach((dia) => {
      const fr = porDia[dia];
      if (!fr) return;
      const ult = grupos[grupos.length - 1];
      if (ult && ult.fr === fr && DIAS_ORDEN_J.indexOf(dia) === DIAS_ORDEN_J.indexOf(ult.hasta) + 1) {
        ult.hasta = dia;
      } else {
        grupos.push({ desde: dia, hasta: dia, fr });
      }
    });
    if (grupos.length === 0) return "Sin días laborables";
    return grupos.map((g) => {
      const a = DIAS_ABREV_J[g.desde], b = DIAS_ABREV_J[g.hasta];
      const salto = DIAS_ORDEN_J.indexOf(g.hasta) - DIAS_ORDEN_J.indexOf(g.desde);
      const dias = salto === 0 ? a : (salto === 1 ? `${a},${b}` : `${a}-${b}`);
      return `${dias} ${g.fr}`;
    }).join(" · ");
  };
  // 🛡️ Una fecha mala en el historial (el front no valida lo que hay en BD) hacía
  // que toISOString() lanzase RangeError AL PINTAR → se caía la ficha entera.
  // Aquí se devuelve null y la fila sale como «indefinido» en vez de tumbar nada.
  const sumaDias = (iso, n) => {
    if (!iso) return null;
    const d = new Date(`${iso}T12:00:00`);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + n);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  };
  const fechaEs = (iso) => (iso ? iso.split("-").reverse().join("/") : null);

  // Los tramos que se PINTAN: salen del historial que el backend ya guarda.
  // El «hasta» no se almacena: es el día antes de que empiece el siguiente.
  const tramosJornada = React.useMemo(() => {
    const hist = [...(Array.isArray(client.jornada_historial) ? client.jornada_historial : [])]
      .filter((h) => h && h.vigente_desde)
      .sort((a, b) => String(a.vigente_desde).localeCompare(String(b.vigente_desde)));
    const hoy = new Date().toISOString().slice(0, 10);
    const out = [];
    if (hist.length === 0) {
      out.push({ desde: null, hasta: null, jornada: diasDeJornada(client.jornada) });
    } else {
      if (diasDeJornada(hist[0].anterior).length > 0) {
        out.push({ desde: null, hasta: sumaDias(hist[0].vigente_desde, -1), jornada: diasDeJornada(hist[0].anterior) });
      }
      hist.forEach((h, i) => {
        out.push({
          desde: h.vigente_desde,
          hasta: i + 1 < hist.length ? sumaDias(hist[i + 1].vigente_desde, -1) : null,
          jornada: diasDeJornada(h.jornada),
          registrado_por: h.registrado_por, registrado_en: h.registrado_en,
        });
      });
    }
    return out.map((t) => ({
      ...t,
      minutos: minutosDeJornada(t.jornada),
      texto: textoJornada(t.jornada),
      vigenteHoy: (!t.desde || t.desde <= hoy) && (!t.hasta || t.hasta >= hoy),
      eliminable: !!t.desde && t.desde >= hoy,   // hoy o futuro (Ann 30-10-2026, Marina)
    }));
  }, [client.jornada_historial, client.jornada]);
  const addFranjaJ = (i) => setJornadaEdit((p) => p.map((d, idx) => idx === i ? { ...d, franjas: [...d.franjas, { inicio: "09:00", fin: "14:00" }] } : d));
  const setFranjaJ = (i, fi, k, v) => setJornadaEdit((p) => p.map((d, idx) => idx === i ? { ...d, franjas: d.franjas.map((f, j) => j === fi ? { ...f, [k]: v } : f) } : d));
  const delFranjaJ = (i, fi) => setJornadaEdit((p) => p.map((d, idx) => idx === i ? { ...d, franjas: d.franjas.filter((_, j) => j !== fi) } : d));
  const guardarJornada = async () => {
    setGuardandoJornada(true);
    try {
      await axios.put(`${API}/config/profesionales/${id}`, { jornada: jornadaEdit, jornada_vigente_desde: jornadaVigenteDesde });
      // Si se ha puesto un «hasta», el horario que regía ANTES vuelve al día siguiente.
      if (jornadaVigenteHasta && jornadaVigenteHasta >= jornadaVigenteDesde) {
        const previo = tramosJornada.filter((t) => !t.desde || t.desde < jornadaVigenteDesde).pop();
        const vuelta = sumaDias(jornadaVigenteHasta, 1);
        // Si la fecha no es válida NO se manda: el backend trataría un valor vacío
        // como HOY y activaría la «vuelta» hoy mismo, pisando el horario temporal.
        if (!vuelta) {
          toast.error("La fecha de «hasta» no es válida: no se ha programado la vuelta al horario anterior.");
        } else {
          await axios.put(`${API}/config/profesionales/${id}`, {
            jornada: diasDeJornada(previo && previo.jornada),
            jornada_vigente_desde: vuelta,
          });
        }
      }
      const hoyStr = new Date().toISOString().slice(0, 10);
      if (jornadaVigenteDesde <= hoyStr) setClient((prev) => ({ ...prev, jornada: jornadaEdit }));
      try {
        const r = await axios.get(`${API}/config/profesionales`);
        const doc = (r.data || []).find((x) => String(x.id) === String(id) || String(x._id) === String(id));
        if (doc) setClient((prev) => ({ ...prev, jornada_historial: doc.jornada_historial || [], jornada: doc.jornada || prev.jornada }));
      } catch { /* la tabla se refrescará al recargar la ficha */ }
      toast.success(
        jornadaVigenteHasta
          ? `Jornada guardada · del ${jornadaVigenteDesde} al ${jornadaVigenteHasta}`
          : (jornadaVigenteDesde <= hoyStr
              ? `Jornada guardada · rige desde ${jornadaVigenteDesde}`
              : `Jornada programada · regirá desde ${jornadaVigenteDesde} (hasta entonces sigue la actual)`));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar la jornada");
    } finally { setGuardandoJornada(false); }
  };

  // EMPLEADO · GUARDADO de la ficha (autopista):
  //  · Cuadrante  → PUT /config/profesionales/{id}
  //  · RRHH       → PUT /laboral/v2/empleados/por-profesional/{id}  (laboral_empleados)
  const guardarFicha = async () => {
    // Gate: los tramos VIGENTES (sin "Hasta") del Reparto del coste deben sumar 100%.
    const tramosVigentes = (salario.reparto || []).filter((r) => r.sede && !r.hasta);
    if (tramosVigentes.length > 0) {
      const sumaVig = tramosVigentes.reduce((a, r) => a + (Number(r.porcentaje) || 0), 0);
      if (Math.abs(sumaVig - 100) > 0.01) {
        toast.error(`El reparto del coste vigente debe sumar 100% (ahora suma ${sumaVig}%). Revisa los tramos sin fecha "Hasta".`);
        return;
      }
    }
    setSavingGlobal(true);
    const apellidos = `${client.primer_apellido || ""} ${client.segundo_apellido || ""}`.trim() || (client.apellidos || "");
    const numOrNull = (x) => (x === "" || x === undefined || x === null ? null : Number(x));
    let errs = [];
    // 1) Cuadrante → config_profesionales (vía oficial)
    try {
      await axios.put(`${API}/config/profesionales/${id}`, {
        nombre: client.nombre, apellidos,
        email: client.email, telefono: client.telefono,
        // Domicilio NO va al cuadrante: la fuente de verdad es laboral_empleados (RRHH).
        // El campo direccion de config_profesionales se deja como esté (no lo pisamos).
        color: client.color, profesion_ids: client.profesion_ids, sede: client.sede,
        es_profesional_salud: client.es_profesional_salud, numero_colegiado: client.numero_colegiado,
        es_autonomo: client.es_autonomo, mostrar_en_vista_mes: client.mostrar_en_vista_mes,
        no_aparece_cuadrante: client.no_aparece_cuadrante, es_sustituto: client.es_sustituto,
        activo: client.activo !== false,
        // Rango de disponibilidad (cuadrante). "" → null para no fijar límite.
        disponibilidad_desde: client.disponibilidad_desde || null,
        disponibilidad_hasta: client.disponibilidad_hasta || null,
        // F2 · Clasificación laboral / Cuadro de Mando. tipo_relacion deriva
        // es_autonomo en el backend; funcion_contable alimenta la P&L (B0).
        tipo_relacion: client.tipo_relacion || undefined,
        funcion_contable: client.funcion_contable !== undefined ? client.funcion_contable : undefined,
        incluir_en_laboral_v2: client.incluir_en_laboral_v2,
        incluir_en_cuadro_mando: client.incluir_en_cuadro_mando,
        estado_revision: client.estado_revision || undefined,
        notas_ann: client.notas_ann !== undefined ? client.notas_ann : undefined,
        jornada: jornadaEdit || client.jornada,
      });
    } catch (e) { errs.push(`Cuadrante: ${e.response?.data?.detail || e.message}`); }
    // 2) RRHH → laboral_empleados (por profesional) · autopista v2
    try {
      await axios.put(`${API}/laboral/v2/empleados/por-profesional/${id}`, {
        documento: client.dni || null, naf: client.naf || null, iban: client.iban || null,
        tipo_documento: client.tipo_documento || null,
        fecha_nacimiento: client.fecha_nacimiento || null, sexo: client.sexo || null,
        // Domicilio → laboral_empleados (RRHH es la fuente de verdad; lo rellena la nómina).
        direccion: client.direccion || null, numero: client.numero || null, piso: client.piso || null,
        codigo_postal: client.codigo_postal || null, ciudad: client.ciudad || null,
        provincia: client.provincia || null, pais: client.pais || null, barrio: client.barrio || null,
        // Contacto · email corporativo de la empresa (el personal va al cuadrante).
        email_corporativo: client.email_corporativo || null,
        // Contactos de emergencia · se manda SIEMPRE la lista (aunque quede vacía)
        // para que quitar el último contacto se guarde de verdad.
        contactos_emergencia: (client.contactos_emergencia || [])
          .filter((c) => (c.nombre || "").trim() || (c.telefono || "").trim())
          .map((c) => ({ nombre: (c.nombre || "").trim(), perfil: (c.perfil || "").trim(), telefono: (c.telefono || "").trim() })),
        // Perfil profesional · estudios y titulaciones (el nº de colegiado va al cuadrante).
        nivel_estudios: client.nivel_estudios || null,
        titulaciones: (client.titulaciones || [])
          .filter((t) => (t.titulo || "").trim() || t.documento_id)
          .map((t) => ({ titulo: (t.titulo || "").trim(), documento_id: t.documento_id || null })),
        colegiatura_documento_id: client.colegiatura_documento_id || null,
        // Acuerdos (pestaña Incorporación).
        estado_profesional: client.estado_profesional || null,
        pluriempleo_empresa: client.pluriempleo_empresa || null,
        pluriempleo_sector: client.pluriempleo_sector || null,
        pluriempleo_jornada: client.pluriempleo_jornada || null,
        pluriempleo_horario: client.pluriempleo_horario || null,
        competencia_desleal: !!client.competencia_desleal,
        contrato_formacion: !!client.contrato_formacion,
        contrato_formacion_fin: client.contrato_formacion_fin || null,
        nivel_convenio: client.nivel_convenio || null,
        // Grupos de documentos obligatorios (pestaña Documentación).
        requiere_docs_clases_online: !!client.requiere_docs_clases_online,
        requiere_docs_fisioterapia: !!client.requiere_docs_fisioterapia,
        // Observaciones de la ficha (generales + internas solo-administración).
        observaciones: client.observaciones || null, comentarios_confidenciales: client.comentarios_confidenciales || null,
        tipo_contrato: contrato.tipo_contrato || null, convenio: contrato.convenio || null,
        categoria_profesional: contrato.categoria_profesional || null, grupo_cotizacion: contrato.grupo_cotizacion || null,
        funcion: contrato.funcion || null, puesto: contrato.puesto || null, departamento: contrato.departamento || null,
        fecha_alta: contrato.fecha_alta || null, fecha_fin: contrato.fecha_fin || null,
        jornada: contrato.jornada || null, porcentaje_jornada: numOrNull(contrato.porcentaje_jornada),
        horas_semanales: numOrNull(contrato.horas_semanales), num_pagas: numOrNull(contrato.num_pagas),
        irpf_porcentaje: numOrNull(salario.irpf_porcentaje),
        salario_bruto_anual: numOrNull(salario.bruto_anual), prorratea_pagas: salario.prorratea_pagas,
        salario_complementos: (salario.complementos || []).map((c) => ({ concepto: c.concepto || "", importe: Number(c.importe) || 0 })),
        salario_pluses: (salario.pluses || []).map((c) => ({ concepto: c.concepto || "", importe: Number(c.importe) || 0 })),
        situacion_familiar: salario.situacion_familiar || null, num_descendientes: numOrNull(salario.num_descendientes),
        imputacion_sedes: (salario.reparto || []).filter((r) => r.sede).map((r) => ({
          sede: r.sede, porcentaje: Number(r.porcentaje) || 0, funcion: r.funcion || null,
          vigente_desde: r.desde || null, vigente_hasta: r.hasta || null,
        })),
        incorporacion: incorp,
      });
    } catch (e) {
      if (e.response?.status === 404) errs.push("RRHH: Laboral v2 está apagado (se guardará al encenderlo en el deploy dedicado).");
      else errs.push(`RRHH: ${e.response?.data?.detail || e.message}`);
    }
    setSavingGlobal(false);
    if (errs.length === 0) { toast.success("Ficha guardada"); setHasUnsavedChanges(false); }
    else errs.forEach((m) => toast.error(m));
  };

  // EMPLEADO · cargar RRHH guardado (laboral_empleados) y rellenar los editores.
  // Se ejecuta UNA vez, tras cargar el profesional (evita pisar ediciones del usuario).
  const rrhhLoadedRef = React.useRef(false);
  const cargarEmpleadoRRHH = async () => {
    try {
      const r = await axios.get(`${API}/laboral/v2/empleados/por-profesional/${id}`);
      const e = r.data;
      if (!e || !e.exists) return;
      setClient((prev) => ({ ...prev,
        dni: prev.dni || e.documento || "", naf: prev.naf || e.naf || "", iban: prev.iban || e.iban || "",
        tipo_documento: prev.tipo_documento || e.tipo_documento || "",
        fecha_nacimiento: prev.fecha_nacimiento || e.fecha_nacimiento || "", sexo: prev.sexo || e.sexo || "",
        // Domicilio: RRHH es la fuente de verdad → gana el valor de laboral_empleados;
        // si está vacío, cae al del cuadrante (migración suave de direcciones antiguas).
        direccion: e.direccion || prev.direccion || "", numero: e.numero || prev.numero || "", piso: e.piso || prev.piso || "",
        codigo_postal: e.codigo_postal || prev.codigo_postal || "", ciudad: e.ciudad || prev.ciudad || "",
        provincia: e.provincia || prev.provincia || "", pais: e.pais || prev.pais || "", barrio: e.barrio || prev.barrio || "",
        email_corporativo: prev.email_corporativo || e.email_corporativo || "",
        contactos_emergencia: (prev.contactos_emergencia && prev.contactos_emergencia.length)
          ? prev.contactos_emergencia : (e.contactos_emergencia || []),
        nivel_estudios: prev.nivel_estudios || e.nivel_estudios || "",
        titulaciones: (prev.titulaciones && prev.titulaciones.length)
          ? prev.titulaciones : (e.titulaciones || []),
        colegiatura_documento_id: prev.colegiatura_documento_id || e.colegiatura_documento_id || null,
        estado_profesional: prev.estado_profesional || e.estado_profesional || "",
        pluriempleo_empresa: prev.pluriempleo_empresa || e.pluriempleo_empresa || "",
        pluriempleo_sector: prev.pluriempleo_sector || e.pluriempleo_sector || "",
        pluriempleo_jornada: prev.pluriempleo_jornada || e.pluriempleo_jornada || "",
        pluriempleo_horario: prev.pluriempleo_horario || e.pluriempleo_horario || "",
        competencia_desleal: prev.competencia_desleal ?? e.competencia_desleal ?? false,
        contrato_formacion: prev.contrato_formacion ?? e.contrato_formacion ?? false,
        contrato_formacion_fin: prev.contrato_formacion_fin || e.contrato_formacion_fin || "",
        nivel_convenio: prev.nivel_convenio || e.nivel_convenio || "",
        requiere_docs_clases_online: prev.requiere_docs_clases_online ?? e.requiere_docs_clases_online ?? false,
        requiere_docs_fisioterapia: prev.requiere_docs_fisioterapia ?? e.requiere_docs_fisioterapia ?? false,
        observaciones: e.observaciones || prev.observaciones || "", comentarios_confidenciales: e.comentarios_confidenciales || prev.comentarios_confidenciales || "",
      }));
      setContrato((p) => ({ ...p,
        tipo_contrato: e.tipo_contrato || "", convenio: e.convenio || "", categoria_profesional: e.categoria_profesional || "",
        grupo_cotizacion: e.grupo_cotizacion || "", funcion: e.funcion || "", puesto: e.puesto || "", departamento: e.departamento || "",
        fecha_alta: e.fecha_alta || "", fecha_fin: e.fecha_fin || "", jornada: e.jornada || "completa",
        porcentaje_jornada: e.porcentaje_jornada ?? 100, horas_semanales: e.horas_semanales ?? 40, num_pagas: e.num_pagas ?? 12, irpf_porcentaje: e.irpf_porcentaje ?? 0,
      }));
      setSalario((p) => ({ ...p,
        bruto_anual: e.salario_bruto_anual ?? 0, num_pagas: e.num_pagas ?? 12, prorratea_pagas: e.prorratea_pagas ?? true,
        complementos: e.salario_complementos || [], pluses: e.salario_pluses || [],
        irpf_porcentaje: e.irpf_porcentaje ?? 0, situacion_familiar: e.situacion_familiar || "", num_descendientes: e.num_descendientes ?? 0,
        reparto: (e.imputacion_sedes || []).map((t) => ({ funcion: t.funcion || "", sede: t.sede || "", porcentaje: t.porcentaje ?? 0, desde: t.vigente_desde || "", hasta: t.vigente_hasta || "" })),
      }));
      if (e.incorporacion) setIncorp((p) => ({ ...p, ...e.incorporacion, checklist: e.incorporacion.checklist || {} }));
    } catch { /* Laboral v2 apagado o sin empleado → editores en blanco */ }
  };
  React.useEffect(() => {
    if (rrhhLoadedRef.current) return;
    if (!client || !(client.id || client._id)) return;
    rrhhLoadedRef.current = true;
    cargarEmpleadoRRHH();
  }, [client]);

  // EMPLEADO · documentos personales (titulación, colegiatura, contrato…).
  // Autopista: laboral_documentos con tipo="empleado_doc", cifrados en reposo.
  const cargarDocsEmpleado = React.useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/laboral/v2/empleados-documentos/por-profesional/${id}`);
      setDocsEmpleado(Array.isArray(r.data) ? r.data : (r.data?.documentos || []));
    } catch { setDocsEmpleado([]); }
  }, [id]);

  // Sube un documento del trabajador y devuelve sus metadatos (con _id) para poder
  // engancharlo a una titulación / a la colegiatura. Lanza si falla (lo capta quien llama).
  const subirDocumentoEmpleado = async (file, tipoDocumento, descripcion) => {
    const fd = new FormData();
    fd.append("archivo", file);
    fd.append("tipo_documento", tipoDocumento);
    if (descripcion) fd.append("descripcion", descripcion);
    const r = await axios.post(
      `${API}/laboral/v2/empleados-documentos/por-profesional/${id}`, fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    cargarDocsEmpleado();
    return r.data;
  };
  const urlDescargaDoc = (docId) => `${API}/laboral/v2/empleados-documentos/${docId}/descargar`;

  React.useEffect(() => { cargarDocsEmpleado(); }, [cargarDocsEmpleado]);

  const handleSubirDocEmpleado = async () => {
    if (!docFile) { toast.error("Selecciona un archivo"); return; }
    if (!docTipo) { toast.error("Marca el tipo de documento"); return; }
    setSubiendoDoc(true);
    try {
      await subirDocumentoEmpleado(docFile, docTipo);
      toast.success("Documento subido");
      setDocFile(null); setDocTipo("");
    } catch (e) {
      toast.error(parseErrorDetail(e, "Error al subir el documento"));
    } finally { setSubiendoDoc(false); }
  };
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  // Código postal → población/provincia (tabla local del CRM; solo al escribir, nunca al abrir la ficha)
  const cpEmpleado = useCodigoPostal();

  // Baja definitiva (botón junto a fecha de alta)
  const [showBajaDefinitivaModal, setShowBajaDefinitivaModal] = React.useState(false);
  const [bajaDefForm, setBajaDefForm] = React.useState({
    fecha_baja: new Date().toISOString().split("T")[0],
    motivo: "",
    comentario: "",
    // PR Ann 2026-05-20 · clasificacion baja CRM
    // "normal" (default seguro): devueltos NO se anulan (deuda recobrable)
    // "incobrable": cliente no va a pagar
    // "devolucion_no_recobrada": cliente devolvió y no recuperaremos
    clasificacion_baja: "normal",
  });
  const [bajaDefProcessing, setBajaDefProcessing] = React.useState(false);

  // Cambio de SEDE de clases fijas · autopista única (Ann 2026-05-20).
  // Modal dedicado: NO es baja, es operativa propia. Cierra etapa actual
  // con motivo_cierre='cambio_sede' y abre etapa nueva exenta de CI día 1
  // mes siguiente. Llama POST /admin/clients/{id}/clases-fijas/cambiar-sede.
  // Modal completo (Ann 2026-05-20 · v2): permite seleccionar también
  // cuota nueva y turnos nuevos en la misma operación. Si se dejan vacíos
  // se heredan los de la etapa actual.
  const [mostrarModalCambioSedeCF, setMostrarModalCambioSedeCF] = React.useState(false);
  const [cambioSedeCFForm, setCambioSedeCFForm] = React.useState({
    nueva_sede: "",
    nuevo_cuota_id: "",       // "" = heredar cuota actual
    nuevo_turno_fijo: [],     // [] = heredar turno actual
    motivo: "",
    comentario: "",
  });
  const [cambioSedeCFProcessing, setCambioSedeCFProcessing] = React.useState(false);
  const [cambioSedeCFError, setCambioSedeCFError] = React.useState(null);

  // Reinscribir cliente desactivado (solo a nivel cliente, NO toca servicios).
  // Llama a POST /admin/clients/{id}/reinscribir-cliente.
  // Después de reinscribir, el admin gestiona cada servicio por separado en la pestaña Inscripciones.
  const [showReinscribirModal, setShowReinscribirModal] = React.useState(false);
  const [reinscribirProcessing, setReinscribirProcessing] = React.useState(false);
  const [altaClinicaProcessing, setAltaClinicaProcessing] = React.useState(false);
  const [altaClinicaError, setAltaClinicaError] = React.useState(null);
  // PR-FASE-A-3 · estados de modales baja/reactivación clínica
  const [bajaClinicaModalOpen, setBajaClinicaModalOpen] = React.useState(false);
  const [reactivarClinicaModalOpen, setReactivarClinicaModalOpen] = React.useState(false);
  // PR-FASE-A-3 · trigger de refresco · al incrementar, useEffect [id, clinicaRefreshTick]
  // dispara fetchClientData() de nuevo. Permite refrescar ficha tras baja/reactivación
  // sin extraer fetchClientData del useEffect (cambio mínimamente invasivo).
  const [clinicaRefreshTick, setClinicaRefreshTick] = React.useState(0);
  const refreshClientData = React.useCallback(() => {
    setClinicaRefreshTick((t) => t + 1);
  }, []);

  const [expandedBonoId, setExpandedBonoId] = React.useState(null);
  const [bonoSesiones, setBonoSesiones] = React.useState({});

  const [isAnotarCambioOpen, setIsAnotarCambioOpen] = React.useState(false);
  const [cambioFormData, setCambioFormData] = React.useState({
    fecha_evento: new Date().toISOString().split("T")[0],
    tipo_evento: "observacion_interna",
    descripcion: "",
    comentario_interno: "",
  });
  const [submittingCambio, setSubmittingCambio] = React.useState(false);

  // ── Clases Fijas: baja explícita + indicador cobros ──
  const [bajaClasesLoading, setBajaClasesLoading] = React.useState(false);
  const [cobrosIndicador, setCobrosIndicador] = React.useState(null); // { encontrados, esperados }
  const [generandoCobros, setGenerandoCobros] = React.useState(false);
  // Formulario baja Clases Fijas (sustituye al confirm() nativo).
  //  - bajaModal: null cuando no abierto; objeto { fecha, motivo_id, comentario } cuando abierto.
  //  - motivosBajaCatalogo: cargado desde /admin/motivos-baja (seed inicial 9 motivos).
  const [bajaModal, setBajaModal] = React.useState(null);
  const [motivosBajaCatalogo, setMotivosBajaCatalogo] = React.useState([]);
  // BLOQUE 6A · Estado canónico (Ann 2026-05-07):
  // Snapshot del resultado de `calcular_estado_operativo_cliente` que
  // viaja en la respuesta de /admin/clients/{id}/ficha. La cabecera y
  // las acciones (botón "Dar de baja") leen de aquí en lugar de derivar
  // de campos sueltos del documento `users`. Ver caso testigo Montserrat.
  const [estadoCanonico, setEstadoCanonico] = React.useState(null);
  // FASE D1 · Predeploy Bloque 4 (Ann 2026-05-06):
  // Snapshot del valor de inscrito_clases_fijas al cargar la ficha. Se usa
  // para detectar la transición true→false en "Guardar cambios" y bloquearla
  // en frontend antes de llamar al backend (evita 409 LEGACY_PATH_BLOCKED_BAJA_CLASES
  // y obliga a usar el botón canónico de baja).
  const originalInscritoClasesFijasRef = React.useRef(null);

  // ─── Sub-fase 5.3.1 · Skeleton router (Ann 2026-05-07) ───
  // Snapshots de los 3 campos críticos de Clases Fijas + SEPA básicos al
  // cargar la ficha. Se usarán en Sub-fase 5.3.2 para decidir qué endpoints
  // atómicos llamar (cambiar-forma-cobro / cambiar-cuota / cambiar-horario)
  // en lugar de un único PUT update_client.
  // ⚠️ EN 5.3.1 ESTOS REFS NO SE LEEN DESDE handleGlobalSave todavía.
  //    El router (frontend/src/utils/clientNewProfileRouting.js) sólo se
  //    ejercita por tests unitarios.
  const originalFormaPagoRef = React.useRef(null);
  const originalCuotaIdRef = React.useRef(null);
  const originalTurnoFijoRef = React.useRef(null);  // string serializado canónico
  const originalSepaSnapshotRef = React.useRef(null);  // {sepa, pagador}

  // ─── Sub-fase 5.3.2-B · refs adicionales para el router conectado ───
  // originalSedeRef: necesario para detectar cambio de sede con cliente
  //                  inscrito (router devuelve bloqueo).
  // originalPersonalSnapshotRef: snapshot de los campos personales (whitelist)
  //                  + excluidos pendientes para detectar deltas en PUT light.
  const originalSedeRef = React.useRef(null);
  const originalPersonalSnapshotRef = React.useRef(null);

  // Anti-doble-click del botón Guardar · cubre todo el ciclo del router.
  const [savingGlobal, setSavingGlobal] = React.useState(false);

  const fetchCobrosIndicador = React.useCallback(async () => {
    if (!id) return;
    try {
      const anyo = new Date().getFullYear();
      const res = await axios.get(`${API}/cobros/rejilla-anual/${id}`, { params: { anyo } });
      const subs = res.data?.suscripciones || [];
      if (subs.length > 0) {
        const meses = subs[0].meses || [];
        const esperados = meses.length;
        const encontrados = meses.filter(m => m.cobro_id).length;
        setCobrosIndicador({ encontrados, esperados, anyo });
      } else {
        setCobrosIndicador({ encontrados: 0, esperados: 0, anyo });
      }
    } catch { setCobrosIndicador(null); }
  }, [id]);

  // (useEffect de cobrosIndicador movido después de declarar suscripcionActiva)

  // Abre el formulario de baja. Ya NO usa confirm() nativo: muestra un modal
  // con fecha, motivo (del catálogo motivos_baja) y comentario obligatorio ≥10 chars.
  const handleBajaClasesFijas = async () => {
    // Cargar catálogo en lazy si aún no está.
    if (motivosBajaCatalogo.length === 0) {
      try {
        const res = await axios.get(`${API}/admin/motivos-baja`, { params: { estado: "activo", limit: 100 } });
        const activos = (res.data?.motivos || []).filter(m => m.activo !== false);
        // Ordenar por campo "orden" si viene; fallback alfabético.
        activos.sort((a, b) => {
          if (a.orden != null && b.orden != null) return a.orden - b.orden;
          return (a.nombre || "").localeCompare(b.nombre || "", "es");
        });
        setMotivosBajaCatalogo(activos);
      } catch (err) {
        toast.error("No se pudo cargar el catálogo de motivos de baja");
        return;
      }
    }
    setBajaModal({
      fecha: new Date().toISOString().split("T")[0],
      motivo_id: "",
      comentario: "",
    });
  };

  // FASE D1 · Predeploy Bloque 4 (Ann 2026-05-06):
  // Antes este flujo usaba PUT /admin/clients/{id} con inscrito_clases_fijas:false
  // como camino legacy de baja. Tras el PR #124 ese camino devuelve 409
  // LEGACY_PATH_BLOCKED_BAJA_CLASES. Ahora usamos el endpoint canónico
  // POST /admin/clients/{id}/desactivar (cascada coherente, audit_log, post-check)
  // y un PUT secundario únicamente con comentario_baja + motivos_baja
  // (campos que NO disparan ningún bloqueo legacy). Patrón espejo del flujo
  // canónico de ClientsTab.jsx > handleDesactivar.
  const handleConfirmarBajaClases = async () => {
    if (!bajaModal) return;
    const { fecha, motivo_id, comentario } = bajaModal;
    if (!fecha) { toast.error("Indica la fecha de baja"); return; }
    if (!motivo_id) { toast.error("Selecciona el motivo de baja"); return; }
    const comTrim = (comentario || "").trim();
    if (comTrim.length < 10) { toast.error("El comentario debe tener al menos 10 caracteres"); return; }
    setBajaClasesLoading(true);
    try {
      // mes_causa_baja: derivado de la fecha de baja efectiva (YYYY-MM).
      const mesCausaBaja = (fecha || "").slice(0, 7) || null;
      // Paso 1 · canónico /desactivar (motivo='baja' aplica cascada coherente).
      // FASE 2 (Ann 2026-05-07): enviar fecha_baja efectiva del modal para que
      // la atómica use la fecha real seleccionada (no now()).
      await axios.post(`${API}/admin/clients/${id}/desactivar`, {
        motivo: "baja",
        baja_con_deuda: false,
        mensaje_baja_deuda: null,
        cuota_incidencia: null,
        fecha_fin_prevista_is: null,
        fecha_baja: fecha,
        fecha_comunicacion_baja: null,
        mes_causa_baja: mesCausaBaja,
      });
      // Paso 2 · PUT secundario solo con campos no-disparadores · comentario + motivos.
      await axios.put(`${API}/admin/clients/${id}`, {
        comentario_baja: comTrim,
        motivos_baja: [motivo_id],
      });
      setClient(prev => ({
        ...prev,
        estado_cliente: "desactivado",
        inscrito_clases_fijas: false,
        estado_clases: "baja",
        fecha_baja_clases: fecha,
        motivos_baja: [motivo_id],
        comentario_baja: comTrim,
      }));
      setCobrosIndicador(null);
      setBajaModal(null);
      toast.success("Baja de Clases Fijas ejecutada. Suscripción cerrada y cobros futuros anulados.");
    } catch (err) {
      toast.error(parseErrorDetail(err, "Error al dar de baja"));
    } finally {
      setBajaClasesLoading(false);
    }
  };

  const handleGenerarCobrosRestantes = async () => {
    if (!cobrosIndicador) return;
    setGenerandoCobros(true);
    try {
      // Buscar suscripción activa del cliente
      const susRes = await axios.get(`${API}/suscripciones/${id}`);
      const susActiva = (susRes.data || []).find(s => s.estado === "activa");
      if (!susActiva) { toast.error("No se encontró suscripción activa"); return; }
      const susId = susActiva.id || susActiva._id;
      await axios.post(`${API}/suscripciones/${susId}/generar-cobros-anuales`, null, {
        params: { anyo: cobrosIndicador.anyo }
      });
      toast.success("Cobros restantes generados");
      await fetchCobrosIndicador();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error generando cobros");
    } finally {
      setGenerandoCobros(false);
    }
  };

  // ── Incidencia: activar/cerrar ──
  const [incidenciaUI, setIncidenciaUI] = React.useState(false); // mostrar formulario de activar
  const [cierreIncUI, setCierreIncUI] = React.useState(false); // mostrar formulario de cerrar
  // PR-IS-modal-cierre (Ann 2026-05-22) · motivo y comentario al cerrar IS.
  // Antes el frontend enviaba `motivo_cierre: "recuperacion"` hardcoded y no
  // enviaba `comentario`. El backend ya los soportaba pero la UI no los pedía.
  const [cierreMotivo, setCierreMotivo] = React.useState("recuperacion");
  const [cierreComentario, setCierreComentario] = React.useState("");
  // PR-IS-junio (Ann 2026-05-22) · "Editar IS" eliminado (llamaba PUT 410).
  // Estados editarIncUI/editIncForm/setEditIncForm/setEditarIncUI retirados.
  // Bloque 1 — Inscripción a clases fijas (autopista nueva).
  // Sustituye al Modal Matrícula viejo (POST directo a /api/cobros, fuente del bug huérfano).
  const [showInscripcionModal, setShowInscripcionModal] = React.useState(false);
  // PR-IS-EDIT-3 (Ann 2026-05-24) · modal "Editar fechas IS"
  const [showEditarFechasIS, setShowEditarFechasIS] = React.useState(false);
  const [cierreIncFechaFin, setCierreIncFechaFin] = React.useState(new Date().toISOString().split("T")[0]);
  const [incidenciaLoading, setIncidenciaLoading] = React.useState(false);
  const [incFechaInicio, setIncFechaInicio] = React.useState(new Date().toISOString().split("T")[0]);
  const [incFechaFin, setIncFechaFin] = React.useState("");
  const [tiposIncidencia, setTiposIncidencia] = React.useState([]);
  const [incTipoId, setIncTipoId] = React.useState("");
  // PR-IS-junio (Ann 2026-05-22) · motivo obligatorio + comentario.
  // Lista cerrada espejo de backend MOTIVOS_VALIDOS. Si motivo='otro',
  // el comentario es obligatorio.
  const [incMotivo, setIncMotivo] = React.useState("");
  const [incComentario, setIncComentario] = React.useState("");
  const [incCheckboxConfirmado, setIncCheckboxConfirmado] = React.useState(false);
  const [suscripcionActiva, setSuscripcionActiva] = React.useState(null);

  // PR-IS-junio (Ann 2026-05-22) · lista cerrada de motivos del alta IS.
  // Espejo del set MOTIVOS_VALIDOS en services/abrir_incidencia_salud.py.
  // "otro" exige comentario obligatorio (validación frontend + backend).
  const INC_MOTIVOS = [
    { value: "lesion",       label: "Lesión" },
    { value: "operacion",    label: "Operación" },
    { value: "recuperacion", label: "Recuperación" },
    { value: "embarazo",     label: "Embarazo" },
    { value: "enfermedad",   label: "Enfermedad" },
    { value: "otro",         label: "Otro (requiere comentario)" },
  ];

  // PR-IS-modal-cierre (Ann 2026-05-22) · lista cerrada de motivos de cierre IS.
  // Espejo de MOTIVOS_CIERRE_VALIDOS en services/cerrar_incidencia_salud.py.
  const MOTIVOS_CIERRE = [
    { value: "recuperacion", label: "Recuperación" },
    { value: "baja_servicio", label: "Baja de servicio" },
    { value: "cambio_tipo",  label: "Cambio de tipo" },
    { value: "manual",       label: "Cierre manual" },
    { value: "baja_cliente", label: "Baja del cliente" },
  ];

  // Cargar tipos de incidencia y suscripción activa
  const fetchIncidenciaData = React.useCallback(async () => {
    if (!id) return;
    try {
      const susRes = await axios.get(`${API}/suscripciones/${id}`);
      const susList = susRes?.data || [];
      // Norma Ann 2026-05-23 · is_programada es suscripción real (registrada y
      // visible · NO activa todavía). Debe entrar en este filtro para que la
      // ficha NO la trate como "sin suscripción real" y muestre sus datos.
      const activa = susList.find(s =>
        s.estado === "activa" ||
        s.estado === "pausada_incidencia" ||
        s.estado === "is_programada"
      );
      setSuscripcionActiva(activa || null);
    } catch (err) {
      console.error("Error cargando suscripción activa:", err?.response?.status, err?.response?.data?.detail || err.message);
      toast.error("Error al recargar suscripción. Los datos mostrados pueden no estar actualizados.");
    }
  }, [id]);

  // Cargar suscripción cuando el cliente termina de cargarse
  const clientLoaded = !!client.id;
  React.useEffect(() => {
    if (clientLoaded && id) fetchIncidenciaData();
  }, [clientLoaded, id, fetchIncidenciaData]);

  React.useEffect(() => {
    // Norma Ann 2026-05-23 · is_programada es suscripción real · sí debe
    // disparar el indicador de cobros (su rejilla está operativa con cuota_is
    // registrada desde fecha_inicio).
    if (suscripcionActiva && (
      suscripcionActiva.estado === "activa" ||
      suscripcionActiva.estado === "pausada_incidencia" ||
      suscripcionActiva.estado === "is_programada"
    )) {
      fetchCobrosIndicador();
    } else {
      setCobrosIndicador(null);
    }
  }, [suscripcionActiva, fetchCobrosIndicador]);

  React.useEffect(() => {
    const loadTipos = async () => {
      try {
        const res = await axios.get(`${API}/cobros/tipos-incidencia`);
        if (res?.data && Array.isArray(res.data)) {
          setTiposIncidencia(res.data);
          if (res.data.length > 0 && !incTipoId) setIncTipoId(res.data[0].id);
        }
      } catch {}
    };
    loadTipos();
  }, []);

  const handleActivarIncidencia = async () => {
    if (!suscripcionActiva) { toast.error("No se encontró suscripción activa"); return; }
    if (!incTipoId) { toast.error("Seleccione un tipo de incidencia"); return; }
    if (!incFechaInicio) { toast.error("Fecha de inicio obligatoria"); return; }
    // PR-IS-junio · validaciones nuevas frontend (backend re-valida en
    // _validar_motivo_comentario con códigos MOTIVO_OBLIGATORIO /
    // MOTIVO_INVALIDO / COMENTARIO_OBLIGATORIO_SI_OTRO).
    if (!incMotivo) {
      toast.error("Selecciona el motivo de la incidencia");
      return;
    }
    if (incMotivo === "otro" && !(incComentario || "").trim()) {
      toast.error("Si el motivo es 'Otro', el comentario es obligatorio para explicar el motivo");
      return;
    }
    if (incFechaFin && incFechaFin < incFechaInicio) {
      toast.error("La fecha fin no puede ser anterior a la fecha inicio");
      return;
    }
    if (!incCheckboxConfirmado) {
      toast.error("Marca la casilla de confirmación para activar la IS");
      return;
    }

    setIncidenciaLoading(true);
    try {
      const susId = suscripcionActiva.id || suscripcionActiva._id;
      const res = await axios.post(`${API}/suscripciones/${susId}/activar-incidencia`, {
        tipo_incidencia_id: incTipoId,
        fecha_inicio: incFechaInicio,
        fecha_fin: incFechaFin || null,
        motivo: incMotivo,
        comentario: (incComentario || "").trim() || null,
      });
      const data = res.data;
      toast.success(`Incidencia activada. ${data.cobros_recalculados?.meses_recalculados || 0} cobros recalculados.`);
      setClient(prev => ({ ...prev, estado_clases: "pausado" }));
      setIncidenciaUI(false);
      // Reset campos para próxima activación
      setIncMotivo("");
      setIncComentario("");
      setIncCheckboxConfirmado(false);
      await fetchIncidenciaData();
      fetchCobrosIndicador();
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Error al activar incidencia"));
    } finally {
      setIncidenciaLoading(false);
    }
  };

  const handleCerrarIncidencia = async () => {
    if (!suscripcionActiva) return;
    if (!cierreIncFechaFin) { toast.error("Indica la fecha de finalización de la incidencia"); return; }

    // Recoger turnos válidos del estado local (los que el admin acaba de seleccionar)
    const turnosParaEnviar = (localTurnos || []).filter(t => t.dia && t.hora).map(t => ({ dia: t.dia, hora: t.hora }));

    // PR-IS-CIERRE-PRG-1 (Ann 2026-05-24) · bifurcación según fecha_fin
    // futura/pasada. Si futura → endpoint programar-cierre-incidencia
    // (registra intención · cliente sigue en IS hasta esa fecha · aplicar
    // manual cuando llegue). Si pasada/hoy → endpoint cerrar-incidencia
    // (cierre inmediato · comportamiento existente).
    const hoyYMD = new Date().toISOString().slice(0, 10);
    const esFutura = cierreIncFechaFin > hoyYMD;
    const susId = suscripcionActiva.id || suscripcionActiva._id;

    if (esFutura) {
      if (!confirm(
        `Vas a PROGRAMAR el cierre de la IS para el ${cierreIncFechaFin}.\n\n` +
        `El cliente seguirá en IS hasta esa fecha.\n` +
        `Cuando llegue el día, podrás aplicar el cierre desde la ficha.\n\n` +
        `¿Continuar?`
      )) return;

      setIncidenciaLoading(true);
      try {
        await axios.post(`${API}/suscripciones/${susId}/programar-cierre-incidencia`, {
          fecha_fin: cierreIncFechaFin,
          motivo_cierre: cierreMotivo,
          comentario: (cierreComentario || "").trim() || null,
          turno_fijo: turnosParaEnviar,
        });
        toast.success(`Cierre de IS programado para ${cierreIncFechaFin}.`);
        setCierreIncUI(false);
        await fetchIncidenciaData();
        fetchCobrosIndicador();
      } catch (err) {
        toast.error(extractApiErrorMessage(err, "Error al programar cierre de incidencia"));
      } finally {
        setIncidenciaLoading(false);
      }
      return;
    }

    // Cierre inmediato (fecha_fin <= hoy) · endpoint existente sin cambios.

    // PR-IS-cierre-no-bloqueante (Ann 2026-05-22) · el cierre NUNCA se bloquea
    // por falta de turnos. Si la IS liberó horario y no se asigna turno nuevo,
    // se muestra confirm() informativo con los turnos anteriores como referencia.
    // Backend devuelve `sin_turno_reasignado: true` en ese caso y emite audit
    // warning IS_CERRADA_SIN_TURNO_REASIGNADO + alerta post-cierre en la ficha.
    const ia = suscripcionActiva.incidencia_activa || {};
    const liberaHorario = ia.mantiene_reserva_horario === false;
    // Lectura preferida: turno_anterior_snapshot (autopista PR-L11/L13).
    // Fallback legacy: turno_liberado (sólo si algún camino antiguo lo escribió).
    const turnosPrevios = (ia.turno_anterior_snapshot && ia.turno_anterior_snapshot.length > 0)
      ? ia.turno_anterior_snapshot
      : (ia.turno_liberado || []);
    if (liberaHorario && turnosParaEnviar.length === 0) {
      const previosTexto = turnosPrevios.length > 0
        ? turnosPrevios.map(t => `${t.dia} ${t.hora}`).join(", ")
        : "(no había turnos previos registrados)";
      const ok = confirm(
        `Vas a finalizar la IS sin asignar nuevos turnos.\n\n` +
        `Después tendrás que asignarlos manualmente desde la ficha.\n\n` +
        `Antes de la IS estaba apuntado a: ${previosTexto}\n\n` +
        `¿Continuar con el cierre?`
      );
      if (!ok) return;
    } else {
      if (!confirm(`¿Finalizar la incidencia con fecha ${cierreIncFechaFin}?\n\nEl servicio volverá a estado activo y los cobros se recalcularán.`)) return;
    }

    setIncidenciaLoading(true);
    try {
      // PR-IS-modal-cierre (Ann 2026-05-22) · motivo_cierre desde estado del
      // modal (antes hardcoded "recuperacion") + comentario opcional.
      const res = await axios.post(`${API}/suscripciones/${susId}/cerrar-incidencia`, {
        fecha_fin: cierreIncFechaFin,
        motivo_cierre: cierreMotivo,
        comentario: (cierreComentario || "").trim() || null,
        turno_fijo: turnosParaEnviar,
      });
      const data = res.data;
      toast.success(`Incidencia finalizada (${data.incidencia_cerrada?.dias_totales || 0} días). ${data.cobros_recalculados?.meses_recalculados || 0} cobros recalculados.`);
      setClient(prev => ({ ...prev, estado_clases: "activo" }));
      setCierreIncUI(false);
      await fetchIncidenciaData();
      fetchCobrosIndicador();
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Error al finalizar incidencia"));
    } finally {
      setIncidenciaLoading(false);
    }
  };

  // PR-IS-CIERRE-PRG-1 (Ann 2026-05-24) · cancelar cierre programado pendiente.
  const handleCancelarCierreProgramado = async () => {
    if (!suscripcionActiva) return;
    const susId = suscripcionActiva.id || suscripcionActiva._id;
    const cp = suscripcionActiva?.incidencia_activa?.cierre_programado;
    if (!cp) return;
    if (!confirm(
      `¿Cancelar el cierre programado para ${cp.fecha_fin}?\n\n` +
      `La IS continuará abierta sin fecha de cierre prevista.`
    )) return;
    setIncidenciaLoading(true);
    try {
      await axios.delete(`${API}/suscripciones/${susId}/cierre-programado`);
      toast.success("Cierre programado cancelado.");
      await fetchIncidenciaData();
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Error al cancelar cierre programado"));
    } finally {
      setIncidenciaLoading(false);
    }
  };

  // PR-IS-CIERRE-PRG-1 (Ann 2026-05-24) · aplicar cierre programado cuando llega
  // la fecha. Delega al orquestador cerrar_incidencia_salud_atomica vía
  // endpoint /aplicar-cierre-programado (NO duplica lógica de cierre).
  const handleAplicarCierreProgramado = async () => {
    if (!suscripcionActiva) return;
    const susId = suscripcionActiva.id || suscripcionActiva._id;
    const cp = suscripcionActiva?.incidencia_activa?.cierre_programado;
    if (!cp) return;
    if (!confirm(
      `¿Aplicar ahora el cierre programado de la IS (fecha ${cp.fecha_fin})?\n\n` +
      `El cliente volverá a estado activo y los cobros se recalcularán.`
    )) return;
    setIncidenciaLoading(true);
    try {
      const res = await axios.post(`${API}/suscripciones/${susId}/aplicar-cierre-programado`);
      const data = res?.data?.resultado_orquestador || {};
      toast.success(
        `Cierre programado aplicado (${data?.incidencia_cerrada?.dias_totales || 0} días). ` +
        `${data?.cobros_recalculados?.meses_recalculados || 0} cobros recalculados.`
      );
      setClient(prev => ({ ...prev, estado_clases: "activo" }));
      await fetchIncidenciaData();
      fetchCobrosIndicador();
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Error al aplicar cierre programado"));
    } finally {
      setIncidenciaLoading(false);
    }
  };

  const handleExpandBono = async (bonoId) => {
    if (expandedBonoId === bonoId) {
      setExpandedBonoId(null);
      return;
    }
    setExpandedBonoId(bonoId);
    if (!bonoSesiones[bonoId]) {
      try {
        const res = await axios.get(`${API}/bonos/${bonoId}/sesiones`);
        setBonoSesiones((prev) => ({
          ...prev,
          [bonoId]: res.data.sesiones || [],
        }));
      } catch (error) {
        console.error("Error fetching sesiones for bono:", error);
      }
    }
  };

  // 1. Bloqueador de navegación interna eliminado porque requiere Data Router en React Router v6

  // 2. Bloqueador de cierre de pestaña o recarga (Navegador)
  React.useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ""; // Required for Chrome / modern browsers
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const [events, setEvents] = React.useState([]);
  const [facturas, setFacturas] = React.useState([]);
  const [anyoCobros, setAnyoCobros] = React.useState(new Date().getFullYear());
  const [recibos, setRecibos] = React.useState([]);
  const [errorAnulacion, setErrorAnulacion] = React.useState(null); // { facturaId, factura, msg, ts }

  // Alertas reales basadas en datos del cliente (DESPUÉS de declarar recibos)
  const clientAlerts = React.useMemo(() => {
    if (!client.id) return [];
    const alerts = [];
    // Deuda activa
    if (client.tiene_deuda || client.deuda_calculada > 0) {
      alerts.push({
        id: "deuda", type: "deuda", bg: "var(--cn-danger)",
        icon: (<><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01M18 12h.01" /></>),
        title: "Cliente con deuda",
        desc: client.deuda_calculada > 0 ? `Deuda pendiente: ${client.deuda_calculada.toFixed(2)}€` : "Deuda registrada en el sistema",
        date: "Urgente",
      });
    }
    // Recibos devueltos
    const devueltos = recibos.filter(r => r.estado === "devuelto");
    if (devueltos.length > 0) {
      alerts.push({
        id: "devuelto", type: "deuda", bg: "var(--cn-danger)",
        icon: (<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>),
        title: `Recibo${devueltos.length > 1 ? "s" : ""} devuelto${devueltos.length > 1 ? "s" : ""}`,
        desc: `${devueltos.length} recibo${devueltos.length > 1 ? "s" : ""} devuelto${devueltos.length > 1 ? "s" : ""} sin resolver`,
        date: "Urgente",
      });
    }
    // IS activa
    const isISLocal = !!(suscripcionActiva?.incidencia_activa) || client.motivo_desactivacion === "incidencia_de_salud";
    if (isISLocal) {
      const nombre = suscripcionActiva?.incidencia_activa?.tipo_incidencia_nombre || "Incidencia de Salud";
      alerts.push({
        id: "is", type: "operativa", bg: "var(--cn-warning)",
        icon: (<><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></>),
        title: nombre,
        desc: "Cliente en incidencia de salud activa",
        date: "Activo",
      });
    }
    return alerts;
  }, [client?.tiene_deuda, client?.deuda_calculada, client?.motivo_desactivacion, recibos, suscripcionActiva]);
  // CRITICO: Modal preview estándar para facturas y recibos (iframe PDF + Imprimir + Descargar + Email + Cerrar)
  const [docPreview, setDocPreview] = React.useState(null); // { type: "factura"|"recibo", id, pdfUrl, nombre }
  const [docPreviewLoading, setDocPreviewLoading] = React.useState(false);
  const [docSendingEmail, setDocSendingEmail] = React.useState(false);

  // Turnos fijos states
  const [localTurnos, setLocalTurnos] = React.useState([]);
  const [horariosDisponibles, setHorariosDisponibles] = React.useState({});
  const [horariosConIntensidad, setHorariosConIntensidad] = React.useState([]);
  const [aforoData, setAforoData] = React.useState([]);
  const [cuotas, setCuotas] = React.useState([]);

  const updateTurno = (index, field, value) => {
    const newArr = [...localTurnos];
    newArr[index] = { ...newArr[index], [field]: value };
    if (field === "dia") newArr[index].hora = "";
    setLocalTurnos(newArr);
    setHasUnsavedChanges(true); // Flag changes from non-native selectors
  };

  const isSlotFull = (dia, hora) => {
    const slot = aforoData.find((s) => s.dia === dia && s.hora === hora);
    return slot?.lleno || false;
  };

  const getSlotInfo = (dia, hora) => {
    const slot = aforoData.find((s) => s.dia === dia && s.hora === hora);
    if (!slot) return null;
    const sedeAforo = client?.sede?.toLowerCase() === "verges" ? 5 : 8; // Aforo depends on sede
    return `${slot.ocupados}/${sedeAforo} - ${slot.disponibles} disponibles`;
  };

  const getHorarioIntensidad = (dia, hora) => {
    const hInfo = horariosConIntensidad.find(
      (h) => h.dia === dia && h.hora === hora,
    );
    return hInfo || { nombre: "Estándar", color: "#9ca3af" };
  };

  React.useEffect(() => {
    if (client) {
      // NOTE: Compare if stringified version equals currently active stringified version 
      // to avoid unnecessary re-renders when backend updates but turno_fijo remains unchanged.
      let turnos = client.turno_fijo || [];
      let turnosCopy = [...turnos];
      while (turnosCopy.length < 14) turnosCopy.push({ dia: "", hora: "" });
      
      const turnosStr = JSON.stringify(turnosCopy);
      const currentLocalStr = JSON.stringify(localTurnos);
      
      if (turnosStr !== currentLocalStr) {
        setLocalTurnos([...turnosCopy]);
      }
    }
  }, [client ? JSON.stringify(client.turno_fijo) : null]);

  React.useEffect(() => {
    if (!client.id) return;
    const fetchHorarios = async () => {
      try {
        const response = await axios.get(`${API}/admin/horarios-fijos/list`, {
          params: { sede: client.sede || "MUNTANER" },
        });
        const horariosMap = {};
        DIAS.forEach((dia) => (horariosMap[dia] = []));
        const horariosConInfo = [];

        response.data.forEach((horario) => {
          if (horario.estado === "activo" && DIAS.includes(horario.dia)) {
            if (!horariosMap[horario.dia].includes(horario.hora)) {
              horariosMap[horario.dia].push(horario.hora);
            }

            const intensidadStr = horario.intensidad_nombre || "Estándar";
            const intensidadColor = horario.intensidad_color || "#9ca3af";

            horariosConInfo.push({
              dia: horario.dia,
              hora: horario.hora,
              intensidad_nombre: intensidadStr,
              intensidad_color: intensidadColor,
              capacidad: horario.capacidad,
              ocupacion: horario.ocupacion,
            });
          }
        });

        DIAS.forEach((dia) => {
          horariosMap[dia].sort((a, b) => {
            const [ha, ma] = a.split(":").map(Number);
            const [hb, mb] = b.split(":").map(Number);
            return ha * 60 + ma - (hb * 60 + mb);
          });
        });

        setHorariosDisponibles(horariosMap);
        setHorariosConIntensidad(horariosConInfo);
      } catch (error) {
        console.error("Error fetching horarios", error);
      }
    };

    const fetchAforoData = async () => {
      try {
        const response = await axios.get(`${API}/admin/aforo-horarios`, {
          params: { sede: client.sede || "MUNTANER" },
        });
        setAforoData(response.data);
      } catch (error) {
        console.error("Error loading aforo data:", error);
      }
    };

    const fetchCuotas = async () => {
      try {
        const response = await axios.get(`${API}/admin/cuotas`);
        setCuotas(response.data);
      } catch (error) {
        console.error("Error loading cuotas:", error);
      }
    };

    fetchHorarios();
    fetchAforoData();
    fetchCuotas();
  }, [client ? client.sede : null]);

  React.useEffect(() => {
    if (isNuevo && !id) {
      // Modo creación: cliente vacío en memoria, nada en BD
      setClient({
        nombre: "", primer_apellido: "", segundo_apellido: "", dni: "", email: "",
        telefono: "", direccion: "", ciudad: "", codigo_postal: "", provincia: "", pais: "ES",
        // AUTOPISTA · estado inicial respeta sede activa del admin/staff
        // (MUNTANER / MEFIR / CONSULTAS). Fallback "MUNTANER" solo si el
        // contexto aún no la ha resuelto. Prohibido hardcodear (bug P0
        // PR alta-consultas-muntaner).
        role: "cliente", servicio: "fijo", sede: sedeActiva || "MUNTANER",
        fecha_alta: new Date().toISOString().split("T")[0],
        turno_fijo: [], inscrito_clases_fijas: false, estado_clases: null,
        cuota_id: null, forma_pago: null, observaciones: "",
      });
      setLoading(false);
      setHasUnsavedChanges(true);
      return;
    }
    if (!id) {
      setError("ID de cliente no proporcionado en la ruta.");
      setLoading(false);
      return;
    }

    const fetchClientData = async () => {
      try {
        // ADAPTADO A EMPLEADO (in-place sobre el clon): cargar el PROFESIONAL,
        // no un cliente. Se mapea a la forma `clientData` que usa la cabecera.
        const profRes = await axios.get(`${API}/config/profesionales`);
        const profDoc = (profRes.data || []).find((p) => String(p.id) === String(id) || String(p._id) === String(id));
        if (!profDoc) { setError("No se ha encontrado el profesional. Verifica el ID."); setLoading(false); return; }
        const _ap = (profDoc.apellidos || "").trim().split(/\s+/);
        const fichaRes = { data: {} };
        const clientData = {
          ...profDoc,
          id: profDoc.id || profDoc._id,
          primer_apellido: profDoc.primer_apellido || _ap[0] || "",
          segundo_apellido: profDoc.segundo_apellido || _ap.slice(1).join(" ") || "",
        };
        const deudasArray = [];
        clientData.deuda_calculada = deudasArray.reduce(
          (acc, d) => acc + (d.monto || 0),
          0,
        );

        if (fichaRes.data.relacion_asistencial) {
          clientData.relacion_asistencial = fichaRes.data.relacion_asistencial;
        }
        if (fichaRes.data.bonos) {
          clientData.bonos = fichaRes.data.bonos;
        }
        if (fichaRes.data.ultimas_citas) {
          clientData.ultimas_citas = fichaRes.data.ultimas_citas;
        }

        // Autocompletar el checkbox "Inscrito a Clases Fijas" si ya venía con turnos previamente configurados en el sistema
        if (clientData.inscrito_clases_fijas === undefined) {
          clientData.inscrito_clases_fijas =
            clientData.cuota_id &&
            Array.isArray(clientData.turno_fijo) &&
            clientData.turno_fijo.some((t) => t.dia && t.hora);
        }

        // Default fecha_alta to today if not set
        if (!clientData.fecha_alta) {
          clientData.fecha_alta = new Date().toISOString().split("T")[0];
        }
        // Si es menor, propagar datos del tutor a pagador SEPA si estan vacios
        const esMenor = clientData.fecha_nacimiento ? getAge(clientData.fecha_nacimiento) < 18 : false;
        if (esMenor && clientData.tutor_nombre) {
          if (!clientData.tiene_pagador_alternativo) {
            clientData.tiene_pagador_alternativo = true;
          }
          if (!clientData.pagador_nombre) clientData.pagador_nombre = clientData.tutor_nombre;
          if (!clientData.pagador_apellidos) clientData.pagador_apellidos = clientData.tutor_apellidos;
          if (!clientData.pagador_dni) clientData.pagador_dni = clientData.tutor_dni;
          if (!clientData.pagador_relacion) clientData.pagador_relacion = clientData.tutor_relacion;
          if (!clientData.pagador_telefono) clientData.pagador_telefono = clientData.tutor_telefono;
          if (!clientData.pagador_email) clientData.pagador_email = clientData.tutor_email;
          if (!clientData.sepa_titular_cuenta) {
            clientData.sepa_titular_cuenta = `${clientData.tutor_nombre || ""} ${clientData.tutor_apellidos || ""}`.trim();
          }
          if (!clientData.sepa_titular_dni) clientData.sepa_titular_dni = clientData.tutor_dni;
        }
        // Propagate fecha_alta to fecha_alta_clases if missing
        if (clientData.inscrito_clases_fijas && !clientData.fecha_alta_clases) {
          clientData.fecha_alta_clases = clientData.fecha_alta.split("T")[0];
        }
        // Default estado_clases if inscrito but no estado
        if (clientData.inscrito_clases_fijas && !clientData.estado_clases) {
          clientData.estado_clases = "activo";
        }
        setClient(clientData);
        // FASE D1 · snapshot del valor original de inscrito_clases_fijas
        // (se usa en handleGlobalSave para bloquear desinscripción indirecta).
        originalInscritoClasesFijasRef.current = !!clientData.inscrito_clases_fijas;
        // Sub-fase 5.3.1 · snapshots para router (no usados todavía en save).
        originalFormaPagoRef.current = clientData.forma_pago || null;
        originalCuotaIdRef.current = clientData.cuota_id || null;
        originalTurnoFijoRef.current = serializarTurno(
          Array.isArray(clientData.turno_fijo) ? clientData.turno_fijo : []);
        originalSepaSnapshotRef.current = {
          sepa: {
            sepa_iban: clientData.sepa_iban || "",
            sepa_bic: clientData.sepa_bic || "",
            sepa_titular_cuenta: clientData.sepa_titular_cuenta || "",
            sepa_titular_dni: clientData.sepa_titular_dni || "",
          },
          pagador: {
            tiene_pagador_alternativo: !!clientData.tiene_pagador_alternativo,
            pagador_nombre: clientData.pagador_nombre || "",
            pagador_apellidos: clientData.pagador_apellidos || "",
            pagador_dni: clientData.pagador_dni || "",
            pagador_relacion: clientData.pagador_relacion || "",
            pagador_telefono: clientData.pagador_telefono || "",
            pagador_email: clientData.pagador_email || "",
            pagador_direccion: clientData.pagador_direccion || "",
            pagador_piso: clientData.pagador_piso || "",
            pagador_codigo_postal: clientData.pagador_codigo_postal || "",
            pagador_ciudad: clientData.pagador_ciudad || "",
            pagador_provincia: clientData.pagador_provincia || "",
          },
        };
        // Sub-fase 5.3.2-B · snapshots adicionales para el router conectado.
        originalSedeRef.current = clientData.sede || null;
        originalPersonalSnapshotRef.current = _snapshotPersonalYExcluidos(
          clientData, clientData.como_nos_conocio || "");
        // BLOQUE 6A · estado canónico devuelto por backend (read-only).
        setEstadoCanonico(fichaRes.data.estado_canonico || null);
        setComoNosConocio(clientData.como_nos_conocio || "");

        // Fetch events: manuales (cliente-eventos) + unificados (historial sistema)
        try {
          const [evtRes, histRes] = await Promise.all([
            axios.get(`${API}/cliente-eventos/${id}`).catch(() => ({ data: [] })),
            axios.get(`${API}/admin/clients/${id}/historial-unificado`).catch(() => ({ data: { eventos: [] } })),
          ]);

          const manuales = (Array.isArray(evtRes.data) ? evtRes.data : []).map(e => ({
            ...e,
            _source: "manual",
          }));

          // Colores: verde=alta/inscripción/activación, granate=baja, naranja=incidencia
          const getColorByTipo = (tipo, categoria) => {
            if (categoria === "incidencia") return "#d97706"; // naranja
            if (tipo && (tipo.includes("baja") || tipo.includes("desactivacion") || tipo.includes("cierre"))) return "#881337"; // granate
            return "#16a34a"; // verde: alta, inscripción, reactivación, alta_cliente, etc.
          };
          const sistema = (histRes.data?.eventos || []).map(e => ({
            id: `sys_${e.tipo}_${e.fecha}`,
            fecha_evento: e.fecha,
            tipo_evento: e.categoria === "incidencia" ? "Incidencias" : e.categoria === "servicio" ? "Altas/Bajas" : "Altas/Bajas",
            subtipo_evento: e.titulo,
            descripcion: e.detalle || "",
            comentario_interno: e.servicio ? `Servicio: ${e.servicio}` : "",
            created_at: e.fecha,
            _source: "sistema",
            _categoria: e.categoria,
            _color: getColorByTipo(e.tipo, e.categoria),
            _usuario: e.usuario || "",
            _tipo_sistema: e.tipo,
            _ref_id: e.referencia_id || "",
            _ref_titulo: (e.titulo || "").replace(/ activada$/, "").replace(/ finalizada$/, ""),
            _is_meta: e.meta || null,
          }));

          // Agrupar incidencias: el backend ahora emite 1 solo evento por IS con meta completo.
          // Cada evento de IS se renderiza como UN bloque único (tarjeta estructurada).
          // Además deduplicamos eventos manuales (incidencia_salud_inicio/fin) cuando ya
          // existe un bloque sistema equivalente para esa misma suscripción + fecha_inicio.
          let merged;
          try {
            const incSistema = sistema.filter(e => e._tipo_sistema === "incidencia_activada");
            const otrosSistema = sistema.filter(e => e._tipo_sistema !== "incidencia_activada" && e._tipo_sistema !== "incidencia_cerrada");

            // Conjunto de claves (suscripcion_id + fecha_inicio) ya cubiertas por sistema
            const coveredKeys = new Set();
            incSistema.forEach(is => {
              const meta = is._is_meta || {};
              const susId = is._ref_id || "";
              const fi = meta.fecha_inicio || "";
              if (susId && fi) coveredKeys.add(`${susId}|${fi}`);
            });

            // Filtrar manuales: esconder incidencia_salud_inicio/fin redundantes
            const manualesFiltrados = manuales.filter(m => {
              const t = m.tipo_evento;
              if (t !== "incidencia_salud_inicio" && t !== "incidencia_salud_fin") return true;
              const k = `${m.suscripcion_id || ""}|${m.fecha_evento || ""}`;
              return !coveredKeys.has(k);
            });

            // Filtrar evento sistema "Cliente pausado por incidencia de salud (legacy)"
            // (tipo desactivacion_cliente con motivo incidencia_de_salud) cuando ya hay
            // una IS estructurada para el cliente. La IS estructurada cubre la trazabilidad
            // del hecho; el evento legacy sólo añade ruido visual (granate, fecha distinta,
            // duplica info). Si NO hay IS estructurada (caso muy antiguo), se conserva.
            const hayIsEstructurada = incSistema.length > 0;
            const otrosFiltrados = otrosSistema.filter(e => {
              if (e._tipo_sistema !== "desactivacion_cliente") return true;
              const desc = (e.descripcion || "").toLowerCase();
              const subt = (e.subtipo_evento || "").toLowerCase();
              const esLegacyIs = desc.includes("incidencia_de_salud") || subt.includes("(legacy)");
              if (!esLegacyIs) return true;
              return !hayIsEstructurada;
            });

            // Preparar tarjetas IS enriquecidas (marcador _is_block = true)
            const incidenciasAgrupadas = incSistema.map(actEv => {
              const meta = actEv._is_meta || {};
              return {
                ...actEv,
                subtipo_evento: meta.tipo_incidencia_nombre || actEv._ref_titulo || "Incidencia de salud",
                _is_block: true,
                _incidencia_activa: meta.estado_is === "EN CURSO",
              };
            });

            merged = [...manualesFiltrados, ...otrosFiltrados, ...incidenciasAgrupadas];
          } catch (groupErr) {
            console.error("Error agrupando incidencias, mostrando sin agrupar:", groupErr);
            merged = [...manuales, ...sistema];
          }

          // Orden cronológico ASCENDENTE (pasado arriba → futuro abajo) con
          // desempate por prioridad_tipo en eventos del mismo día. Replica la
          // lógica del backend (server.py get_historial_unificado) para que en
          // importaciones/legacy donde users.fecha_alta y suscripciones.fecha_inicio
          // caen el mismo día con timestamps distintos, el orden sea siempre:
          // alta_cliente → alta_servicio → cambios → IS → bajas.
          // Fallback "9999-12-31" para que eventos manuales sin fecha queden al final.
          const PRIORIDAD_TIPO_HISTORIAL = {
            alta_cliente: 1,
            alta_servicio: 2,
            reinscripcion_servicio: 3,
            cambio_cuota: 4,
            cambio_sede: 5,
            reactivacion_cliente: 6,
            incidencia_activada: 7,
            incidencia_cerrada: 8,
            cierre_etapa: 9,
            baja_servicio: 10,
            desactivacion_cliente: 11,
          };
          const tipoOrden = (ev) => {
            const t = ev?._tipo_sistema || ev?.tipo_evento;
            return PRIORIDAD_TIPO_HISTORIAL[t] ?? 50;
          };
          merged.sort((a, b) => {
            const fa = a.fecha_evento || a.created_at || "9999-12-31";
            const fb = b.fecha_evento || b.created_at || "9999-12-31";
            const c1 = fa.slice(0, 10).localeCompare(fb.slice(0, 10));
            if (c1 !== 0) return c1;
            const c2 = tipoOrden(a) - tipoOrden(b);
            if (c2 !== 0) return c2;
            return fa.localeCompare(fb);
          });

          setEvents(merged);
        } catch (e) {
          console.error("No se pudieron cargar eventos", e);
        }

        // Fetch invoices safely
        try {
          const factRes = await axios.get(
            `${API}/facturacion/facturas?cliente_id=${id}`,
          );
          setFacturas(
            Array.isArray(factRes.data)
              ? factRes.data
              : factRes.data.facturas || [],
          );
        } catch (e) {
          console.error("No se pudieron cargar facturas", e);
        }

        // Fetch recibos safely
        try {
          const recRes = await axios.get(`${API}/recibos?cliente_id=${id}`);
          setRecibos(
            Array.isArray(recRes.data)
              ? recRes.data
              : recRes.data.recibos || []
          );
        } catch (e) {
          console.error("No se pudieron cargar recibos", e);
        }

        setError(null);
      } catch (err) {
        console.error("Error cargando cliente:", err);
        setError("No se pudo cargar la ficha del cliente. Verifica el ID.");
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [id, clinicaRefreshTick]);

  const validarDNI = (dni) => {
    if (!dni) return false;
    const validChars = "TRWAGMYFPDXBNJZSQVHLCKE";
    const nifRexp = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]{1}$/i;
    const nieRexp = /^[XYZ]{1}[0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]{1}$/i;
    const str = dni.toString().toUpperCase().replace(/\s/g, "");

    if (!nifRexp.test(str) && !nieRexp.test(str)) return false;

    const nieToNum = str
      .replace(/^[X]/, "0")
      .replace(/^[Y]/, "1")
      .replace(/^[Z]/, "2");
    const letter = str.substr(-1);
    const charIndex = parseInt(nieToNum.substr(0, 8)) % 23;

    return validChars.charAt(charIndex) === letter;
  };

  const validarIBAN = (iban) => {
    if (!iban) return false;
    const str = iban.replace(/\s+/g, "").toUpperCase();
    if (str.length < 15 || str.length > 34) return false;
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(str)) return false;

    const country = str.substring(0, 2);
    const check = str.substring(2, 4);
    const bban = str.substring(4);
    const reordered = bban + country + check;

    let digits = "";
    for (let i = 0; i < reordered.length; i++) {
      const charCode = reordered.charCodeAt(i);
      if (charCode >= 65 && charCode <= 90) {
        digits += (charCode - 55).toString();
      } else {
        digits += reordered.charAt(i);
      }
    }

    let rem = 0;
    for (let i = 0; i < digits.length; i++) {
      rem = (rem * 10 + parseInt(digits.charAt(i))) % 97;
    }

    return rem === 1;
  };
  const getAge = (dateString) => {
    if (!dateString) return "—";
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const isMenor = client?.fecha_nacimiento
    ? getAge(client.fecha_nacimiento) < 18
    : false;

  const isSepaValidoObj = () => {
    if (client.forma_pago !== "domiciliacion") return true; // Si no es sepa, no hay bloqueo

    const ibanValido = validarIBAN(client.sepa_iban);

    if (client.tiene_pagador_alternativo) {
      const dniValido = validarDNI(client.pagador_dni);
      const tieneNombre = client.pagador_nombre && client.pagador_apellidos;
      const tieneRelacion = client.pagador_relacion;
      return ibanValido && dniValido && tieneNombre && tieneRelacion;
    } else {
      // By default, if it's SEPA and minor, and distinct payer isn't ticked, we check against the tutor's DNI instead.
      if (isMenor) {
        const dniTutorValido = validarDNI(client.tutor_dni);
        return ibanValido && dniTutorValido;
      } else {
        const dniValido = validarDNI(client.dni);
        return ibanValido && dniValido;
      }
    }
  };

  const handleAnotarCambio = async () => {
    if (!cambioFormData.descripcion.trim()) {
      alert("La descripción es obligatoria.");
      return;
    }
    setSubmittingCambio(true);
    try {
      const payload = {
        cliente_id: id,
        ...cambioFormData,
      };
      const res = await axios.post(`${API}/cliente-eventos`, payload);
      setEvents((prev) =>
        [res.data, ...prev].sort(
          (a, b) => new Date(b.fecha_evento) - new Date(a.fecha_evento),
        ),
      );
      setIsAnotarCambioOpen(false);
      setCambioFormData({
        fecha_evento: new Date().toISOString().split("T")[0],
        tipo_evento: "observacion_interna",
        descripcion: "",
        comentario_interno: "",
      });
    } catch (error) {
      console.error("Error al registrar cambio:", error);
      alert("Error al guardar el cambio. Verifica la consola.");
    } finally {
      setSubmittingCambio(false);
    }
  };

  // ─── Sub-fase 5.3.2-B · helpers internos del router conectado ───────
  // Snapshot canónico de los campos personales (whitelist) + excluidos
  // pendientes. Usado para detectar deltas en PUT light y advertencias.
  function _snapshotPersonalYExcluidos(cd, comoNosConocioVal) {
    const snap = {};
    for (const k of PUT_LIGHT_WHITELIST_CONSERVADORA) {
      if (k === "como_nos_conocio") {
        snap[k] = comoNosConocioVal || null;
      } else {
        snap[k] = cd?.[k] === undefined ? null : cd[k];
      }
    }
    for (const k of PUT_LIGHT_EXCLUIDOS_PENDIENTES) {
      snap[`__excl__${k}`] = cd?.[k] === undefined ? null : cd[k];
    }
    return snap;
  }

  // Construye el objeto `originals` que el router espera, tomando los refs
  // capturados al cargar la ficha. Plano (top-level whitelist) + sepa{} +
  // pagador{} para que también sirva a construirPayloadPutLight.
  function _buildOriginalsForRouting() {
    const snap = originalPersonalSnapshotRef.current || {};
    const personalTop = {};
    for (const k of PUT_LIGHT_WHITELIST_CONSERVADORA) {
      personalTop[k] = snap[k] ?? null;
    }
    return {
      cliente_id: id,
      formaPago: originalFormaPagoRef.current,
      cuotaId: originalCuotaIdRef.current,
      turnoFijoSerializado: originalTurnoFijoRef.current,
      inscritoClasesFijas: !!originalInscritoClasesFijasRef.current,
      sede: originalSedeRef.current,
      sepa: originalSepaSnapshotRef.current?.sepa || {},
      pagador: originalSepaSnapshotRef.current?.pagador || {},
      ...personalTop,
    };
  }

  // Construye el objeto `current` con el estado actual de la UI: client +
  // localTurnos + comoNosConocio + flags de cambio personal y otros pendientes.
  function _buildCurrentForRouting() {
    const cleanTurnos = (localTurnos || []).filter(t => t.dia && t.hora);
    const snap = originalPersonalSnapshotRef.current || {};
    // Top-level personales (whitelist)
    const personalTop = {};
    for (const k of PUT_LIGHT_WHITELIST_CONSERVADORA) {
      if (k === "como_nos_conocio") {
        personalTop[k] = comoNosConocio || null;
      } else {
        personalTop[k] = client?.[k] === undefined ? null : client[k];
      }
    }
    // Detección personalCambia
    let personalCambia = false;
    for (const k of PUT_LIGHT_WHITELIST_CONSERVADORA) {
      if ((personalTop[k] ?? null) !== (snap[k] ?? null)) {
        personalCambia = true;
        break;
      }
    }
    // Detección otros pendientes (campos excluidos que cambiaron)
    const otrosPendientes = [];
    for (const k of PUT_LIGHT_EXCLUIDOS_PENDIENTES) {
      const actual = client?.[k] === undefined ? null : client[k];
      const original = snap[`__excl__${k}`] ?? null;
      if (actual !== original) otrosPendientes.push(k);
    }
    // Cuota seleccionada para horas_semana (advertencia turno-cuota)
    const cuotaSel = (cuotas || []).find(c =>
      c?.id === client?.cuota_id || c?._id === client?.cuota_id);
    // PR-BECA-2 (Ann 2026-05-27): flag de la cuota ORIGINAL (la guardada
    // en BD antes de cualquier edición en pantalla). El router usa este
    // flag combinado con `cuotaNuevaEsBecada` para decidir el orden de
    // las acciones en transiciones a/desde becado.
    const cuotaOriginalSel = (cuotas || []).find(c =>
      c?.id === originalCuotaIdRef.current
      || c?._id === originalCuotaIdRef.current);
    return {
      cliente_id: id,
      formaPago: client?.forma_pago ?? null,
      cuotaId: client?.cuota_id ?? null,
      turnoFijo: cleanTurnos,
      inscritoClasesFijas: !!client?.inscrito_clases_fijas,
      sede: client?.sede ?? null,
      sepa: {
        sepa_iban: client?.sepa_iban || "",
        sepa_bic: client?.sepa_bic || "",
        sepa_titular_cuenta: client?.sepa_titular_cuenta || "",
        sepa_titular_dni: client?.sepa_titular_dni || "",
      },
      pagador: {
        tiene_pagador_alternativo: !!client?.tiene_pagador_alternativo,
        pagador_nombre: client?.pagador_nombre || "",
        pagador_apellidos: client?.pagador_apellidos || "",
        pagador_dni: client?.pagador_dni || "",
        pagador_relacion: client?.pagador_relacion || "",
        pagador_telefono: client?.pagador_telefono || "",
        pagador_email: client?.pagador_email || "",
        pagador_direccion: client?.pagador_direccion || "",
        pagador_piso: client?.pagador_piso || "",
        pagador_codigo_postal: client?.pagador_codigo_postal || "",
        pagador_ciudad: client?.pagador_ciudad || "",
        pagador_provincia: client?.pagador_provincia || "",
      },
      personalCambia,
      otrosPendientes,
      horasSemanaCuotaNueva: cuotaSel?.horas_semana ?? null,
      cuotaNuevaEsBecada: cuotaSel?.es_becado === true,
      cuotaActualEsBecada: cuotaOriginalSel?.es_becado === true,
      ...personalTop,
    };
  }

  function _buildContextoCliente() {
    return {
      estado_clases: client?.estado_clases || null,
      estado_cliente: client?.estado_cliente || null,
      sus_estado: suscripcionActiva?.estado || null,
    };
  }

  // Toast por motivo de bloqueo del router (frontend).
  function _toastBloqueo(motivo) {
    const mapa = {
      [MOTIVOS_BLOQUEO.DESINSCRIPCION_VIA_GUARDAR_NO_SOPORTADA]:
        "Para dar de baja de clases fijas usa el botón de baja/desactivación. " +
        "No se permite desinscribir desde 'Guardar cambios'.",
      [MOTIVOS_BLOQUEO.CLIENTE_DE_BAJA]:
        "🛑 Cliente desactivado o de baja · reactiva el servicio antes de modificarlo.",
      [MOTIVOS_BLOQUEO.IS_ACTIVA_BLOQUEA_CAMBIO]:
        "🛑 Cliente con incidencia de salud activa · cierra la IS antes de cambiar cuota/horario/forma de cobro.",
      [MOTIVOS_BLOQUEO.CAMBIO_SEDE_CLIENTE_INSCRITO_NO_SOPORTADO]:
        "🛑 El cambio de sede de un cliente inscrito requiere flujo dedicado · pendiente.",
      [MOTIVOS_BLOQUEO.CAMBIO_SEDE_REQUIERE_MODAL_DEDICADO]:
        "ℹ️ Usa 'Cambiar sede de clases fijas' · el cambio directo de sede no está permitido para clientes con clases fijas activas.",
      [MOTIVOS_BLOQUEO.BECADO_FUERA_ALCANCE_UI]:
        "🛑 El cambio a cuota becada requiere un flujo específico y no puede " +
        "aplicarse desde el cambio normal de membresía.",
      [MOTIVOS_BLOQUEO.CLIENTE_NO_INSCRITO_USE_BOTON_INSCRIPCION]:
        "ℹ️ Este cliente todavía no está inscrito a Clases Fijas. " +
        "Usa el botón verde \"Inscribir a clases fijas\" del bloque de " +
        "Clases Fijas para darle el alta. Cuota, forma de pago y turnos se " +
        "configuran desde ese modal.",
    };
    toast.error(mapa[motivo] || `🛑 Operación bloqueada: ${motivo}`,
      { duration: 8000 });
  }

  // Mapea code backend → mensaje legible.
  function _toastErrorBackend(accionTipo, err) {
    const code = err?.response?.data?.detail?.code
      || err?.response?.data?.code
      || null;
    const detalle = parseErrorDetail(err, "Error desconocido");
    const tipoLegible = ({
      [TIPOS_ACCION.CAMBIAR_FORMA_COBRO]: "Cambio forma de cobro",
      [TIPOS_ACCION.CAMBIAR_CUOTA]: "Cambio cuota",
      [TIPOS_ACCION.CAMBIAR_HORARIO]: "Cambio horario",
      [TIPOS_ACCION.PUT_LIGHT]: "Guardar datos personales",
    })[accionTipo] || "Operación";
    const mensajesPorCode = {
      IS_ACTIVA_BLOQUEA_CAMBIO_HORARIO:
        "Cliente con incidencia de salud activa · cierra la IS antes.",
      IS_ACTIVA_BLOQUEA_CAMBIO_CUOTA:
        "Cliente con incidencia de salud activa · cierra la IS antes.",
      IS_ACTIVA_BLOQUEA_CAMBIO_FORMA_COBRO:
        "Cliente con incidencia de salud activa · cierra la IS antes.",
      CLIENTE_DE_BAJA: "Cliente desactivado o de baja.",
      CLIENTE_NO_INSCRITO_CF: "Cliente no inscrito en clases fijas.",
      AFORO_SUPERADO: detalle,
      DATOS_BANCARIOS_INSUFICIENTES:
        "Faltan datos SEPA · completa IBAN/titular/DNI antes de pasar a domiciliación.",
      BECADO_FUERA_ALCANCE: "Cuota becada · flujo automático no soportado.",
      TURNO_NO_COINCIDE_CUOTA: detalle,
      TURNO_FIJO_REQUERIDO: detalle,
      SIN_SUSCRIPCION_ACTIVA: "Estado de suscripción inconsistente.",
      MULTIPLES_SUSCRIPCIONES_ACTIVAS:
        "Estado inconsistente · más de 1 suscripción activa.",
      SEDE_INVALIDA: "Sede no válida.",
      POSTCONDICION_FALLIDA: "Postcondición violada · estado restaurado.",
      ROLLBACK_EJECUTADO: "Operación cancelada · estado restaurado.",
      ROLLBACK_FALLIDO_INTEGRIDAD_CRITICA:
        "Error técnico crítico · contacta soporte.",
    };
    const msg = mensajesPorCode[code] || detalle;
    return { tipoLegible, code, msg };
  }

  function _resumenAcciones(ejecutadas) {
    return ejecutadas.map(({ accion }) => ({
      [TIPOS_ACCION.CAMBIAR_FORMA_COBRO]: "forma de cobro",
      [TIPOS_ACCION.CAMBIAR_CUOTA]: "cuota",
      [TIPOS_ACCION.CAMBIAR_HORARIO]: "horario",
      [TIPOS_ACCION.PUT_LIGHT]: "datos personales",
    })[accion.tipo] || accion.tipo).join(" + ");
  }

  // Ejecutor concreto de una acción · convierte payloadHint en payload real
  // y llama a axios al endpoint correspondiente.
  async function _ejecutarAccionAxios(accion, current, originals) {
    if (!id) throw new Error("ID de cliente no disponible");
    if (accion.tipo === TIPOS_ACCION.CAMBIAR_FORMA_COBRO) {
      return await axios.post(
        `${API}${ROUTING_ENDPOINTS.CAMBIAR_FORMA_COBRO(id)}`,
        {
          nueva_forma_pago: current.formaPago,
          motivo: null,
          comentario: null,
        },
      );
    }
    if (accion.tipo === TIPOS_ACCION.CAMBIAR_CUOTA) {
      const body = {
        nueva_cuota_id: current.cuotaId,
        motivo: null,
        comentario: null,
      };
      if (accion.payloadHint?.nuevo_turno_fijo) {
        body.nuevo_turno_fijo = accion.payloadHint.nuevo_turno_fijo;
      }
      return await axios.post(
        `${API}${ROUTING_ENDPOINTS.CAMBIAR_CUOTA(id)}`, body);
    }
    if (accion.tipo === TIPOS_ACCION.CAMBIAR_HORARIO) {
      return await axios.post(
        `${API}${ROUTING_ENDPOINTS.CAMBIAR_HORARIO(id)}`,
        {
          nuevo_turno_fijo: current.turnoFijo,
          motivo: null,
          comentario: null,
        },
      );
    }
    if (accion.tipo === TIPOS_ACCION.PUT_LIGHT) {
      const scope = accion.payloadHint?.scope === "sepa_pre_fp"
        ? "sepa_pre_fp" : "personal_y_sepa";
      const payload = construirPayloadPutLight(current, originals, scope);
      // Si por algún motivo no hay delta, no llamar (idempotente)
      if (Object.keys(payload).length === 0) {
        return { data: { ok: true, skipped: true } };
      }
      return await axios.put(
        `${API}${ROUTING_ENDPOINTS.PUT_LIGHT(id)}`, payload);
    }
    throw new Error(`Tipo de acción desconocido: ${accion.tipo}`);
  }

  // Refresca refs originales tras éxito · mantiene coherencia para próximos
  // guardados (evita falsos positivos de "ha cambiado").
  function _refrescarRefsOriginalesDesde(cd) {
    originalFormaPagoRef.current = cd?.forma_pago || null;
    originalCuotaIdRef.current = cd?.cuota_id || null;
    originalTurnoFijoRef.current = serializarTurno(
      Array.isArray(cd?.turno_fijo) ? cd.turno_fijo : []);
    originalInscritoClasesFijasRef.current = !!cd?.inscrito_clases_fijas;
    originalSedeRef.current = cd?.sede || null;
    originalSepaSnapshotRef.current = {
      sepa: {
        sepa_iban: cd?.sepa_iban || "",
        sepa_bic: cd?.sepa_bic || "",
        sepa_titular_cuenta: cd?.sepa_titular_cuenta || "",
        sepa_titular_dni: cd?.sepa_titular_dni || "",
      },
      pagador: {
        tiene_pagador_alternativo: !!cd?.tiene_pagador_alternativo,
        pagador_nombre: cd?.pagador_nombre || "",
        pagador_apellidos: cd?.pagador_apellidos || "",
        pagador_dni: cd?.pagador_dni || "",
        pagador_relacion: cd?.pagador_relacion || "",
        pagador_telefono: cd?.pagador_telefono || "",
        pagador_email: cd?.pagador_email || "",
        pagador_direccion: cd?.pagador_direccion || "",
        pagador_piso: cd?.pagador_piso || "",
        pagador_codigo_postal: cd?.pagador_codigo_postal || "",
        pagador_ciudad: cd?.pagador_ciudad || "",
        pagador_provincia: cd?.pagador_provincia || "",
      },
    };
    originalPersonalSnapshotRef.current = _snapshotPersonalYExcluidos(
      cd, cd?.como_nos_conocio || "");
  }

  // Refetch ficha post-éxito · actualiza UI + refs.
  async function _refrescarFichaYRefs() {
    try {
      const fichaRes = await axios.get(`${API}/admin/clients/${id}/ficha`);
      const cd = fichaRes.data.cliente || fichaRes.data;
      // Conservar campos no devueltos por la ficha si existen en client (deudas
      // calculadas, relacion_asistencial, bonos, ultimas_citas).
      cd.deuda_calculada = client?.deuda_calculada;
      cd.relacion_asistencial = fichaRes.data.relacion_asistencial
        || client?.relacion_asistencial;
      cd.bonos = fichaRes.data.bonos || client?.bonos;
      cd.ultimas_citas = fichaRes.data.ultimas_citas || client?.ultimas_citas;
      setClient(cd);
      setEstadoCanonico(fichaRes.data.estado_canonico || null);
      setLocalTurnos(Array.isArray(cd.turno_fijo) ? cd.turno_fijo : []);
      setComoNosConocio(cd.como_nos_conocio || "");
      _refrescarRefsOriginalesDesde(cd);
      // Refrescar indicador de cobros (rejilla franja superior)
      try { await fetchCobrosIndicador(); } catch {}
    } catch (e) {
      console.warn("No se pudo refrescar ficha tras guardado:", e);
    }
  }

  const handleGlobalSave = async () => {
    // FASE D1 · Predeploy Bloque 4 (Ann 2026-05-06):
    // Bloqueo preventivo · transición inscrito_clases_fijas true→false.
    // Si el cliente tenía inscrito_clases_fijas=true al cargar la ficha y
    // la admin lo desinscribe e intenta "Guardar cambios", NO llamamos al
    // backend (devolvería 409 LEGACY_PATH_BLOCKED_BAJA_CLASES tras el deploy
    // del Bloque 4). Se obliga a usar el botón canónico de baja del bloque
    // de Clases Fijas, que ahora apunta a POST /desactivar con cascada
    // coherente. Solo aplica en modo edición (id ya existente).
    if (
      id &&
      originalInscritoClasesFijasRef.current === true &&
      client?.inscrito_clases_fijas === false
    ) {
      toast.error(
        "Para dar de baja de clases fijas usa el botón de baja/desactivación. " +
        "No se permite desinscribir desde 'Guardar cambios'.",
        { duration: 8000 },
      );
      return;
    }

    // 1. Validator Tutor (Menores) — aviso pero no bloquea el guardado
    if (isMenor) {
      const isTutorValid =
        client.tutor_nombre &&
        client.tutor_relacion &&
        client.tutor_telefono &&
        validarDNI(client.tutor_dni);
      if (!isTutorValid) {
        toast.warning(
          "⚠️ Paciente menor de edad: faltan datos del Adulto Responsable (Nombre, Relación, Teléfono y/o DNI válido). Se guardarán los datos disponibles.",
        );
      }
    }

    // 2. Validator SEPA — WARNING no bloqueante.
    // Antes: bloqueaba el guardado entero si faltaba algún dato SEPA/pagador.
    // Ahora: se guardan los datos disponibles y se avisa de lo que falta, para
    // que el admin pueda rellenar la ficha progresivamente sin perder lo escrito.
    // La ficha solo se remesará cuando los datos SEPA estén completos (el endpoint
    // de remesa valida aparte y deja el recibo en ERROR si falta algo).
    if (client.forma_pago === "domiciliacion" && !isSepaValidoObj()) {
      const faltan = [];
      if (!validarIBAN(client.sepa_iban)) faltan.push("IBAN válido");
      if (client.tiene_pagador_alternativo) {
        if (!validarDNI(client.pagador_dni)) faltan.push("DNI del pagador");
        if (!client.pagador_nombre || !client.pagador_apellidos) faltan.push("nombre + apellidos del pagador");
        if (!client.pagador_relacion) faltan.push("relación del pagador");
      } else if (isMenor) {
        if (!validarDNI(client.tutor_dni)) faltan.push("DNI del tutor");
      } else {
        if (!validarDNI(client.dni)) faltan.push("DNI del titular");
      }
      toast.warning(
        `⚠️ Datos SEPA incompletos (falta: ${faltan.join(", ")}). Se guardan los datos disponibles; completa lo que falte para poder remesar.`,
        { duration: 10000 },
      );
      // NO return — sigue el guardado.
    }

    // ─── Sub-fase 7 · bloqueo si se CAMBIA forma_pago a domiciliación
    // con datos bancarios incompletos. Sólo aplica al CAMBIO (no si ya
    // estaba en domiciliación previamente · ese caso lo cubre el warning
    // anterior no bloqueante). Pista clara con destino "Datos principales".
    const fpCambiaADomi = (
      id
      && originalFormaPagoRef.current !== "domiciliacion"
      && client?.forma_pago === "domiciliacion"
    );
    if (fpCambiaADomi) {
      const faltaSepaPista = mensajeFaltaSepaParaInscripcion(client, {
        isMenor,
        validarIBAN,
        validarDNI,
      });
      if (faltaSepaPista) {
        toast.error(faltaSepaPista, { duration: 10000 });
        return;
      }
    }

    // ─── Sub-fase 5.3.2-B · anti-doble-click ─────────────────────────
    if (savingGlobal) return;
    setSavingGlobal(true);

    try {
      // ─── MODO CREACIÓN (rama legacy preservada · NO router) ─────────
      // El router actual cubre edición. La creación sigue por POST
      // /auth/register con payload "fat" igual que antes. NO se cambia.
      if (isNuevo && !id) {
        const cleanTurnos = localTurnos
          ? localTurnos.filter((t) => t.dia && t.hora)
          : [];
        let sedeCalculada = client.sede;
        if (client.inscrito_clases_fijas && client.estado_clases !== "baja" && client.sede) {
          sedeCalculada = client.sede;
        } else if (client.es_paciente && client.estado_clinica !== "baja" && client.sede_clinica) {
          sedeCalculada = client.sede_clinica;
        }
        const payload = {
          nombre: client.nombre || null,
          primer_apellido: client.primer_apellido || null,
          segundo_apellido: client.segundo_apellido || null,
          email: client.email || null,
          dni: client.dni || null,
          telefono: client.telefono || null,
          telefono_secundario: client.telefono_secundario || null,
          direccion: client.direccion || null,
          numero: client.numero || null,
          piso: client.piso || null,
          codigo_postal: client.codigo_postal || null,
          ciudad: client.ciudad || null,
          provincia: client.provincia || null,
          pais: client.pais || null,
          barrio: client.barrio || null,
          fecha_nacimiento: client.fecha_nacimiento || null,
          sexo: client.sexo || null,
          profesion: client.profesion || null,
          como_nos_conocio: comoNosConocio || null,
          derivador_nombre: client.derivador_nombre || null,
          derivador_id: client.derivador_id || null,
          fecha_alta: client.fecha_alta || null,
          tiene_whatsapp: client.tiene_whatsapp || false,
          tiene_telegram: client.tiene_telegram || false,
          sede: sedeCalculada || client.sede || null,
          cuota_id: client.cuota_id || null,
          inscrito_clases_fijas: client.inscrito_clases_fijas,
          servicio: client.servicio || "fijo",
          es_paciente: client.es_paciente || false,
          activo_clinica: !!(client.es_paciente && client.estado_clinica === "activo"),
          plataforma_gestor_activa: client.plataforma_gestor_activa !== false,
          plataforma_canal_activa: client.plataforma_canal_activa !== false,
          plataforma_paciente_activa: client.plataforma_paciente_activa || false,
          fecha_alta_clases: client.fecha_alta_clases || null,
          estado_clases: client.estado_clases || null,
          fecha_baja_clases: client.fecha_baja_clases || null,
          motivo_pausa_clases: client.motivo_pausa_clases || null,
          fecha_alta_clinica: client.fecha_alta_clinica || null,
          estado_clinica: client.estado_clinica || null,
          sede_clinica: client.sede_clinica || null,
          fecha_baja_clinica: client.fecha_baja_clinica || null,
          rol_general: client.rol_general || "cliente",
          membresia_id: client.membresia_id || "",
          membresia_estado: client.membresia_estado || "activa",
          capabilities_extra: client.capabilities_extra || [],
          capabilities_denied: client.capabilities_denied || [],
          turno_fijo: cleanTurnos,
          observaciones: client.observaciones || null,
          comentarios_confidenciales: client.comentarios_confidenciales || null,
          cliente_especial: client.cliente_especial || false,
          es_monitorizado: client.es_monitorizado || false,
          tarifa_familiar: client.tarifa_familiar || false,
          notas_operativas: client.notas_operativas || null,
          forma_pago: client.forma_pago || null,
          tipo_beca: client.forma_pago === "becado" ? (client.tipo_beca || null) : null,
          sepa_iban: client.sepa_iban || null,
          sepa_bic: client.sepa_bic || null,
          sepa_titular_cuenta: client.sepa_titular_cuenta || null,
          sepa_titular_dni: client.sepa_titular_dni || null,
          sepa_mandato_id: client.sepa_mandato_id || null,
          sepa_mandato_fecha: client.sepa_mandato_fecha || null,
          sepa_firmado: client.sepa_firmado !== undefined ? client.sepa_firmado : false,
          sepa_archivado: client.sepa_archivado !== undefined ? client.sepa_archivado : false,
          tiene_pagador_alternativo: client.tiene_pagador_alternativo !== undefined ? client.tiene_pagador_alternativo : false,
          pagador_nombre: client.pagador_nombre || null,
          pagador_apellidos: client.pagador_apellidos || null,
          pagador_dni: client.pagador_dni || null,
          pagador_relacion: client.pagador_relacion || null,
          pagador_telefono: client.pagador_telefono || null,
          pagador_email: client.pagador_email || null,
          pagador_direccion: client.pagador_direccion || null,
          pagador_piso: client.pagador_piso || null,
          pagador_codigo_postal: client.pagador_codigo_postal || null,
          pagador_ciudad: client.pagador_ciudad || null,
          pagador_provincia: client.pagador_provincia || null,
          tutor_nombre: client.tutor_nombre || null,
          tutor_apellidos: client.tutor_apellidos || null,
          tutor_dni: client.tutor_dni || null,
          tutor_relacion: client.tutor_relacion || null,
          tutor_telefono: client.tutor_telefono || null,
          tutor_email: client.tutor_email || null,
          facturacion_automatica: client.facturacion_automatica || false,
          email_facturacion: client.email_facturacion || null,
        };
        if (!payload.nombre || !payload.primer_apellido || !payload.email) {
          toast.error("Nombre, primer apellido y email son obligatorios.");
          return;
        }
        const ts = Date.now();
        const res = await axios.post(`${API}/auth/register`, {
          ...payload,
          password: `tmp_${ts}_${Math.random().toString(36).slice(2)}`,
          role: "cliente",
        });
        const newId = res.data?.id || res.data?._id;
        if (newId) {
          setId(newId);
          setSearchParams({ id: newId });
          toast.success("Cliente creado correctamente");
        }
        setHasUnsavedChanges(false);
        return;
      }

      // ─── MODO EDICIÓN (router atómico) ────────────────────────────
      const originals = _buildOriginalsForRouting();
      const current = _buildCurrentForRouting();
      const ctx = _buildContextoCliente();
      const decision = decidirRoutingGuardado(originals, current, ctx);

      // Bloqueo frontend · no llamar a backend, mantener hasUnsavedChanges=true.
      if (decision.bloqueado) {
        _toastBloqueo(decision.motivo_bloqueo);
        return;
      }

      // Sin cambios reales · no llamar a backend.
      if (!decision.acciones || decision.acciones.length === 0) {
        toast.info("ℹ️ No hay cambios que guardar");
        // Igualmente reseteamos el flag · el estado actual coincide con el ref.
        setHasUnsavedChanges(false);
        return;
      }

      // Advertencias previas (no bloqueantes).
      for (const w of (decision.advertencias || [])) {
        const code = w?.code;
        const det = w?.detail || {};
        if (code === MOTIVOS_BLOQUEO.TURNO_NO_COINCIDE_CUOTA_UI) {
          toast.warning(
            `⚠️ La nueva cuota requiere ${det.horas_semana_cuota_nueva} turnos · ` +
            `tienes ${det.turnos_actuales}. Ajusta el horario antes de guardar.`,
            { duration: 8000 });
          return;  // bloquear hasta que admin ajuste turnos
        }
      }

      // Ejecutar la secuencia de acciones serial estricto.
      let resultado;
      try {
        resultado = await ejecutarSecuenciaDeAcciones(
          decision,
          (accion) => _ejecutarAccionAxios(accion, current, originals),
        );
      } catch (errSec) {
        // Una acción falló · construir mensaje claro con metadatos.
        const ejecutadas = errSec._accionesEjecutadas || [];
        const fallida = errSec._accionFallida;
        const { tipoLegible, msg } = _toastErrorBackend(fallida?.tipo, errSec);
        if (ejecutadas.length === 0) {
          toast.error(`🛑 ${tipoLegible} falló: ${msg}`, { duration: 9000 });
        } else {
          const aplicadas = _resumenAcciones(ejecutadas);
          toast.error(
            `⚠️ Aplicado: ${aplicadas}. ${tipoLegible} falló: ${msg}. ` +
            `Vuelve a guardar para terminar.`,
            { duration: 12000 });
          // Refresco selectivo · sólo refs de las que sí pasaron, para que
          // el siguiente Guardar no reintente esas acciones. NO machacamos
          // `client` para no perder los cambios personales en pantalla.
          for (const { accion } of ejecutadas) {
            if (accion.tipo === TIPOS_ACCION.CAMBIAR_FORMA_COBRO) {
              originalFormaPagoRef.current = current.formaPago;
            } else if (accion.tipo === TIPOS_ACCION.CAMBIAR_CUOTA) {
              originalCuotaIdRef.current = current.cuotaId;
              originalTurnoFijoRef.current = serializarTurno(current.turnoFijo);
            } else if (accion.tipo === TIPOS_ACCION.CAMBIAR_HORARIO) {
              originalTurnoFijoRef.current = serializarTurno(current.turnoFijo);
            }
            // PUT_LIGHT pre-FP que sí pasó · refrescar snapshot SEPA basicos.
            if (accion.tipo === TIPOS_ACCION.PUT_LIGHT
                && accion.payloadHint?.scope === "sepa_pre_fp") {
              originalSepaSnapshotRef.current = {
                sepa: { ...(current.sepa || {}) },
                pagador: { ...(current.pagador || {}) },
              };
            }
          }
          // Mantener hasUnsavedChanges=true para que el admin pueda reintentar.
        }
        return;  // sin throw adicional · catch externo no toca flags
      }

      // Éxito total · refrescar ficha + refs + rejilla, toast final.
      await _refrescarFichaYRefs();
      setHasUnsavedChanges(false);
      const resumen = _resumenAcciones(resultado.ejecutadas);
      toast.success(`✅ Cambios guardados correctamente · ${resumen}`,
        { duration: 6000 });
    } catch (err) {
      // Catch defensivo · no esperado en el flujo router (errores tipados se
      // gestionan en el bloque interno). NO se hace setHasUnsavedChanges(false)
      // para no perder los cambios al cerrar la ficha sin guardar.
      console.error("Error inesperado al guardar la ficha:", err);
      const detalleLegible = parseErrorDetail(err, "Error desconocido");
      toast.error(`Error al guardar: ${detalleLegible}`, { duration: 8000 });
    } finally {
      setSavingGlobal(false);
    }
  };

  const handleAltaClinica = async () => {
    if (!id || altaClinicaProcessing) return;
    // Regla Ann (2026-05-12): Fisioterapia/consulta clínica SIEMPRE en CONSULTAS.
    // NO usar client.sede como fallback porque es la sede general/clases fijas
    // y mezclaría dominios (caso bug Laura Molas + AAnn Pruebas Cuatro:
    // sede general MUNTANER se heredaba como sede_clinica MUNTANER, incorrecto).
    const sedeClinica = client.sede_clinica || "CONSULTAS";
    const fechaAltaClinica = (
      client.fecha_alta_clinica
        ? String(client.fecha_alta_clinica).slice(0, 10)
        : new Date().toISOString().split("T")[0]
    );

    if (!confirm(`¿Dar de alta a ${client.nombre || "este cliente"} en Fisioterapia (${sedeClinica})?`)) {
      return;
    }

    setAltaClinicaProcessing(true);
    setAltaClinicaError(null);
    try {
      const res = await axios.post(`${API}/admin/clients/${id}/alta-clinica`, {
        sede_clinica: sedeClinica,
        fecha_alta_clinica: fechaAltaClinica,
      });
      const data = res.data || {};
      setClient((prev) => ({
        ...prev,
        es_paciente: true,
        activo_clinica: true,
        estado_clinica: "activo",
        sede_clinica: data.sede_clinica || sedeClinica,
        fecha_alta_clinica: data.fecha_alta_clinica || fechaAltaClinica,
        servicio: data.servicio_aplicado || prev.servicio,
      }));
      toast.success(data.ya_activo ? "Fisioterapia ya estaba activa" : "Fisioterapia activada", {
        duration: 6000,
      });
    } catch (err) {
      const msg = parseErrorDetail(err, "Error al activar Fisioterapia");
      setAltaClinicaError({
        msg,
        ts: new Date().toLocaleString("es-ES"),
      });
      toast.error(msg, { duration: 12000 });
    } finally {
      setAltaClinicaProcessing(false);
    }
  };

  if (loading) {
    return (
      <div
        className="client-new-root cn-empleado"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>Cargando ficha del empleado...</p>
      </div>
    );
  }

  if (error || (!isNuevo && !client.id)) {
    return (
      <div
        className="client-new-root cn-empleado"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        <h2 className="cn-title" style={{ color: "var(--cn-danger)" }}>
          Error
        </h2>
        <p>{error || "Empleado no encontrado"}</p>
        <button className="cn-btn cn-btn--outline" onClick={() => navigate('/admin?tab=laboral&sub=profesionales')}>
          Cerrar ficha
        </button>
      </div>
    );
  }

  // ── Detección de borrador: cliente creado desde "Nuevo Cliente 2" con datos pendientes ──
  const esBorrador = client.email && client.email.includes("@pendiente.tmp");
  const campoPendiente = (valor) => (!valor || valor === "" || (typeof valor === "string" && valor.startsWith("borrador_")));
  const estiloPendiente = (valor) => campoPendiente(valor) ? { color: "#94a3b8", fontStyle: "italic" } : {};

  const getInitials = (n, m) => {
    return ((n ? n[0] : "") + (m ? m[0] : "")).toUpperCase() || "NA";
  };

  // ─────────────────────────────────────────────────────────────────
  // BLOQUE 6A · Cabecera de ficha · única verdad = estado canónico
  // ─────────────────────────────────────────────────────────────────
  // Antes la cabecera derivaba "Alta activa" / "Baja" de campos sueltos
  // del documento users (estado_cliente / estado_clases / es_paciente)
  // y el botón "Dar de baja" usaba SOLO estado_cliente. Resultado: caso
  // Montserrat mostraba a la vez etiqueta "Baja" y botón "Dar de baja".
  //
  // Ahora la cabecera prioriza `estadoCanonico` (devuelto por backend
  // /admin/clients/{id}/ficha vía calcular_estado_operativo_cliente).
  // Si por algún motivo no llega (fallback), conserva la lógica vieja.
  const __norm = (v) => (v || "").toString().toLowerCase();
  const __clasesVivas = client ? ["activo", "pausado"].includes(__norm(client.estado_clases)) : false;
  const __clinicaViva = client ? ["activo", "pausado"].includes(__norm(client.estado_clinica)) : false;
  const __pacienteVivo = !!(client && client.es_paciente && client.activo_clinica);
  const __isDesactivado = client ? __norm(client.estado_cliente) === "desactivado" : false;
  const __tieneServicioVivo = __clasesVivas || __clinicaViva || __pacienteVivo;
  const isActivoLegacy = !__isDesactivado && __tieneServicioVivo;
  const isISLegacy = client?.motivo_desactivacion === "incidencia_de_salud";
  // PR-IS-PRG-1 (Ann 2026-05-22) · diferenciamos IS PROGRAMADA (futura · no
  // operativa todavía) de IS ACTIVA (vigente · cliente pausado).
  // Señales canónicas:
  //   - sus.estado === 'is_programada' AND incidencia_activa.estado === 'programada'
  //     → IS futura. Cabecera NO debe pintar naranja activa, sino "IS programada".
  //   - sus.estado === 'pausada_incidencia' AND incidencia_activa.estado === 'abierta'
  //     → IS vigente. Cabecera naranja "Incidencia de Salud" como hasta ahora.
  const isISProgramada = !!(
    suscripcionActiva
    && suscripcionActiva.estado === "is_programada"
    && suscripcionActiva.incidencia_activa
  );
  // PR-IS-OPERATIVO-F1 (Ann 2026-06-01) · si estado_is_operativo está disponible
  // (backend F1 desplegado), úsarlo como fuente de verdad.
  // Fallback: lógica anterior para compatibilidad con backends no migrados.
  const isISActivaNueva = suscripcionActiva?.estado_is_operativo
    ? suscripcionActiva.estado_is_operativo === "IS_ACTIVA"
    : !!(
        suscripcionActiva
        && suscripcionActiva.estado !== "is_programada"
        && suscripcionActiva.incidencia_activa
        && (suscripcionActiva.incidencia_activa.estado || "abierta") !== "programada"
      );
  // Mantengo `isISNuevo` para compatibilidad con resto del código (cubre ambos).
  const isISNuevo = isISProgramada || isISActivaNueva;
  const isIS = isISLegacy || isISActivaNueva;  // IS legacy + IS activa real (programada NO cuenta como IS vigente)

  // Lectura canónica (read-only · puede ser null si fallback)
  const ec = estadoCanonico || null;
  const ecGlobal = ec?.estado_operativo_global || null;     // legacy técnico
  const ecRequiereRevision = !!(ec?.flags?.requiere_revision);
  const ecMotivoRevision = ec?.flags?.motivo_revision || null;
  const ecAcciones = ec?.acciones_permitidas || null;
  // ── Modelo estructurado (Fase 1 Opción B · Ann 2026-05-08) ──
  // Estado principal del cliente solo puede ser alta / baja / incidencia_salud.
  // Servicios (clases_fijas, fisioterapia, etc.) van separados.
  const ecPrincipal = ec?.estado_cliente_principal || null;
  const ecEtiquetaPrincipal = ec?.etiqueta_ui?.estado_principal || null;
  const ecServiciosUI = ec?.etiqueta_ui?.servicios || [];
  const ecAlertasIntegridad = ec?.alertas_integridad || [];

  // Indicador efectivo de "cliente de baja a efectos visuales".
  // Estado principal directo del modelo nuevo · fallback heurística legacy.
  const isActivo = ec ? (ecPrincipal === "alta") : isActivoLegacy;
  const esClienteDeBaja = ec ? (ecPrincipal === "baja") : (!isActivoLegacy && !isIS);
  // Señal visual cabecera naranja para Incidencia de Salud (Ann 2026-05-09).
  // Prioridad cabecera: baja (gris) > IS (naranja) > alta (normal).
  //
  // PR-L13 (Ann 2026-05-20) · CORTOCIRCUITO ARREGLADO:
  // Si `incidencia_activa` está poblada en la suscripción, debe pintar IS
  // SIEMPRE, aunque `estado_canonico.principal` diga otra cosa.
  // Esto cubre casos tipo Josefina donde el backend no había marcado
  // estado_canonico=incidencia_salud aún. Fuente de verdad real:
  // suscripciones.incidencia_activa.
  // PR-IS-PRG-1 (Ann 2026-05-22) · IS PROGRAMADA NO debe pintar cabecera
  // naranja activa. Sólo IS activa real (pausada_incidencia + incidencia_activa
  // con estado distinto a 'programada') marca al cliente como IS visualmente.
  const esClienteIS = ec
    ? (ecPrincipal === "incidencia_salud" && !isISProgramada || isISActivaNueva)
    : isIS;

  // ── Construcción del badge superior ──
  // Regla Ann 2026-05-08: el badge principal solo puede ser
  // Alta (verde) / Baja (rojo) / Incidencia de Salud (naranja).
  // Servicios se muestran APARTE en zona dedicada · alerta de integridad
  // se muestra en banner separado, no contamina el badge.

  // Norma Ann 2026-05-23 · cabecera IS con fechas visibles:
  //   "Incidencia de salud · Inicio IS: 01/06/2026 · Final IS: indefinida"
  //   "IS programada · Inicio IS: 01/06/2026 · Final IS: 15/08/2026"
  // Prioridad fecha final: fecha_fin_real > fecha_fin_prevista > fecha_fin > "indefinida".
  // Aplica tanto a IS activa (pausada_incidencia/abierta) como a IS programada
  // (is_programada/programada). No inventar fechas: si falta fecha_inicio
  // (no debería ocurrir), devolver el texto base sin extras.
  const _formatoBadgeIS = (textoBase, ia) => {
    if (!ia) return textoBase;
    const fmt = (d) => {
      if (!d) return null;
      try { return new Date(String(d).slice(0, 10)).toLocaleDateString("es-ES"); }
      catch { return String(d).slice(0, 10); }
    };
    const fiTxt = fmt(ia.fecha_inicio);
    if (!fiTxt) return textoBase;
    const ffTxt = fmt(ia.fecha_fin_real)
               || fmt(ia.fecha_fin_prevista)
               || fmt(ia.fecha_fin)
               || "indefinida";
    return `${textoBase} · Inicio IS: ${fiTxt} · Final IS: ${ffTxt}`;
  };

  let estadoBadge = (
    <span className="cn-badge cn-badge--neutral">Desconocido</span>
  );
  let estadoTexto = "Desconocido";

  if (ec && ecPrincipal) {
    // Camino estructurado · 3 valores
    if (ecPrincipal === "alta") {
      estadoBadge = <span className="cn-badge cn-badge--success">{ecEtiquetaPrincipal || "Alta"}</span>;
      estadoTexto = ecEtiquetaPrincipal || "Alta";
    } else if (ecPrincipal === "baja") {
      estadoBadge = <span className="cn-badge cn-badge--danger">{ecEtiquetaPrincipal || "Baja"}</span>;
      estadoTexto = ecEtiquetaPrincipal || "Baja";
    } else if (ecPrincipal === "incidencia_salud") {
      // Para IS preservamos el nombre específico de la incidencia si llega
      // desde la suscripción nueva · si no, etiqueta canónica.
      // PR-IS-PRG-1 (Ann 2026-05-22) · si la IS está PROGRAMADA (futura), el
      // badge es neutro (no naranja).
      // Norma Ann 2026-05-23 · badge incluye Inicio IS y Final IS (helper).
      const _ia = suscripcionActiva?.incidencia_activa || null;
      if (isISProgramada) {
        const texto = _formatoBadgeIS("IS programada", _ia);
        estadoBadge = <span className="cn-badge cn-badge--neutral">{texto}</span>;
        estadoTexto = texto;
      } else if (isISActivaNueva) {
        const incNombre = suscripcionActiva.incidencia_activa.tipo_incidencia_nombre || (ecEtiquetaPrincipal || "Incidencia de Salud");
        const texto = _formatoBadgeIS(incNombre, _ia);
        estadoBadge = <span className="cn-badge cn-badge--warning">{texto}</span>;
        estadoTexto = texto;
      } else {
        const baseTxt = ecEtiquetaPrincipal || "Incidencia de Salud";
        const texto = _formatoBadgeIS(baseTxt, _ia);
        estadoBadge = <span className="cn-badge cn-badge--warning">{texto}</span>;
        estadoTexto = texto;
      }
    }
  } else {
    // Fallback legacy (cuando backend no devolvió estado_canonico)
    // PR-IS-PRG-1 (Ann 2026-05-22) · diferenciar programada antes que activa.
    // Norma Ann 2026-05-23 · badge incluye Inicio IS y Final IS (helper).
    const _ia = suscripcionActiva?.incidencia_activa || null;
    if (isISProgramada) {
      const texto = _formatoBadgeIS("IS programada", _ia);
      estadoBadge = <span className="cn-badge cn-badge--neutral">{texto}</span>;
      estadoTexto = texto;
    } else if (isISActivaNueva) {
      const incNombre = suscripcionActiva.incidencia_activa.tipo_incidencia_nombre || "Incidencia";
      const texto = _formatoBadgeIS(incNombre, _ia);
      estadoBadge = <span className="cn-badge cn-badge--warning">{texto}</span>;
      estadoTexto = texto;
    } else if (isISLegacy) {
      // IS legacy sin sus.incidencia_activa estructurada · sin fechas embebidas:
      // el helper devolverá el texto base sin extras.
      const texto = _formatoBadgeIS("Incidencia de Salud", _ia);
      estadoBadge = <span className="cn-badge cn-badge--warning">{texto}</span>;
      estadoTexto = texto;
    } else if (isActivoLegacy) {
      estadoBadge = <span className="cn-badge cn-badge--success">Alta</span>;
      estadoTexto = "Alta";
    } else {
      estadoBadge = <span className="cn-badge cn-badge--danger">Baja</span>;
      estadoTexto = "Baja";
    }
  }

  // ── Permisos para acciones de baja ──
  // Reglas Ann BLOQUE 6A:
  //   · Si `requiere_revision`, NO mostrar botón "Dar de baja" como acción
  //     normal habilitada. Mostrar versión deshabilitada con tooltip.
  //   · Si `acciones_permitidas.puede_dar_baja === false`, no mostrar acción
  //     de baja en absoluto.
  //   · Si todo OK, mostrar botón "Dar de baja" normal.
  // Sin estado canónico (fallback) caemos a la condición legacy.
  const _canonicoPuedeBaja = !!(ecAcciones && ecAcciones.puede_dar_baja);
  const _legacyPuedeBaja = !!(id && client && (client.estado_cliente || "").toLowerCase() !== "desactivado");
  const puedeBajaNormal = ec
    ? (_canonicoPuedeBaja && !ecRequiereRevision)
    : _legacyPuedeBaja;
  const mostrarBajaDeshabilitada = ec && _canonicoPuedeBaja && ecRequiereRevision;
  const tooltipBajaBloqueada = ecMotivoRevision
    ? `No se puede dar de baja hasta resolver el estado del cliente: ${ecMotivoRevision}`
    : "No se puede dar de baja hasta resolver el estado del cliente.";

  const getDateFormat = (d) =>
    d
      ? new Date(d).toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  const getEventColor = (tipo) => {
    if (tipo === "Incidencias" || tipo === "INCIDENCIA_SALUD" || tipo === "INCIDENCIA") return "#d97706"; // naranja
    if (tipo === "queja" || tipo === "incidente_negativo") return "#dc2626"; // rojo
    if (tipo === "incidente_positivo") return "#16a34a"; // verde
    if (tipo === "BAJA" || tipo === "Altas/Bajas") return "#16a34a";
    if (tipo === "FACTURACION") return "#3b82f6";
    return "#64748b";
  };

  const getAllowedHours = () => {
    if (!client || !client.cuota_id) return 2;
    const selectedCuota = cuotas.find((c) => c.id === client.cuota_id);
    return selectedCuota && selectedCuota.horas_semana !== undefined ? selectedCuota.horas_semana : 2;
  };
  const maxHours = getAllowedHours();

  const handleCloseProfile = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "Hay cambios sin guardar en la ficha. ¿Estás seguro de que quieres salir y perderlos?",
      );
      if (!confirmLeave) return;
    }
    // Cerrar ficha de PROFESIONAL → volver SIEMPRE al listado de Profesionales.
    navigate('/admin?tab=laboral&sub=profesionales');
  };

  // === MODAL PREVIEW: Funciones estándar para facturas y recibos ===
  // CRITICO: Arquitectura idéntica para TODOS los roles (admin, editor, gestor)
  const openDocPreview = async (type, docId, nombre) => {
    setDocPreview({ type, id: docId, pdfUrl: null, nombre: nombre || docId });
    setDocPreviewLoading(true);
    try {
      const endpoint = type === "recibo"
        ? `${API}/recibos/${docId}/pdf?t=${Date.now()}`
        : `${API}/facturacion/facturas/${docId}/pdf?t=${Date.now()}`;
      const resp = await axios.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      setDocPreview(prev => ({ ...prev, pdfUrl: url }));
    } catch (err) {
      toast.error(`Error al cargar ${type}`, { duration: 5000 });
      setDocPreview(null);
    } finally {
      setDocPreviewLoading(false);
    }
  };
  const closeDocPreview = () => { setDocPreview(null); };
  const handleDocPrint = () => {
    if (!docPreview?.pdfUrl) return;
    const w = window.open(docPreview.pdfUrl, "_blank");
    if (w) w.onload = () => w.print();
  };
  const handleDocDownload = () => {
    if (!docPreview?.pdfUrl) return;
    const link = document.createElement("a");
    link.href = docPreview.pdfUrl;
    const label = docPreview.type === "recibo" ? "Recibo" : "Factura";
    link.setAttribute("download", `${label}_${docPreview.nombre}.pdf`.replace(/\s+/g, "_"));
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const handleDocEmail = async () => {
    if (!docPreview) return;
    setDocSendingEmail(true);
    try {
      const endpoint = docPreview.type === "recibo"
        ? `${API}/recibos/${docPreview.id}/enviar-email`
        : `${API}/facturacion/facturas/${docPreview.id}/enviar-email`;
      const res = await axios.post(endpoint);
      toast.success(res.data.message || "Email enviado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar email");
    } finally {
      setDocSendingEmail(false);
    }
  };

  return (
    <div
      className="client-new-root cn-empleado"
      onChange={() => setHasUnsavedChanges(true)}
    >
      {/* Botón "Volver al listado" (solo si la ficha se abrió con returnKey de contexto) */}
      {returnCtx && (
        <div style={{ padding: "8px 24px 0", maxWidth: 1400, margin: "0 auto" }}>
          <button
            type="button"
            onClick={handleVolver}
            style={{ fontSize: 13, color: "var(--cn-primary, #2563eb)", background: "transparent", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span style={{ fontSize: 16 }}>←</span> Volver al listado
          </button>
        </div>
      )}

      {/* A) CABECERA SUPERIOR FIJA EXECUTIVA */}
      <div className={`cn-header-container${esClienteDeBaja ? " cn-header-container--baja" : (esClienteIS ? " cn-header-container--is" : "")}`}>
        <header className="cn-header">
          <div className="cn-exec-header">
            <div className="cn-exec-left">
              <div className="cn-avatar-wrapper">
                <input
                  type="file"
                  accept="image/*"
                  ref={fotoInputRef}
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!file.type.startsWith('image/')) { toast.error('Selecciona una imagen válida'); return; }
                    if (file.size > 15 * 1024 * 1024) { toast.error('Máximo 15MB'); return; }
                    try {
                      // Optimizar: redimensionar a max 800px y comprimir
                      const base64 = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const img = new Image();
                          img.onload = () => {
                            let { width, height } = img;
                            const max = 800;
                            if (width > max || height > max) {
                              const ratio = Math.min(max / width, max / height);
                              width = Math.round(width * ratio);
                              height = Math.round(height * ratio);
                            }
                            const canvas = document.createElement('canvas');
                            canvas.width = width;
                            canvas.height = height;
                            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                            resolve(canvas.toDataURL('image/jpeg', 0.8));
                          };
                          img.src = ev.target.result;
                        };
                        reader.readAsDataURL(file);
                      });
                      const res = await axios.post(`${API}/admin/clients/${id}/foto-perfil`, { foto_base64: base64 });
                      if (res.data.success) {
                        setClient(prev => ({ ...prev, foto_perfil: base64 }));
                        toast.success('Foto actualizada');
                      }
                    } catch (err) {
                      toast.error('Error al subir la foto');
                    }
                    e.target.value = '';
                  }}
                />
                {client.foto_perfil ? (
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="cn-avatar-pic cursor-pointer" onClick={() => fotoInputRef.current?.click()}>
                        <img src={client.foto_perfil} alt="avatar" />
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="w-96 p-2 z-[9999] border-none shadow-2xl bg-transparent"
                      side="right"
                      align="center"
                    >
                      <div className="bg-white p-1 rounded-xl shadow-2xl overflow-hidden border border-slate-200">
                        <img
                          src={client.foto_perfil}
                          alt="Foto de perfil grande"
                          className="w-full h-96 object-cover rounded-lg"
                        />
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ) : (
                  <div className="cn-avatar-pic cursor-pointer" onClick={() => fotoInputRef.current?.click()}>
                    {getInitials(client.nombre, client.primer_apellido)}
                  </div>
                )}
                <button
                  className="cn-avatar-change"
                  onClick={() => fotoInputRef.current?.click()}
                >
                  Cambiar
                </button>
              </div>
              <div className="cn-exec-info">
                {esBorrador && (
                  <div style={{ padding: "6px 12px", marginBottom: "8px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "12px", color: "#92400e", fontWeight: 500 }}>
                    Ficha nueva — completa los datos del cliente y guarda los cambios
                  </div>
                )}
                <h1 className="cn-header-name" style={esBorrador && campoPendiente(client.nombre) ? { color: "#94a3b8", fontStyle: "italic" } : {}}>
                  {campoPendiente(client.nombre) && campoPendiente(client.primer_apellido)
                    ? "Nuevo cliente (pendiente)"
                    : `${client.nombre || ""} ${client.primer_apellido || ""} ${client.segundo_apellido || ""}`
                  }
                  {estadoBadge}
                  {/* Servicios separados del estado principal · regla Ann
                      2026-05-08. Aparecen como chips junto al badge superior.
                      No se mezclan en el badge (eso sería "Alta · Fisio").
                      Fuente: ec.etiqueta_ui.servicios (modelo estructurado).
                      Regla cabecera (Ann 2026-05-08 · rectificación):
                      SOLO se muestran servicios ACTIVOS. Los servicios en
                      baja / pausa IS / no_inscrita NO contaminan la cabecera
                      · esa info vive dentro de su pestaña correspondiente
                      (Inscripción, Clínica, etc.). */}
                  {ec && (() => {
                    const serviciosActivos = ecServiciosUI.filter(
                      (srv) => srv.endsWith(" activa")
                    );
                    if (serviciosActivos.length === 0) return null;
                    return (
                      <span style={{ marginLeft: "8px", display: "inline-flex", gap: "6px", flexWrap: "wrap", verticalAlign: "middle" }}>
                        {serviciosActivos.map((srv) => (
                          <span key={srv} style={{
                            display: "inline-block", padding: "2px 8px", borderRadius: "12px",
                            fontSize: "11px", fontWeight: 600,
                            backgroundColor: "#dcfce7", color: "#166534",
                            border: "1px solid #bbf7d0",
                          }}>
                            {srv}
                          </span>
                        ))}
                      </span>
                    );
                  })()}
                  <div className="cn-header-alerts-inline">
                    {clientAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className="cn-alert-inline-badge"
                        style={{
                          color: alert.bg,
                          backgroundColor: `rgba(var(--cn-${alert.bg.replace("var(--cn-", "").replace(")", "")}-rgb, 217, 119, 6), 0.1)`,
                        }}
                        title={alert.title}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          {alert.icon}
                        </svg>
                      </div>
                    ))}
                  </div>
                </h1>
                <div className="cn-exec-meta">
                  <span>
                    DNI: <strong>{client.dni || "—"}</strong>
                  </span>
                  <span className="cn-divider">|</span>
                  <span>
                    Edad:{" "}
                    <strong>{getAge(client.fecha_nacimiento)} años</strong>
                  </span>
                  <span className="cn-divider">|</span>
                  <span>
                    Sede: <strong>{client.sede || "—"}</strong>
                    {(() => {
                      const sedesSecundarias = [];
                      if (client.es_paciente && client.estado_clinica === "activo" && client.sede_clinica && client.sede_clinica !== client.sede) {
                        sedesSecundarias.push(client.sede_clinica);
                      }
                      return sedesSecundarias.length > 0 ? (
                        <span style={{ fontSize: "11px", color: "var(--cn-text-muted)", marginLeft: "6px", fontWeight: 400 }}>
                          + {sedesSecundarias.join(", ")}
                        </span>
                      ) : null;
                    })()}
                  </span>
                  <span className="cn-divider">|</span>
                  <span>
                    Alta: <strong>{getDateFormat(client.fecha_alta)}</strong>
                  </span>
                  {client.estado_cliente === "desactivado" && client.fecha_desactivacion && (
                    <>
                      <span className="cn-divider">|</span>
                      <span style={{ color: "#dc2626" }}>
                        Baja: <strong>{getDateFormat(client.fecha_desactivacion)}</strong>
                      </span>
                    </>
                  )}
                  {id && (client.estado_cliente || "").toLowerCase() === "desactivado" && (
                    <>
                      <span className="cn-divider">|</span>
                      <button
                        type="button"
                        onClick={() => setShowReinscribirModal(true)}
                        style={{
                          background: "#16a34a",
                          color: "white",
                          border: "none",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        title="Reinscribir cliente: pasa de baja a alta solo a nivel cliente. Los servicios (Clases Fijas, plataformas, SEPA, etc.) se gestionan después por separado."
                      >
                        ✅ Reinscribir cliente
                      </button>
                    </>
                  )}
                  {/* BLOQUE 6A · Botón "Dar de baja" usa estado canónico:
                       · habilitado: puede_dar_baja=true Y NO requiere_revision
                       · deshabilitado con tooltip motivo: requiere_revision=true
                       · oculto: puede_dar_baja=false (cliente desactivado u otro)
                     Sin canónico → fallback legacy. */}
                  {id && puedeBajaNormal && (
                    <>
                      <span className="cn-divider">|</span>
                      <button
                        type="button"
                        onClick={() => setShowBajaDefinitivaModal(true)}
                        style={{
                          background: "#dc2626",
                          color: "white",
                          border: "none",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        title="Dar de baja definitiva al cliente. Cierra suscripciones, desactiva domiciliación y anula cobros futuros."
                      >
                        🛑 Dar de baja
                      </button>
                    </>
                  )}
                  {id && mostrarBajaDeshabilitada && (
                    <>
                      <span className="cn-divider">|</span>
                      <button
                        type="button"
                        disabled
                        style={{
                          background: "#fef3c7",
                          color: "#92400e",
                          border: "1px solid #fde68a",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "not-allowed",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          opacity: 0.85,
                        }}
                        title={tooltipBajaBloqueada}
                      >
                        ⚠️ Dar de baja (bloqueado)
                      </button>
                    </>
                  )}
                </div>
                {/* BLOQUE 6A · Aviso visible cuando hay motivo_revision crítico
                     o el estado canónico es reinscripcion_pendiente / activo_sin_servicio.
                     No es tooltip, es texto leído. Sólo si tenemos canónico. */}
                {/* Banner de alertas de integridad · usa modelo estructurado
                    nuevo (ec.alertas_integridad lista) con fallback al
                    motivo_revision legacy. NO contamina el badge superior. */}
                {ec && ecRequiereRevision && (ecAlertasIntegridad.length > 0 || ecMotivoRevision) && (
                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "11px",
                      color: "#92400e",
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fde68a",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      lineHeight: 1.4,
                    }}
                    title={tooltipBajaBloqueada}
                  >
                    <strong>⚠️ Revisar estado:</strong>{" "}
                    {ecAlertasIntegridad.length > 0
                      ? ecAlertasIntegridad.join(" · ")
                      : ecMotivoRevision}
                  </div>
                )}
                {ec && !ecRequiereRevision && ecGlobal === "reinscripcion_pendiente" && (
                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "11px",
                      color: "#92400e",
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fde68a",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      lineHeight: 1.4,
                    }}
                  >
                    Cliente con estado pendiente de completar. Revisar inscripción antes de operar baja/cobros.
                  </div>
                )}
                {/* Fila de servicios activos */}
                <div className="cn-exec-meta" style={{ marginTop: "2px", fontSize: "12px", color: "var(--cn-text-muted)" }}>
                  {client.inscrito_clases_fijas && client.turno_fijo && client.turno_fijo.length > 0 && (
                    <>
                      <span>
                        Fijas: <strong style={{ color: "var(--cn-text-main)" }}>
                          {client.turno_fijo.filter(t => t.dia && t.hora).map(t => `${t.dia.slice(0,2)} ${t.hora}`).join(", ") || "Sin turnos"}
                        </strong>
                      </span>
                      <span className="cn-divider">|</span>
                    </>
                  )}
                  {client.es_paciente && client.estado_clinica === "activo" && (
                    <>
                      <span><strong style={{ color: "var(--cn-text-main)" }}>Fisioterapia</strong></span>
                      <span className="cn-divider">|</span>
                    </>
                  )}
                  {client.taquilla_numero && (
                    <>
                      <span>Taquilla: <strong style={{ color: "var(--cn-text-main)" }}>{client.taquilla_numero}</strong></span>
                      <span className="cn-divider">|</span>
                    </>
                  )}
                  <span>{client.cambios_mes || 0} cambios</span>
                </div>
              </div>
            </div>

            <div className="cn-exec-right">
              <div className="cn-exec-kpis">
                <div className="cn-exec-kpi">
                  <span className="cn-kpi-lbl">Estado</span>
                  <span className="cn-kpi-val">{estadoTexto}</span>
                </div>
                <div className="cn-exec-kpi">
                  <span className="cn-kpi-lbl">Deuda</span>
                  <span
                    className="cn-kpi-val"
                    style={{
                      color:
                        client.tiene_deuda || client.deuda_calculada > 0
                          ? "var(--cn-danger)"
                          : "var(--cn-text-main)",
                    }}
                  >
                    {client.deuda_calculada > 0
                      ? `${client.deuda_calculada.toFixed(2)}€`
                      : "0.00€"}
                  </span>
                </div>
                <div className="cn-exec-kpi">
                  <span className="cn-kpi-lbl">SEPA</span>
                  <span
                    className="cn-kpi-val"
                    style={{
                      color: client.sepa_firmado
                        ? "var(--cn-success)"
                        : "var(--cn-warning)",
                    }}
                  >
                    {client.sepa_firmado ? "Activo" : "Pendiente"}
                  </span>
                </div>
                <div className="cn-exec-kpi">
                  <span className="cn-kpi-lbl">Cambios</span>
                  <span className="cn-kpi-val">{client.cambios_mes || 0}</span>
                </div>
              </div>

              <div className="cn-exec-actions" style={{ position: "relative" }}>
                <button
                  className="cn-btn cn-btn--outline"
                  style={{
                    position: "relative",
                    width: "34px",
                    height: "34px",
                    padding: 0,
                    borderRadius: "8px",
                  }}
                  onClick={() => setIsAlertsOpen(!isAlertsOpen)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  <span className="cn-alert-counter">{clientAlerts.length}</span>
                </button>

                {isAlertsOpen && (
                  <div className="cn-alerts-dropdown">
                    <div className="cn-alerts-dropdown-header">
                      <span>Avisos Activos</span>
                      <span className="cn-alerts-dropdown-count">
                        {clientAlerts.length}
                      </span>
                    </div>
                    <div className="cn-alerts-dropdown-list">
                      {clientAlerts.map((alert) => (
                        <div key={alert.id} className="cn-alert-dropdown-item">
                          <div
                            className="cn-alert-dropdown-icon"
                            style={{
                              backgroundColor: `rgba(var(--cn-${alert.bg.replace("var(--cn-", "").replace(")", "")}-rgb, 217, 119, 6), 0.1)`,
                              color: alert.bg,
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              {alert.icon}
                            </svg>
                          </div>
                          <div className="cn-alert-dropdown-content">
                            <span className="cn-alert-dropdown-title">
                              {alert.title}
                            </span>
                            <span className="cn-alert-dropdown-desc">
                              {alert.desc}
                            </span>
                          </div>
                          <span className="cn-alert-dropdown-date">
                            {alert.date}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div
                      className="cn-alerts-dropdown-footer"
                      onClick={() => setIsAlertsOpen(false)}
                    >
                      Cerrar bandeja de avisos
                    </div>
                  </div>
                )}

                <span className="cn-divider" style={{ margin: "0 8px" }}></span>

                <button
                  style={{
                    width: 32, height: 32, borderRadius: '50%',
                    backgroundColor: "#cbd5e1", color: "#334155",
                    border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 600, transition: "background 0.15s",
                  }}
                  onClick={handleCloseProfile}
                  onMouseEnter={e => e.target.style.backgroundColor = '#94a3b8'}
                  onMouseLeave={e => e.target.style.backgroundColor = '#cbd5e1'}
                  title="Cerrar ficha"
                >
                  ✕
                </button>
                <button
                  className="cn-btn cn-btn--primary"
                  style={{
                    backgroundColor: "var(--cn-success)",
                    borderColor: "var(--cn-success)",
                    color: "white",
                    opacity: savingGlobal ? 0.7 : 1,
                    cursor: savingGlobal ? "wait" : "pointer",
                  }}
                  onClick={guardarFicha}
                  disabled={savingGlobal}
                >
                  {savingGlobal ? "Guardando..." : "Guardar"}
                </button>
                <button
                  className="cn-btn cn-btn--primary"
                  style={{
                    backgroundColor: "#1e293b",
                    borderColor: "#1e293b",
                    color: "white",
                    opacity: savingGlobal ? 0.7 : 1,
                    cursor: savingGlobal ? "wait" : "pointer",
                  }}
                  onClick={async () => {
                    await guardarFicha();
                    navigate('/admin?tab=laboral&sub=profesionales');
                  }}
                  disabled={savingGlobal}
                >
                  {savingGlobal ? "Guardando..." : "Guardar y cerrar"}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* B) TABS SUPERIORES */}
        <div className="cn-tabs-container">
          <nav className="cn-tabs">
            {/* EMPLEADO · 11 pestañas. La antigua "Documentación" se fusionó DENTRO
                de Incorporación (al final): los documentos del trabajador se piden y
                se suben en el mismo sitio donde se le da de alta. */}
            {[
              ["datos", "Datos"],
              ["incorporacion", "Incorporación"],
              ["contrato", "Contrato"],
              ["salario", "Salario"],
              ["nominas", "Nóminas"],
              ["jornada", "Cuadrante y horario"],
              ["ausencias", "Ausencias"],
              ["formacion", "Formación y PRL"],
              ["desempeno", "Desempeño"],
              ["comunicacion_emp", "Comunicación"],
              ["historial_emp", "Historial"],
            ].map(([tid, label]) => (
              <button
                key={tid}
                className={`cn-tab ${activeTab === tid ? "cn-tab--active" : ""}`}
                onClick={() => setActiveTab(tid)}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>
      <main className="cn-main">
        {/* C) TAB "DATOS PRINCIPALES" */}
        {activeTab === "incorporacion" && (() => {
          const total = CHECKLIST_INCORP.length;
          const hechas = CHECKLIST_INCORP.filter((t) => incorp.checklist[t.id]).length;
          const pct = total ? Math.round((hechas / total) * 100) : 0;
          return (
            <div style={{ padding: 16 }}>
              {/* Progreso */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">Progreso de incorporación</h3></div>
                <div className="cn-panel-content">
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 999, height: 12, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "#16a34a" : "var(--cn-primary)", transition: "width .3s" }} />
                    </div>
                    <strong style={{ minWidth: 110, textAlign: "right" }}>{hechas}/{total} · {pct}%</strong>
                  </div>
                  {pct === 100 && <p style={{ color: "#16a34a", fontWeight: 600, margin: "8px 0 0" }}>✓ Incorporación completada</p>}
                </div>
              </div>
              {/* Datos del alta */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">Datos del alta</h3></div>
                <div className="cn-panel-content">
                  <div className="cn-data-grid cn-data-grid--editable">
                    <div className="cn-data-item"><span className="cn-data-label">Fecha de incorporación</span><input type="date" className="cn-input-editable" value={incorp.fecha_incorporacion} onChange={(e) => setIncorp({ ...incorp, fecha_incorporacion: e.target.value })} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Fecha de alta en Seg. Social</span><input type="date" className="cn-input-editable" value={incorp.fecha_alta_ss} onChange={(e) => setIncorp({ ...incorp, fecha_alta_ss: e.target.value })} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Responsable de la incorporación</span><input type="text" className="cn-input-editable" value={incorp.responsable} onChange={(e) => setIncorp({ ...incorp, responsable: e.target.value })} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Periodo de prueba · inicio</span><input type="date" className="cn-input-editable" value={incorp.prueba_inicio} onChange={(e) => setIncorp({ ...incorp, prueba_inicio: e.target.value })} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Periodo de prueba · fin</span><input type="date" className="cn-input-editable" value={incorp.prueba_fin} onChange={(e) => setIncorp({ ...incorp, prueba_fin: e.target.value })} /></div>
                    {/* Sedes de incorporación · casillas con las sedes REALES del CRM
                        (db.sedes, sin listas escritas a mano). Puede ser más de una. */}
                    <div className="cn-data-item" style={{ gridColumn: "1 / -1" }}>
                      <span className="cn-data-label">Sedes de incorporación</span>
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
                        {sedesCrm.map((sd) => {
                          const nombre = sd.nombre || sd;
                          const marcada = (incorp.sedes || []).includes(nombre);
                          return (
                            <label key={nombre} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                              <input type="checkbox" checked={marcada} style={{ width: 18, height: 18 }}
                                onChange={() => {
                                  const l = marcada
                                    ? (incorp.sedes || []).filter((x) => x !== nombre)
                                    : [...(incorp.sedes || []), nombre];
                                  setIncorp({ ...incorp, sedes: l });
                                  setHasUnsavedChanges(true);
                                }} />
                              <span>{nombre}</span>
                            </label>
                          );
                        })}
                        {sedesCrm.length === 0 && (
                          <span style={{ fontSize: 12, color: "var(--cn-text-muted)" }}>Cargando sedes…</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* ACUERDOS · pactos y condiciones que se firman al incorporarse:
                  exclusividad o pluriempleo (con los datos de la otra empresa),
                  competencia desleal, contrato de formación y encuadre de convenio.
                  Se guardan en laboral_empleados con el resto de la ficha RRHH. */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">Acuerdos</h3></div>
                <div className="cn-panel-content">
                  <div className="cn-data-grid cn-data-grid--editable">
                    <div className="cn-data-item">
                      <span className="cn-data-label">Estado profesional</span>
                      <select className="cn-input-editable" value={client.estado_profesional || ""}
                        onChange={(e) => { setClient({ ...client, estado_profesional: e.target.value }); setHasUnsavedChanges(true); }}>
                        <option value="">— Sin definir —</option>
                        <option value="exclusividad">Exclusividad</option>
                        <option value="pluriempleo">Pluriempleo</option>
                      </select>
                    </div>
                    <div className="cn-data-item">
                      <span className="cn-data-label">Categoría (convenio)</span>
                      <input type="text" className="cn-input-editable" value={contrato.categoria_profesional || ""}
                        onChange={(e) => { setContrato({ ...contrato, categoria_profesional: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                    <div className="cn-data-item">
                      <span className="cn-data-label">Nivel (convenio)</span>
                      <input type="text" className="cn-input-editable" value={client.nivel_convenio || ""}
                        onChange={(e) => { setClient({ ...client, nivel_convenio: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                  </div>

                  {/* Datos de la OTRA empresa · solo si hay pluriempleo. */}
                  {client.estado_profesional === "pluriempleo" && (
                    <div style={{ marginTop: 12, padding: 12, border: "1px solid var(--cn-border)", borderRadius: 8, background: "rgba(255,255,255,0.55)" }}>
                      <span className="cn-data-label" style={{ display: "block", marginBottom: 8 }}>Otra empresa (pluriempleo)</span>
                      <div className="cn-data-grid cn-data-grid--editable">
                        <div className="cn-data-item"><span className="cn-data-label">Nombre de la empresa</span>
                          <input type="text" className="cn-input-editable" value={client.pluriempleo_empresa || ""}
                            onChange={(e) => { setClient({ ...client, pluriempleo_empresa: e.target.value }); setHasUnsavedChanges(true); }} /></div>
                        <div className="cn-data-item"><span className="cn-data-label">Sector</span>
                          <input type="text" className="cn-input-editable" value={client.pluriempleo_sector || ""}
                            onChange={(e) => { setClient({ ...client, pluriempleo_sector: e.target.value }); setHasUnsavedChanges(true); }} /></div>
                        <div className="cn-data-item"><span className="cn-data-label">Jornada</span>
                          <input type="text" className="cn-input-editable" value={client.pluriempleo_jornada || ""}
                            onChange={(e) => { setClient({ ...client, pluriempleo_jornada: e.target.value }); setHasUnsavedChanges(true); }} /></div>
                        <div className="cn-data-item"><span className="cn-data-label">Horario</span>
                          <input type="text" className="cn-input-editable" value={client.pluriempleo_horario || ""}
                            onChange={(e) => { setClient({ ...client, pluriempleo_horario: e.target.value }); setHasUnsavedChanges(true); }} /></div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!client.competencia_desleal} style={{ width: 18, height: 18 }}
                        onChange={(e) => { setClient({ ...client, competencia_desleal: e.target.checked }); setHasUnsavedChanges(true); }} />
                      <span>Pacto de no competencia desleal firmado</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!client.contrato_formacion} style={{ width: 18, height: 18 }}
                        onChange={(e) => { setClient({ ...client, contrato_formacion: e.target.checked }); setHasUnsavedChanges(true); }} />
                      <span>Contrato de formación</span>
                    </label>
                    {client.contrato_formacion && (
                      <div className="cn-data-item" style={{ maxWidth: 260, marginLeft: 28 }}>
                        <span className="cn-data-label">Fin del contrato de formación</span>
                        <input type="date" className="cn-input-editable" value={client.contrato_formacion_fin || ""}
                          onChange={(e) => { setClient({ ...client, contrato_formacion_fin: e.target.value }); setHasUnsavedChanges(true); }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Checklist */}
              <div className="cn-panel">
                <div className="cn-panel-header"><h3 className="cn-panel-title">Checklist de incorporación</h3></div>
                <div className="cn-panel-content">
                  {CHECKLIST_INCORP.map((t) => (
                    <label key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--cn-border)", cursor: "pointer" }}>
                      <input type="checkbox" checked={!!incorp.checklist[t.id]} onChange={() => toggleIncorp(t.id)} style={{ width: 18, height: 18, flexShrink: 0 }} />
                      <span style={{ textDecoration: incorp.checklist[t.id] ? "line-through" : "none", color: incorp.checklist[t.id] ? "var(--cn-text-muted)" : "var(--cn-text-main)" }}>{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

            </div>
          );
        })()}
        {activeTab === "contrato" && (() => {
          let antiguedad = "—";
          if (contrato.fecha_alta) {
            const ini = new Date(contrato.fecha_alta);
            const hoy = new Date();
            if (!isNaN(ini.getTime())) {
              let meses = (hoy.getFullYear() - ini.getFullYear()) * 12 + (hoy.getMonth() - ini.getMonth());
              if (hoy.getDate() < ini.getDate()) meses -= 1;
              if (meses < 0) meses = 0;
              const a = Math.floor(meses / 12), m = meses % 12;
              antiguedad = `${a} año${a !== 1 ? "s" : ""} ${m} mes${m !== 1 ? "es" : ""}`;
            }
          }
          return (
            <div style={{ padding: 16 }}>
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">Datos del contrato</h3></div>
                <div className="cn-panel-content">
                  <div className="cn-data-grid cn-data-grid--editable">
                    <div className="cn-data-item"><span className="cn-data-label">Tipo de contrato</span>
                      <select className="cn-input-editable" value={contrato.tipo_contrato} onChange={(e) => setContr("tipo_contrato", e.target.value)}>
                        <option value="">...</option>
                        {TIPOS_CONTRATO_EMP.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Función</span>
                      <select className="cn-input-editable" value={contrato.funcion} onChange={(e) => setContr("funcion", e.target.value)}>
                        <option value="">...</option>
                        {FUNCIONES_EMP.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Convenio colectivo</span><input className="cn-input-editable" value={contrato.convenio} onChange={(e) => setContr("convenio", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Categoría profesional</span><input className="cn-input-editable" value={contrato.categoria_profesional} onChange={(e) => setContr("categoria_profesional", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Grupo de cotización</span>
                      <select className="cn-input-editable" value={contrato.grupo_cotizacion} onChange={(e) => setContr("grupo_cotizacion", e.target.value)}>
                        <option value="">...</option>
                        {["1","2","3","4","5","6","7","8","9","10","11"].map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Puesto</span><input className="cn-input-editable" value={contrato.puesto} onChange={(e) => setContr("puesto", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Departamento</span><input className="cn-input-editable" value={contrato.departamento} onChange={(e) => setContr("departamento", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Fecha de alta</span><input type="date" className="cn-input-editable" value={contrato.fecha_alta} onChange={(e) => setContr("fecha_alta", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Fecha de fin (si temporal)</span><input type="date" className="cn-input-editable" value={contrato.fecha_fin} onChange={(e) => setContr("fecha_fin", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Antigüedad</span><span className="cn-data-value">{antiguedad}</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">Jornada</span>
                      <select className="cn-input-editable" value={contrato.jornada} onChange={(e) => setContr("jornada", e.target.value)}>
                        <option value="completa">Completa</option>
                        <option value="parcial">Parcial</option>
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">% jornada</span><span className="cn-data-value" title="Calculado desde Horas/semana (40h = jornada completa)">{calcPctJornada(contrato.horas_semanales)}%</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">Horas/semana</span><input type="number" className="cn-input-editable" value={contrato.horas_semanales} onChange={(e) => setContr("horas_semanales", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Nº pagas</span><input type="number" className="cn-input-editable" value={contrato.num_pagas} onChange={(e) => setContr("num_pagas", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">IRPF %</span><input type="number" className="cn-input-editable" value={contrato.irpf_porcentaje} onChange={(e) => setContr("irpf_porcentaje", e.target.value)} /></div>
                  </div>
                </div>
              </div>
              {/* CLASIFICACIÓN LABORAL / CUADRO DE MANDO · fuente = config_profesionales (F2).
                  tipo_relacion (maestro) → deriva es_autonomo. funcion_contable manda el coste
                  a su línea de la P&L (B0). incluir_en_cuadro_mando exige imputación por sede
                  configurada (si no, el guardado avisa con un 409/400 claro). */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">Clasificación y Cuadro de Mando</h3></div>
                <div className="cn-panel-content">
                  <div className="cn-data-grid cn-data-grid--editable">
                    <div className="cn-data-item"><span className="cn-data-label">Tipo de relación</span>
                      <select className="cn-input-editable" value={client.tipo_relacion || "pendiente"} onChange={(e) => { const v = e.target.value; setClient({ ...client, tipo_relacion: v, es_autonomo: v === "autonomo" }); setHasUnsavedChanges(true); }}>
                        {TIPO_RELACION_OPTS_FICHA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Función contable</span>
                      <select className="cn-input-editable" value={client.funcion_contable || ""} onChange={(e) => { setClient({ ...client, funcion_contable: e.target.value }); setHasUnsavedChanges(true); }}>
                        {FUNCION_CONTABLE_OPTS_FICHA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Estado de revisión</span>
                      <select className="cn-input-editable" value={client.estado_revision || "pendiente"} onChange={(e) => { setClient({ ...client, estado_revision: e.target.value }); setHasUnsavedChanges(true); }}>
                        {ESTADO_REVISION_OPTS_FICHA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Incluir en Cuadro de Mando</span>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input type="checkbox" checked={!!client.incluir_en_cuadro_mando} onChange={(e) => { setClient({ ...client, incluir_en_cuadro_mando: e.target.checked }); setHasUnsavedChanges(true); }} />
                        <span>{client.incluir_en_cuadro_mando ? "Sí" : "No"}</span>
                      </label>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Incluir en Nóminas v2</span>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input type="checkbox" checked={!!client.incluir_en_laboral_v2} onChange={(e) => { setClient({ ...client, incluir_en_laboral_v2: e.target.checked }); setHasUnsavedChanges(true); }} />
                        <span>{client.incluir_en_laboral_v2 ? "Sí" : "No"}</span>
                      </label>
                    </div>
                    <div className="cn-data-item" style={{ gridColumn: "1 / -1" }}><span className="cn-data-label">Notas (Ann)</span>
                      <textarea className="cn-input-editable" rows={2} value={client.notas_ann || ""} onChange={(e) => { setClient({ ...client, notas_ann: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                  </div>
                  <p style={{ color: "var(--cn-text-muted)", fontSize: 12, margin: "10px 0 0" }}>
                    <strong>Función contable</strong> determina en qué línea de la cuenta de resultados (P&L) cae su coste de personal.
                    Para <strong>Incluir en Cuadro de Mando / Nóminas v2</strong> el profesional debe tener su <strong>imputación por sede</strong> definida (suma 100%); si falta, el guardado lo avisará.
                  </p>
                </div>
              </div>
              <div className="cn-panel">
                <div className="cn-panel-header"><h3 className="cn-panel-title">Histórico de contratos</h3></div>
                <div className="cn-panel-content">
                  <table className="cn-table" style={{ width: "100%" }}>
                    <thead><tr><th>Desde</th><th>Hasta</th><th>Tipo</th><th>Función</th><th>Motivo del cambio</th></tr></thead>
                    <tbody><tr><td colSpan={5} style={{ textAlign: "center", color: "#94a3b8", padding: 20 }}>Sin histórico todavía (se registrará al guardar revisiones de contrato).</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}
        {activeTab === "salario" && (() => {
          const num = (x) => Number(x || 0);
          const brutoMensual = salario.num_pagas ? num(salario.bruto_anual) / num(salario.num_pagas) : 0;
          const sumaVigente = salario.reparto.filter((r) => !r.hasta).reduce((a, r) => a + num(r.porcentaje), 0);
          const repartoOk = salario.reparto.length === 0 || Math.abs(sumaVigente - 100) <= 0.01;
          const money = (n) => `${num(n).toFixed(2)} €`;
          return (
            <div style={{ padding: 16 }}>
              {/* Salario vigente */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">Salario vigente</h3></div>
                <div className="cn-panel-content">
                  <div className="cn-data-grid cn-data-grid--editable">
                    <div className="cn-data-item"><span className="cn-data-label">Salario bruto anual (€)</span><input type="number" className="cn-input-editable" value={salario.bruto_anual} onChange={(e) => setSal("bruto_anual", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Nº pagas</span><input type="number" className="cn-input-editable" value={salario.num_pagas} onChange={(e) => setSal("num_pagas", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Bruto mensual (calculado)</span><span className="cn-data-value">{money(brutoMensual)}</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">Prorratea pagas extra</span><label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}><input type="checkbox" checked={salario.prorratea_pagas} onChange={(e) => setSal("prorratea_pagas", e.target.checked)} /> Sí</label></div>
                  </div>
                  {/* Complementos */}
                  {["complementos", "pluses"].map((campo) => (
                    <div key={campo} style={{ marginTop: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <strong style={{ textTransform: "capitalize" }}>{campo}</strong>
                        <button className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => addLineaSal(campo)}>+ Añadir</button>
                      </div>
                      {salario[campo].map((l, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                          <input className="cn-input-editable" style={{ flex: 2 }} placeholder="Concepto" value={l.concepto} onChange={(e) => setLineaSal(campo, i, "concepto", e.target.value)} />
                          <input type="number" className="cn-input-editable" style={{ flex: 1 }} placeholder="Importe €" value={l.importe} onChange={(e) => setLineaSal(campo, i, "importe", e.target.value)} />
                          <button className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => delLineaSal(campo, i)}>✕</button>
                        </div>
                      ))}
                      {salario[campo].length === 0 && <p style={{ color: "#94a3b8", fontStyle: "italic", margin: 0, fontSize: 13 }}>Sin {campo}.</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reparto función × sede */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 className="cn-panel-title">Reparto del coste · función × sede</h3>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 999, background: repartoOk ? "#dcfce7" : "#fef2f2", color: repartoOk ? "#166534" : "#dc2626", border: `1px solid ${repartoOk ? "#bbf7d0" : "#fecaca"}` }}>Σ vigente: {sumaVigente}%</span>
                </div>
                <div className="cn-panel-content">
                  <table className="cn-table" style={{ width: "100%" }}>
                    <thead><tr><th>Función</th><th>Sede</th><th>%</th><th>Desde</th><th>Hasta</th><th></th></tr></thead>
                    <tbody>
                      {salario.reparto.map((r, i) => (
                        <tr key={i}>
                          <td><select className="cn-input-editable" value={r.funcion} onChange={(e) => setRepartoF(i, "funcion", e.target.value)}><option value="">...</option>{FUNCIONES_EMP.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></td>
                          <td>
                            <select className="cn-input-editable" value={r.sede} onChange={(e) => setRepartoF(i, "sede", e.target.value)}>
                              <option value="">Sede...</option>
                              {sedesCrm.map((s) => <option key={s.nombre || s.id} value={s.nombre}>{s.nombre}</option>)}
                            </select>
                          </td>
                          <td><input type="number" className="cn-input-editable" style={{ width: 70 }} value={r.porcentaje} onChange={(e) => setRepartoF(i, "porcentaje", e.target.value)} /></td>
                          <td><input type="date" className="cn-input-editable" value={r.desde} onChange={(e) => setRepartoF(i, "desde", e.target.value)} /></td>
                          <td><input type="date" className="cn-input-editable" value={r.hasta} onChange={(e) => setRepartoF(i, "hasta", e.target.value)} /></td>
                          <td><button className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => delReparto(i)}>✕</button></td>
                        </tr>
                      ))}
                      {salario.reparto.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", color: "#94a3b8", padding: 16 }}>Sin reparto. Añade tramos función×sede.</td></tr>}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>Total vigente</td>
                        <td style={{ fontWeight: 700, color: repartoOk ? "#166534" : "#dc2626" }}>{sumaVigente}%</td>
                        <td colSpan={3}></td>
                      </tr>
                    </tfoot>
                  </table>
                  <button className="cn-btn cn-btn--outline cn-btn--sm" style={{ marginTop: 8 }} onClick={addReparto}>+ Añadir tramo función × sede</button>
                </div>
              </div>

              {/* IRPF / Modelo 145 */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">IRPF y situación familiar (modelo 145)</h3></div>
                <div className="cn-panel-content">
                  <div className="cn-data-grid cn-data-grid--editable">
                    <div className="cn-data-item"><span className="cn-data-label">IRPF %</span><input type="number" className="cn-input-editable" value={salario.irpf_porcentaje} onChange={(e) => setSal("irpf_porcentaje", e.target.value)} /></div>
                    <div className="cn-data-item"><span className="cn-data-label">Situación familiar</span>
                      <select className="cn-input-editable" value={salario.situacion_familiar} onChange={(e) => setSal("situacion_familiar", e.target.value)}>
                        <option value="">...</option>
                        <option value="1">1 · Soltero/viudo/divorciado con hijos</option>
                        <option value="2">2 · Casado, cónyuge sin rentas</option>
                        <option value="3">3 · Resto de situaciones</option>
                      </select>
                    </div>
                    <div className="cn-data-item"><span className="cn-data-label">Nº descendientes</span><input type="number" className="cn-input-editable" value={salario.num_descendientes} onChange={(e) => setSal("num_descendientes", e.target.value)} /></div>
                  </div>
                </div>
              </div>

              {/* Histórico salarial */}
              <div className="cn-panel">
                <div className="cn-panel-header"><h3 className="cn-panel-title">Histórico salarial</h3></div>
                <div className="cn-panel-content">
                  <table className="cn-table" style={{ width: "100%" }}>
                    <thead><tr><th>Vigente desde</th><th>Bruto anual</th><th>Motivo</th><th>Usuario</th></tr></thead>
                    <tbody><tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: 20 }}>Sin histórico todavía (se registrará al guardar revisiones salariales).</td></tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}
        {activeTab === "nominas" && (() => {
          const num = (x) => Number(x || 0);
          const money = (n) => `${num(n).toFixed(2)} €`;
          const anios = Array.from(new Set(nominas.map((n) => (n.periodo || "").slice(0, 4)).filter(Boolean))).sort().reverse();
          const delAnio = nominas.filter((n) => (n.periodo || "").startsWith(anioNom));
          const tot = delAnio.reduce((a, n) => ({
            bruto: a.bruto + num(n.devengos?.total_devengado),
            liquido: a.liquido + num(n.liquido),
            coste: a.coste + num(n.coste_empresa?.coste_total_empresa),
            irpf: a.irpf + num(n.deducciones?.irpf_importe),
          }), { bruto: 0, liquido: 0, coste: 0, irpf: 0 });
          const sel = nominaSel;
          return (
            <div style={{ padding: 16 }}>
              {/* Aviso: las sube el agente de IA */}
              <div className="cn-panel" style={{ marginBottom: 16, borderLeft: "3px solid var(--cn-primary)" }}>
                <div className="cn-panel-content" style={{ fontSize: 13, color: "var(--cn-text-muted)" }}>
                  Las nóminas las sube el <strong>agente de IA</strong> y reparte su información en la ficha. Aquí solo se consultan.
                </div>
              </div>

              {/* Resumen del año */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 className="cn-panel-title">Resumen {anioNom}</h3>
                  <select className="cn-input-editable" style={{ width: 110 }} value={anioNom} onChange={(e) => setAnioNom(e.target.value)}>
                    {[anioNom, ...anios.filter((a) => a !== anioNom)].map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="cn-panel-content">
                  <div className="cn-data-grid cn-data-grid--compact">
                    <div className="cn-data-item"><span className="cn-data-label">Nóminas</span><span className="cn-data-value">{delAnio.length}</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">Bruto acumulado</span><span className="cn-data-value">{money(tot.bruto)}</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">Líquido acumulado</span><span className="cn-data-value">{money(tot.liquido)}</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">IRPF acumulado</span><span className="cn-data-value">{money(tot.irpf)}</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">Coste empresa acumulado</span><span className="cn-data-value">{money(tot.coste)}</span></div>
                  </div>
                </div>
              </div>

              {/* Tabla de nóminas */}
              <div className="cn-panel" style={{ marginBottom: 16 }}>
                <div className="cn-panel-header"><h3 className="cn-panel-title">Nóminas ({nominas.length})</h3></div>
                <div className="cn-panel-content">
                  <table className="cn-table" style={{ width: "100%" }}>
                    <thead><tr><th>Periodo</th><th>Fecha pago</th><th>Bruto</th><th>Deducciones</th><th>Líquido</th><th>Coste empresa</th><th>Estado</th><th>PDF</th></tr></thead>
                    <tbody>
                      {nominas.map((n, i) => (
                        <tr key={n._id || n.id || i} style={{ cursor: "pointer", background: sel === n ? "var(--cn-surface-alt)" : undefined }} onClick={() => setNominaSel(sel === n ? null : n)}>
                          <td style={{ fontWeight: 600 }}>{n.periodo || "—"}</td>
                          <td>{n.fecha_pago || "—"}</td>
                          <td>{money(n.devengos?.total_devengado)}</td>
                          <td>{money(n.deducciones?.total_deducciones)}</td>
                          <td>{money(n.liquido)}</td>
                          <td>{money(n.coste_empresa?.coste_total_empresa)}</td>
                          <td>{n.estado || "—"}</td>
                          <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>{n.documento_pdf_id ? (
                            <>
                              <button className="cn-btn cn-btn--outline cn-btn--sm" title="Vista previa" onClick={async (e) => { e.stopPropagation(); try { const r = await axios.get(`${API}/laboral/v2/documentos/${n.documento_pdf_id}/descargar`, { responseType: "blob" }); window.open(window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank"); } catch { toast.error("No se pudo abrir la nómina"); } }}>Ver</button>{" "}
                              <button className="cn-btn cn-btn--outline cn-btn--sm" title="Descargar" onClick={async (e) => { e.stopPropagation(); try { const r = await axios.get(`${API}/laboral/v2/documentos/${n.documento_pdf_id}/descargar`, { responseType: "blob" }); const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); const a = document.createElement("a"); a.href = url; a.download = `nomina_${(n.periodo || "").replace(/[^0-9-]/g, "")}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); } catch { toast.error("No se pudo descargar la nómina"); } }}>Descargar</button>
                            </>
                          ) : "—"}</td>
                        </tr>
                      ))}
                      {nominas.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>Aún no hay nóminas. El agente de IA las subirá y aparecerán aquí.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Detalle de la nómina seleccionada */}
              {sel && (
                <div className="cn-panel">
                  <div className="cn-panel-header"><h3 className="cn-panel-title">Detalle nómina · {sel.periodo}</h3></div>
                  <div className="cn-panel-content">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                      <div>
                        <strong style={{ color: "var(--cn-primary)" }}>Devengos</strong>
                        <div className="cn-data-grid cn-data-grid--compact" style={{ marginTop: 6 }}>
                          <div className="cn-data-item"><span className="cn-data-label">Salario base</span><span className="cn-data-value">{money(sel.devengos?.salario_base)}</span></div>
                          <div className="cn-data-item"><span className="cn-data-label">Prorrata pagas</span><span className="cn-data-value">{money(sel.devengos?.prorrata_pagas_extra)}</span></div>
                          <div className="cn-data-item"><span className="cn-data-label">Horas extra</span><span className="cn-data-value">{money(sel.devengos?.horas_extra_importe)}</span></div>
                          <div className="cn-data-item"><span className="cn-data-label">Total devengado</span><span className="cn-data-value">{money(sel.devengos?.total_devengado)}</span></div>
                        </div>
                      </div>
                      <div>
                        <strong style={{ color: "#b45309" }}>Deducciones</strong>
                        <div className="cn-data-grid cn-data-grid--compact" style={{ marginTop: 6 }}>
                          <div className="cn-data-item"><span className="cn-data-label">SS trabajador</span><span className="cn-data-value">{money(sel.deducciones?.ss_trabajador_total)}</span></div>
                          <div className="cn-data-item"><span className="cn-data-label">IRPF</span><span className="cn-data-value">{money(sel.deducciones?.irpf_importe)}</span></div>
                          <div className="cn-data-item"><span className="cn-data-label">Anticipos</span><span className="cn-data-value">{money(sel.deducciones?.anticipos)}</span></div>
                          <div className="cn-data-item"><span className="cn-data-label">Total deducciones</span><span className="cn-data-value">{money(sel.deducciones?.total_deducciones)}</span></div>
                        </div>
                      </div>
                      <div>
                        <strong style={{ color: "#dc2626" }}>Coste empresa</strong>
                        <div className="cn-data-grid cn-data-grid--compact" style={{ marginTop: 6 }}>
                          <div className="cn-data-item"><span className="cn-data-label">SS empresa</span><span className="cn-data-value">{money(sel.coste_empresa?.ss_empresa_total)}</span></div>
                          <div className="cn-data-item"><span className="cn-data-label">Coste total empresa</span><span className="cn-data-value">{money(sel.coste_empresa?.coste_total_empresa)}</span></div>
                        </div>
                      </div>
                    </div>
                    {(sel.reparto_sedes || []).length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <strong>Reparto por sede</strong>
                        <div style={{ marginTop: 6 }}>{(sel.reparto_sedes || []).map((r, i) => <span key={i} style={{ marginRight: 12 }}>{r.sede} {r.porcentaje}% ({money(r.importe_coste)})</span>)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {activeTab === "jornada" && (
          <div style={{ padding: 16 }}>
            {/* CUADRANTE Y CLÍNICA · identidad de agenda del profesional.
                Fuente de verdad = config_profesionales (cuadrante). Se persiste con
                el botón GUARDAR de la cabecera de la ficha (guardarFicha → PUT). */}
            <div className="cn-panel" style={{ marginBottom: 16 }}>
              <div className="cn-panel-header"><h3 className="cn-panel-title">Cuadrante y clínica</h3></div>
              <div className="cn-panel-content">
                <div className="cn-data-grid cn-data-grid--editable">
                  <div className="cn-data-item"><span className="cn-data-label">Profesional activo</span>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={client.activo !== false} onChange={(e) => { setClient({ ...client, activo: e.target.checked }); setHasUnsavedChanges(true); }} />
                      <span>{client.activo !== false ? "Sí" : "No (oculto en el cuadrante)"}</span>
                    </label>
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Aparece en el cuadrante</span>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={!client.no_aparece_cuadrante} onChange={(e) => { setClient({ ...client, no_aparece_cuadrante: !e.target.checked }); setHasUnsavedChanges(true); }} />
                      <span>{!client.no_aparece_cuadrante ? "Sí" : "No"}</span>
                    </label>
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Mostrar en vista mes</span>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={client.mostrar_en_vista_mes !== false} onChange={(e) => { setClient({ ...client, mostrar_en_vista_mes: e.target.checked }); setHasUnsavedChanges(true); }} />
                      <span>{client.mostrar_en_vista_mes !== false ? "Sí" : "No"}</span>
                    </label>
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Es profesional de la salud</span>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!client.es_profesional_salud} onChange={(e) => { setClient({ ...client, es_profesional_salud: e.target.checked }); setHasUnsavedChanges(true); }} />
                      <span>{client.es_profesional_salud ? "Sí (aparece en Clínica)" : "No"}</span>
                    </label>
                  </div>
                  {client.es_profesional_salud && (
                    <div className="cn-data-item"><span className="cn-data-label">Nº de colegiado</span>
                      <input className="cn-input-editable" value={client.numero_colegiado || ""} onChange={(e) => { setClient({ ...client, numero_colegiado: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                  )}
                  <div className="cn-data-item"><span className="cn-data-label">Es sustituto</span>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!client.es_sustituto} onChange={(e) => { setClient({ ...client, es_sustituto: e.target.checked }); setHasUnsavedChanges(true); }} />
                      <span>{client.es_sustituto ? "Sí" : "No"}</span>
                    </label>
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Autónomo</span>
                    <span className="cn-data-value">{client.tipo_relacion === "autonomo" ? "Sí" : "No"} <span style={{ color: "var(--cn-text-muted)", fontSize: 12 }}>(se deriva del Tipo de relación · pestaña Contrato)</span></span>
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Sede</span>
                    <select className="cn-input-editable" value={client.sede || "MUNTANER"} onChange={(e) => { setClient({ ...client, sede: e.target.value }); setHasUnsavedChanges(true); }}>
                      {SEDES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Color en el cuadrante</span>
                    <select className="cn-input-editable" value={client.color || "#3B82F6"} onChange={(e) => { setClient({ ...client, color: e.target.value }); setHasUnsavedChanges(true); }}>
                      {COLORES_CUADRANTE.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Disponibilidad · desde</span>
                    <input type="date" className="cn-input-editable" value={client.disponibilidad_desde || ""} onChange={(e) => { setClient({ ...client, disponibilidad_desde: e.target.value || null }); setHasUnsavedChanges(true); }} />
                  </div>
                  <div className="cn-data-item"><span className="cn-data-label">Disponibilidad · hasta</span>
                    <input type="date" className="cn-input-editable" value={client.disponibilidad_hasta || ""} onChange={(e) => { setClient({ ...client, disponibilidad_hasta: e.target.value || null }); setHasUnsavedChanges(true); }} />
                  </div>
                </div>
                <p style={{ color: "var(--cn-text-muted)", fontSize: 12, margin: "10px 0 0" }}>Se guardan con el botón <strong>Guardar</strong> de la cabecera de la ficha (cuadrante · <code>config_profesionales</code>). El rango de disponibilidad limita en qué fechas aparece en la agenda.</p>
              </div>
            </div>

            {/* Jornada semanal EDITABLE (fuente = esta ficha · PUT /config/profesionales) */}
            <div className="cn-panel" id="jornada-editor" style={{ marginBottom: 16 }}>
              <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 className="cn-panel-title">Jornada semanal</h3>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 10, fontSize: 13 }} title="La jornada nueva rige desde esta fecha; los días anteriores conservan la jornada que tenían">
                  Rige desde
                  <input type="date" className="cn-input-editable" value={jornadaVigenteDesde} onChange={(e) => setJornadaVigenteDesde(e.target.value)} style={{ width: 150 }} />
                </label>
                {/* HASTA · opcional. Si se rellena, el horario ANTERIOR vuelve al día
                    siguiente: así se puede decir «de tal fecha a tal fecha este horario». */}
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 10, fontSize: 13 }} title="Opcional. Si lo rellenas, a partir del día siguiente vuelve el horario que había antes.">
                  hasta
                  <input type="date" className="cn-input-editable" value={jornadaVigenteHasta}
                    min={jornadaVigenteDesde}
                    onChange={(e) => setJornadaVigenteHasta(e.target.value)} style={{ width: 150 }} />
                  {jornadaVigenteHasta ? (
                    <button type="button" className="cn-btn cn-btn--outline cn-btn--sm" title="Quitar el hasta (dejarlo indefinido)"
                      onClick={() => setJornadaVigenteHasta("")}>indefinido</button>
                  ) : (
                    <span style={{ color: "var(--cn-text-muted)", fontSize: 12 }}>(indefinido)</span>
                  )}
                </label>
                <button className="cn-btn cn-btn--primary" disabled={guardandoJornada} onClick={guardarJornada}>{guardandoJornada ? "Guardando..." : "Guardar jornada"}</button>
              </div>
              <div className="cn-panel-content">
                {(jornadaEdit || []).map((d, i) => (
                  <div key={d.dia} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--cn-border)" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 130, flexShrink: 0, cursor: "pointer" }}>
                      <input type="checkbox" checked={d.activo} onChange={() => toggleDiaJ(i)} /> <strong>{d.dia}</strong>
                    </label>
                    <div style={{ flex: 1 }}>
                      {d.activo ? (
                        <>
                          {d.franjas.map((f, fi) => (
                            <div key={fi} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                              <select className="cn-input-editable" style={{ width: 100 }} value={f.inicio} onChange={(e) => setFranjaJ(i, fi, "inicio", e.target.value)}>{HORAS_EMP.map((h) => <option key={h} value={h}>{h}</option>)}</select>
                              <span>—</span>
                              <select className="cn-input-editable" style={{ width: 100 }} value={f.fin} onChange={(e) => setFranjaJ(i, fi, "fin", e.target.value)}>{HORAS_EMP.map((h) => <option key={h} value={h}>{h}</option>)}</select>
                              <button className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => delFranjaJ(i, fi)}>✕</button>
                            </div>
                          ))}
                          <button className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => addFranjaJ(i)}>+ Añadir franja</button>
                        </>
                      ) : <span style={{ color: "#94a3b8" }}>No laborable</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* JORNADAS REGISTRADAS · «de tal fecha a tal fecha». Solo LEE lo que el
                backend ya guarda (jornada_historial). El «hasta» se calcula: es el día
                antes de que empiece la siguiente, así no hay huecos ni solapes. */}
            <div className="cn-panel" style={{ marginBottom: 16 }}>
              <div className="cn-panel-header">
                <h3 className="cn-panel-title">Jornadas registradas</h3>
              </div>
              <div className="cn-panel-content">
                <div style={{ overflowX: "auto" }}>
                  <table className="cn-table" style={{ width: "100%", fontSize: 14 }}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: "nowrap" }}>Desde</th>
                        <th style={{ whiteSpace: "nowrap" }}>Hasta</th>
                        <th>Horario</th>
                        <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Horas/semana</th>
                        <th style={{ whiteSpace: "nowrap" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tramosJornada.map((t, i) => (
                        <tr key={i} style={{ background: t.vigenteHoy ? "rgba(7,112,105,.06)" : undefined }}>
                          <td style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {fechaEs(t.desde) || <span style={{ color: "var(--cn-text-muted)" }}>Antes del 1.er cambio</span>}
                          </td>
                          <td style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {fechaEs(t.hasta) || <strong>indefinido</strong>}
                          </td>
                          <td>
                            {t.texto}
                            {t.vigenteHoy && (
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, color: "#1F7A4D", background: "rgba(31,122,77,.12)" }}>VIGENTE HOY</span>
                            )}
                            {t.eliminable && (
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, color: "#B45309", background: "rgba(180,83,9,.12)" }}>PROGRAMADA</span>
                            )}
                            {t.registrado_por && (
                              <div style={{ fontSize: 11, color: "var(--cn-text-muted)", marginTop: 2 }}>
                                Registrado por {t.registrado_por}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                            {horasTexto(t.minutos)}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <button type="button" className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => editarTramoJ(t)} title="Cargar este tramo en el editor para corregirlo" data-testid="btn-editar-tramo">Editar</button>
                            {t.eliminable && (
                              <button type="button" className="cn-btn cn-btn--outline cn-btn--sm" style={{ marginLeft: 6, color: "#B91C1C", borderColor: "#B91C1C" }} onClick={() => eliminarTramoJ(t)} title="Eliminar este tramo programado (vuelve el horario anterior)" data-testid="btn-eliminar-tramo">Eliminar</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {tramosJornada.length === 0 && (
                        <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--cn-text-muted)", padding: 18 }}>Sin jornadas registradas todavía.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--cn-text-muted)" }}>
                  Pon el horario arriba, elige <strong>Rige desde</strong> (y <strong>hasta</strong>, si es temporal)
                  y pulsa <strong>Guardar jornada</strong>. Lo anterior no se pierde: queda aquí con sus fechas.
                </p>
              </div>
            </div>

            {/* Horas complementarias (REAL · endpoints vivos) */}
            <div className="cn-panel" style={{ marginBottom: 16 }}>
              <div className="cn-panel-header"><h3 className="cn-panel-title">Horas complementarias</h3></div>
              <div className="cn-panel-content">
                {resumenHoras && (
                  <div className="cn-data-grid cn-data-grid--compact" style={{ marginBottom: 12 }}>
                    <div className="cn-data-item"><span className="cn-data-label">Pendientes</span><span className="cn-data-value">{fmtMinEmp(resumenHoras.pendientes_minutos)}</span></div>
                    <div className="cn-data-item"><span className="cn-data-label">Compensadas</span><span className="cn-data-value">{fmtMinEmp(resumenHoras.compensadas_minutos)}</span></div>
                  </div>
                )}
                <table className="cn-table" style={{ width: "100%" }}>
                  <thead><tr><th>Fecha</th><th>Tiempo</th><th>Motivo</th><th>Estado</th></tr></thead>
                  <tbody>
                    {horasComp.map((h, i) => (
                      <tr key={h._id || h.id || i}>
                        <td>{h.fecha}</td>
                        <td>{fmtMinEmp(h.tiempo_minutos)}</td>
                        <td>{h.motivo || "—"}</td>
                        <td>{h.estado || "—"}</td>
                      </tr>
                    ))}
                    {horasComp.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: 20 }}>Sin horas complementarias registradas.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Control horario (fichajes) — pendiente */}
            <div className="cn-panel">
              <div className="cn-panel-header"><h3 className="cn-panel-title">Control horario (registro de jornada)</h3></div>
              <div className="cn-panel-content">
                <p style={{ color: "var(--cn-text-muted)", fontStyle: "italic", margin: "0 0 10px" }}>Registro diario de jornada (fichajes) — obligatorio por normativa. Módulo nuevo a desarrollar.</p>
                <span style={{ display: "inline-block", background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 12 }}>PENDIENTE DE DESARROLLAR</span>
              </div>
            </div>
          </div>
        )}
        {activeTab === "ausencias" && (
          <PlaceholderEmpleado titulo="Ausencias" desc="Vacaciones (devengo/saldo), permisos, bajas médicas (IT + parte de baja) y sustituciones." />
        )}
        {activeTab === "formacion" && (
          <PlaceholderEmpleado titulo="Formación y PRL" desc="Titulaciones, colegiación, cursos, formación PRL y reconocimientos médicos, con sus vigencias." />
        )}
        {activeTab === "desempeno" && (
          <PlaceholderEmpleado titulo="Desempeño" desc="Evaluaciones / objetivos del trabajador y compensación (bonus / incentivos / bonificaciones)." />
        )}
        {activeTab === "comunicacion_emp" && (
          <PlaceholderEmpleado titulo="Comunicación" desc="Envío de credenciales y emails al trabajador, con su historial de comunicaciones." />
        )}
        {activeTab === "historial_emp" && (
          <PlaceholderEmpleado titulo="Historial" desc="Auditoría de cambios de la ficha del trabajador (quién, qué y cuándo)." />
        )}

        {activeTab === "datos" && (
          <div className="cn-tab-content">
            {/* ESTRUCTURA TRI-PARTITA SUPERIOR (IDENTIFICACIÓN | DIRECCIÓN | CONTACTO) */}

            {/* 0. PANEL ADULTO RESPONSABLE (Condicional) */}
            {isMenor && (
              <div
                className="cn-panel"
                style={{
                  margin: "0 0 24px 0",
                  borderTop: "3px solid var(--cn-warning)",
                  boxShadow: "var(--cn-shadow)",
                  backgroundColor: "#fffbeb",
                }}
              >
                <div className="cn-panel-header">
                  <h3 className="cn-panel-title" style={{ color: "#b45309" }}>
                    Datos del Adulto Responsable (Tutor Legal)
                  </h3>
                  <p className="cn-panel-subtitle">
                    Obligatorio por ser el cliente menor de edad.
                  </p>
                </div>
                <div
                  className="cn-panel-content"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "16px",
                  }}
                >
                  <div className="cn-data-item">
                    <span className="cn-data-label">
                      Nombre{" "}
                      <span style={{ color: "var(--cn-danger)" }}>*</span>
                    </span>
                    <input
                      type="text"
                      className="cn-input-editable"
                      value={client.tutor_nombre || ""}
                      onChange={(e) => {
                        setClient({ ...client, tutor_nombre: e.target.value, pagador_nombre: e.target.value, sepa_titular_cuenta: `${e.target.value} ${client.tutor_apellidos || ""}`.trim() });
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        border: !client.tutor_nombre
                          ? "1px solid var(--cn-danger)"
                          : "",
                      }}
                    />
                  </div>
                  <div className="cn-data-item">
                    <span className="cn-data-label">Apellidos</span>
                    <input
                      type="text"
                      className="cn-input-editable"
                      value={client.tutor_apellidos || ""}
                      onChange={(e) => {
                        setClient({
                          ...client,
                          tutor_apellidos: e.target.value, pagador_apellidos: e.target.value, sepa_titular_cuenta: `${client.tutor_nombre || ""} ${e.target.value}`.trim(),
                        });
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>
                  <div className="cn-data-item">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span className="cn-data-label">
                        DNI / NIE Adulto{" "}
                        <span style={{ color: "var(--cn-danger)" }}>*</span>
                      </span>
                      {client.tutor_dni && !validarDNI(client.tutor_dni) && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "var(--cn-danger)",
                            fontWeight: 600,
                          }}
                        >
                          Inválido
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      className="cn-input-editable"
                      value={client.tutor_dni || ""}
                      onChange={(e) => {
                        setClient({ ...client, tutor_dni: e.target.value, pagador_dni: e.target.value, sepa_titular_dni: e.target.value });
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        textTransform: "uppercase",
                        border:
                          !client.tutor_dni || !validarDNI(client.tutor_dni)
                            ? "2px solid var(--cn-danger)"
                            : "",
                      }}
                    />
                  </div>
                  <div className="cn-data-item">
                    <span className="cn-data-label">
                      Relación con el menor{" "}
                      <span style={{ color: "var(--cn-danger)" }}>*</span>
                    </span>
                    <select
                      className="cn-input-editable"
                      value={client.tutor_relacion || ""}
                      onChange={(e) => {
                        setClient({
                          ...client,
                          tutor_relacion: e.target.value, pagador_relacion: e.target.value,
                        });
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        border: !client.tutor_relacion
                          ? "1px solid var(--cn-danger)"
                          : "",
                      }}
                    >
                      <option value="">Seleccione...</option>
                      <option value="padre">Padre</option>
                      <option value="madre">Madre</option>
                      <option value="tutor">Tutor / Legal</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div className="cn-data-item">
                    <span className="cn-data-label">
                      Teléfono Contacto{" "}
                      <span style={{ color: "var(--cn-danger)" }}>*</span>
                    </span>
                    <input
                      type="tel"
                      className="cn-input-editable"
                      value={client.tutor_telefono || ""}
                      onChange={(e) => {
                        setClient({
                          ...client,
                          tutor_telefono: e.target.value, pagador_telefono: e.target.value,
                        });
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        border: !client.tutor_telefono
                          ? "1px solid var(--cn-danger)"
                          : "",
                      }}
                    />
                  </div>
                  <div className="cn-data-item">
                    <span className="cn-data-label">Email</span>
                    <input
                      type="email"
                      className="cn-input-editable"
                      value={client.tutor_email || ""}
                      onChange={(e) => {
                        setClient({ ...client, tutor_email: e.target.value, pagador_email: e.target.value });
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gridTemplateRows: "auto auto",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              {/* COLUMNA 1 · los paneles se apilan seguidos (flex), NO por filas de
                  rejilla: así el de abajo sube pegado al de arriba y no queda el
                  hueco que dejaba una columna más alta que otra. */}
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", gridColumn: 1, gridRow: "1 / 3" }}>
              {/* COL 1: Identificacion */}
              <div style={{ gridColumn: 1, gridRow: 1 }}>
              {/* 1. IDENTIFICACIÓN PERSONAL */}
              <div
                className="cn-panel"
                style={{
                  margin: 0,
                  boxShadow: "var(--cn-shadow)",
                }}
              >
                <div className="cn-panel-header">
                  <h3 className="cn-panel-title">Identificación Personal</h3>
                </div>
                <div
                  className="cn-panel-content"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {/* Nombre + Apellidos en una fila */}
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className="cn-data-item" style={{ flex: 1 }}>
                      <span className="cn-data-label">Nombre</span>
                      <input type="text" className="cn-input-editable" value={client.nombre || ""}
                        placeholder={esBorrador ? "Pendiente..." : ""}
                        style={estiloPendiente(client.nombre)}
                        onChange={(e) => { setClient({ ...client, nombre: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                    <div className="cn-data-item" style={{ flex: 1 }}>
                      <span className="cn-data-label">1er apellido</span>
                      <input type="text" className="cn-input-editable" value={client.primer_apellido || ""}
                        placeholder={esBorrador ? "Pendiente..." : ""}
                        style={estiloPendiente(client.primer_apellido)}
                        onChange={(e) => { setClient({ ...client, primer_apellido: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                    <div className="cn-data-item" style={{ flex: 1 }}>
                      <span className="cn-data-label">2o apellido</span>
                      <input type="text" className="cn-input-editable" value={client.segundo_apellido || ""}
                        placeholder={esBorrador ? "Pendiente..." : ""}
                        style={estiloPendiente(client.segundo_apellido)}
                        onChange={(e) => { setClient({ ...client, segundo_apellido: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                  </div>
                  {/* Tipo doc. + DNI + NAF + Fecha nac. + Sexo en una fila */}
                  <div style={{ display: "flex", gap: "10px" }}>
                    {/* EMPLEADO: tipo de documento (lo rellena también el volcado de nómina). */}
                    <div className="cn-data-item" style={{ flex: 0.7, minWidth: "90px" }}>
                      <span className="cn-data-label">Tipo doc.</span>
                      <select className="cn-input-editable" value={client.tipo_documento || "DNI"}
                        onChange={(e) => { setClient({ ...client, tipo_documento: e.target.value }); setHasUnsavedChanges(true); }}>
                        {["DNI", "NIE", "Pasaporte", "NIF"].map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="cn-data-item" style={{ flex: 1.2, minWidth: "120px" }}>
                      <span className="cn-data-label">DNI / NIE</span>
                      <input type="text" className="cn-input-editable" value={client.dni || ""}
                        placeholder={esBorrador ? "Pendiente..." : ""}
                        style={{ fontWeight: 600, whiteSpace: "nowrap", ...estiloPendiente(client.dni) }}
                        onChange={(e) => { setClient({ ...client, dni: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                    {/* EMPLEADO: NAF (nº afiliación Seguridad Social). */}
                    <div className="cn-data-item" style={{ flex: 1, minWidth: "120px" }}>
                      <span className="cn-data-label">NAF (Seg. Social)</span>
                      <input type="text" className="cn-input-editable" value={client.naf || ""}
                        onChange={(e) => { setClient({ ...client, naf: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>
                    <div className="cn-data-item" style={{ flex: 0.8 }}>
                      <span className="cn-data-label">Fecha nacimiento</span>
                      <input type="date" className="cn-input-editable"
                        value={client.fecha_nacimiento ? client.fecha_nacimiento.split("T")[0] : ""}
                        onChange={(e) => { setClient({ ...client, fecha_nacimiento: e.target.value }); setHasUnsavedChanges(true); }}
                      />
                    </div>
                    <div className="cn-data-item" style={{ flex: 0.5, minWidth: "80px" }}>
                      <span className="cn-data-label">Sexo</span>
                      <select className="cn-input-editable" value={client.sexo || ""}
                        onChange={(e) => { setClient({ ...client, sexo: e.target.value }); setHasUnsavedChanges(true); }}>
                        <option value="">...</option>
                        <option value="M">Hombre</option>
                        <option value="F">Mujer</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                  </div>
                  {/* EMPLEADO: "Profesión" y "Cómo nos ha conocido" eran campos de CLIENTE
                      (no aplican a un trabajador). Ocultados de Datos personales. */}
                  {/* EMPLEADO: cuenta bancaria para PAGARLE la nómina (no hay mandato SEPA). */}
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className="cn-data-item" style={{ flex: 1 }}>
                      <span className="cn-data-label">Cuenta bancaria (IBAN) · para el pago de la nómina</span>
                      <input type="text" className="cn-input-editable" value={client.iban || ""}
                        onChange={(e) => { setClient({ ...client, iban: e.target.value }); setHasUnsavedChanges(true); }}
                        placeholder="ES00 0000 0000 0000 0000 0000" />
                    </div>
                  </div>
                  {esComoNosConocioMedico(comoNosConocio) && (
                    <DerivadorSelector
                      clienteId={null}
                      registrar={false}
                      derivadorId={client.derivador_id || null}
                      derivadorNombre={client.derivador_nombre || ""}
                      isAdmin={false}
                      onChange={(v) => { setClient({ ...client, ...v }); setHasUnsavedChanges(true); }}
                    />
                  )}
                </div>
              </div>

              </div>
              {/* COL 1, FILA 2: Direccion */}
              <div
                className="cn-panel"
                style={{
                  margin: 0,
                  boxShadow: "var(--cn-shadow)",
                  gridColumn: 1, gridRow: 2,
                }}
              >
                <div className="cn-panel-header">
                  <h3 className="cn-panel-title">Dirección</h3>
                </div>
                <div
                  className="cn-panel-content"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className="cn-data-item" style={{ flex: 3 }}>
                      <span className="cn-data-label">Calle</span>
                      <input
                        type="text"
                        name="calle_dom_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={client.direccion || ""}
                        onChange={(e) => {
                          setClient({ ...client, direccion: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                    <div className="cn-data-item" style={{ flex: 1, minWidth: "60px" }}>
                      <span className="cn-data-label">Num.</span>
                      <input
                        type="text"
                        name="numero_dom_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={client.numero || ""}
                        onChange={(e) => {
                          setClient({ ...client, numero: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                    <div className="cn-data-item" style={{ flex: 1, minWidth: "60px" }}>
                      <span className="cn-data-label">Piso</span>
                      <input
                        type="text"
                        name="puerta_dom_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={client.piso || ""}
                        onChange={(e) => {
                          setClient({ ...client, piso: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className="cn-data-item" style={{ flex: 1, minWidth: "70px" }}>
                      <span className="cn-data-label">C.P.</span>
                      <input
                        type="text"
                        name="codigo_postal_dom_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={client.codigo_postal || ""}
                        onChange={(e) => {
                          const cp = e.target.value;
                          const updates = { codigo_postal: cp };
                          // CRITICO: Siempre sobreescribir ciudad/provincia/pais desde CP
                          if (cp.length >= 2) {
                            const info = CP_PROVINCIA[cp.substring(0, 2)];
                            if (info) {
                              if (info.provincia) updates.provincia = info.provincia;
                              if (info.pais) updates.pais = info.pais;
                              if (info.ciudad) updates.ciudad = info.ciudad;
                            }
                          }
                          if (cp.length === 5) {
                            const barrio = CP_BARRIO_BCN[cp];
                            if (barrio) updates.barrio = barrio;
                          }
                          setClient({ ...client, ...updates });
                          setHasUnsavedChanges(true);
                          cpEmpleado.alEscribir(cp, (d) => setClient((prev) =>
                            prev.codigo_postal === cp ? { ...prev, ciudad: d.poblacion, provincia: d.provincia, pais: "ES" } : prev));
                        }}
                      />
                      {cpEmpleado.estado === 'buscando' && <span className="cn-data-hint" style={{ fontSize: 11, color: '#6b7280' }}>Buscando población…</span>}
                      {cpEmpleado.estado === 'no_encontrado' && <span className="cn-data-hint" style={{ fontSize: 11, color: '#b45309' }}>CP no encontrado: escribe la población a mano.</span>}
                    </div>
                    <div className="cn-data-item" style={{ flex: 2 }}>
                      <span className="cn-data-label">Ciudad</span>
                      <input
                        type="text"
                        name="ciudad_dom_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        list={cpEmpleado.poblaciones.length > 1 ? "cp-poblaciones-empleado" : undefined}
                        value={client.ciudad || ""}
                        onChange={(e) => {
                          const ciudad = e.target.value;
                          const updates = { ciudad };
                          const info = CIUDADES_PROVINCIA[ciudad.toLowerCase().trim()];
                          if (info) {
                            updates.provincia = info.provincia;
                            updates.pais = info.pais;
                          }
                          setClient({ ...client, ...updates });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      {cpEmpleado.poblaciones.length > 1 && (
                        <>
                          <datalist id="cp-poblaciones-empleado">{cpEmpleado.poblaciones.map((p) => <option key={p} value={p} />)}</datalist>
                          <span style={{ fontSize: 11, color: '#6b7280' }}>Este CP abarca {cpEmpleado.poblaciones.length} poblaciones: elige la correcta.</span>
                        </>
                      )}
                    </div>
                    <div className="cn-data-item" style={{ flex: 2 }}>
                      <span className="cn-data-label">Provincia</span>
                      <input
                        type="text"
                        name="provincia_dom_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={client.provincia || ""}
                        onChange={(e) => {
                          setClient({ ...client, provincia: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                    <div className="cn-data-item" style={{ flex: 1 }}>
                      <span className="cn-data-label">Pais</span>
                      <input
                        type="text"
                        name="pais_dom_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={client.pais || "ES"}
                        onChange={(e) => {
                          setClient({ ...client, pais: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                  </div>
                  <div className="cn-data-item">
                    <span className="cn-data-label">Barrio</span>
                    <input
                      type="text"
                      className="cn-input-editable"
                      value={client.barrio || ""}
                      onChange={(e) => {
                        setClient({ ...client, barrio: e.target.value });
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Se rellena automaticamente con el C.P."
                    />
                  </div>
                </div>
              </div>
                {/* COL 1 (debajo de Dirección): PERFIL PROFESIONAL — EMPLEADO.
                    Formación del trabajador: nivel de estudios, titulaciones (varias,
                    cada una con su archivo) y, SOLO si es profesional de salud, la
                    colegiatura. Los archivos van al almacén cifrado del empleado
                    (laboral_documentos · tipo="empleado_doc"), el mismo que alimenta
                    la pestaña Documentación: no hay camino paralelo. */}
                <div
                  className="cn-panel"
                  style={{ margin: 0, gridColumn: 2, gridRow: 2, boxShadow: "var(--cn-shadow)" }}
                >
                  <div className="cn-panel-header">
                    <h3 className="cn-panel-title">Perfil profesional</h3>
                  </div>
                  <div className="cn-panel-content" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div className="cn-data-item">
                      <span className="cn-data-label">Nivel de estudios</span>
                      <input
                        type="text" className="cn-input-editable"
                        placeholder="Ej.: Conservatorio de Danza"
                        value={client.nivel_estudios || ""}
                        onChange={(e) => { setClient({ ...client, nivel_estudios: e.target.value }); setHasUnsavedChanges(true); }} />
                    </div>

                    <div>
                      <span className="cn-data-label" style={{ display: "block", marginBottom: "8px" }}>Titulaciones</span>
                      {(client.titulaciones || []).map((t, i) => (
                        <div key={i} style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                          <input
                            type="text" className="cn-input-editable" style={{ flex: 1 }}
                            placeholder="Nombre de la titulación" value={t.titulo || ""}
                            onChange={(e) => {
                              const l = [...(client.titulaciones || [])];
                              l[i] = { ...l[i], titulo: e.target.value };
                              setClient({ ...client, titulaciones: l });
                              setHasUnsavedChanges(true);
                            }} />
                          {t.documento_id ? (
                            <a className="cn-btn cn-btn--outline cn-btn--sm" href={urlDescargaDoc(t.documento_id)}
                               target="_blank" rel="noreferrer">Ver archivo</a>
                          ) : (
                            <label className="cn-btn cn-btn--outline cn-btn--sm" style={{ cursor: "pointer", margin: 0 }}>
                              Subir
                              <input type="file" style={{ display: "none" }}
                                onChange={async (e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = "";
                                  if (!f) return;
                                  try {
                                    const d = await subirDocumentoEmpleado(f, "Titulación", t.titulo || null);
                                    const l = [...(client.titulaciones || [])];
                                    l[i] = { ...l[i], documento_id: d._id };
                                    setClient({ ...client, titulaciones: l });
                                    setHasUnsavedChanges(true);
                                    toast.success("Titulación subida. Recuerda GUARDAR la ficha.");
                                  } catch (err) { toast.error(parseErrorDetail(err, "Error al subir la titulación")); }
                                }} />
                            </label>
                          )}
                          <button type="button" className="cn-btn cn-btn--outline cn-btn--sm" title="Quitar esta titulación"
                            onClick={() => {
                              setClient({ ...client, titulaciones: (client.titulaciones || []).filter((_, j) => j !== i) });
                              setHasUnsavedChanges(true);
                            }}>Quitar</button>
                        </div>
                      ))}
                      {(client.titulaciones || []).length === 0 && (
                        <p style={{ margin: "0 0 8px", fontSize: "12px", color: "var(--cn-text-muted)" }}>Sin titulaciones.</p>
                      )}
                      <button type="button" className="cn-btn cn-btn--outline cn-btn--sm"
                        onClick={() => {
                          setClient({ ...client, titulaciones: [...(client.titulaciones || []), { titulo: "", documento_id: null }] });
                          setHasUnsavedChanges(true);
                        }}>Añadir titulación</button>
                    </div>

                    {/* Colegiatura · solo para profesionales de salud (fisioterapeutas).
                        Se marca con la casilla "Profesional de salud" de la pestaña Contrato.
                        El nº de colegiado es el MISMO campo que allí (una sola verdad). */}
                    {client.es_profesional_salud && (
                      <div style={{ borderTop: "1px solid var(--cn-border)", paddingTop: "12px" }}>
                        <span className="cn-data-label" style={{ display: "block", marginBottom: "8px" }}>Colegiatura</span>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <input
                            type="text" className="cn-input-editable" style={{ flex: 1 }}
                            placeholder="Nº de colegiado" value={client.numero_colegiado || ""}
                            onChange={(e) => { setClient({ ...client, numero_colegiado: e.target.value }); setHasUnsavedChanges(true); }} />
                          {client.colegiatura_documento_id ? (
                            <a className="cn-btn cn-btn--outline cn-btn--sm" href={urlDescargaDoc(client.colegiatura_documento_id)}
                               target="_blank" rel="noreferrer">Ver archivo</a>
                          ) : (
                            <label className="cn-btn cn-btn--outline cn-btn--sm" style={{ cursor: "pointer", margin: 0 }}>
                              Subir
                              <input type="file" style={{ display: "none" }}
                                onChange={async (e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = "";
                                  if (!f) return;
                                  try {
                                    const d = await subirDocumentoEmpleado(f, "Colegiatura", client.numero_colegiado || null);
                                    setClient({ ...client, colegiatura_documento_id: d._id });
                                    setHasUnsavedChanges(true);
                                    toast.success("Colegiatura subida. Recuerda GUARDAR la ficha.");
                                  } catch (err) { toast.error(parseErrorDetail(err, "Error al subir la colegiatura")); }
                                }} />
                            </label>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* COLUMNA 2 · mismo criterio que la 1. */}
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", gridColumn: 2, gridRow: "1 / 3" }}>
              {/* COL 2: CONTACTO */}
                <div
                  className="cn-panel"
                  style={{
                    margin: 0, gridColumn: 2, gridRow: 1,
                    boxShadow: "var(--cn-shadow)",
                  }}
                >
                  <div className="cn-panel-header">
                    <h3 className="cn-panel-title">Contacto</h3>
                  </div>
                  <div
                    className="cn-panel-content"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    <div className="cn-data-item">
                      <span className="cn-data-label">Email principal</span>
                      <input
                        type="email"
                        name="email_contacto_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={esBorrador && campoPendiente(client.email) ? "" : (client.email || "")}
                        placeholder={esBorrador ? "email@pendiente.com" : ""}
                        onChange={(e) => {
                          setClient({ ...client, email: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        style={esBorrador && campoPendiente(client.email) ? { color: "#94a3b8", fontStyle: "italic" } : { color: "var(--cn-primary)", fontWeight: 500 }}
                      />
                    </div>
                    {/* EMPLEADO · segundo email: el CORPORATIVO de la empresa
                        (buzón del puesto). Se guarda en laboral_empleados (RRHH),
                        no en el cuadrante: el email del cuadrante es el personal. */}
                    <div className="cn-data-item">
                      <span className="cn-data-label">Email corporativo</span>
                      <input
                        type="email"
                        name="email_corp_prevent_autofill"
                        autoComplete="off"
                        className="cn-input-editable"
                        value={client.email_corporativo || ""}
                        placeholder="nombre@empresa.com"
                        onChange={(e) => {
                          setClient({ ...client, email_corporativo: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <div className="cn-data-item" style={{ flex: 1 }}>
                        <span className="cn-data-label">Tel. principal</span>
                        <input type="tel" name="tel_princ_prevent_autofill" autoComplete="off"
                          className="cn-input-editable" value={client.telefono || ""} style={{ fontWeight: 600 }}
                          onChange={(e) => { setClient({ ...client, telefono: e.target.value }); setHasUnsavedChanges(true); }} />
                      </div>
                      <div className="cn-data-item" style={{ flex: 1 }}>
                        <span className="cn-data-label">Tel. secundario</span>
                        <input type="tel" name="tel_sec_prevent_autofill" autoComplete="off"
                          className="cn-input-editable" value={client.telefono_secundario || ""}
                          onChange={(e) => { setClient({ ...client, telefono_secundario: e.target.value }); setHasUnsavedChanges(true); }} />
                      </div>
                    </div>
                    {/* EMPLEADO · CONTACTOS DE EMERGENCIA · a quién avisar si le pasa
                        algo. Se pueden añadir varios (nombre y apellidos + teléfono). */}
                    <div style={{ borderTop: "1px solid var(--cn-border)", paddingTop: "12px" }}>
                      <span className="cn-data-label" style={{ display: "block", marginBottom: "8px" }}>
                        Contactos de emergencia
                      </span>
                      {(client.contactos_emergencia || []).map((c, i) => (
                        <div key={i} style={{
                          display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px",
                          // Cada contacto en su TARJETA: se ve de un vistazo que nombre,
                          // perfil y teléfono son de la MISMA persona.
                          border: "1px solid var(--cn-border)", borderRadius: "8px",
                          padding: "10px", background: "rgba(255,255,255,0.55)",
                        }}>
                          <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--cn-text-muted)", letterSpacing: ".02em" }}>
                            Contacto {i + 1}
                          </span>
                          {/* Arriba: nombre y, a su lado, el PERFIL (marido, hija…), a
                              todo el ancho de la tarjeta para que se lean completos. */}
                          <div style={{ display: "flex", gap: "6px", minWidth: 0 }}>
                            <input
                              type="text" className="cn-input-editable" style={{ flex: 2, minWidth: 0 }}
                              placeholder="Nombre y apellidos" value={c.nombre || ""}
                              onChange={(e) => {
                                const l = [...(client.contactos_emergencia || [])];
                                l[i] = { ...l[i], nombre: e.target.value };
                                setClient({ ...client, contactos_emergencia: l });
                                setHasUnsavedChanges(true);
                              }} />
                            <input
                              type="text" className="cn-input-editable" style={{ flex: 1, minWidth: 0 }}
                              placeholder="Marido, hija…" value={c.perfil || ""}
                              onChange={(e) => {
                                const l = [...(client.contactos_emergencia || [])];
                                l[i] = { ...l[i], perfil: e.target.value };
                                setClient({ ...client, contactos_emergencia: l });
                                setHasUnsavedChanges(true);
                              }} />
                          </div>
                          {/* Debajo: teléfono y, a su lado, Quitar (en rojo discreto:
                              es la única acción que destruye datos de la tarjeta). */}
                          <div style={{ display: "flex", gap: "6px", minWidth: 0 }}>
                            <input
                              type="tel" className="cn-input-editable" style={{ flex: 1, minWidth: 0 }}
                              placeholder="Teléfono" value={c.telefono || ""}
                              onChange={(e) => {
                                const l = [...(client.contactos_emergencia || [])];
                                l[i] = { ...l[i], telefono: e.target.value };
                                setClient({ ...client, contactos_emergencia: l });
                                setHasUnsavedChanges(true);
                              }} />
                            <button
                              type="button" className="cn-btn cn-btn--sm"
                              title="Quitar este contacto"
                              style={{ flexShrink: 0, color: "#b91c1c", border: "1px solid #fca5a5", background: "#fef2f2" }}
                              onClick={() => {
                                const l = (client.contactos_emergencia || []).filter((_, j) => j !== i);
                                setClient({ ...client, contactos_emergencia: l });
                                setHasUnsavedChanges(true);
                              }}>Quitar</button>
                          </div>
                        </div>
                      ))}
                      {(client.contactos_emergencia || []).length === 0 && (
                        <p style={{ margin: "0 0 8px", fontSize: "12px", color: "var(--cn-text-muted)" }}>
                          Sin contactos de emergencia.
                        </p>
                      )}
                      <button
                        type="button" className="cn-btn cn-btn--outline cn-btn--sm"
                        onClick={() => {
                          setClient({ ...client, contactos_emergencia: [...(client.contactos_emergencia || []), { nombre: "", perfil: "", telefono: "" }] });
                          setHasUnsavedChanges(true);
                        }}>Añadir contacto</button>
                    </div>
                    <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", cursor: "pointer" }}>
                        <input type="checkbox" checked={client.tiene_whatsapp || false}
                          onChange={(e) => { setClient({ ...client, tiene_whatsapp: e.target.checked }); setHasUnsavedChanges(true); }}
                          style={{ width: "15px", height: "15px", accentColor: "#25D366", cursor: "pointer" }} />
                        WhatsApp
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", cursor: "pointer" }}>
                        <input type="checkbox" checked={client.tiene_telegram || false}
                          onChange={(e) => { setClient({ ...client, tiene_telegram: e.target.checked }); setHasUnsavedChanges(true); }}
                          style={{ width: "15px", height: "15px", accentColor: "#0088cc", cursor: "pointer" }} />
                        Telegram
                      </label>
                    </div>
                  </div>
                </div>

                {/* DATOS DE COBRO SEPA — EMPLEADO: bloque eliminado
                    (no hay mandato SEPA; el IBAN para pagar la nómina está en
                    Identificación personal). */}
                {false && (
                <div
                  className="cn-panel"
                  style={{
                    margin: 0,
                    boxShadow: "var(--cn-shadow)",
                    gridColumn: 2, gridRow: 2,
                  }}
                >
                  <div className="cn-panel-header">
                    {/* Sub-fase 7 · datos bancarios siempre visibles · ya no
                        condicionados a forma_pago=domiciliación. La forma de
                        pago de clases fijas vive en pestaña Inscripción. */}
                    <h3 className="cn-panel-title">Datos bancarios y SEPA</h3>
                    <p className="cn-panel-subtitle" style={{ margin: 0, fontSize: "11px" }}>
                      IBAN, titular, pagador alternativo y mandato SEPA.
                      La forma de pago de clases fijas se configura en la
                      pestaña Inscripción.
                    </p>
                  </div>
                  <div
                    className="cn-panel-content"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    {/* Sub-fase 7 · ELIMINADO el select "Forma de pago" y el
                        condicional `tipo_beca` de Datos principales. Ambos
                        viven ahora en pestaña Inscripción.
                        ELIMINADO el wrapper `forma_pago === "domiciliacion"`:
                        los datos bancarios ahora están siempre visibles. */}

                    <>
                        <div className="cn-data-item">
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span className="cn-data-label">
                              Cuenta Bancaria (IBAN)
                            </span>
                            {client.sepa_iban &&
                              !validarIBAN(client.sepa_iban) && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "var(--cn-danger)",
                                    fontWeight: 600,
                                  }}
                                >
                                  Incompleto / Inválido
                                </span>
                              )}
                          </div>
                          <input
                            type="text"
                            className="cn-input-editable"
                            value={client.sepa_iban || ""}
                            onChange={(e) => {
                              setClient({
                                ...client,
                                sepa_iban: e.target.value,
                              });
                              setHasUnsavedChanges(true);
                            }}
                            placeholder="ESXX XXXX XXXX XXXX"
                            style={{
                              border:
                                client.sepa_iban &&
                                !validarIBAN(client.sepa_iban)
                                  ? "2px solid var(--cn-danger)"
                                  : "",
                            }}
                          />
                        </div>

                        {/* Regla legal Ann 29/04/2026 · titular SEPA por
                            defecto = cliente. Bloque informativo derivado · NO
                            duplica inputs que ya existen en la cabecera de la
                            ficha (nombre, apellidos, DNI). Si el titular es
                            distinto del cliente, el editor marca "Pagador
                            distinto del paciente" y se piden esos datos en su
                            sección. La regla está implementada en backend
                            services/pdf_legal.py · _resolver_deudor_sepa y en
                            frontend utils/mensajesInscripcionClasesFijas.js */}
                        {!client.tiene_pagador_alternativo && (() => {
                          const nombreCompleto = [
                            client.nombre,
                            client.primer_apellido,
                            client.segundo_apellido,
                          ].filter(Boolean).join(" ").trim();
                          const dniMostrar = client.sepa_titular_dni
                            || client.dni
                            || "";
                          return (
                            <div
                              style={{
                                padding: "12px 14px",
                                background: "var(--cn-surface-alt)",
                                borderRadius: "8px",
                                borderLeft: "3px solid var(--cn-primary)",
                                fontSize: "12px",
                                color: "#475569",
                                lineHeight: 1.5,
                              }}
                            >
                              <div style={{ fontWeight: 600, marginBottom: "4px", color: "var(--cn-primary)" }}>
                                Titular SEPA por defecto
                              </div>
                              <div>
                                <strong>Nombre:</strong>{" "}
                                {nombreCompleto || (
                                  <span style={{ color: "var(--cn-danger)" }}>
                                    Falta nombre y apellidos
                                  </span>
                                )}
                              </div>
                              <div>
                                <strong>DNI:</strong>{" "}
                                {dniMostrar || (
                                  <span style={{ color: "var(--cn-danger)" }}>
                                    Falta DNI
                                  </span>
                                )}
                              </div>
                              <div style={{ marginTop: "6px", fontSize: "11px", fontStyle: "italic" }}>
                                Si el titular del IBAN es distinto del cliente,
                                marca "Pagador distinto del paciente" abajo.
                              </div>
                            </div>
                          );
                        })()}

                        <div className="cn-data-item">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: "36px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                client.tiene_pagador_alternativo || false
                              }
                              onChange={(e) => {
                                setClient({
                                  ...client,
                                  tiene_pagador_alternativo: e.target.checked,
                                });
                                setHasUnsavedChanges(true);
                              }}
                              style={{
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                                marginRight: "8px",
                              }}
                              id="tiene_pagador"
                            />
                            <label
                              htmlFor="tiene_pagador"
                              style={{
                                fontSize: "14px",
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              {isMenor
                                ? `La cuenta NO es de ${client.tutor_nombre || "Tutor"} (Añadir otro)`
                                : "Pagador distinto del paciente (Padre/Madre...)"}
                            </label>
                          </div>
                        </div>
                      </>

                    {client.tiene_pagador_alternativo && (
                      <div
                        style={{
                          padding: "16px",
                          background: "var(--cn-surface-alt)",
                          borderRadius: "8px",
                          borderLeft: "3px solid var(--cn-primary)",
                        }}
                      >
                        <h4
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            marginBottom: "16px",
                            color: "var(--cn-primary)",
                          }}
                        >
                          Datos del Pagador Alternativo
                        </h4>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(0, 1fr) minmax(0, 1fr)",
                            gap: "16px",
                          }}
                        >
                          <div className="cn-data-item">
                            <span className="cn-data-label">
                              Nombre del pagador
                            </span>
                            <input
                              type="text"
                              className="cn-input-editable"
                              value={client.pagador_nombre || ""}
                              onChange={(e) => {
                                setClient({
                                  ...client,
                                  pagador_nombre: e.target.value,
                                });
                                setHasUnsavedChanges(true);
                              }}
                            />
                          </div>
                          <div className="cn-data-item">
                            <span className="cn-data-label">
                              Apellidos del pagador
                            </span>
                            <input
                              type="text"
                              className="cn-input-editable"
                              value={client.pagador_apellidos || ""}
                              onChange={(e) => {
                                setClient({
                                  ...client,
                                  pagador_apellidos: e.target.value,
                                });
                                setHasUnsavedChanges(true);
                              }}
                            />
                          </div>
                          <div className="cn-data-item">
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span className="cn-data-label">
                                DNI / NIE Pagador
                              </span>
                              {client.pagador_dni &&
                                !validarDNI(client.pagador_dni) && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      color: "var(--cn-danger)",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Inválido
                                  </span>
                                )}
                            </div>
                            <input
                              type="text"
                              className="cn-input-editable"
                              value={client.pagador_dni || ""}
                              onChange={(e) => {
                                setClient({
                                  ...client,
                                  pagador_dni: e.target.value,
                                });
                                setHasUnsavedChanges(true);
                              }}
                              style={{
                                textTransform: "uppercase",
                                border:
                                  client.pagador_dni &&
                                  !validarDNI(client.pagador_dni)
                                    ? "2px solid var(--cn-danger)"
                                    : "",
                              }}
                            />
                          </div>
                          <div className="cn-data-item">
                            <span className="cn-data-label">
                              Relación con el cliente
                            </span>
                            <select
                              className="cn-input-editable"
                              value={client.pagador_relacion || ""}
                              onChange={(e) => {
                                setClient({
                                  ...client,
                                  pagador_relacion: e.target.value,
                                });
                                setHasUnsavedChanges(true);
                              }}
                            >
                              <option value="">Seleccionar...</option>
                              <option value="padre">Padre</option>
                              <option value="madre">Madre</option>
                              <option value="pareja">Pareja</option>
                              <option value="tutor">Tutor Legal</option>
                              <option value="otro">Otro</option>
                            </select>
                          </div>
                          <div className="cn-data-item">
                            <span className="cn-data-label">
                              Teléfono Pagador
                            </span>
                            <input
                              type="tel"
                              className="cn-input-editable"
                              value={client.pagador_telefono || ""}
                              onChange={(e) => {
                                setClient({
                                  ...client,
                                  pagador_telefono: e.target.value,
                                });
                                setHasUnsavedChanges(true);
                              }}
                            />
                          </div>
                          <div className="cn-data-item">
                            <span className="cn-data-label">Email Pagador</span>
                            <input
                              type="email"
                              className="cn-input-editable"
                              value={client.pagador_email || ""}
                              onChange={(e) => {
                                setClient({
                                  ...client,
                                  pagador_email: e.target.value,
                                });
                                setHasUnsavedChanges(true);
                              }}
                            />
                          </div>
                        </div>

                        {/* DIRECCIÓN DEL PAGADOR (Ann 29/04/2026) */}
                        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px dashed #e5e7eb" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                            <h5 style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "#475569" }}>
                              Dirección del pagador
                            </h5>
                            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", cursor: "pointer", color: "#475569" }}>
                              <input
                                type="checkbox"
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // COPIAR SOLO direccion/piso/CP/ciudad/provincia. NUNCA email/teléfono/DNI/nombre.
                                    setClient({
                                      ...client,
                                      pagador_direccion: client.direccion || "",
                                      pagador_piso: client.piso || "",
                                      pagador_codigo_postal: client.codigo_postal || "",
                                      pagador_ciudad: client.ciudad || "",
                                      pagador_provincia: client.provincia || "",
                                    });
                                    setHasUnsavedChanges(true);
                                  }
                                  // Al desmarcar: NO borrar (regla Ann)
                                }}
                                style={{ width: "14px", height: "14px", cursor: "pointer", accentColor: "#16a34a" }}
                              />
                              Usar la misma dirección que el cliente
                            </label>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: "12px" }}>
                            <div className="cn-data-item">
                              <span className="cn-data-label">Dirección Pagador</span>
                              <input
                                type="text"
                                className="cn-input-editable"
                                value={client.pagador_direccion || ""}
                                onChange={(e) => {
                                  setClient({ ...client, pagador_direccion: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                              />
                            </div>
                            <div className="cn-data-item">
                              <span className="cn-data-label">Piso / Puerta Pagador</span>
                              <input
                                type="text"
                                className="cn-input-editable"
                                value={client.pagador_piso || ""}
                                onChange={(e) => {
                                  setClient({ ...client, pagador_piso: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)", gap: "12px", marginTop: "8px" }}>
                            <div className="cn-data-item">
                              <span className="cn-data-label">CP Pagador</span>
                              <input
                                type="text"
                                className="cn-input-editable"
                                value={client.pagador_codigo_postal || ""}
                                onChange={(e) => {
                                  setClient({ ...client, pagador_codigo_postal: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                              />
                            </div>
                            <div className="cn-data-item">
                              <span className="cn-data-label">Ciudad / Población Pagador</span>
                              <input
                                type="text"
                                className="cn-input-editable"
                                value={client.pagador_ciudad || ""}
                                onChange={(e) => {
                                  setClient({ ...client, pagador_ciudad: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                              />
                            </div>
                            <div className="cn-data-item">
                              <span className="cn-data-label">Provincia Pagador</span>
                              <input
                                type="text"
                                className="cn-input-editable"
                                value={client.pagador_provincia || ""}
                                onChange={(e) => {
                                  setClient({ ...client, pagador_provincia: e.target.value });
                                  setHasUnsavedChanges(true);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="cn-data-item" style={{ marginTop: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", height: "36px" }}>
                        <input type="checkbox" checked={client.sepa_firmado || false}
                          onChange={(e) => { setClient({ ...client, sepa_firmado: e.target.checked }); setHasUnsavedChanges(true); }}
                          style={{ width: "18px", height: "18px", cursor: "pointer", marginRight: "8px", accentColor: "#16a34a" }} id="sepa_firmado" />
                        <label htmlFor="sepa_firmado" style={{ fontSize: "14px", fontWeight: 600, cursor: "pointer", color: client.sepa_firmado ? "#16a34a" : "#dc2626" }}>
                          {client.sepa_firmado ? "Mandato SEPA firmado ✓" : "Mandato SEPA NO firmado"}
                        </label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", height: "36px", marginTop: "4px" }}>
                        <input type="checkbox" checked={client.sepa_archivado || false}
                          onChange={(e) => { setClient({ ...client, sepa_archivado: e.target.checked }); setHasUnsavedChanges(true); }}
                          style={{ width: "18px", height: "18px", cursor: "pointer", marginRight: "8px", accentColor: "#2563eb" }} id="sepa_archivado" />
                        <label htmlFor="sepa_archivado" style={{ fontSize: "14px", fontWeight: 600, cursor: "pointer", color: client.sepa_archivado ? "#2563eb" : "#94a3b8" }}>
                          {client.sepa_archivado ? "Mandato archivado ✓" : "Mandato no archivado"}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* COL 3: OBSERVACIONES (ocupa todo el alto) */}
            <div
              className="cn-panel"
              style={{ margin: 0, display: "flex", flexDirection: "column", gridColumn: 3, gridRow: "1 / 3", alignSelf: "start" }}
            >
              <div className="cn-panel-header">
                <h3 className="cn-panel-title">Observaciones</h3>
                <p className="cn-panel-subtitle">
                  Anotaciones clínicas, de trato o administrativas
                </p>
              </div>
              <div className="cn-panel-content" style={{ flex: 1, display: "flex", flexDirection: "column", gap: "12px" }}>
                <div className="cn-data-item" style={{ flex: 1 }}>
                  <span className="cn-data-label">Observaciones generales</span>
                  <textarea
                    className="cn-input-editable"
                    rows="3"
                    style={{ resize: "vertical" }}
                    value={client.observaciones || ""}
                    onChange={(e) => {
                      setClient({ ...client, observaciones: e.target.value });
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="Escribe aquí las observaciones generales del cliente..."
                  ></textarea>
                </div>

                {/* CRITICO: Solo visible para admin. Contiene notas confidenciales. */}
                {isAdmin && (
                <div
                  className="cn-data-item"
                  style={{
                    backgroundColor: "#fef9c3",
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #fef08a",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                      gap: "8px",
                    }}
                  >
                    <span
                      className="cn-data-label"
                      style={{ margin: 0, color: "#854d0e", fontWeight: 600 }}
                    >
                      Observaciones internas (Administrador)
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        backgroundColor: "#fde047",
                        color: "#854d0e",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontWeight: 600,
                      }}
                    >
                      SOLO ADMINISTRACIÓN
                    </span>
                  </div>
                  <textarea
                    className="cn-input-editable"
                    rows="3"
                    style={{
                      resize: "vertical",
                      backgroundColor: "#fff",
                      border: "1px solid #fde047",
                    }}
                    value={client.comentarios_confidenciales || ""}
                    onChange={(e) => {
                      setClient({
                        ...client,
                        comentarios_confidenciales: e.target.value,
                      });
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="Texto interno visible únicamente para roles de administración..."
                  ></textarea>
                </div>
                )}
                {/* MARCADORES INTERNOS */}
                <div style={{ borderTop: "1px solid var(--cn-border)", paddingTop: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span className="cn-data-label">Marcadores</span>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", cursor: "pointer" }}>
                    <input type="checkbox" checked={client.cliente_especial || false}
                      onChange={(e) => { setClient({ ...client, cliente_especial: e.target.checked }); setHasUnsavedChanges(true); }}
                      style={{ width: "14px", height: "14px", accentColor: "#f59e0b" }} />
                    Cliente VIP / Especial
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", cursor: "pointer" }}>
                    <input type="checkbox" checked={client.tarifa_familiar || false}
                      onChange={(e) => { setClient({ ...client, tarifa_familiar: e.target.checked }); setHasUnsavedChanges(true); }}
                      style={{ width: "14px", height: "14px", accentColor: "#f59e0b" }} />
                    Tarifa familiar / compartida
                  </label>
                </div>
              </div>
            </div>

            {/* DOCUMENTACIÓN DEL TRABAJADOR · bloque propio, a ancho completo y al
                final de Datos. Aquí van los PAPELES (obligatorios por grupo, subida
                y listado). Lo que es perfil (estudios, titulaciones, colegiatura)
                se queda en su bloque "Perfil profesional": una cosa, un sitio. */}


            {/* DOCUMENTOS OBLIGATORIOS POR GRUPO · marcar el grupo saca la lista de
                lo que hay que subir y avisa de lo que falta. */}
            <div className="cn-panel" style={{ marginBottom: 16 }}>
              <div className="cn-panel-header"><h3 className="cn-panel-title">Documentos obligatorios</h3></div>
              <div className="cn-panel-content">
                {GRUPOS_DOC_EMPLEADO.map((g) => {
                  const activo = !!client[g.campo];
                  return (
                    <div key={g.campo} style={{ marginBottom: 14 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                        <input type="checkbox" checked={activo} style={{ width: 18, height: 18 }}
                          onChange={(e) => { setClient({ ...client, [g.campo]: e.target.checked }); setHasUnsavedChanges(true); }} />
                        <span style={{ fontWeight: 600 }}>{g.label}</span>
                      </label>
                      {activo && (
                        <div style={{ marginTop: 8, marginLeft: 28, display: "flex", flexDirection: "column", gap: 8 }}>
                          {g.docs.map((tipo) => {
                            const subido = docsEmpleado.find((d) => (d.tipo_documento || d.tipo) === tipo);
                            return (
                              <div key={tipo} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ flex: 1, minWidth: 0 }}>{tipo}</span>
                                {subido ? (
                                  <>
                                    <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 12 }}>Subido</span>
                                    <a className="cn-btn cn-btn--outline cn-btn--sm" target="_blank" rel="noreferrer"
                                       href={urlDescargaDoc(subido._id || subido.id)}>Ver</a>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ color: "#b45309", fontWeight: 600, fontSize: 12 }}>Falta</span>
                                    <label className="cn-btn cn-btn--outline cn-btn--sm" style={{ cursor: "pointer", margin: 0 }}>
                                      Subir
                                      <input type="file" style={{ display: "none" }}
                                        onChange={async (e) => {
                                          const f = e.target.files?.[0];
                                          e.target.value = "";
                                          if (!f) return;
                                          try {
                                            await subirDocumentoEmpleado(f, tipo);
                                            toast.success(`${tipo} subido`);
                                          } catch (err) { toast.error(parseErrorDetail(err, "Error al subir el documento")); }
                                        }} />
                                    </label>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                <p style={{ margin: 0, fontSize: 12, color: "var(--cn-text-muted)" }}>
                  Marcar un grupo se guarda al pulsar Guardar ficha. Los archivos se suben al momento.
                </p>
              </div>
            </div>

            <div className="cn-panel" style={{ marginBottom: 16 }}>
              <div className="cn-panel-header"><h3 className="cn-panel-title">Subir nuevo documento</h3></div>
              <div className="cn-panel-content">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div className="cn-data-item" style={{ flex: 1, minWidth: 220 }}>
                    <span className="cn-data-label">Tipo de documento</span>
                    <select className="cn-input-editable" value={docTipo} onChange={(e) => setDocTipo(e.target.value)}>
                      <option value="">Marca el tipo...</option>
                      {TIPOS_DOC_EMPLEADO.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="cn-data-item" style={{ flex: 1, minWidth: 220 }}>
                    <span className="cn-data-label">Archivo</span>
                    <input type="file" className="cn-input-editable" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                  </div>
                  <button className="cn-btn cn-btn--primary" disabled={subiendoDoc} onClick={handleSubirDocEmpleado}>
                    {subiendoDoc ? "Subiendo..." : "Subir documento"}
                  </button>
                </div>
              </div>
            </div>
            <div className="cn-panel">
              <div className="cn-panel-header"><h3 className="cn-panel-title">Documentos ({docsEmpleado.length})</h3></div>
              <div className="cn-panel-content">
                <table className="cn-table" style={{ width: "100%" }}>
                  <thead><tr><th>Tipo</th><th>Nombre</th><th>Fecha</th><th></th></tr></thead>
                  <tbody>
                    {docsEmpleado.map((d, i) => (
                      <tr key={d.id || d._id || i}>
                        <td>{d.tipo_documento || d.tipo || "—"}</td>
                        <td>{d.nombre_original || d.nombre || "—"}</td>
                        <td>{(d.created_at || "").slice(0, 10) || "—"}</td>
                        <td style={{ textAlign: "right" }}>
                          {(d.id || d._id) && <a className="cn-btn cn-btn--outline cn-btn--sm" href={urlDescargaDoc(d.id || d._id)} target="_blank" rel="noreferrer">Ver</a>}
                        </td>
                      </tr>
                    ))}
                    {docsEmpleado.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: 20 }}>Sin documentos. Sube el primero arriba.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          
            </div>
          </div>
        )}

        {/* D) TAB "COBRO, FACTURACIÓN Y SEPA" */}
        {activeTab === "cobros" && (
          <div className="cn-tab-content" style={{ maxWidth: "900px", margin: "0 auto" }}>
            {/* B. Rejilla anual */}
            <div>
              <RejillaAnualPagos clienteId={id} anyo={anyoCobros} onAnyoChange={setAnyoCobros} />
            </div>

            {/* D. Facturación automática */}
            <div style={{ border: "1px solid #fed7aa", borderRadius: "8px", overflow: "hidden", marginBottom: "16px", backgroundColor: "#fffbeb" }}>
              <div style={{ padding: "10px 14px", backgroundColor: "#fef3c7", borderBottom: client.facturacion_automatica ? "1px solid #fde68a" : "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: "#92400e" }}>Facturación automática</span>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "12px", color: "#92400e" }}>
                  <input type="checkbox" checked={client.facturacion_automatica || false}
                    onChange={(e) => { setClient({ ...client, facturacion_automatica: e.target.checked }); setHasUnsavedChanges(true); }}
                    style={{ accentColor: "#d97706", width: "16px", height: "16px" }} />
                  Generar factura al confirmar cobro
                </label>
              </div>
              {client.facturacion_automatica && (
                <div style={{ padding: "12px 14px", display: "flex", alignItems: "flex-end", gap: "10px" }}>
                  <div className="cn-data-item" style={{ flex: 1 }}>
                    <span className="cn-data-label" style={{ fontSize: "11px" }}>Email donde enviar la factura</span>
                    <input type="email" className="cn-input-editable"
                      value={client.email_facturacion || client.email || ""}
                      onChange={(e) => { setClient({ ...client, email_facturacion: e.target.value }); setHasUnsavedChanges(true); }}
                      style={{ fontSize: "12px" }} />
                  </div>
                  <button onClick={async () => {
                      try {
                        await axios.put(`${API}/admin/clients/${id}/facturacion`, {
                          facturacion_automatica: client.facturacion_automatica,
                          email_facturacion: client.email_facturacion || null,
                        });
                        toast.success("Configuración de facturación guardada");
                      } catch (e) { toast.error("Error al guardar facturación"); }
                    }} style={{ padding: "6px 16px", fontSize: "12px", fontWeight: 600, border: "none", borderRadius: "6px", backgroundColor: "#d97706", color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>
                      Guardar
                    </button>
                </div>
              )}
            </div>

            {/* E. Facturas del cliente */}
            <div style={{ border: "1px solid var(--cn-border)", borderRadius: "8px", overflow: "hidden", marginBottom: "16px" }}>
              <div style={{ padding: "8px 12px", backgroundColor: "#e0ecf8", borderBottom: "1px solid #c5d9f0" }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e3a5f" }}>Facturas del cliente</span>
              </div>
              {errorAnulacion && (
                <div style={{
                  margin: "10px 12px",
                  padding: "10px 12px",
                  background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderLeft: "4px solid #dc2626",
                  borderRadius: "4px",
                  color: "#7f1d1d",
                  fontSize: "13px",
                  lineHeight: 1.45,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "10px",
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                      Error al anular factura {errorAnulacion.factura}
                    </div>
                    <div style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: "12px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      background: "#fff",
                      border: "1px solid #fecaca",
                      borderRadius: "3px",
                      padding: "6px 8px",
                    }}>{errorAnulacion.msg}</div>
                    <div style={{ fontSize: "11px", color: "#9b2c2c", marginTop: "6px" }}>
                      {errorAnulacion.ts} · cierra este aviso pulsando ×
                    </div>
                  </div>
                  <button
                    onClick={() => setErrorAnulacion(null)}
                    title="Cerrar mensaje"
                    style={{
                      background: "none",
                      border: "1px solid #fca5a5",
                      cursor: "pointer",
                      color: "#7f1d1d",
                      fontSize: "14px",
                      lineHeight: 1,
                      padding: "2px 8px",
                      borderRadius: "3px",
                    }}
                  >×</button>
                </div>
              )}
              {modalMotivo}
              <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#edf2fa" }}>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f", width: "90px" }}>N Factura</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f", width: "75px" }}>Fecha</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f" }}>Concepto</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f", width: "80px" }}>Metodo</th>
                    <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: "#1e3a5f", width: "70px" }}>Importe</th>
                    <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, color: "#1e3a5f", width: "70px" }}>Estado</th>
                    <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, color: "#1e3a5f", width: "70px" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = facturas.filter(f => {
                      // Excluir facturas que NO son fiscales (sin número, sin RF):
                      // - DESCARTADA: borrador descartado por admin
                      // - ERROR: borrador del lote con validaciones fallidas (sin número fiscal, sin RF Verifactu) — decisión Ann 2026-05-02
                      if (f.estado === "DESCARTADA") return false;
                      if (f.estado === "ERROR") return false;
                      const fecha = f.fecha_expedicion || f.created_at;
                      return fecha && new Date(fecha).getFullYear() === anyoCobros;
                    });
                    return filtered.length === 0 ? (
                    <tr><td colSpan="7" style={{ textAlign: "center", padding: "16px", color: "#9ca3af", fontStyle: "italic" }}>Sin facturas en {anyoCobros}</td></tr>
                  ) : (
                    filtered.map((f, fIdx) => (
                      <tr key={f.id} style={{ borderBottom: "1px solid #e8edf3", backgroundColor: fIdx % 2 === 0 ? "#ffffff" : "#f0f4fa" }}>
                        <td style={{ padding: "5px 10px", fontWeight: 600, color: "var(--cn-primary)" }}>
                          {f.serie && f.numero ? `${f.serie}-${f.fecha_expedicion ? new Date(f.fecha_expedicion).getFullYear() : ""}-${f.numero}` : f.numero || <span style={{ color: "#9ca3af", fontStyle: "italic", fontWeight: 400 }}>Borrador</span>}
                        </td>
                        <td style={{ padding: "5px 10px", color: "#6b7280" }}>{f.fecha_expedicion ? new Date(f.fecha_expedicion).toLocaleDateString("es-ES", {day: "2-digit", month: "2-digit", year: "2-digit"}) : "-"}</td>
                        <td style={{ padding: "5px 10px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.concepto}>{f.concepto || "-"}</td>
                        <td style={{ padding: "5px 10px", textTransform: "capitalize" }}>{f.metodo_pago || "-"}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>{f.total ? f.total.toFixed(2) : "0.00"}</td>
                        <td style={{ padding: "5px 10px", textAlign: "center" }}>
                          <span style={{
                            padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 600,
                            color: f.estado_pago === "PAGADA" ? "#047857" : f.estado === "EMITIDA" ? "#16a34a" : f.estado === "ANULADA" ? "#dc2626" : f.estado === "RECTIFICATIVA" ? "#7c3aed" : "#d97706",
                            backgroundColor: f.estado_pago === "PAGADA" ? "#d1fae5" : f.estado === "EMITIDA" ? "#f0fdf4" : f.estado === "ANULADA" ? "#fef2f2" : f.estado === "RECTIFICATIVA" ? "#f5f3ff" : "#fffbeb",
                          }}>{f.estado_pago === "PAGADA" ? "COBRADA" : f.estado || "DRAFT"}</span>
                        </td>
                        <td style={{ padding: "5px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
                          {f.estado === "EMITIDA" && (
                            <>
                              <button title="Ver factura" style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", color: "#16a34a" }}
                                onClick={() => openDocPreview("factura", f.id, `${f.serie||"MPD"}_${f.numero||""}`)}>
                                <FileText className="w-4 h-4" />
                              </button>
                              <button title="Anular factura" style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", color: "#dc2626", position: "relative" }}
                                onClick={async () => {
                                  const facturaLabel = `${f.serie||""}-${f.numero}`;
                                  // [Ann 30-10-2026] motivo del catálogo (desplegable único), nunca texto libre
                                  const motivoLimpio = await pedirMotivo(facturaLabel);
                                  if (!motivoLimpio) return; // usuario canceló
                                  try {
                                    await axios.post(
                                      `${API}/facturacion/facturas/${f.id}/anular?motivo=${encodeURIComponent(motivoLimpio)}`
                                    );
                                    toast.success(`Factura ${facturaLabel} anulada`);
                                    setErrorAnulacion(null);
                                    const res = await axios.get(`${API}/facturacion/facturas?cliente_id=${id}`);
                                    setFacturas(Array.isArray(res.data) ? res.data : res.data.facturas || []);
                                  } catch (err) {
                                    let msg = "Error desconocido al anular factura";
                                    const d = err?.response?.data?.detail;
                                    if (typeof d === "string") msg = d;
                                    else if (Array.isArray(d)) {
                                      msg = d.map(e =>
                                        typeof e === "string" ? e :
                                        `${(e.loc||[]).filter(x => x!=="body" && x!=="query").join('.')}: ${e.msg||JSON.stringify(e)}`
                                      ).join(" · ");
                                    } else if (d && typeof d === "object") msg = JSON.stringify(d);
                                    else if (err?.message) msg = err.message;
                                    const status = err?.response?.status;
                                    if (status) msg = `[HTTP ${status}] ${msg}`;
                                    setErrorAnulacion({
                                      facturaId: f.id,
                                      factura: facturaLabel,
                                      msg,
                                      ts: new Date().toLocaleString("es-ES"),
                                    });
                                    toast.error(msg, { duration: 12000 });
                                  }
                                }}>
                                <span style={{ position: "relative", display: "inline-flex" }}>
                                  <FileText className="w-4 h-4" style={{ opacity: 0.5 }} />
                                  <XCircle className="w-3 h-3" style={{ position: "absolute", bottom: "-2px", left: "-2px", color: "#dc2626", backgroundColor: "#fff", borderRadius: "50%" }} />
                                </span>
                              </button>
                            </>
                          )}
                          {f.estado === "DRAFT" && (
                            <>
                              <button title="Emitir factura" style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", color: "#9ca3af" }}
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/facturacion/facturas/${f.id}/emitir`);
                                    toast.success("Factura emitida");
                                    const res = await axios.get(`${API}/facturacion/facturas?cliente_id=${id}`);
                                    setFacturas(Array.isArray(res.data) ? res.data : res.data.facturas || []);
                                  } catch (err) { toast.error(err.response?.data?.detail || "Error", { duration: 5000 }); }
                                }}>
                                <FileText className="w-4 h-4" />
                              </button>
                              <button title="Eliminar" style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: "#dc2626" }}
                                onClick={async () => {
                                  if (!window.confirm("Eliminar borrador?")) return;
                                  try {
                                    await axios.delete(`${API}/facturacion/facturas/${f.id}`);
                                    toast.success("Eliminado");
                                    const res = await axios.get(`${API}/facturacion/facturas?cliente_id=${id}`);
                                    setFacturas(Array.isArray(res.data) ? res.data : res.data.facturas || []);
                                  } catch (err) { toast.error(err.response?.data?.detail || "Error", { duration: 5000 }); }
                                }}>
                                <Trash2 style={{ width: 14, height: 14 }} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  ); })()}
                </tbody>
              </table>
            </div>

            {/* E. Recibos del cliente */}
            <div style={{ border: "1px solid var(--cn-border)", borderRadius: "8px", overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", backgroundColor: "#e0ecf8", borderBottom: "1px solid #c5d9f0" }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e3a5f" }}>Recibos y cobros</span>
              </div>
              <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#edf2fa" }}>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f", width: "90px" }}>N Recibo</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f", width: "75px" }}>Fecha</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f" }}>Concepto</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f", width: "80px" }}>Metodo</th>
                    <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: "#1e3a5f", width: "70px" }}>Importe</th>
                    <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, color: "#1e3a5f", width: "70px" }}>Estado</th>
                    <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, color: "#1e3a5f", width: "70px" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filteredR = recibos.filter(r => {
                      const fecha = r.fecha_emision || r.created_at;
                      return fecha && new Date(fecha).getFullYear() === anyoCobros;
                    });
                    return filteredR.length === 0 ? (
                    <tr><td colSpan="7" style={{ textAlign: "center", padding: "16px", color: "#9ca3af", fontStyle: "italic" }}>Sin recibos en {anyoCobros}</td></tr>
                  ) : (
                    filteredR.map((r, idx) => {
                      const fec = r.fecha_emision || r.created_at;
                      const dt = fec ? new Date(fec) : null;
                      const dStr = dt ? dt.toLocaleDateString("es-ES", {day: "2-digit", month: "2-digit", year: "2-digit"}) : "-";
                      let imp = r.importe || 0;
                      if (typeof imp !== "number") imp = parseFloat(imp) || 0;
                      const anulado = r.estado === "anulado";
                      return (
                        <tr key={r.id || idx} style={{ borderBottom: "1px solid #e8edf3", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f0f4fa", opacity: anulado ? 0.5 : 1 }}>
                          <td style={{ padding: "5px 10px", fontWeight: 500 }}>{r.numero_recibo || "S/N"}</td>
                          <td style={{ padding: "5px 10px", color: "#6b7280" }}>{dStr}</td>
                          <td style={{ padding: "5px 10px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.concepto}>{r.concepto || "-"}</td>
                          <td style={{ padding: "5px 10px", textTransform: "capitalize" }}>{r.metodo_pago || "-"}</td>
                          <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>{imp.toFixed(2)}</td>
                          <td style={{ padding: "5px 10px", textAlign: "center" }}>
                            <span style={{
                              padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 600,
                              color: anulado ? "#dc2626" : "#16a34a",
                              backgroundColor: anulado ? "#fef2f2" : "#f0fdf4",
                            }}>{anulado ? "Anulado" : "Cobrado"}</span>
                          </td>
                          <td style={{ padding: "5px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
                            {r.id && !anulado && (
                              <button title="Ver recibo" style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", color: "#16a34a" }}
                                onClick={() => openDocPreview("recibo", r.id, r.numero_recibo || r.id)}>
                                <Receipt className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ); })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* E) TAB "HISTORIAL" */}
        {activeTab === "historial" && (
          <div className="cn-tab-content">
            <div
              className="cn-panel"
              style={{ maxWidth: "1000px", margin: "0 auto", width: "100%" }}
            >
              <div className="cn-history-ctrls">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "24px" }}
                >
                  <h3 className="cn-panel-title">Registro de Actividad</h3>
                  <div className="cn-history-filters">
                    {[
                      "Todo",
                      "Altas/Bajas",
                      "Facturación",
                      "Incidencias",
                      "Cobros",
                    ].map((f) => (
                      <button
                        key={f}
                        className={`cn-filter-btn ${historyFilter === f ? "cn-filter-btn--active" : ""}`}
                        onClick={() => setHistoryFilter(f)}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <button className="cn-btn cn-btn--outline cn-btn--sm" style={{ fontSize: "11px" }} onClick={() => {
                  setEventoForm({
                    tipo_evento: 'queja',
                    fecha_evento: new Date().toISOString().split('T')[0],
                    descripcion: '',
                    comentario_interno: '',
                    titulo_queja: '',
                    comentarios_terapeutas: '',
                  });
                  setShowEventoModal(true);
                }}>
                  + Registrar queja / incidente
                </button>
              </div>

              <div className="cn-history-list">
                {events.length === 0 ? (
                  <div
                    className="cn-empty-state cn-empty-state--compact"
                    style={{ border: "none", background: "transparent" }}
                  >
                    <p
                      className="cn-empty-state-desc"
                      style={{
                        fontStyle: "italic",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      No hay eventos registrados en el historial de este
                      cliente.
                    </p>
                  </div>
                ) : (
                  events
                    .filter(
                      (ev) =>
                        historyFilter === "Todo" ||
                        ev.tipo_evento === historyFilter,
                    )
                    .map((ev, idx) => {
                      // Helper TZ-safe: YYYY-MM-DD → DD/MM/YYYY sin aplicar offset local.
                      // Si el string lleva hora, usa toLocaleDateString (respeta zona).
                      const fmtDateSafe = (s) => {
                        if (!s) return "—";
                        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
                        if (m) return `${m[3]}/${m[2]}/${m[1]}`;
                        return new Date(s).toLocaleDateString("es-ES", {
                          day: "2-digit", month: "2-digit", year: "numeric",
                        });
                      };

                      const rawFecha = ev.fecha_evento || ev.created_at;
                      const dateStr = fmtDateSafe(rawFecha);
                      // timeStr solo tiene sentido si el string tiene hora
                      let timeStr = "";
                      if (rawFecha && /T\d{2}:\d{2}/.test(rawFecha)) {
                        try {
                          timeStr = new Date(rawFecha).toLocaleTimeString("es-ES", {
                            hour: "2-digit", minute: "2-digit",
                          });
                        } catch { /* noop */ }
                      }
                      const color = ev._color || getEventColor(ev.tipo_evento);
                      const isSistema = ev._source === "sistema";
                      const isBlock = ev._is_block === true;
                      const meta = ev._is_meta || {};

                      // Para bloques IS, usar fecha_inicio del meta como fecha canónica visible
                      const dateStrCanonica = isBlock && meta.fecha_inicio
                        ? fmtDateSafe(meta.fecha_inicio)
                        : dateStr;

                      return (
                        <div key={ev.id || `ev_${idx}`} className="cn-history-row">
                          <div className="cn-history-stamp">
                            <span className="cn-history-date">{dateStrCanonica}</span>
                            {!isBlock && timeStr && <span className="cn-history-time">{timeStr}</span>}
                          </div>

                          <div className="cn-history-track">
                            <div
                              className="cn-history-dot"
                              style={{ borderColor: color, backgroundColor: isSistema ? color : "transparent" }}
                            ></div>
                            <div className="cn-history-line"></div>
                          </div>

                          <div className="cn-history-card" style={{
                            backgroundColor: `${color}0D`,
                            borderLeft: `3px solid ${color}`,
                          }}>
                            <div className="cn-history-card-top">
                              <h4 className="cn-history-card-title">
                                {ev.subtipo_evento || ev.tipo_evento}
                              </h4>
                              <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                {isBlock && meta.estado_is && (
                                  <span style={{
                                    fontSize: "9px", fontWeight: 700, padding: "1px 6px", borderRadius: "4px",
                                    // PR-IS-STATE-1 (Ann 2026-05-24) · 3 colores canónicos:
                                    //   EN CURSO   → naranja  (IS realmente activa)
                                    //   PROGRAMADA → gris neutral (IS futura · no activada todavía)
                                    //   CERRADA    → gris histórico (IS finalizada)
                                    // ⚠ PR-IS-STATE-2B (Ann 2026-05-24) · los strings
                                    // "EN CURSO" / "PROGRAMADA" / "CERRADA" son
                                    // CONTRATO con backend/services/historial_estado_visual_is.py
                                    // (constantes ESTADO_VISUAL_*). Test estático
                                    // test_TESTAUTOPISTA_is_estado_contrato.py asegura
                                    // que coinciden y bloquea regresión silenciosa.
                                    backgroundColor:
                                      meta.estado_is === "EN CURSO" ? "#d97706" :
                                      meta.estado_is === "PROGRAMADA" ? "#9ca3af" :
                                      "#64748b",
                                    color: "#fff",
                                  }}>
                                    {meta.estado_is}
                                  </span>
                                )}
                                {!isBlock && ev._incidencia_activa && (
                                  <span style={{ fontSize: "9px", fontWeight: 700, padding: "1px 6px", borderRadius: "4px", backgroundColor: "#d97706", color: "#fff" }}>
                                    EN CURSO
                                  </span>
                                )}
                                <span
                                  className="cn-history-badge"
                                  style={{
                                    color: "#fff",
                                    borderColor: color,
                                    backgroundColor: color,
                                  }}
                                >
                                  {isSistema ? (ev._categoria || "sistema") : ev.tipo_evento}
                                </span>
                              </div>
                            </div>

                            {isBlock ? (
                              <div className="cn-history-card-desc" style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: "10px", rowGap: "2px", fontSize: "12px" }}>
                                <span style={{ color: "#64748b" }}>Fecha notificación:</span>
                                <span>{fmtDateSafe(meta.fecha_notificacion)}</span>
                                <span style={{ color: "#64748b" }}>Inicio:</span>
                                <span>{fmtDateSafe(meta.fecha_inicio)}</span>
                                <span style={{ color: "#64748b" }}>Fin:</span>
                                <span>{meta.fecha_fin ? fmtDateSafe(meta.fecha_fin) : "en curso"}</span>
                                <span style={{ color: "#64748b" }}>Estado:</span>
                                <span>{meta.estado_is || "—"}</span>
                                <span style={{ color: "#64748b" }}>Tipo:</span>
                                <span>{meta.tipo_incidencia_nombre || "—"}</span>
                                {(() => {
                                  const fmtEur = (v) => v != null ? `${Number(v).toFixed(2).replace('.', ',')} €` : "—";
                                  const pVig = meta.precio_incidencia_vigente != null ? meta.precio_incidencia_vigente : meta.precio_incidencia;
                                  const pApe = meta.precio_incidencia_apertura != null ? meta.precio_incidencia_apertura : pVig;
                                  const difieren = pApe != null && pVig != null && Number(pApe) !== Number(pVig);
                                  if (difieren) {
                                    return (
                                      <>
                                        <span style={{ color: "#64748b" }}>Cuota IS en apertura:</span>
                                        <span>{fmtEur(pApe)}</span>
                                        <span style={{ color: "#64748b" }}>Cuota IS vigente actual:</span>
                                        <span>{fmtEur(pVig)}</span>
                                      </>
                                    );
                                  }
                                  return (
                                    <>
                                      <span style={{ color: "#64748b" }}>Cuota IS en apertura:</span>
                                      <span>{fmtEur(pApe)}</span>
                                    </>
                                  );
                                })()}
                                <span style={{ color: "#64748b" }}>Servicio afectado:</span>
                                <span>{meta.servicio_afectado || "Clases Fijas"}</span>
                                {meta.estado_is === "CERRADA" && meta.motivo_cierre && (
                                  <>
                                    <span style={{ color: "#64748b" }}>Motivo cierre:</span>
                                    <span>{meta.motivo_cierre}</span>
                                  </>
                                )}
                                {meta.estado_is === "CERRADA" && meta.dias_totales != null && (
                                  <>
                                    <span style={{ color: "#64748b" }}>Duración:</span>
                                    <span>{meta.dias_totales} día{meta.dias_totales === 1 ? "" : "s"}</span>
                                  </>
                                )}
                              </div>
                            ) : (
                              <p className="cn-history-card-desc">
                                {ev.descripcion || "Sin descripción."}
                              </p>
                            )}

                            {!isBlock && ev.comentario_interno && (
                              <div className="cn-history-card-comment">
                                {ev.comentario_interno}
                              </div>
                            )}
                            {isSistema && ev._usuario && (
                              <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "4px" }}>
                                Por: {ev._usuario}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          </div>
        )}

        {/* D-BIS) TAB INSCRIPCION */}
        {activeTab === "inscripcion" && (
          <div className="cn-tab-content">
            {/* Fecha alta cliente — desde que el cliente se reactiva alguna vez, la fecha de alta
                original queda blindada (gris, no editable) y al lado se muestra la fecha de reactivación.
                Predicado: client.fecha_reactivacion existe → bloqueo permanente. */}
            {(() => {
              const fueReactivado = !!client.fecha_reactivacion;
              return (
                <div className="cn-panel" style={{ marginBottom: "16px" }}>
                  <div className="cn-panel-content" style={{ display: "flex", alignItems: "center", gap: "16px", padding: "10px 16px", flexWrap: "wrap" }}>
                    <span style={{ margin: 0, whiteSpace: "nowrap", fontSize: "14px", fontWeight: 700, color: fueReactivado ? "#9ca3af" : "#1e293b" }}>
                      Fecha de alta como cliente
                    </span>
                    <input
                      type="date"
                      className="cn-input-editable"
                      style={{
                        maxWidth: "180px",
                        ...(fueReactivado ? { color: "#9ca3af", backgroundColor: "#f3f4f6", borderColor: "#e5e7eb", cursor: "not-allowed" } : {}),
                      }}
                      value={client.fecha_alta ? client.fecha_alta.split("T")[0] : ""}
                      disabled={fueReactivado}
                      title={fueReactivado ? "Cliente reactivado — la fecha de alta original queda como histórico no modificable" : undefined}
                      onChange={(e) => { setClient({ ...client, fecha_alta: e.target.value }); setHasUnsavedChanges(true); }}
                      onPaste={(e) => {
                        if (fueReactivado) { e.preventDefault(); return; }
                        e.preventDefault();
                        const pasted = (e.clipboardData || window.clipboardData).getData("text").trim();
                        // Acepta DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, YYYY/MM/DD
                        let parsed = null;
                        const dmy = pasted.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
                        const ymd = pasted.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})$/);
                        if (dmy) parsed = `${dmy[3]}-${dmy[2].padStart(2,"0")}-${dmy[1].padStart(2,"0")}`;
                        else if (ymd) parsed = `${ymd[1]}-${ymd[2].padStart(2,"0")}-${ymd[3].padStart(2,"0")}`;
                        if (parsed && !isNaN(new Date(parsed).getTime())) {
                          setClient({ ...client, fecha_alta: parsed }); setHasUnsavedChanges(true);
                        } else { toast.error("Formato de fecha no reconocido. Usa DD/MM/YYYY"); }
                      }} />
                    {fueReactivado && (
                      <>
                        <span style={{ color: "#cbd5e1" }}>|</span>
                        <span style={{ margin: 0, whiteSpace: "nowrap", fontSize: "14px", fontWeight: 700, color: "#16a34a" }}>
                          Fecha de reactivación
                        </span>
                        <span style={{ fontSize: "13px", color: "#16a34a", fontWeight: 600 }}>
                          {getDateFormat(client.fecha_reactivacion)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Servicios + Firmas */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: "16px", alignItems: "start" }}>

              {/* CLASES FIJAS */}
              <div className="cn-panel" style={{ margin: 0, position: "relative", zIndex: 10, backgroundColor: suscripcionActiva?.estado === "pausada_incidencia" ? "#fffbeb" : "", borderColor: suscripcionActiva?.estado === "pausada_incidencia" ? "#fde68a" : "" }}>
                <div className="cn-panel-header" style={{ backgroundColor: suscripcionActiva?.estado === "pausada_incidencia" ? "#fef3c7" : client.inscrito_clases_fijas ? "#f0fdf4" : "" }}>
                  {/* Bloque 1: el header NO abre inscripción al hacer click. La acción
                      principal es el botón [Inscribir a clases fijas] (más abajo). */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="checkbox" checked={client.inscrito_clases_fijas || false} readOnly
                        style={{ width: "16px", height: "16px", accentColor: "var(--cn-primary)", cursor: "default" }} />
                      <h3 className="cn-panel-title" style={{ margin: 0 }}>Clases Fijas</h3>
                    </div>
                    {client.inscrito_clases_fijas && client.estado_clases && (
                      <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: "4px",
                        color: client.estado_clases === "activo" ? "#16a34a" : client.estado_clases === "pausado" ? "#d97706" : "#dc2626",
                        backgroundColor: client.estado_clases === "activo" ? "#dcfce7" : client.estado_clases === "pausado" ? "#fef3c7" : "#fee2e2" }}>
                        {client.estado_clases === "activo" ? "Inscrito" : client.estado_clases === "pausado" ? "Pausado (IS)" : "No inscrito"}
                      </span>
                    )}
                  </div>
                  <p className="cn-panel-subtitle" style={{ margin: 0 }}>Grupales presenciales</p>
                </div>
                {/* Bloque 1 — Botón único de inscripción (sustituye al Modal Matrícula viejo).
                    Solo visible cuando el cliente NO está inscrito ni tiene suscripción activa. */}
                {!client.inscrito_clases_fijas && !suscripcionActiva && (
                  <div className="cn-panel-content" style={{ fontSize: "13px", textAlign: "center", padding: "16px" }}>
                    <button
                      onClick={() => setShowInscripcionModal(true)}
                      style={{ fontSize: "13px", padding: "8px 18px", backgroundColor: "#16a34a", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}>
                      Inscribir a clases fijas
                    </button>
                  </div>
                )}

                {(client.inscrito_clases_fijas || suscripcionActiva) && (
                  <div className="cn-panel-content" style={{ fontSize: "13px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "12px" }}>
                      <div className="cn-data-item">
                        <span className="cn-data-label">Estado del servicio</span>
                        <select className="cn-input-editable" value={client.estado_clases || "activo"}
                          onChange={(e) => { setClient({ ...client, estado_clases: e.target.value }); setHasUnsavedChanges(true); }}
                          style={{ fontWeight: 600, color: (client.estado_clases || "activo") === "activo" ? "#16a34a" : (client.estado_clases || "activo") === "pausado" ? "#d97706" : "#dc2626" }}>
                          <option value="activo">Inscrito</option>
                          <option value="pausado">Pausado (IS)</option>
                          <option value="baja">No inscrito</option>
                        </select>
                      </div>
                      <div className="cn-data-item">
                        <span className="cn-data-label">Fecha inscripción</span>
                        <input type="date" className="cn-input-editable" value={(client.fecha_alta_clases || client.fecha_alta || "").split("T")[0]}
                          onChange={(e) => { setClient({ ...client, fecha_alta_clases: e.target.value }); setHasUnsavedChanges(true); }} />
                      </div>
                      <div className="cn-data-item">
                        <span className="cn-data-label">Sede</span>
                        {/* Ann 2026-05-20 · cliente con CF activas: selector
                            deshabilitado · usar modal "Cambiar sede de clases fijas"
                            (autopista única). Cualquier intento de bypass cae en
                            el HTTP 409 USE_AUTOPISTA_CAMBIO_SEDE_CF del backend. */}
                        {client.inscrito_clases_fijas === true ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <select
                              className="cn-input-editable"
                              value={client.sede || ""}
                              disabled
                              title="Sede bloqueada · usar 'Cambiar sede de clases fijas'"
                              style={{ opacity: 0.6, cursor: "not-allowed" }}>
                              <option value="">Seleccione...</option>
                              {SEDES.map((s) => (<option key={s} value={s}>{s}</option>))}
                            </select>
                            <button
                              type="button"
                              onClick={() => {
                                // Defensa anti cross-cliente (Ann 2026-05-20 · post-incidente Madeleine):
                                // No abrir el modal si client.id no coincide con
                                // el id del searchParams (estado stale durante navegación).
                                if (!client?.id || String(client.id) !== String(id)) {
                                  toast.error(
                                    "🛑 Ficha no sincronizada · recarga la página antes de cambiar la sede.",
                                    { duration: 8000 },
                                  );
                                  return;
                                }
                                setMostrarModalCambioSedeCF(true);
                              }}
                              disabled={loading || !client?.id || String(client.id) !== String(id)}
                              style={{
                                marginTop: "4px",
                                padding: "6px 10px",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#1d4ed8",
                                backgroundColor: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                borderRadius: "4px",
                                cursor: (loading || !client?.id || String(client.id) !== String(id)) ? "not-allowed" : "pointer",
                                opacity: (loading || !client?.id || String(client.id) !== String(id)) ? 0.5 : 1,
                              }}
                              title="Abrir flujo oficial de cambio de sede de clases fijas">
                              📍 Cambiar sede de clases fijas
                            </button>
                          </div>
                        ) : (
                          <select className="cn-input-editable" value={client.sede || ""}
                            onChange={(e) => { setClient({ ...client, sede: e.target.value }); setHasUnsavedChanges(true); }}>
                            <option value="">Seleccione...</option>
                            {SEDES.map((s) => (<option key={s} value={s}>{s}</option>))}
                          </select>
                        )}
                      </div>
                      <div className="cn-data-item">
                        <span className="cn-data-label">Tarifa</span>
                        <select className="cn-input-editable" value={client.cuota_id || ""}
                          onChange={(e) => {
                            const nuevaCuotaId = e.target.value;
                            // PR-BECA-2 (Ann 2026-05-27): acoplamiento bidireccional
                            // cuota becada ↔ forma_pago='becado'. Si admin elige una
                            // cuota becada, auto-set forma_pago='becado'. Si sale de
                            // cuota becada hacia una normal mientras forma_pago sigue
                            // siendo 'becado', limpiar forma_pago y avisar para que
                            // el admin elija una válida antes de guardar.
                            const cuotaNuevaSel = (cuotas || []).find(c =>
                              c?.id === nuevaCuotaId || c?._id === nuevaCuotaId);
                            const cuotaNuevaEsBecada = cuotaNuevaSel?.es_becado === true;
                            const nuevo = { ...client, cuota_id: nuevaCuotaId };
                            if (cuotaNuevaEsBecada) {
                              if (nuevo.forma_pago !== "becado") {
                                nuevo.forma_pago = "becado";
                                toast.info(
                                  "ℹ️ Cuota becada · forma de pago ajustada a 'Becado' automáticamente.",
                                  { duration: 6000 });
                              }
                            } else if (nuevo.forma_pago === "becado") {
                              // Sale de becado: hay que elegir una forma de pago normal.
                              nuevo.forma_pago = "";
                              toast.warning(
                                "⚠️ Has cambiado a una cuota normal. Selecciona una nueva forma de pago (efectivo, tarjeta, domiciliación o transferencia) antes de guardar.",
                                { duration: 10000 });
                            }
                            setClient(nuevo);
                            setHasUnsavedChanges(true);
                          }}>
                          <option value="">-- Sin membresia --</option>
                          {cuotas.map((cuota) => (<option key={cuota.id} value={cuota.id}>{cuota.nombre} - {cuota.precio_final || cuota.precio}EUR</option>))}
                        </select>
                      </div>
                      {/* Sub-fase 7 · Forma de pago de Clases Fijas. Vive aquí
                          (NO en Datos principales). Al guardar, el router
                          5.3.2-B detecta el cambio y dispara el endpoint
                          atómico cambiar-forma-cobro. Datos bancarios siguen
                          en Datos principales (IBAN, titular, DNI, pagador,
                          mandato). PR-BECA-2 (Ann 2026-05-27): 'becado'
                          habilitado · acoplamiento bidireccional con la
                          cuota becada (auto-set en cualquier sentido). */}
                      <div className="cn-data-item">
                        <span className="cn-data-label">Forma de pago</span>
                        <select className="cn-input-editable"
                          value={client.forma_pago || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            // Validación cruzada SEPA: aviso pre-bloqueo
                            // si elige domiciliación con datos incompletos.
                            if (v === "domiciliacion") {
                              const falta = mensajeFaltaSepaParaInscripcion(
                                { ...client, forma_pago: v },
                                { isMenor, validarIBAN, validarDNI },
                              );
                              if (falta) {
                                toast.warning(falta, { duration: 10000 });
                                // Permitimos seleccionar visualmente pero
                                // validar() bloqueará el guardado · al
                                // mismo tiempo el aviso da pista clara al
                                // admin de qué completar.
                              }
                            }
                            const nuevo = { ...client, forma_pago: v };
                            // PR-BECA-2 (Ann 2026-05-27): acoplamiento
                            // bidireccional becado ↔ cuota becada.
                            //  - Si elige 'becado' → auto-set la cuota becada
                            //    del catálogo (id hardcoded · OK BECA-2,
                            //    pasará a config en PR-BECA-3 si procede).
                            //  - Si sale de 'becado' (estaba becado y ahora
                            //    elige otra forma) y la cuota actual es la
                            //    becada → limpiar cuota_id y avisar al admin
                            //    de que elija una cuota normal.
                            const CUOTA_BECADA_ID =
                              "2e56f652-f4fa-41f3-bf12-3e0df5ef1faa";
                            if (v === "becado") {
                              nuevo.cuota_id = CUOTA_BECADA_ID;
                            } else if (
                              client?.forma_pago === "becado"
                              && client?.cuota_id === CUOTA_BECADA_ID
                            ) {
                              nuevo.cuota_id = "";
                              toast.warning(
                                "⚠️ Has salido de 'Becado'. Selecciona una nueva tarifa normal antes de guardar.",
                                { duration: 10000 });
                            }
                            setClient(nuevo);
                            setHasUnsavedChanges(true);
                          }}
                          style={{ fontWeight: 600 }}>
                          <option value="">Sin configurar</option>
                          <option value="efectivo">Efectivo</option>
                          <option value="tarjeta">Tarjeta</option>
                          <option value="domiciliacion">Domiciliación bancaria (SEPA)</option>
                          <option value="transferencia">Transferencia bancaria</option>
                          <option value="becado">Becado</option>
                        </select>
                      </div>
                      {client.forma_pago === "becado" && (
                        <div className="cn-data-item">
                          <span className="cn-data-label">Tipo de beca</span>
                          <select className="cn-input-editable"
                            value={client.tipo_beca || ""}
                            onChange={(e) => {
                              setClient({ ...client, tipo_beca: e.target.value });
                              setHasUnsavedChanges(true);
                            }}
                            style={{ fontWeight: 600 }}>
                            <option value="">Seleccionar...</option>
                            <option value="familiar_trabajador">Familiar de trabajador</option>
                            <option value="colaborador_becado">Colaborador becado</option>
                            <option value="vip">VIP</option>
                          </select>
                        </div>
                      )}
                      {/* Aviso visual permanente si elige domiciliación con SEPA
                          incompleto. El validar() de handleGlobalSave bloquea
                          el guardado real con el mismo mensaje. */}
                      {client.forma_pago === "domiciliacion" && (() => {
                        const falta = mensajeFaltaSepaParaInscripcion(
                          client, { isMenor, validarIBAN, validarDNI });
                        if (!falta) return null;
                        return (
                          <div style={{
                            padding: "8px 12px",
                            backgroundColor: "#fef3c7",
                            border: "1px solid #fde68a",
                            borderRadius: "6px",
                            color: "#92400e",
                            fontSize: "12px",
                          }}>
                            ⚠️ {falta}
                          </div>
                        );
                      })()}
                    </div>
                    <span className="cn-data-label">Turnos fijos</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "6px" }}>
                      {localTurnos.slice(0, maxHours).map((turno, index) => (
                        <div key={index} style={{ padding: "6px 8px", backgroundColor: "#fff", border: "1px solid var(--cn-border)", borderRadius: "6px" }}>
                          <span style={{ fontSize: "10px", fontWeight: 600, color: "var(--cn-primary)", display: "block", marginBottom: "4px" }}>Clase {index + 1}</span>
                          <select value={turno.dia} onChange={(e) => updateTurno(index, "dia", e.target.value)}
                            className="cn-input-editable" style={{ width: "100%", padding: "4px 8px", fontSize: "12px", marginBottom: "4px" }}>
                            <option value="">Dia...</option>
                            {DIAS.map((dia) => (<option key={dia} value={dia}>{dia}</option>))}
                          </select>
                          <CustomColorSelect
                            value={turno.hora} onChange={(value) => updateTurno(index, "hora", value)}
                            disabled={!turno.dia} placeholder="Hora..."
                            options={(() => {
                              if (!turno.dia || !horariosDisponibles[turno.dia]) return [];
                              const baseOptions = horariosDisponibles[turno.dia].map((hora) => {
                                const isFull = isSlotFull(turno.dia, hora);
                                const slotInfo = getSlotInfo(turno.dia, hora);
                                const intensidadInfo = getHorarioIntensidad(turno.dia, hora);
                                const iName = intensidadInfo?.nombre || "Estandar";
                                const intensidadLabel = iName !== "Estandar" && iName !== "undefined" ? ` [${iName}]` : "";
                                return { value: hora, label: `${hora}${intensidadLabel} ${slotInfo ? `(${slotInfo})` : ""}`, color: intensidadInfo?.color || "#9ca3af", disabled: isFull };
                              });
                              if (turno.hora && !baseOptions.some((opt) => opt.value === turno.hora)) {
                                const slotInfo = getSlotInfo(turno.dia, turno.hora);
                                baseOptions.push({ value: turno.hora, label: `${turno.hora} (Actual) ${slotInfo ? `(${slotInfo})` : ""}`, color: "#9ca3af", disabled: false });
                                baseOptions.sort((a, b) => { const [ha, ma] = a.value.split(":").map(Number); const [hb, mb] = b.value.split(":").map(Number); return ha * 60 + ma - (hb * 60 + mb); });
                              }
                              return baseOptions;
                            })()}
                            className="text-sm border-gray-300"
                          />
                        </div>
                      ))}
                    </div>

                    {/* ── Aviso: inscrito sin suscripción real ── */}
                    {!suscripcionActiva && client.inscrito_clases_fijas && (
                      <div style={{ marginTop: "12px", padding: "8px 10px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", fontSize: "12px", color: "#991b1b" }}>
                        <strong>Sin suscripción real.</strong> Este cliente figura como inscrito, pero no tiene suscripción activa creada.
                        No se pueden gestionar incidencias ni baja del servicio hasta corregir su suscripción.
                        <span style={{ display: "block", marginTop: "4px", color: "#78716c", fontSize: "11px" }}>
                          Guarda la ficha con sede y cuota asignadas para crear la suscripción automáticamente.
                        </span>
                      </div>
                    )}

                    {/* ── Indicador de cobros del ejercicio ── */}
                    {suscripcionActiva && (
                      suscripcionActiva.estado === "activa" ||
                      suscripcionActiva.estado === "pausada_incidencia" ||
                      suscripcionActiva.estado === "is_programada"
                    ) && cobrosIndicador && (
                      <div style={{ marginTop: "12px", padding: "8px 10px", backgroundColor: cobrosIndicador.encontrados >= cobrosIndicador.esperados ? "#f0fdf4" : "#fffbeb", border: `1px solid ${cobrosIndicador.encontrados >= cobrosIndicador.esperados ? "#bbf7d0" : "#fde68a"}`, borderRadius: "6px", fontSize: "12px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span>
                            <strong>Cobros {cobrosIndicador.anyo}:</strong>{" "}
                            <span style={{ fontFamily: "monospace" }}>{cobrosIndicador.encontrados}/{cobrosIndicador.esperados}</span>
                            {" "}generados{" "}
                            {cobrosIndicador.encontrados >= cobrosIndicador.esperados
                              ? <span style={{ color: "#16a34a" }}>✓</span>
                              : <span style={{ color: "#d97706" }}>⚠</span>}
                          </span>
                          {cobrosIndicador.encontrados < cobrosIndicador.esperados && (
                            <button
                              onClick={handleGenerarCobrosRestantes}
                              disabled={generandoCobros}
                              style={{ fontSize: "11px", padding: "2px 8px", backgroundColor: "#d97706", color: "#fff", border: "none", borderRadius: "4px", cursor: generandoCobros ? "not-allowed" : "pointer", opacity: generandoCobros ? 0.6 : 1 }}
                            >
                              {generandoCobros ? "Generando..." : "Generar restantes"}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Alerta post-cierre IS · turnos sin reasignar (PR-IS-cierre-no-bloqueante · Ann 2026-05-22) ──
                        Aparece cuando el cliente vuelve de IS sin turnos asignados:
                          · sus.estado === 'activa' (IS ya cerrada)
                          · NO hay incidencia_activa abierta
                          · El último historial tiene sin_turno_reasignado=true
                          · users.turno_fijo sigue vacío
                        Es informativa, NO bloquea ninguna acción. Sirve para que
                        recepción asigne turnos manualmente. Muestra los turnos
                        anteriores guardados en turno_anterior_snapshot. */}
                    {suscripcionActiva && suscripcionActiva.estado === "activa" && !suscripcionActiva.incidencia_activa && (() => {
                      const hist = suscripcionActiva.historial_incidencias || [];
                      if (hist.length === 0) return null;
                      const ultima = hist[hist.length - 1];
                      const sinTurno = ultima?.sin_turno_reasignado === true;
                      const turnosClienteActual = client.turno_fijo || [];
                      if (!sinTurno || turnosClienteActual.length > 0) return null;
                      const turnosPrevios = (ultima.turno_anterior_snapshot && ultima.turno_anterior_snapshot.length > 0)
                        ? ultima.turno_anterior_snapshot
                        : (ultima.turno_liberado || []);
                      const fmtD = (s) => { if (!s) return ""; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).substring(0,10); };
                      return (
                        <div style={{ marginTop: "12px", padding: "10px 12px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "12px", color: "#92400e" }}>
                          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                            ⚠ Hay que asignar nuevos turnos a este cliente.
                          </div>
                          <div style={{ color: "#78716c", fontSize: "11px" }}>
                            La IS de {fmtD(ultima.fecha_inicio)} → {fmtD(ultima.fecha_fin_real || ultima.fecha_fin)} se cerró sin reasignar turnos.
                            {turnosPrevios.length > 0 && (
                              <span style={{ display: "block", marginTop: "3px" }}>
                                Antes de la IS estaba apuntado a: <strong>{turnosPrevios.map(t => `${t.dia} ${t.hora}`).join(", ")}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ── Incidencia: activar (suscripción activa sin IS) ── */}
                    {suscripcionActiva && suscripcionActiva.estado === "activa" && !suscripcionActiva.incidencia_activa && !hasUnsavedChanges && (
                      <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid var(--cn-border)" }}>
                        {!incidenciaUI ? (
                          <button
                            // PR-UX-modal-IS-calendario (Ann 2026-05-22) · Reset del
                            // estado del modal al abrir · garantiza que la fecha
                            // inicio precargada es HOY (calendario nativo abre en
                            // mes actual) y que motivo/comentario/checkbox empiezan
                            // limpios en cada nueva IS. Coherente con el handler de
                            // "Finalizar incidencia" que ya hace su propio reset.
                            onClick={() => {
                              const hoy = new Date().toISOString().split("T")[0];
                              setIncFechaInicio(hoy);
                              setIncFechaFin("");
                              setIncMotivo("");
                              setIncComentario("");
                              setIncCheckboxConfirmado(false);
                              setIncidenciaUI(true);
                            }}
                            style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: "#fff", color: "#d97706", border: "1px solid #fde68a", borderRadius: "5px", cursor: "pointer" }}
                            onMouseEnter={(e) => { e.target.style.backgroundColor = "#fffbeb"; }}
                            onMouseLeave={(e) => { e.target.style.backgroundColor = "#fff"; }}
                          >
                            Activar incidencia
                          </button>
                        ) : (
                          // PR-IS-junio (Ann 2026-05-22) · modal Activar IS 100% autopista
                          //   - onChange={(e)=>e.stopPropagation()} en el contenedor evita que
                          //     el wrapper global `client-new-root` (línea ~2477) marque
                          //     hasUnsavedChanges=true y cause que el bloque entero desaparezca
                          //     al teclear en cualquier input (bug del modal con 01/06/2026).
                          //   - Campos: tipo / fecha_inicio / fecha_fin / motivo / comentario.
                          //   - Resumen previo + checkbox obligatorio antes de activar.
                          <div
                            onChange={(e) => e.stopPropagation()}
                            style={{ padding: "10px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "12px" }}
                          >
                            <div style={{ fontWeight: 600, color: "#92400e", marginBottom: "8px" }}>Activar incidencia de salud</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <div>
                                <span style={{ color: "#78716c", fontSize: "11px" }}>Tipo <span style={{ color: "#dc2626" }}>*</span></span>
                                <select
                                  value={incTipoId}
                                  onChange={(e) => setIncTipoId(e.target.value)}
                                  className="cn-input-editable"
                                  style={{ fontSize: "12px", padding: "3px 6px" }}
                                >
                                  {tiposIncidencia.map(t => (
                                    <option key={t.id || t._id} value={t.id || t._id}>{t.nombre}</option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ display: "flex", gap: "8px" }}>
                                <div style={{ flex: 1 }}>
                                  <span style={{ color: "#78716c", fontSize: "11px" }}>Fecha inicio <span style={{ color: "#dc2626" }}>*</span></span>
                                  <input type="date" className="cn-input-editable" style={{ fontSize: "12px", padding: "3px 6px" }}
                                    value={incFechaInicio} onChange={(e) => setIncFechaInicio(e.target.value)} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <span style={{ color: "#78716c", fontSize: "11px" }}>Fecha fin prevista (opcional)</span>
                                  <input type="date" className="cn-input-editable" style={{ fontSize: "12px", padding: "3px 6px" }}
                                    value={incFechaFin} onChange={(e) => setIncFechaFin(e.target.value)} />
                                </div>
                              </div>
                              <div>
                                <span style={{ color: "#78716c", fontSize: "11px" }}>Motivo <span style={{ color: "#dc2626" }}>*</span></span>
                                <select
                                  value={incMotivo}
                                  onChange={(e) => setIncMotivo(e.target.value)}
                                  className="cn-input-editable"
                                  style={{ fontSize: "12px", padding: "3px 6px" }}
                                >
                                  <option value="">Selecciona el motivo…</option>
                                  {INC_MOTIVOS.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <span style={{ color: "#78716c", fontSize: "11px" }}>
                                  Comentario{incMotivo === "otro" ? <span style={{ color: "#dc2626" }}> *</span> : <span style={{ color: "#a8a29e" }}> (opcional)</span>}
                                </span>
                                <textarea
                                  value={incComentario}
                                  onChange={(e) => setIncComentario(e.target.value)}
                                  rows={2}
                                  placeholder={incMotivo === "otro" ? "Obligatorio: explica brevemente el motivo" : "Información adicional (opcional)"}
                                  className="cn-input-editable"
                                  style={{ fontSize: "12px", padding: "3px 6px", width: "100%", resize: "vertical" }}
                                />
                              </div>

                              {/* Resumen previo · Ann decisión 4 opción B */}
                              {incTipoId && incFechaInicio && incMotivo && (() => {
                                const tipoSel = tiposIncidencia.find(t => (t.id || t._id) === incTipoId);
                                const motivoSel = INC_MOTIVOS.find(m => m.value === incMotivo);
                                const precio = tipoSel?.precio_mensual ?? "—";
                                const fmt = (s) => { if (!s) return "—"; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).substring(0,10); };
                                return (
                                  <div style={{ marginTop: "8px", padding: "8px 10px", backgroundColor: "#fff7ed", border: "1px dashed #fdba74", borderRadius: "5px" }}>
                                    <div style={{ fontWeight: 600, color: "#9a3412", marginBottom: "4px", fontSize: "11px" }}>
                                      Vas a activar una IS con estos datos:
                                    </div>
                                    <ul style={{ margin: 0, padding: "0 0 0 18px", color: "#78716c", fontSize: "11px", lineHeight: 1.5 }}>
                                      <li><strong>Cliente:</strong> {client.nombre} {client.primer_apellido} {client.segundo_apellido || ""}</li>
                                      <li><strong>Tipo:</strong> {tipoSel?.nombre || "—"}</li>
                                      <li><strong>Fecha inicio:</strong> {fmt(incFechaInicio)}</li>
                                      <li><strong>Fecha fin prevista:</strong> {incFechaFin ? fmt(incFechaFin) : <em>indefinida (revisar_pendiente)</em>}</li>
                                      <li><strong>Motivo:</strong> {motivoSel?.label || incMotivo}</li>
                                      <li><strong>Comentario:</strong> {(incComentario || "").trim() || <em>—</em>}</li>
                                      <li><strong>Precio mensual IS:</strong> {typeof precio === "number" ? `${precio.toFixed(2).replace(".",",")}€` : precio}</li>
                                    </ul>
                                    <div style={{ marginTop: "4px", fontSize: "11px", color: "#9a3412", fontStyle: "italic" }}>
                                      ⚠ Se recalculará la rejilla de cobros mientras dure la IS.
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Checkbox confirmación · Ann decisión 4 opción B */}
                              <label style={{ display: "flex", alignItems: "flex-start", gap: "6px", marginTop: "6px", fontSize: "11px", color: "#374151", cursor: "pointer" }}>
                                <input
                                  type="checkbox"
                                  checked={incCheckboxConfirmado}
                                  onChange={(e) => setIncCheckboxConfirmado(e.target.checked)}
                                  style={{ marginTop: "2px" }}
                                />
                                <span>
                                  Entiendo que se pausará la cuota normal y se aplicará la cuota IS hasta finalizar la incidencia.
                                </span>
                              </label>

                              <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                                <button
                                  onClick={handleActivarIncidencia}
                                  disabled={incidenciaLoading || !incCheckboxConfirmado || !incMotivo || (incMotivo === "otro" && !(incComentario || "").trim())}
                                  style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: "#d97706", color: "#fff", border: "none", borderRadius: "5px", cursor: incidenciaLoading ? "not-allowed" : "pointer", opacity: (incidenciaLoading || !incCheckboxConfirmado || !incMotivo || (incMotivo === "otro" && !(incComentario || "").trim())) ? 0.5 : 1 }}
                                >
                                  {incidenciaLoading ? "Activando..." : "Activar incidencia"}
                                </button>
                                <button
                                  onClick={() => { setIncidenciaUI(false); setIncMotivo(""); setIncComentario(""); setIncCheckboxConfirmado(false); }}
                                  style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: "#fff", color: "#78716c", border: "1px solid #d6d3d1", borderRadius: "5px", cursor: "pointer" }}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── IS programada (futura · no activa todavía) ──
                        PR-IS-STATE-1B (Ann 2026-05-24) · cierre simétrico con
                        PR-IS-STATE-1. Lee el campo computado por el backend
                        `suscripcionActiva.is_programada_visible` que cubre
                        TODAS las fuentes: incidencia_activa (autopista nueva)
                        y historial_incidencias (caso AAnn · IS futura atrapada
                        en historial por flujo legacy).
                        Bloque NEUTRO (no naranja) con datos registrados.
                        Textos autorizados: título "IS programada" · badge "Programada".
                        Si fuente=historial, se añade marca "(en historial)" para
                        distinguir caso legacy del flujo canónico, y NO se muestra
                        botón Editar fechas IS (editar-fechas opera solo sobre
                        incidencia_activa, no sobre historial). */}
                    {suscripcionActiva?.is_programada_visible && (
                      <div style={{ marginTop: "12px", padding: "10px", backgroundColor: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <span style={{ fontWeight: 600, color: "#374151" }}>
                            IS programada
                            {suscripcionActiva.is_programada_visible.fuente === "historial" && (
                              <span style={{ marginLeft: "6px", fontSize: "10px", color: "#6b7280", fontWeight: 400 }}>(en historial)</span>
                            )}
                          </span>
                          <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: "4px", backgroundColor: "#e5e7eb", color: "#374151" }}>
                            Programada
                          </span>
                        </div>
                        {(() => {
                          const ia = suscripcionActiva.is_programada_visible.data || {};
                          const fmtD = (s) => { if (!s) return "—"; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).substring(0,10); };
                          const fiTxt = fmtD(ia.fecha_inicio);
                          const ffTxt = ia.fecha_fin_real ? fmtD(ia.fecha_fin_real)
                                       : ia.fecha_fin_prevista ? fmtD(ia.fecha_fin_prevista)
                                       : ia.fecha_fin ? fmtD(ia.fecha_fin)
                                       : "indefinida";
                          const snap = (ia.turno_anterior_snapshot && ia.turno_anterior_snapshot.length > 0)
                                       ? ia.turno_anterior_snapshot : [];
                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", color: "#374151", fontSize: "12px" }}>
                              <div><span style={{ fontWeight: 600 }}>Inicio IS:</span> {fiTxt}</div>
                              <div><span style={{ fontWeight: 600 }}>Final IS:</span> {ffTxt}</div>
                              <div><span style={{ fontWeight: 600 }}>Precio IS:</span> {ia.precio_incidencia != null ? `${Number(ia.precio_incidencia).toFixed(2).replace(".",",")}€/mes` : "—"}</div>
                              {ia.motivo && <div><span style={{ fontWeight: 600 }}>Motivo:</span> {ia.motivo}</div>}
                              {ia.comentario && <div><span style={{ fontWeight: 600 }}>Comentario:</span> {ia.comentario}</div>}
                              {snap.length > 0 && (
                                <div><span style={{ fontWeight: 600 }}>Turnos registrados:</span> {snap.map(t => `${t.dia} ${t.hora}`).join(", ")}</div>
                              )}
                            </div>
                          );
                        })()}
                        {/* PR-IS-EDIT-3 · botón Editar fechas IS (IS programada).
                            PR-IS-STATE-1B: solo cuando fuente=incidencia_activa
                            (editar-fechas no opera sobre historial). */}
                        {suscripcionActiva.is_programada_visible.fuente === "incidencia_activa" && (
                          <div style={{ marginTop: "8px" }}>
                            <button
                              type="button"
                              data-testid="btn-editar-fechas-is-programada"
                              onClick={() => setShowEditarFechasIS(true)}
                              style={{ fontSize: "12px", padding: "5px 10px", backgroundColor: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: "5px", cursor: "pointer" }}
                            >
                              Editar fechas IS
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* PR-IS-AUTOPISTA-A1 (Ann 2026-06-01) · Banner IS_ACTIVABLE ──
                        Visible cuando la IS ya debería estar activa (fi <= hoy) pero
                        sus.estado sigue siendo 'is_programada' por falta de activación.
                        Alerta en familia visual IS (--is-*) + botón "Activar IS" que llama
                        al endpoint oficial POST /suscripciones/{id}/activar-is-programada.
                        El endpoint ya existe (suscripciones.py:1581, PR-IS-PRG-3). */}
                    {suscripcionActiva?.estado_is_operativo === "IS_ACTIVABLE" && (
                      <div
                        style={{ marginTop: "12px", padding: "12px", backgroundColor: "var(--is-bg-soft)", border: "2px solid var(--is-primary)", borderRadius: "6px", fontSize: "12px" }}
                        data-testid="banner-is-activable"
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                          <span style={{ fontSize: "14px" }}>⚠️</span>
                          <span style={{ fontWeight: 700, color: "var(--is-text)" }}>IS activable desde {suscripcionActiva.incidencia_activa?.fecha_inicio || "—"}</span>
                          <span style={{ marginLeft: "auto", fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: "4px", backgroundColor: "var(--is-bg-medium)", color: "var(--is-primary)" }}>
                            Pendiente de activación
                          </span>
                        </div>
                        <div style={{ color: "#78716c", marginBottom: "10px" }}>
                          La fecha de inicio ya llegó pero la IS no se ha activado aún. Pulsa el botón para activarla oficialmente. Esta acción requiere autorización y deja trazabilidad.
                        </div>
                        <button
                          type="button"
                          data-testid="btn-activar-is-programada"
                          disabled={incidenciaLoading}
                          onClick={async () => {
                            if (!suscripcionActiva?.id) return;
                            const ia = suscripcionActiva.incidencia_activa || {};
                            const fi = ia.fecha_inicio || "—";
                            const ok = confirm(
                              `¿Activar la IS de este cliente?\n\nFecha inicio: ${fi}\n\nEsta acción pausará la suscripción, liberará el turno fijo y dejará audit_log.`
                            );
                            if (!ok) return;
                            try {
                              setIncidenciaLoading(true);
                              await axios.post(`${API}/suscripciones/${suscripcionActiva.id}/activar-is-programada`);
                              toast.success("IS activada correctamente");
                              if (typeof refreshClientData === "function") refreshClientData();
                            } catch (err) {
                              toast.error(extractApiErrorMessage(err, "Error al activar IS programada"));
                            } finally {
                              setIncidenciaLoading(false);
                            }
                          }}
                          style={{ fontSize: "12px", padding: "6px 14px", backgroundColor: incidenciaLoading ? "#d1d5db" : "var(--is-primary)", color: "#fff", border: "none", borderRadius: "5px", cursor: incidenciaLoading ? "not-allowed" : "pointer", fontWeight: 600 }}
                        >
                          {incidenciaLoading ? "Activando..." : "Activar IS"}
                        </button>
                      </div>
                    )}

                    {/* ── Estado pausado por incidencia ── */}
                    {suscripcionActiva?.estado === "pausada_incidencia" && suscripcionActiva?.incidencia_activa && (
                      <div style={{ marginTop: "12px", padding: "10px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "12px" }}>
                        {/* PR-IS-AUTOPISTA-A1 (Ann 2026-06-01) · Banner IS_VENCIDA ──
                            Alerta IS (familia --is-alert, NO rojo externo) cuando la fecha de
                            fin efectiva (cierre_programado.fecha_fin o, si no, fecha_fin_prevista)
                            < hoy sin fecha_fin_real. No cierra IS automáticamente; guía al admin
                            a "Finalizar IS" / "Aplicar cierre programado". */}
                        {suscripcionActiva.estado_is_operativo === "IS_VENCIDA" && (() => {
                          const _ia = suscripcionActiva.incidencia_activa || {};
                          const _finEfectiva = (_ia.cierre_programado && _ia.cierre_programado.fecha_fin) || _ia.fecha_fin_prevista || "—";
                          return (
                          <div
                            style={{ marginBottom: "10px", padding: "8px 10px", backgroundColor: "var(--is-bg-soft)", border: "1px solid var(--is-alert)", borderRadius: "5px", display: "flex", alignItems: "flex-start", gap: "6px" }}
                            data-testid="banner-is-vencida"
                          >
                            <span style={{ fontSize: "14px", flexShrink: 0 }}>🔴</span>
                            <div>
                              <span style={{ fontWeight: 700, color: "var(--is-alert)" }}>IS vencida · pendiente de cierre formal</span>
                              <div style={{ color: "var(--is-alert)", fontSize: "11px", marginTop: "2px" }}>
                                La fecha de fin ({_finEfectiva}) ya pasó sin cierre registrado. El cliente sigue apareciendo en IS. Usa el botón "Finalizar IS" o "Aplicar cierre programado" para cerrarla correctamente.
                              </div>
                            </div>
                          </div>
                          );
                        })()}
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <span style={{ fontWeight: 600, color: "#92400e" }}>
                            Pausado — {suscripcionActiva.incidencia_activa.tipo_incidencia_nombre || "Incidencia"}
                          </span>
                          <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: "4px", backgroundColor: "#fef3c7", color: "#d97706" }}>
                            {suscripcionActiva.estado_is_operativo === "IS_VENCIDA" ? "IS vencida" : "En pausa"}
                          </span>
                        </div>
                        {(() => {
                          const ia = suscripcionActiva.incidencia_activa;
                          const fmtD = (s) => { if (!s) return "—"; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).substring(0,10); };
                          const diasIS = (() => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ia.fecha_inicio || "")); if (!m) return 0; const fi = new Date(+m[1], +m[2]-1, +m[3]); return Math.max(0, Math.floor((new Date() - fi) / 86400000)); })();
                          const mesesIS = Math.floor(diasIS / 30);
                          const superaMax = diasIS > 90;
                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", color: "#78716c", marginBottom: "8px", fontSize: "12px" }}>
                              <div><span style={{ color: "#92400e", fontWeight: 600 }}>Desde:</span> {fmtD(ia.fecha_inicio)}{ia.fecha_fin ? ` — Hasta: ${fmtD(ia.fecha_fin)}` : " — Sin fecha fin definida"}</div>
                              {ia.fecha_notificacion && <div><span style={{ color: "#92400e", fontWeight: 600 }}>Notificada el:</span> {fmtD(ia.fecha_notificacion)}</div>}
                              <div><span style={{ color: "#92400e", fontWeight: 600 }}>Cuota IS:</span> {ia.precio_incidencia != null ? `${Number(ia.precio_incidencia).toFixed(2).replace(".",",")}€/mes` : "—"}</div>
                              <div>
                                <span style={{ color: "#92400e", fontWeight: 600 }}>Duración:</span> {diasIS} días ({mesesIS} mes{mesesIS !== 1 ? "es" : ""})
                                {superaMax && <span style={{ marginLeft: "6px", fontSize: "10px", fontWeight: 700, padding: "1px 6px", borderRadius: "4px", backgroundColor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>Máximo alcanzado — regularizar</span>}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Aviso de horario liberado · PR-IS-cierre-no-bloqueante (Ann 2026-05-22)
                            Lectura preferida: turno_anterior_snapshot (escrito por autopista
                            PR-L11/L13). Fallback legacy: turno_liberado (sólo si algún camino
                            antiguo lo pobló). El aviso es informativo, NO obliga a asignar
                            turno antes del cierre (el cierre nunca bloquea). */}
                        {suscripcionActiva.incidencia_activa.mantiene_reserva_horario === false && (() => {
                          const ia = suscripcionActiva.incidencia_activa;
                          const turnosPrevios = (ia.turno_anterior_snapshot && ia.turno_anterior_snapshot.length > 0)
                            ? ia.turno_anterior_snapshot
                            : (ia.turno_liberado || []);
                          return (
                            <div style={{ padding: "6px 8px", marginBottom: "8px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "4px", fontSize: "11px", color: "#92400e" }}>
                              <strong>Horario liberado.</strong> El cliente no ocupa hueco durante la IS. Al finalizar la incidencia se recomienda asignar nuevos turnos (no es obligatorio para cerrar).
                              {turnosPrevios.length > 0 && (
                                <span style={{ display: "block", marginTop: "3px", color: "#78716c" }}>
                                  Antes de la IS estaba apuntado a: {turnosPrevios.map(t => `${t.dia} ${t.hora}`).join(", ")}
                                </span>
                              )}
                            </div>
                          );
                        })()}

                        {/* AUTOPISTA_MARKER · PR-IS-junio (Ann 2026-05-22 · decisión 6 opción A)
                            "Editar IS" ELIMINADO: el botón y el formulario inline llamaban
                            PUT /api/suscripciones/{id}/incidencia-activa, bloqueado HTTP 410
                            GONE desde PR-L13 (Ann 2026-05-20). Mantenerlo era código muerto
                            que producía un toast de error genérico al admin.
                            Reemplazado por nota operativa. Para modificar una IS:
                            cerrar la actual + abrir nueva por la autopista canónica. */}
                        {!cierreIncUI && (
                          <div style={{ marginBottom: "6px", padding: "6px 8px", fontSize: "11px", color: "#78716c", fontStyle: "italic", backgroundColor: "#f5f5f4", border: "1px solid #e7e5e4", borderRadius: "5px" }}>
                            Para modificar una IS, finaliza la incidencia y abre una nueva si procede.
                          </div>
                        )}

                        {/* AUTOPISTA_MARKER · PR-IS-CIERRE-PRG-1 (Ann 2026-05-24)
                            Banner cierre programado · visible cuando
                            sus.incidencia_activa.cierre_programado está poblado.
                            Botón "Aplicar cierre programado" deshabilitado hasta
                            la fecha programada · texto "Disponible a partir de
                            DD/MM/AAAA" cuando no se puede aún. */}
                        {!cierreIncUI && (() => {
                          const cp = suscripcionActiva?.incidencia_activa?.cierre_programado;
                          if (!cp) return null;
                          const hoyYMD = new Date().toISOString().slice(0, 10);
                          const ya_disponible = cp.fecha_fin <= hoyYMD;
                          return (
                            <div
                              data-testid="banner-cierre-programado-is"
                              style={{ marginBottom: "8px", padding: "10px 12px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "5px", fontSize: "12px", color: "#1e40af" }}
                            >
                              <div style={{ marginBottom: "6px" }}>
                                <strong>IS con cierre programado</strong> para el {cp.fecha_fin}
                                {cp.motivo_cierre && cp.motivo_cierre !== "manual" ? ` · motivo: ${cp.motivo_cierre}` : ""}
                                {cp.programado_at ? ` · registrado ${String(cp.programado_at).slice(0, 10)}` : ""}
                              </div>
                              {cp.comentario && (
                                <div style={{ marginBottom: "6px", color: "#475569", fontStyle: "italic" }}>
                                  Comentario: {cp.comentario}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                <button
                                  type="button"
                                  data-testid="btn-aplicar-cierre-programado"
                                  onClick={handleAplicarCierreProgramado}
                                  disabled={!ya_disponible || incidenciaLoading}
                                  title={ya_disponible ? "Aplicar el cierre programado ahora" : `Disponible a partir de ${cp.fecha_fin}`}
                                  style={{
                                    fontSize: "12px", padding: "5px 12px",
                                    backgroundColor: ya_disponible ? "#1d4ed8" : "#cbd5e1",
                                    color: "#fff", border: "none", borderRadius: "5px",
                                    cursor: (ya_disponible && !incidenciaLoading) ? "pointer" : "not-allowed",
                                    opacity: (ya_disponible && !incidenciaLoading) ? 1 : 0.7,
                                  }}
                                >
                                  {ya_disponible
                                    ? (incidenciaLoading ? "Aplicando..." : "Aplicar cierre programado")
                                    : `Disponible a partir de ${cp.fecha_fin}`}
                                </button>
                                <button
                                  type="button"
                                  data-testid="btn-cancelar-cierre-programado"
                                  onClick={handleCancelarCierreProgramado}
                                  disabled={ya_disponible || incidenciaLoading}
                                  title={ya_disponible ? "Solo se puede cancelar antes de la fecha programada" : "Cancelar cierre programado"}
                                  style={{
                                    fontSize: "12px", padding: "5px 12px",
                                    backgroundColor: "#fff",
                                    color: ya_disponible ? "#94a3b8" : "#b91c1c",
                                    border: `1px solid ${ya_disponible ? "#e2e8f0" : "#fecaca"}`,
                                    borderRadius: "5px",
                                    cursor: (!ya_disponible && !incidenciaLoading) ? "pointer" : "not-allowed",
                                    opacity: (!ya_disponible && !incidenciaLoading) ? 1 : 0.6,
                                  }}
                                >
                                  Cancelar cierre programado
                                </button>
                              </div>
                            </div>
                          );
                        })()}

                        {!cierreIncUI ? (
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            <button
                              onClick={() => {
                                setCierreIncUI(true);
                                setCierreIncFechaFin(new Date().toISOString().split("T")[0]);
                                // PR-IS-modal-cierre · resetear motivo+comentario al abrir
                                setCierreMotivo("recuperacion");
                                setCierreComentario("");
                              }}
                              style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: "#16a34a", color: "#fff", border: "none", borderRadius: "5px", cursor: "pointer" }}
                            >
                              Finalizar incidencia de salud
                            </button>
                            {/* PR-IS-EDIT-3 · botón Editar fechas IS (IS activa) */}
                            <button
                              type="button"
                              data-testid="btn-editar-fechas-is-activa"
                              onClick={() => setShowEditarFechasIS(true)}
                              style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: "#fff", color: "#92400e", border: "1px solid #fde68a", borderRadius: "5px", cursor: "pointer" }}
                            >
                              Editar fechas IS
                            </button>
                          </div>
                        ) : (
                          // PR-IS-junio · stopPropagation defensivo para que cambiar la
                          // fecha de finalización no marque hasUnsavedChanges=true en el
                          // wrapper global `client-new-root`. Coherente con el modal Activar.
                          // PR-IS-modal-cierre (Ann 2026-05-22) · ampliado con select de
                          // motivo de cierre y textarea de comentario opcional. Antes el
                          // frontend enviaba `motivo_cierre: "recuperacion"` hardcoded y
                          // ningún comentario.
                          <div
                            onChange={(e) => e.stopPropagation()}
                            style={{ marginTop: "6px", padding: "10px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "5px" }}
                          >
                            <div style={{ marginBottom: "8px" }}>
                              <span style={{ color: "#78716c", fontSize: "11px" }}>Fecha de finalización <span style={{ color: "#dc2626" }}>*</span></span>
                              <input type="date" className="cn-input-editable"
                                style={{ fontSize: "12px", padding: "3px 6px", marginTop: "2px" }}
                                value={cierreIncFechaFin}
                                onChange={(e) => setCierreIncFechaFin(e.target.value)} />
                            </div>
                            <div style={{ marginBottom: "8px" }}>
                              <span style={{ color: "#78716c", fontSize: "11px" }}>Motivo de cierre / reincorporación <span style={{ color: "#dc2626" }}>*</span></span>
                              <select
                                value={cierreMotivo}
                                onChange={(e) => setCierreMotivo(e.target.value)}
                                className="cn-input-editable"
                                style={{ fontSize: "12px", padding: "3px 6px", marginTop: "2px", width: "100%" }}
                              >
                                {MOTIVOS_CIERRE.map(m => (
                                  <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                              </select>
                            </div>
                            <div style={{ marginBottom: "8px" }}>
                              <span style={{ color: "#78716c", fontSize: "11px" }}>Comentario <span style={{ color: "#a8a29e" }}>(opcional)</span></span>
                              <textarea
                                value={cierreComentario}
                                onChange={(e) => setCierreComentario(e.target.value)}
                                rows={2}
                                placeholder="Observaciones sobre el cierre"
                                className="cn-input-editable"
                                style={{ fontSize: "12px", padding: "3px 6px", marginTop: "2px", width: "100%", resize: "vertical" }}
                              />
                            </div>
                            {/* PR-IS-CIERRE-PRG-1 · si fecha_fin > hoy el botón
                                cambia a "Programar cierre" y handleCerrarIncidencia
                                bifurca al endpoint /programar-cierre-incidencia. */}
                            {(() => {
                              const hoyYMD2 = new Date().toISOString().slice(0, 10);
                              const esFuturaUI = cierreIncFechaFin && cierreIncFechaFin > hoyYMD2;
                              const labelOk = esFuturaUI
                                ? (incidenciaLoading ? "Programando..." : "Programar cierre")
                                : (incidenciaLoading ? "Finalizando..." : "Confirmar finalización");
                              const colorOk = esFuturaUI ? "#1d4ed8" : "#16a34a";
                              return (
                                <>
                                  {esFuturaUI && (
                                    <div style={{ marginBottom: "8px", padding: "6px 8px", fontSize: "11px", color: "#1e40af", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "4px" }}>
                                      Esta fecha es futura. Se registrará como <strong>cierre programado</strong>. El cliente seguirá en IS hasta el {cierreIncFechaFin}, momento en que podrás aplicar el cierre desde la ficha.
                                    </div>
                                  )}
                                  <div style={{ display: "flex", gap: "8px" }}>
                                    <button
                                      onClick={handleCerrarIncidencia}
                                      disabled={incidenciaLoading}
                                      data-testid={esFuturaUI ? "btn-programar-cierre-is" : "btn-confirmar-cierre-is"}
                                      style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: incidenciaLoading ? "#9ca3af" : colorOk, color: "#fff", border: "none", borderRadius: "5px", cursor: incidenciaLoading ? "not-allowed" : "pointer", opacity: incidenciaLoading ? 0.6 : 1 }}
                                    >
                                      {labelOk}
                                    </button>
                                    <button
                                      onClick={() => { setCierreIncUI(false); setCierreMotivo("recuperacion"); setCierreComentario(""); }}
                                      style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: "#fff", color: "#78716c", border: "1px solid #d6d3d1", borderRadius: "5px", cursor: "pointer" }}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Botón de baja (solo si inscrito y guardado) o aviso de guardar ──
                        Norma Ann 2026-05-23 · is_programada se considera
                        suscripción real · debe permitir dar de baja (caso
                        cliente cancela antes de que arranque la IS). */}
                    {suscripcionActiva && (
                      suscripcionActiva.estado === "activa" ||
                      suscripcionActiva.estado === "pausada_incidencia" ||
                      suscripcionActiva.estado === "is_programada"
                    ) && (
                      <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid var(--cn-border)" }}>
                        {hasUnsavedChanges ? (
                          <span style={{ fontSize: "12px", color: "#d97706", fontStyle: "italic" }}>
                            Guarda los cambios para completar la inscripción
                          </span>
                        ) : (
                          <button
                            onClick={handleBajaClasesFijas}
                            disabled={bajaClasesLoading}
                            style={{ fontSize: "12px", padding: "5px 12px", backgroundColor: "#fff", color: "#dc2626", border: "1px solid #fecaca", borderRadius: "5px", cursor: bajaClasesLoading ? "not-allowed" : "pointer", opacity: bajaClasesLoading ? 0.6 : 1, transition: "all 0.15s" }}
                            onMouseEnter={(e) => { e.target.style.backgroundColor = "#fef2f2"; }}
                            onMouseLeave={(e) => { e.target.style.backgroundColor = "#fff"; }}
                          >
                            {bajaClasesLoading ? "Procesando baja..." : "Dar de baja de Clases Fijas"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* FISIOTERAPIA + CONTINUO 8 (columna derecha servicios) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="cn-panel" style={{ margin: 0 }}>
                <div className="cn-panel-header" style={{ cursor: "pointer", backgroundColor: client.es_paciente ? "#f0fdf4" : "" }}
                  onClick={() => {
                    if (!client.es_paciente) {
                      handleAltaClinica();
                    } else {
                      toast.error("La baja o pausa de Fisioterapia se gestiona desde su propio proceso. No se cambia desde Guardar.", { duration: 9000 });
                    }
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="checkbox" checked={client.es_paciente || false} readOnly disabled={altaClinicaProcessing}
                        style={{ width: "16px", height: "16px", accentColor: "var(--cn-primary)", cursor: "pointer" }} />
                      <h3 className="cn-panel-title" style={{ margin: 0 }}>Fisioterapia</h3>
                    </div>
                    {client.es_paciente && client.estado_clinica && (
                      <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: "4px",
                        color: client.estado_clinica === "activo" ? "#16a34a" : client.estado_clinica === "pausado" ? "#d97706" : "#dc2626",
                        backgroundColor: client.estado_clinica === "activo" ? "#dcfce7" : client.estado_clinica === "pausado" ? "#fef3c7" : "#fee2e2" }}>
                        {client.estado_clinica === "activo" ? "Activo" : client.estado_clinica === "pausado" ? "Pausado" : "Baja"}
                      </span>
                    )}
                  </div>
                  <p className="cn-panel-subtitle" style={{ margin: 0 }}>
                    {altaClinicaProcessing ? "Activando..." : "Paciente de clinica"}
                  </p>
                </div>
                {altaClinicaError && (
                  <div style={{
                    margin: "10px 12px 0",
                    padding: "10px 12px",
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    color: "#991b1b",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Error al activar Fisioterapia</div>
                    <div>{altaClinicaError.msg}</div>
                    <div style={{ marginTop: 4, color: "#7f1d1d" }}>{altaClinicaError.ts}</div>
                  </div>
                )}
                {client.es_paciente && (
                  <div className="cn-panel-content" style={{ fontSize: "13px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div className="cn-data-item">
                        <span className="cn-data-label">Estado</span>
                        <select className="cn-input-editable" value={client.estado_clinica || "activo"} disabled
                          onChange={() => {}}
                          style={{ fontWeight: 600, color: (client.estado_clinica || "activo") === "activo" ? "#16a34a" : (client.estado_clinica || "activo") === "pausado" ? "#d97706" : "#dc2626" }}>
                          <option value="activo">Activo</option>
                          <option value="pausado">Pausado</option>
                          <option value="baja">Baja del servicio</option>
                        </select>
                      </div>
                      <div className="cn-data-item">
                        <span className="cn-data-label">Fecha alta servicio</span>
                        <input type="date" className="cn-input-editable" value={client.fecha_alta_clinica ? client.fecha_alta_clinica.split("T")[0] : ""} disabled
                          onChange={() => {}} />
                      </div>
                      <div className="cn-data-item">
                        <span className="cn-data-label">Sede</span>
                        <select className="cn-input-editable" value={client.sede_clinica || ""} disabled
                          onChange={() => {}}>
                          <option value="">Seleccione...</option>
                          {SEDES.map((s) => (<option key={s} value={s}>{s}</option>))}
                        </select>
                      </div>

                      {/* PR-FASE-A-3 · acciones administrativas conscientes
                          (NO usar checkbox silencioso · toda acción pasa por
                          endpoint oficial: baja-clinica / reactivar-clinica) */}
                      {client.estado_clinica === "activo" && client.activo_clinica === true && client.sede_clinica === "CONSULTAS" && (
                        <div className="cn-data-item" style={{ marginTop: 6 }}>
                          <button
                            type="button"
                            onClick={() => setBajaClinicaModalOpen(true)}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #dc2626",
                              borderRadius: 6,
                              background: "#fff",
                              color: "#dc2626",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: 13,
                            }}
                          >
                            Dar de baja de fisio
                          </button>
                        </div>
                      )}

                      {client.estado_clinica === "baja" && (
                        <div className="cn-data-item" style={{ marginTop: 6 }}>
                          {(client.fecha_baja_clinica || client.motivo_baja_clinica) && (
                            <div style={{ fontSize: 12, color: "#7f1d1d", marginBottom: 6 }}>
                              {client.fecha_baja_clinica && (
                                <>Última baja: {String(client.fecha_baja_clinica).slice(0, 10)}</>
                              )}
                              {client.motivo_baja_clinica && (
                                <> · Motivo: {client.motivo_baja_clinica}</>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => setReactivarClinicaModalOpen(true)}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #16a34a",
                              borderRadius: 6,
                              background: "#fff",
                              color: "#16a34a",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: 13,
                            }}
                          >
                            Reactivar fisio
                          </button>
                        </div>
                      )}

                      {client.estado_clinica === "pausado" && (
                        <div className="cn-data-item" style={{ marginTop: 6 }}>
                          <div style={{
                            padding: "8px 10px",
                            background: "#fef3c7",
                            border: "1px solid #fcd34d",
                            borderRadius: 6,
                            color: "#92400e",
                            fontSize: 12,
                          }}>
                            Cliente en pausa clínica / incidencia de salud activa.
                            Resolver IS antes de cambiar el estado clínico.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Modales PR-FASE-A-3 · render condicional (no monta cuando cerrado) */}
                {bajaClinicaModalOpen && (
                  <BajaClinicaModal
                    isOpen={bajaClinicaModalOpen}
                    onClose={() => setBajaClinicaModalOpen(false)}
                    clientId={id}
                    clientName={`${client.nombre || ""} ${client.primer_apellido || ""}`.trim()}
                    onSuccess={refreshClientData}
                  />
                )}
                {reactivarClinicaModalOpen && (
                  <ReactivarClinicaModal
                    isOpen={reactivarClinicaModalOpen}
                    onClose={() => setReactivarClinicaModalOpen(false)}
                    clientId={id}
                    clientName={`${client.nombre || ""} ${client.primer_apellido || ""}`.trim()}
                    fechaBajaPrevia={client.fecha_baja_clinica}
                    motivoBajaPrevio={client.motivo_baja_clinica}
                    observacionesBajaPrevia={client.observaciones_baja_clinica}
                    onSuccess={refreshClientData}
                  />
                )}
              </div>

              {/* CONTINUO 8 */}
              <div className="cn-panel" style={{ margin: 0 }}>
                <div className="cn-panel-header" style={{ cursor: "pointer", backgroundColor: (client.servicio || "").toLowerCase() === "continuo_8" ? "#f0fdf4" : "" }}
                  onClick={() => {
                    const isActive = (client.servicio || "").toLowerCase() === "continuo_8";
                    setClient({ ...client, servicio: isActive ? "fijo" : "continuo_8" });
                    setHasUnsavedChanges(true);
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input type="checkbox" checked={(client.servicio || "").toLowerCase() === "continuo_8"} readOnly
                      style={{ width: "16px", height: "16px", accentColor: "var(--cn-primary)", cursor: "pointer" }} />
                    <h3 className="cn-panel-title" style={{ margin: 0 }}>Continuo 8</h3>
                  </div>
                  <p className="cn-panel-subtitle" style={{ margin: 0 }}>Paquete de 8 clases en 30 dias</p>
                </div>
              </div>

              {/* TAQUILLA */}
              <TaquillaInscripcionCard clienteId={id} sede={client.sede} />
              </div>

              {/* FIRMAS / DOCUMENTOS LEGALES (columna derecha) */}
              <LegalDocsPanel clientId={id} clientData={client} suscripcionActiva={suscripcionActiva} />

            </div>
          </div>
        )}

        {/* F) TAB "PLATAFORMAS" */}
        {activeTab === "accesos" && (
          <div className="cn-tab-content">
            {/* Credenciales + espacio derecho */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px", marginBottom: "16px" }}>
              <div className="cn-panel" style={{ margin: 0 }}>
                <div className="cn-panel-header">
                  <h3 className="cn-panel-title">Credenciales Generales</h3>
                  <p className="cn-panel-subtitle">Clave maestra de acceso a las plataformas</p>
                </div>
                <div className="cn-panel-content">
                  <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div className="cn-data-item" style={{ flex: 0, minWidth: "200px", position: "relative" }}>
                      <span className="cn-data-label">Nueva contrasena</span>
                      <div style={{ position: "relative" }}>
                        <input
                          type={client._showPassword ? "text" : "password"}
                          className="cn-input-editable"
                          placeholder="Escribir nueva contrasena..."
                          value={client._newPassword || ""}
                          onChange={(e) => setClient({ ...client, _newPassword: e.target.value })}
                          style={{ fontFamily: "monospace", fontSize: "13px", paddingRight: "32px" }}
                        />
                        <button type="button" onClick={() => setClient({ ...client, _showPassword: !client._showPassword })}
                          style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: "2px", color: "#6b7280" }}
                          title={client._showPassword ? "Ocultar" : "Mostrar"}>
                          {client._showPassword ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <button className="cn-btn cn-btn--sm"
                      style={{ fontSize: "12px", padding: "6px 12px", marginBottom: "2px", backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" }}
                      onClick={() => {
                        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
                        let pass = "";
                        for (let i = 0; i < 10; i++) pass += chars.charAt(Math.floor(Math.random() * chars.length));
                        setClient({ ...client, _newPassword: pass });
                        toast.success("Contrasena generada");
                      }}>
                      Generar contrasena nueva
                    </button>
                    <button className="cn-btn cn-btn--outline cn-btn--sm"
                      style={{ fontSize: "12px", padding: "6px 12px", marginBottom: "2px" }}
                      onClick={async () => {
                        if (!client._newPassword || client._newPassword.length < 6) {
                          toast.error("La contrasena debe tener al menos 6 caracteres");
                          return;
                        }
                        try {
                          await axios.post(`${API}/admin/clients/${id}/cambiar-password`, { nueva_password: client._newPassword });
                          toast.success("Contrasena actualizada");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Error al cambiar contrasena");
                        }
                      }}>
                      Guardar contrasena
                    </button>
                    <button className="cn-btn cn-btn--sm"
                      style={{ fontSize: "12px", padding: "6px 12px", marginBottom: "2px", backgroundColor: "#16a34a", color: "#fff", border: "none" }}
                      onClick={async () => {
                        if (!client._newPassword) { toast.error("Genera o escribe una contrasena primero"); return; }
                        if (!client.email) { toast.error("El cliente no tiene email"); return; }
                        try {
                          await axios.post(`${API}/admin/clients/${id}/cambiar-password`, { nueva_password: client._newPassword });
                          await axios.post(`${API}/email/enviar-credenciales`, { cliente_id: id, plataforma: "gestor_cambios", password: client._newPassword });
                          toast.success("Credenciales guardadas y enviadas a " + client.email);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Error al enviar credenciales");
                        }
                      }}>
                      Enviar credenciales
                    </button>
                  </div>
                </div>
              </div>
              <div>{/* Espacio reservado */}</div>
            </div>

            {/* Plataformas en 4 columnas */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px" }}>
              {/* Gestor de Cambios */}
              <div className="cn-panel" style={{ margin: 0, opacity: client.plataforma_gestor_activa !== false ? 1 : 0.6 }}>
                <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <h3 className="cn-panel-title" style={{ margin: 0 }}>Gestor de Cambios</h3>
                    <p className="cn-panel-subtitle" style={{ margin: 0 }}>App de recuperacion de clases</p>
                  </div>
                  <label style={{ position: "relative", display: "inline-block", width: "36px", height: "20px", cursor: "pointer" }}>
                    <input type="checkbox" checked={client.plataforma_gestor_activa !== false}
                      onChange={(e) => { setClient({ ...client, plataforma_gestor_activa: e.target.checked }); setHasUnsavedChanges(true); }}
                      style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{
                      position: "absolute", top: 0, left: 0, right: 0, bottom: 0, borderRadius: "10px", transition: "0.3s",
                      backgroundColor: client.plataforma_gestor_activa !== false ? "#16a34a" : "#d1d5db",
                    }}>
                      <span style={{
                        position: "absolute", height: "16px", width: "16px", left: client.plataforma_gestor_activa !== false ? "18px" : "2px",
                        bottom: "2px", backgroundColor: "#fff", borderRadius: "50%", transition: "0.3s",
                      }} />
                    </span>
                  </label>
                </div>
                <div className="cn-panel-content" style={{ fontSize: "13px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span className="cn-data-label" style={{ margin: 0 }}>Email</span>
                    <span style={{ fontWeight: 500 }}>{client.email || "-"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="cn-data-label" style={{ margin: 0 }}>Contrasena</span>
                    <span>General</span>
                  </div>
                  <button className="cn-btn cn-btn--outline cn-btn--sm" style={{ fontSize: "11px", padding: "4px 10px", marginTop: "10px", width: "100%" }}
                    onClick={async () => {
                      if (!client._newPassword) { toast.error("Escribe la contrasena en Credenciales arriba"); return; }
                      try {
                        await axios.post(`${API}/email/enviar-credenciales`, { cliente_id: id, plataforma: "gestor_cambios", password: client._newPassword });
                        toast.success("Credenciales Gestor enviadas");
                      } catch (err) { toast.error(err.response?.data?.detail || "Error al enviar"); }
                    }}>
                    Enviar credenciales
                  </button>
                </div>
              </div>

              {/* Canal Videos */}
              <div className="cn-panel" style={{ margin: 0, opacity: client.plataforma_canal_activa !== false ? 1 : 0.6 }}>
                <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <h3 className="cn-panel-title" style={{ margin: 0 }}>Canal Videos</h3>
                    <p className="cn-panel-subtitle" style={{ margin: 0 }}>Plataforma de contenidos</p>
                  </div>
                  <label style={{ position: "relative", display: "inline-block", width: "36px", height: "20px", cursor: "pointer" }}>
                    <input type="checkbox" checked={client.plataforma_canal_activa !== false}
                      onChange={(e) => { setClient({ ...client, plataforma_canal_activa: e.target.checked }); setHasUnsavedChanges(true); }}
                      style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{
                      position: "absolute", top: 0, left: 0, right: 0, bottom: 0, borderRadius: "10px", transition: "0.3s",
                      backgroundColor: client.plataforma_canal_activa !== false ? "#16a34a" : "#d1d5db",
                    }}>
                      <span style={{
                        position: "absolute", height: "16px", width: "16px", left: client.plataforma_canal_activa !== false ? "18px" : "2px",
                        bottom: "2px", backgroundColor: "#fff", borderRadius: "50%", transition: "0.3s",
                      }} />
                    </span>
                  </label>
                </div>
                <div className="cn-panel-content" style={{ fontSize: "13px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span className="cn-data-label" style={{ margin: 0 }}>Email</span>
                    <span style={{ fontWeight: 500 }}>{client.email || "-"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="cn-data-label" style={{ margin: 0 }}>Contrasena</span>
                    <span>General</span>
                  </div>
                  <button className="cn-btn cn-btn--outline cn-btn--sm" style={{ fontSize: "11px", padding: "4px 10px", marginTop: "10px", width: "100%" }}
                    onClick={async () => {
                      if (!client._newPassword) { toast.error("Escribe la contrasena en Credenciales arriba"); return; }
                      try {
                        await axios.post(`${API}/email/enviar-credenciales`, { cliente_id: id, plataforma: "canal_videos", password: client._newPassword });
                        toast.success("Credenciales Canal enviadas");
                      } catch (err) { toast.error(err.response?.data?.detail || "Error al enviar"); }
                    }}>
                    Enviar credenciales
                  </button>
                </div>
              </div>

              {/* Plataforma Paciente */}
              <div className="cn-panel" style={{ margin: 0, opacity: client.plataforma_paciente_activa ? 1 : 0.6 }}>
                <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <h3 className="cn-panel-title" style={{ margin: 0 }}>Plataforma Paciente</h3>
                    <p className="cn-panel-subtitle" style={{ margin: 0 }}>Acceso clinico para pacientes</p>
                  </div>
                  <label style={{ position: "relative", display: "inline-block", width: "36px", height: "20px", cursor: "pointer" }}>
                    <input type="checkbox" checked={client.plataforma_paciente_activa || false}
                      onChange={(e) => {
                        if (e.target.checked && !client.es_paciente) {
                          toast.error("La plataforma de paciente solo se puede activar si el cliente tiene activada la inscripcion en Fisioterapia (pestana Inscripcion)", { duration: 8000 });
                          return;
                        }
                        setClient({ ...client, plataforma_paciente_activa: e.target.checked });
                        setHasUnsavedChanges(true);
                      }}
                      style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{
                      position: "absolute", top: 0, left: 0, right: 0, bottom: 0, borderRadius: "10px", transition: "0.3s",
                      backgroundColor: client.plataforma_paciente_activa ? "#16a34a" : "#d1d5db",
                    }}>
                      <span style={{
                        position: "absolute", height: "16px", width: "16px", left: client.plataforma_paciente_activa ? "18px" : "2px",
                        bottom: "2px", backgroundColor: "#fff", borderRadius: "50%", transition: "0.3s",
                      }} />
                    </span>
                  </label>
                </div>
                <div className="cn-panel-content" style={{ fontSize: "13px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span className="cn-data-label" style={{ margin: 0 }}>Email</span>
                    <span style={{ fontWeight: 500 }}>{client.es_paciente ? (client.email || "-") : "-"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="cn-data-label" style={{ margin: 0 }}>Requisito</span>
                    <span style={{ fontSize: "12px" }}>{client.es_paciente ? "Paciente activo" : "Activar en Inscripcion"}</span>
                  </div>
                  {client.es_paciente && (
                    <button className="cn-btn cn-btn--outline cn-btn--sm" style={{ fontSize: "11px", padding: "4px 10px", marginTop: "10px", width: "100%" }}
                      onClick={async () => {
                        if (!client._newPassword) { toast.error("Escribe la contrasena en Credenciales arriba"); return; }
                        try {
                          await axios.post(`${API}/email/enviar-credenciales`, { cliente_id: id, plataforma: "plataforma_paciente", password: client._newPassword });
                          toast.success("Credenciales Paciente enviadas");
                        } catch (err) { toast.error(err.response?.data?.detail || "Error al enviar"); }
                      }}>
                      Enviar credenciales
                    </button>
                  )}
                </div>
              </div>

              {/* Paciente Monitorizado */}
              <div className="cn-panel" style={{ margin: 0, opacity: client.es_monitorizado ? 1 : 0.6 }}>
                <div className="cn-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 className="cn-panel-title" style={{ margin: 0, color: '#7c3aed', fontSize: '13px' }}>Monitorizado</h3>
                    <p className="cn-panel-subtitle" style={{ margin: 0 }}>Seguimiento sin tratamiento</p>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: '36px', height: '20px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={client.es_monitorizado || false}
                      onChange={(e) => { setClient({ ...client, es_monitorizado: e.target.checked }); setHasUnsavedChanges(true); }}
                      style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '10px', transition: '0.3s',
                      backgroundColor: client.es_monitorizado ? '#7c3aed' : '#d1d5db',
                    }}>
                      <span style={{
                        position: 'absolute', height: '16px', width: '16px', left: client.es_monitorizado ? '18px' : '2px',
                        bottom: '2px', backgroundColor: '#fff', borderRadius: '50%', transition: '0.3s',
                      }} />
                    </span>
                  </label>
                </div>
                {client.es_monitorizado && (
                  <div className="cn-panel-content" style={{ fontSize: '11px', color: '#7c3aed' }}>
                    Aparece en listado violeta del panel clínico.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* G) TAB "DOCUMENTACIÓN Y FIRMAS" */}
        {/* H) TAB "BONOS/CITAS" */}
        {activeTab === "pagos_consumos" && (
          <div className="cn-tab-content">
            <div className="cn-twin-panels">
              {/* COLUMNA IZQUIERDA: BONOS */}
              <div
                className="cn-panel"
                style={{ flex: 1, marginBottom: "24px" }}
              >
                <div
                  className="cn-panel-header"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <h3 className="cn-panel-title">Bonos del Cliente</h3>
                    <p className="cn-panel-subtitle">
                      Paquetes adquiridos y sesiones restantes
                    </p>
                  </div>
                  <button
                    className="cn-btn cn-btn--sm cn-btn--outline"
                    onClick={() => window.open("/admin?tab=bonos", "_blank")}
                  >
                    Ir a Bonos
                  </button>
                </div>
                <div className="cn-panel-content">
                  <table className="cn-table">
                    <thead>
                      <tr>
                        <th>Bono</th>
                        <th>Sesiones</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {client.bonos && client.bonos.length > 0 ? (
                        client.bonos.map((bono) => (
                          <React.Fragment key={bono.id}>
                            <tr
                              onClick={() => handleExpandBono(bono.id)}
                              style={{
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                              }}
                              className={
                                expandedBonoId === bono.id
                                  ? "bg-slate-50"
                                  : "hover:bg-slate-50"
                              }
                            >
                              <td>
                                <div style={{ fontWeight: 500, color: "var(--cn-primary)" }}>
                                  {bono.tipo || "Bono"}
                                </div>
                                <div style={{ fontSize: "11px", color: "var(--cn-text-muted)", marginTop: "2px" }}>
                                  Compra: {bono.fecha_compra ? new Date(bono.fecha_compra).toLocaleDateString("es-ES") : "—"}
                                </div>
                              </td>
                              <td style={{ verticalAlign: "middle" }}>
                                <span style={{ fontWeight: 600 }}>
                                  {bono.sesiones_usadas || 0}
                                </span>{" "}
                                / {bono.sesiones_total || 0}
                              </td>
                              <td style={{ verticalAlign: "middle" }}>
                                <span
                                  className={`cn-badge ${bono.estado === "activo" ? "cn-badge--success" : ""}`}
                                  style={
                                    bono.estado !== "activo"
                                      ? {
                                          backgroundColor: "var(--cn-surface-alt)",
                                          color: "var(--cn-text-muted)",
                                        }
                                      : {}
                                  }
                                >
                                  {bono.estado}
                                </span>
                              </td>
                            </tr>
                            {/* SESIONES DESPLEGABLES DEL BONO */}
                            {expandedBonoId === bono.id && (
                              <tr style={{ backgroundColor: "#f8fafc" }}>
                                <td colSpan="3" style={{ padding: "16px" }}>
                                  <div
                                    style={{
                                      background: "white",
                                      borderRadius: "8px",
                                      border: "1px solid #e2e8f0",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <table className="cn-table" style={{ margin: 0 }}>
                                      <thead style={{ background: "#f1f5f9" }}>
                                        <tr>
                                          <th style={{ fontSize: "12px", padding: "8px 12px" }}>Ses.</th>
                                          <th style={{ fontSize: "12px", padding: "8px 12px" }}>Fecha/Hora</th>
                                          <th style={{ fontSize: "12px", padding: "8px 12px" }}>Terapeuta</th>
                                          <th style={{ fontSize: "12px", padding: "8px 12px" }}>Estado</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {bonoSesiones[bono.id] && bonoSesiones[bono.id].length > 0 ? (
                                          [...bonoSesiones[bono.id]]
                                            .sort((a, b) => {
                                              if (!!a.sin_numero !== !!b.sin_numero) return a.sin_numero ? 1 : -1;  // canceladas en plazo al final
                                              const fechaA = a.fecha ? new Date(`${a.fecha}T${a.hora_inicio || '00:00'}`).getTime() : Infinity;
                                              const fechaB = b.fecha ? new Date(`${b.fecha}T${b.hora_inicio || '00:00'}`).getTime() : Infinity;
                                              return fechaA - fechaB;
                                            })
                                            .map((sesion, idx) => (
                                            <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                              <td style={{ fontSize: "13px", fontWeight: 500, padding: "8px 12px" }}>
                                                {sesion.sin_numero ? '—' : (sesion.numero_visible ?? sesion.numero_sesion)}
                                              </td>
                                              <td style={{ fontSize: "13px", padding: "8px 12px" }}>
                                                {sesion.fecha
                                                  ? `${new Date(sesion.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })} ${sesion.hora_inicio || ""}`
                                                  : "—"}
                                              </td>
                                              <td style={{ fontSize: "13px", padding: "8px 12px" }}>
                                                {sesion.profesional_nombre || "—"}
                                              </td>
                                              <td style={{ padding: "8px 12px" }}>
                                                <span className="cn-badge" style={{ fontSize: "11px", padding: "2px 6px" }}>
                                                  {sesion.estado === "usada" ? "consumida" : sesion.estado?.replace(/_/g, " ")}
                                                </span>
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td colSpan="4" style={{ textAlign: "center", color: "var(--cn-text-muted)", fontSize: "13px", padding: "16px" }}>
                                              Cargando o sin citas vinculadas...
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" style={{ textAlign: "center", color: "var(--cn-text-muted)" }}>
                            No hay bonos registrados
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* COLUMNA DERECHA: CITAS INDEPENDIENTES */}
              <div className="cn-panel" style={{ flex: 1, marginBottom: "24px" }}>
                <div
                  className="cn-panel-header"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <h3 className="cn-panel-title">Citas de Agenda</h3>
                    <p className="cn-panel-subtitle">Histórico de atenciones</p>
                  </div>
                  <button
                    className="cn-btn cn-btn--sm cn-btn--outline"
                    onClick={() => window.open("/admin?tab=clinica", "_blank")}
                  >
                    Ir a Agenda
                  </button>
                </div>
                <div className="cn-panel-content">
                  <table className="cn-table">
                    <thead>
                      <tr>
                        <th>Fecha y Hora</th>
                        <th>Tratamiento</th>
                        <th>Terapeuta</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {client.ultimas_citas && client.ultimas_citas.length > 0 ? (
                        client.ultimas_citas.map((cita) => (
                          <tr key={cita.id}>
                            <td>
                              <div style={{ fontWeight: 500 }}>
                                {cita.fecha ? new Date(cita.fecha).toLocaleDateString("es-ES") : "—"}
                              </div>
                              <div style={{ fontSize: "12px", color: "var(--cn-text-muted)" }}>
                                {cita.hora_inicio || ""} - {cita.hora_fin || ""}
                              </div>
                            </td>
                            <td>{cita.tratamiento || "—"}</td>
                            <td>{cita.profesional || "—"}</td>
                            <td>
                              <span className="cn-badge" style={{ fontSize: "11px" }}>
                                {cita.estado}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" style={{ textAlign: "center", color: "var(--cn-text-muted)" }}>
                            No constan citas recientes
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* I) TAB "COMUNICACIÓN" */}
        {activeTab === "comunicacion" && (
          <div className="cn-tab-content">
            <ComunicacionPanel clientId={id} clientEmail={client?.email} clientName={`${client?.nombre || ''} ${client?.primer_apellido || ''}`} />
          </div>
        )}
        
        {/* J) TAB "OPERATIVA" — datos reales de taquilla vía GET /taquillas/cliente/{id} */}
        {activeTab === "operativa" && (
          <div className="cn-tab-content">
            <TaquillaPanel clienteId={id} />

            {/* D) BLOQUE "RESUMEN OPERATIVO" */}
            <div
              className="cn-panel"
              style={{
                borderTop: "3px solid var(--cn-success)",
                marginTop: "24px",
              }}
            >
              <div className="cn-panel-header">
                <h3 className="cn-panel-title">Estado Global de Operativa</h3>
              </div>
              <div
                className="cn-panel-content"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  padding: "16px",
                }}
              >
                <div
                  style={{
                    minWidth: "48px",
                    minHeight: "48px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(var(--cn-success-rgb), 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--cn-success)",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: "15px",
                      color: "var(--cn-text-main)",
                    }}
                  >
                    Todo correcto y revisado
                  </p>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "13px",
                      color: "var(--cn-text-muted)",
                    }}
                  >
                    No hay irregularidades operativas ni falta de marcadores
                    para este cliente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* N) TAB "CAMBIOS" */}
        {activeTab === "cambios" && (
          <div className="cn-tab-content" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/* BLOQUE SUPERIOR: Control de Faltas */}
            <div className="cn-panel">
              <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 className="cn-panel-title">Control de Faltas y Recuperaciones</h3>
                  <p className="cn-panel-subtitle">Registro de inasistencias, cancelaciones y reprogramaciones del cliente.</p>
                </div>
                <button
                  className="cn-btn cn-btn--outline"
                  onClick={() => navigate("/admin/cuadrante")}
                >
                  <CalendarClock size={16} style={{ marginRight: "8px" }} />
                  Ir a Agenda
                </button>
              </div>
              <div className="cn-panel-content">
                {(!client?.ultimas_citas || client.ultimas_citas.filter(c => ["cancelada", "cancelada_con_aviso", "cancelada_dentro_plazo", "cancelada_fuera_plazo", "reprogramada", "ausente", "falta"].includes(c.estado?.toLowerCase())).length === 0) ? (
                  <p style={{ textAlign: "center", color: "var(--cn-text-muted)", padding: "16px" }}>No hay registros de faltas o cancelaciones recientes.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="cn-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Servicio</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {client.ultimas_citas.filter(c => ["cancelada", "cancelada_con_aviso", "cancelada_dentro_plazo", "cancelada_fuera_plazo", "reprogramada", "ausente", "falta"].includes(c.estado?.toLowerCase())).map((falta, idx) => (
                          <tr key={idx}>
                            <td>{falta.fecha || falta.fecha_inicio ? new Date(falta.fecha || falta.fecha_inicio).toLocaleDateString() : "—"}</td>
                            <td>{falta.actividad || falta.tipo_servicio || "—"}</td>
                            <td>
                              <span className="cn-badge cn-badge--warning" style={{ textTransform: "capitalize" }}>
                                {falta.estado?.replace(/_/g, " ")}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* BLOQUE INFERIOR: Log Histórico */}
            <div className="cn-panel">
              <div className="cn-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 className="cn-panel-title">Log Histórico de Cambios (Eventos)</h3>
                  <p className="cn-panel-subtitle">Trazabilidad de cambios operativos y administrativos.</p>
                </div>
                <button
                  className="cn-btn cn-btn--primary"
                  onClick={() => setIsAnotarCambioOpen(true)}
                >
                  <Plus size={16} style={{ marginRight: "8px" }} />
                  Anotar Cambio Administrativo
                </button>
              </div>
              <div className="cn-panel-content">
                {!events || events.length === 0 ? (
                  <p style={{ textAlign: "center", color: "var(--cn-text-muted)", padding: "16px" }}>No hay eventos registrados.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="cn-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Autor</th>
                          <th>Tipo Evento</th>
                          <th>Descripción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((evt, idx) => (
                          <tr key={evt.id || idx}>
                            <td>{new Date(evt.fecha_evento).toLocaleDateString()}</td>
                            <td>{evt.usuario_id === "sistema" ? "Sistema" : (evt.usuario_id || "—")}</td>
                            <td>
                              <span className="cn-badge cn-badge--neutral" style={{ textTransform: "capitalize" }}>
                                {evt.tipo_evento?.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td>
                              <div><strong>{evt.descripcion}</strong></div>
                              {evt.comentario_interno && (
                                <div style={{ fontSize: "0.85em", color: "var(--cn-text-muted)", marginTop: "4px" }}>
                                  {evt.comentario_interno}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Modal Anotar Cambio */}
      {isAnotarCambioOpen && (
        <div className="cn-modal-overlay">
          <div className="cn-modal" style={{ maxWidth: "500px" }}>
            <div className="cn-modal-header">
              <h3 className="cn-modal-title">Anotar Cambio Administrativo</h3>
              <button
                className="cn-modal-close"
                onClick={() => setIsAnotarCambioOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="cn-modal-body" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div className="cn-form-group">
                <label className="cn-label">Fecha del Cambio</label>
                <input
                  type="date"
                  className="cn-input"
                  value={cambioFormData.fecha_evento}
                  onChange={(e) => setCambioFormData({ ...cambioFormData, fecha_evento: e.target.value })}
                />
              </div>
              <div className="cn-form-group">
                <label className="cn-label">Tipo de Cambio</label>
                <select
                  className="cn-input cn-select"
                  value={cambioFormData.tipo_evento}
                  onChange={(e) => setCambioFormData({ ...cambioFormData, tipo_evento: e.target.value })}
                >
                  <option value="observacion_interna">Observación Interna / General</option>
                  <option value="cambio_horario">Cambio de Horario</option>
                  <option value="cambio_cuota">Cambio de Cuota / Formato</option>
                </select>
              </div>
              <div className="cn-form-group">
                <label className="cn-label">Descripción</label>
                <input
                  type="text"
                  className="cn-input"
                  placeholder="Ej: Se ha cambiado el turno de los jueves..."
                  value={cambioFormData.descripcion}
                  onChange={(e) => setCambioFormData({ ...cambioFormData, descripcion: e.target.value })}
                />
              </div>
              <div className="cn-form-group">
                <label className="cn-label">Comentario Interno (Opcional)</label>
                <textarea
                  className="cn-input"
                  rows={3}
                  placeholder="Nota interna que no verá el cliente..."
                  value={cambioFormData.comentario_interno}
                  onChange={(e) => setCambioFormData({ ...cambioFormData, comentario_interno: e.target.value })}
                ></textarea>
              </div>
            </div>
            <div className="cn-modal-footer">
              <button
                className="cn-btn cn-btn--outline"
                onClick={() => setIsAnotarCambioOpen(false)}
                disabled={submittingCambio}
              >
                Cancelar
              </button>
              <button
                className="cn-btn cn-btn--primary"
                onClick={handleAnotarCambio}
                disabled={submittingCambio}
              >
                {submittingCambio ? "Guardando..." : "Guardar Cambio"}
              </button>
            </div>
          </div>
        </div>
      )}
      </main>

      {/* MODAL BAJA DE CLASES FIJAS — fecha + motivo (catálogo) + comentario ≥10, obligatorios */}
      {bajaModal && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => { if (!bajaClasesLoading) setBajaModal(null); }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "10px", padding: "22px", width: "440px", maxWidth: "92vw", boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}
          >
            <h3 style={{ margin: "0 0 6px", fontSize: "16px", fontWeight: 700, color: "#991b1b" }}>Dar de baja de Clases Fijas</h3>
            <p style={{ margin: "0 0 14px", fontSize: "12px", color: "#7f1d1d" }}>
              Se cerrará la suscripción con la fecha indicada y se anularán los cobros posteriores. Acción irreversible desde este botón.
            </p>

            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 600, marginBottom: "4px", color: "#374151" }}>Fecha de baja</label>
              <input
                type="date"
                value={bajaModal.fecha}
                onChange={e => setBajaModal(m => ({ ...m, fecha: e.target.value }))}
                style={{ width: "100%", padding: "7px 10px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "5px", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 600, marginBottom: "4px", color: "#374151" }}>Motivo de baja</label>
              <select
                value={bajaModal.motivo_id}
                onChange={e => setBajaModal(m => ({ ...m, motivo_id: e.target.value }))}
                style={{ width: "100%", padding: "7px 10px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "5px", backgroundColor: "#fff" }}
              >
                <option value="">— Selecciona un motivo —</option>
                {motivosBajaCatalogo.map(m => (
                  <option key={m.id} value={m.id}>{m.nombre}</option>
                ))}
              </select>
              {motivosBajaCatalogo.length === 0 && (
                <div style={{ fontSize: "11px", color: "#b45309", marginTop: "4px" }}>
                  Catálogo de motivos no disponible. Contacta con un super_admin.
                </div>
              )}
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 600, marginBottom: "4px", color: "#374151" }}>
                Comentario (mín. 10 caracteres)
              </label>
              <textarea
                value={bajaModal.comentario}
                onChange={e => setBajaModal(m => ({ ...m, comentario: e.target.value }))}
                rows={3}
                placeholder="Explica brevemente la razón de la baja para dejar trazabilidad…"
                style={{ width: "100%", padding: "7px 10px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "5px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
              />
              <div style={{ fontSize: "10px", color: (bajaModal.comentario || "").trim().length >= 10 ? "#16a34a" : "#92400e", marginTop: "3px", textAlign: "right" }}>
                {(bajaModal.comentario || "").trim().length}/10
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setBajaModal(null)}
                disabled={bajaClasesLoading}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "6px", background: "#fff", cursor: bajaClasesLoading ? "not-allowed" : "pointer" }}
              >
                Cancelar
              </button>
              {(() => {
                const comOk = (bajaModal.comentario || "").trim().length >= 10;
                const valido = !!bajaModal.fecha && !!bajaModal.motivo_id && comOk;
                const disabled = bajaClasesLoading || !valido;
                return (
                  <button
                    onClick={handleConfirmarBajaClases}
                    disabled={disabled}
                    title={!valido ? "Rellena fecha, motivo y comentario (mín 10)" : ""}
                    style={{ padding: "8px 16px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "6px",
                      background: disabled ? "#9ca3af" : "#dc2626", color: "#fff",
                      cursor: bajaClasesLoading ? "wait" : (valido ? "pointer" : "not-allowed") }}
                  >
                    {bajaClasesLoading ? "Procesando baja..." : "Confirmar baja"}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* MODAL PREVIEW DOCUMENTO — Arquitectura estándar: iframe PDF + Imprimir + Descargar + Email + Cerrar */}
      {/* CRITICO: Idéntico para admin, editor, gestor — norma inquebrantable */}
      {docPreview && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={closeDocPreview}>
          <div style={{ background: "#f3f4f6", borderRadius: "12px", width: "800px", maxWidth: "95vw", maxHeight: "95vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ flex: 1, padding: "24px", minHeight: "500px", display: "flex", flexDirection: "column" }}>
              {docPreviewLoading ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "12px", color: "#6b7280" }}>
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <p style={{ margin: 0, fontSize: "13px" }}>Generando vista previa...</p>
                </div>
              ) : docPreview.pdfUrl ? (
                <iframe
                  src={docPreview.pdfUrl}
                  style={{ flex: 1, width: "100%", minHeight: "600px", border: "none", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
                  title={`Vista Previa - ${docPreview.type}`}
                />
              ) : null}
            </div>
            {!docPreviewLoading && docPreview.pdfUrl && (
              <div style={{ padding: "12px 24px 20px", display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
                <button onClick={handleDocPrint}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 500, border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#374151" }}>
                  <Printer className="w-4 h-4" />
                  Imprimir {docPreview.type === "recibo" ? "Recibo" : "Factura"}
                </button>
                <button onClick={handleDocDownload}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 500, border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#374151" }}>
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </button>
                <button onClick={handleDocEmail} disabled={docSendingEmail}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "8px", background: "#7c3aed", color: "#fff", cursor: docSendingEmail ? "wait" : "pointer", opacity: docSendingEmail ? 0.7 : 1 }}>
                  {docSendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Enviar por Email
                </button>
                <button onClick={closeDocPreview}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 500, border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#6b7280" }}>
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* MODAL AÑADIR EVENTO */}
      {showEventoModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowEventoModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-800">Registrar queja / incidente</h3>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Tipo</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={eventoForm.tipo_evento}
                  onChange={e => setEventoForm({...eventoForm, tipo_evento: e.target.value})}>
                  <option value="queja">Queja</option>
                  <option value="incidente_negativo">Incidente negativo</option>
                  <option value="incidente_positivo">Incidente positivo</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Fecha</label>
                <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={eventoForm.fecha_evento}
                  onChange={e => setEventoForm({...eventoForm, fecha_evento: e.target.value})} />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  {eventoForm.tipo_evento === 'queja' ? 'Titulo de la queja' : 'Titulo'}
                </label>
                <input type="text" className="w-full border rounded px-3 py-2 text-sm" value={eventoForm.titulo_queja}
                  onChange={e => setEventoForm({...eventoForm, titulo_queja: e.target.value})}
                  placeholder={eventoForm.tipo_evento === 'queja' ? 'Titulo breve de la queja' : 'Titulo breve del incidente'} />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Descripcion</label>
                <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[80px]" value={eventoForm.descripcion}
                  onChange={e => setEventoForm({...eventoForm, descripcion: e.target.value})}
                  placeholder="Descripcion detallada..." />
              </div>

              {eventoForm.tipo_evento === 'queja' && (
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Comentarios para terapeutas</label>
                  <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[60px]" value={eventoForm.comentarios_terapeutas}
                    onChange={e => setEventoForm({...eventoForm, comentarios_terapeutas: e.target.value})}
                    placeholder="Comentarios internos para el equipo terapeutico..." />
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Comentario interno</label>
                <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[40px]" value={eventoForm.comentario_interno}
                  onChange={e => setEventoForm({...eventoForm, comentario_interno: e.target.value})}
                  placeholder="Nota interna (no visible para el paciente)" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button className="cn-btn cn-btn--outline cn-btn--sm" onClick={() => setShowEventoModal(false)}>Cancelar</button>
              <button className="cn-btn cn-btn--primary cn-btn--sm" onClick={async () => {
                try {
                  await axios.post(`${API}/cliente-eventos`, {
                    cliente_id: id,
                    tipo_evento: eventoForm.tipo_evento,
                    fecha_evento: eventoForm.fecha_evento,
                    descripcion: eventoForm.descripcion,
                    comentario_interno: eventoForm.comentario_interno,
                    titulo_queja: eventoForm.titulo_queja || null,
                    comentarios_terapeutas: eventoForm.comentarios_terapeutas || null,
                  });
                  toast.success(eventoForm.tipo_evento === 'queja' ? 'Queja registrada' : 'Incidente registrado');
                  setShowEventoModal(false);
                  // Recargar eventos
                  const evtRes = await axios.get(`${API}/cliente-eventos/${id}`);
                  setEvents(Array.isArray(evtRes.data) ? evtRes.data : []);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Error al guardar evento');
                }
              }}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: Dar de baja definitiva ═══ */}
      {showBajaDefinitivaModal && (() => {
        // Validaciones derivadas · mismos criterios que el botón disabled
        // Mensajes literales aprobados por Ann 2026-05-09
        const _comTrim = bajaDefForm.comentario.trim();
        const errFecha = !bajaDefForm.fecha_baja ? 'La fecha efectiva es obligatoria.' : '';
        const errMotivo = !bajaDefForm.motivo.trim() ? 'El motivo es obligatorio.' : '';
        const errComentario = !_comTrim
          ? 'El comentario es obligatorio.'
          : (_comTrim.length < 10
              ? `El comentario debe tener al menos 10 caracteres (actualmente ${_comTrim.length}).`
              : '');
        const motivosBloqueo = [errFecha, errMotivo, errComentario].filter(Boolean);
        const STYLE_INPUT_OK = { width: "100%", padding: "6px 8px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "4px" };
        const STYLE_INPUT_ERR = { width: "100%", padding: "6px 8px", fontSize: "13px", border: "1px solid #dc2626", borderRadius: "4px", background: "#fef2f2" };
        const STYLE_TEXTAREA_OK = { ...STYLE_INPUT_OK, resize: "vertical" };
        const STYLE_TEXTAREA_ERR = { ...STYLE_INPUT_ERR, resize: "vertical" };
        const errorTextStyle = { fontSize: "12px", color: "#dc2626", marginTop: "4px", fontWeight: 500 };
        return (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => !bajaDefProcessing && setShowBajaDefinitivaModal(false)}
        >
          <div
            style={{
              background: "white", borderRadius: "8px", padding: "24px",
              maxWidth: "560px", width: "90%", boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 12px 0", color: "#dc2626", fontSize: "18px" }}>
              🛑 Dar de baja definitiva
            </h2>
            <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#6b7280" }}>
              Esta acción da de baja TOTAL al cliente: cierra clases fijas, cierra clínica (si es paciente), desactiva las 3 plataformas (canal, gestor, paciente), da de baja el mandato SEPA, desactiva la FR recurrente y anula los cobros futuros no cobrados.<br/>
              <strong>Las facturas SIF ya emitidas NO se tocan</strong> (cadena fiscal).
            </p>

            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Fecha efectiva de baja *
              </label>
              <input
                type="date"
                value={bajaDefForm.fecha_baja}
                onChange={(e) => setBajaDefForm({ ...bajaDefForm, fecha_baja: e.target.value })}
                style={errFecha ? STYLE_INPUT_ERR : STYLE_INPUT_OK}
                disabled={bajaDefProcessing}
                aria-invalid={errFecha ? 'true' : 'false'}
              />
              {errFecha && (
                <div style={errorTextStyle}>⚠ {errFecha}</div>
              )}
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Motivo * (breve)
              </label>
              <input
                type="text"
                value={bajaDefForm.motivo}
                onChange={(e) => setBajaDefForm({ ...bajaDefForm, motivo: e.target.value })}
                placeholder="p.ej. cambio de domicilio, insatisfacción, salud..."
                style={errMotivo ? STYLE_INPUT_ERR : STYLE_INPUT_OK}
                disabled={bajaDefProcessing}
                maxLength={200}
                aria-invalid={errMotivo ? 'true' : 'false'}
              />
              {errMotivo && (
                <div style={errorTextStyle}>⚠ {errMotivo}</div>
              )}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Comentario * (mínimo 10 caracteres)
              </label>
              <textarea
                value={bajaDefForm.comentario}
                onChange={(e) => setBajaDefForm({ ...bajaDefForm, comentario: e.target.value })}
                placeholder="Detalle de la baja, contexto..."
                rows={3}
                style={errComentario ? STYLE_TEXTAREA_ERR : STYLE_TEXTAREA_OK}
                disabled={bajaDefProcessing}
                maxLength={2000}
                aria-invalid={errComentario ? 'true' : 'false'}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "4px", gap: "8px" }}>
                <div style={errComentario ? { ...errorTextStyle, marginTop: 0, flex: 1 } : { flex: 1 }}>
                  {errComentario ? `⚠ ${errComentario}` : ''}
                </div>
                <div style={{ fontSize: "11px", color: "#9ca3af", flexShrink: 0 }}>
                  {bajaDefForm.comentario.length} / 2000
                </div>
              </div>
            </div>

            {/* PR Ann 2026-05-20 · Clasificación baja CRM
                "normal" (default): devueltos NO se anulan (deuda recobrable)
                "incobrable" / "devolucion_no_recobrada": devueltos se anulan
                  con motivo específico, facturas marcadas pendiente rectificativa */}
            <div style={{ marginBottom: "16px", padding: "12px", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: "6px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "6px", color: "#92400e" }}>
                Clasificación de la baja *
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px" }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "6px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="clasificacion_baja"
                    value="normal"
                    checked={bajaDefForm.clasificacion_baja === "normal"}
                    onChange={() => setBajaDefForm({ ...bajaDefForm, clasificacion_baja: "normal" })}
                    disabled={bajaDefProcessing}
                  />
                  <span><strong>Normal</strong> · cobros devueltos NO se anulan (deuda recobrable si la hubiera)</span>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "6px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="clasificacion_baja"
                    value="incobrable"
                    checked={bajaDefForm.clasificacion_baja === "incobrable"}
                    onChange={() => setBajaDefForm({ ...bajaDefForm, clasificacion_baja: "incobrable" })}
                    disabled={bajaDefProcessing}
                  />
                  <span><strong>Incobrable</strong> · cliente no va a pagar · devueltos se regularizan con motivo específico</span>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "6px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="clasificacion_baja"
                    value="devolucion_no_recobrada"
                    checked={bajaDefForm.clasificacion_baja === "devolucion_no_recobrada"}
                    onChange={() => setBajaDefForm({ ...bajaDefForm, clasificacion_baja: "devolucion_no_recobrada" })}
                    disabled={bajaDefProcessing}
                  />
                  <span><strong>Devolución no recobrada</strong> · cliente devolvió y no recuperaremos · facturas asociadas quedan pendientes de rectificativa fiscal</span>
                </label>
              </div>
              {bajaDefForm.clasificacion_baja !== "normal" && (
                <div style={{ marginTop: "8px", fontSize: "11px", color: "#92400e", padding: "6px", background: "#fffbeb", borderRadius: "4px" }}>
                  ⚠ Esta baja se marcará como <strong>irregular</strong>. Las devoluciones bancarias (sepa_devoluciones/sepa_recibos) NO se tocan, solo se regulariza el cobro asociado.
                </div>
              )}
            </div>

            {motivosBloqueo.length > 0 && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: "4px", padding: "8px 12px", marginBottom: "12px",
                fontSize: "12px", color: "#991b1b"
              }}>
                <strong>Faltan campos para poder confirmar:</strong>
                <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                  {motivosBloqueo.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                type="button"
                onClick={() => setShowBajaDefinitivaModal(false)}
                disabled={bajaDefProcessing}
                style={{ padding: "8px 16px", fontSize: "13px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={
                  bajaDefProcessing ||
                  !bajaDefForm.fecha_baja ||
                  !bajaDefForm.motivo.trim() ||
                  bajaDefForm.comentario.trim().length < 10
                }
                title={
                  bajaDefProcessing
                    ? 'Procesando…'
                    : (motivosBloqueo.length
                        ? `No se puede confirmar:\n• ${motivosBloqueo.join('\n• ')}`
                        : 'Confirmar baja definitiva')
                }
                onClick={async () => {
                  // PR Ann 2026-05-20 · BAJA CRM TOTAL · autopista única
                  // Migrado del legacy /baja-definitiva al canónico /desactivar
                  // que ahora ejecuta la cascada completa (mandatos SEPA +
                  // plataformas + clínica + cambios cuadrante + cobros).
                  const clasif = bajaDefForm.clasificacion_baja || "normal";
                  const labelClasif = clasif === "incobrable" ? "INCOBRABLE"
                    : clasif === "devolucion_no_recobrada" ? "POR DEVOLUCIÓN NO RECOBRADA"
                    : "NORMAL";
                  if (!window.confirm(
                    `¿Confirmar BAJA TOTAL DEL CLIENTE con fecha ${bajaDefForm.fecha_baja}?\n\n` +
                    `Clasificación: ${labelClasif}\n` +
                    (clasif === "normal"
                      ? "Cobros devueltos NO se anulan (quedan como deuda recobrable).\n"
                      : "Cobros devueltos SE anulan con motivo 'devolución incobrable' y las facturas asociadas quedan pendientes de rectificativa fiscal.\n") +
                    `\nEsta acción es irreversible desde la UI.`
                  )) return;
                  setBajaDefProcessing(true);
                  try {
                    // Llamada al endpoint canónico /desactivar con motivo='baja'
                    // El legacy /baja-definitiva queda bloqueado con HTTP 410
                    const res = await axios.post(`${API}/admin/clients/${id}/desactivar`, {
                      motivo: "baja",
                      fecha_baja: bajaDefForm.fecha_baja,
                      comentario: `${bajaDefForm.motivo.trim()} · ${bajaDefForm.comentario.trim()}`,
                      clasificacion_baja: clasif,
                    });
                    const d = res.data || {};
                    const r = d.resultado_atomico || d;
                    toast.success(
                      `Baja TOTAL CRM aplicada (${labelClasif}). ` +
                      `Suscripción cerrada · cobros anulados: ${(r.cobros_anulados_ids || []).length} ` +
                      `(${(r.cobros_blindados_conservados_ids || []).length} blindados) · ` +
                      `FR desact: ${(r.fr_desactivadas_ids || []).length} · ` +
                      `Mandatos SEPA: ${(r.mandatos_sepa_desactivados_ids || []).length} · ` +
                      `Cambios cuadrante: ${(r.cambios_cancelados_ids || []).length}` +
                      (r.clinica_cerrada ? " · clínica cerrada" : "") +
                      (r.facturas_marcadas_rectificativa_ids && r.facturas_marcadas_rectificativa_ids.length
                        ? ` · ⚠️ ${r.facturas_marcadas_rectificativa_ids.length} factura(s) pendientes de rectificativa fiscal`
                        : "") +
                      (r.baja_irregular_marcada ? " · ⚠️ BAJA IRREGULAR" : ""),
                      { duration: 12000 }
                    );
                    setShowBajaDefinitivaModal(false);
                    window.location.reload();
                  } catch (err) {
                    const detail = err.response?.data?.detail || err.message || "Error al dar de baja";
                    toast.error(`Error: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`, { duration: 10000 });
                  } finally {
                    setBajaDefProcessing(false);
                  }
                }}
                style={{ padding: "8px 16px", fontSize: "13px", background: "#dc2626", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", opacity: bajaDefProcessing ? 0.5 : 1 }}
              >
                {bajaDefProcessing ? "Procesando..." : "Confirmar baja definitiva"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ═══ MODAL: Cambiar sede de clases fijas (autopista única · Ann 2026-05-20) ═══
           NO es una baja. Cierra etapa actual con motivo_cierre='cambio_sede' y
           abre etapa nueva en sede destino día 1 mes siguiente, EXENTA de CI.
           Endpoint atómico: POST /admin/clients/{id}/clases-fijas/cambiar-sede.
           Valida aforo destino ANTES de tocar nada · bloquea si IS activa.
           Modal completo (v2): permite seleccionar también cuota nueva y
           turnos nuevos en la misma operación. Si se dejan vacíos se
           heredan los actuales. */}
      {mostrarModalCambioSedeCF && (() => {
        const sedeActualCF = client?.sede || "";
        const sedesDisponibles = (SEDES || []).filter(s => s !== sedeActualCF);
        // Cuota actual (para mostrar info y comparar)
        const cuotaActualId = client?.cuota_id || "";
        const cuotaActualObj = cuotas.find(c => c.id === cuotaActualId);
        // Cuota destino · si "" se hereda
        const cuotaDestinoId = cambioSedeCFForm.nuevo_cuota_id || cuotaActualId;
        const cuotaDestinoObj = cuotas.find(c => c.id === cuotaDestinoId);
        const cuotaCambia = cambioSedeCFForm.nuevo_cuota_id !== "" &&
                            cambioSedeCFForm.nuevo_cuota_id !== cuotaActualId;
        const horasDestino = cuotaDestinoObj?.horas_semana || 0;
        // Fecha efecto automática (día 1 mes siguiente)
        const _hoy = new Date();
        const _fechaEfecto = new Date(_hoy.getFullYear(),
                                       _hoy.getMonth() + 1, 1);
        const fechaEfectoStr = _fechaEfecto.toISOString().split("T")[0];
        // Turnos del modal (si vacío y horas cambian, exigir nuevos;
        // si vacío y horas iguales, heredar al enviar)
        const turnosModal = cambioSedeCFForm.nuevo_turno_fijo || [];
        // Auto-ajustar longitud array al cambiar cuota
        const necesitaTurnosExplicitos = cuotaCambia &&
          (cuotaActualObj?.horas_semana !== horasDestino);
        // Validaciones UI
        const errSede = !cambioSedeCFForm.nueva_sede
          ? "Selecciona una sede destino distinta de la actual."
          : "";
        let errTurnos = "";
        if (necesitaTurnosExplicitos) {
          const completos = turnosModal.filter(t => t.dia && t.hora);
          if (completos.length !== horasDestino) {
            errTurnos = `La cuota requiere ${horasDestino} turno${horasDestino !== 1 ? "s" : ""} (día + hora). Faltan ${horasDestino - completos.length}.`;
          } else {
            // duplicados
            const pares = new Set(completos.map(t => `${t.dia}|${t.hora}`));
            if (pares.size !== completos.length) {
              errTurnos = "Hay turnos duplicados (mismo día + hora).";
            }
          }
        } else if (turnosModal.length > 0) {
          // si admin ha tocado turnos pero cuota no cambia, deben coincidir con horas actual
          const horasOk = cuotaActualObj?.horas_semana || turnosModal.length;
          const completos = turnosModal.filter(t => t.dia && t.hora);
          if (completos.length !== horasOk) {
            errTurnos = `Los turnos deben coincidir con las ${horasOk} horas de la cuota actual.`;
          } else {
            const pares = new Set(completos.map(t => `${t.dia}|${t.hora}`));
            if (pares.size !== completos.length) {
              errTurnos = "Hay turnos duplicados (mismo día + hora).";
            }
          }
        }
        const motivosBloqueoCSede = [errSede, errTurnos].filter(Boolean);
        const STYLE_INPUT_OK = { width: "100%", padding: "6px 8px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "4px" };
        const STYLE_TA_OK = { ...STYLE_INPUT_OK, resize: "vertical" };
        // Horarios estándar disponibles (helper local sin depender de aforo
        // del cuadrante · backend valida aforo real)
        const HORARIOS_BASE = ["07:00", "08:00", "09:00", "10:00", "11:00",
                                "12:00", "16:00", "17:00", "18:00", "19:00",
                                "20:00"];
        const updateTurnoModal = (idx, field, value) => {
          const nuevos = [...turnosModal];
          while (nuevos.length <= idx) nuevos.push({ dia: "", hora: "" });
          nuevos[idx] = { ...nuevos[idx], [field]: value };
          setCambioSedeCFForm({ ...cambioSedeCFForm, nuevo_turno_fijo: nuevos });
        };
        // Cuántas filas mostrar de turnos
        const numTurnosUI = necesitaTurnosExplicitos
          ? horasDestino
          : Math.max(turnosModal.filter(t => t.dia || t.hora).length, 0);
        return (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "auto", padding: "20px",
          }}
          onClick={() => !cambioSedeCFProcessing && setMostrarModalCambioSedeCF(false)}
        >
          <div
            style={{
              background: "white", borderRadius: "8px", padding: "24px",
              maxWidth: "620px", width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              maxHeight: "92vh", overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 8px 0", color: "#1d4ed8", fontSize: "18px" }}>
              📍 Cambiar sede de clases fijas
            </h2>

            {/* Cabecera cliente · OBLIGATORIA (Ann 2026-05-20 · post-incidente cross-cliente).
                Muestra prominentemente nombre + apellidos + id del cliente target.
                Recepción DEBE verificar visualmente que coincide con quien quiere modificar
                antes de pulsar "Confirmar". */}
            <div style={{
              margin: "0 0 16px 0", padding: "12px 14px",
              background: "#fef3c7", border: "2px solid #f59e0b", borderRadius: "6px",
            }}>
              <div style={{ fontSize: "11px", color: "#92400e", fontWeight: 700, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                ⚠️ Vas a modificar la ficha de:
              </div>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "#7c2d12", lineHeight: 1.2 }}>
                {[
                  client?.nombre,
                  client?.primer_apellido,
                  client?.segundo_apellido,
                ].filter(Boolean).join(" ") || "(sin nombre)"}
              </div>
              <div style={{ fontSize: "11px", color: "#92400e", marginTop: "4px", fontFamily: "ui-monospace, monospace" }}>
                id: {client?.id || "(sin id)"}
              </div>
              {client?.id && String(client.id) !== String(id) && (
                <div style={{ marginTop: "6px", padding: "6px 8px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "4px", fontSize: "11px", color: "#991b1b", fontWeight: 600 }}>
                  🛑 FICHA NO SINCRONIZADA · client.id ≠ URL.id · cierra y recarga la página
                </div>
              )}
            </div>

            <div style={{ margin: "0 0 16px 0", padding: "10px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "4px", fontSize: "12px", color: "#1e40af" }}>
              <strong>Esto NO es una baja.</strong> El cliente sigue activo en el CRM.
              Se cierra la etapa actual de clases fijas con motivo <em>"cambio de sede"</em>
              y se abre una nueva etapa en la sede destino el <strong>día 1 del mes siguiente</strong>.
              La cuota de inscripción queda <strong>exenta</strong>. NO se toca: estado del cliente,
              mandato SEPA, plataformas ni clínica.
            </div>

            {/* 1. Sede actual (info) + Sede destino */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                  Sede actual
                </label>
                <input type="text" value={sedeActualCF || "(sin sede)"} disabled
                  style={{ ...STYLE_INPUT_OK, background: "#f3f4f6", color: "#6b7280" }} />
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                  Sede destino *
                </label>
                <select
                  value={cambioSedeCFForm.nueva_sede}
                  onChange={(e) => setCambioSedeCFForm({ ...cambioSedeCFForm, nueva_sede: e.target.value })}
                  disabled={cambioSedeCFProcessing}
                  style={STYLE_INPUT_OK}>
                  <option value="">Seleccione...</option>
                  {sedesDisponibles.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
            </div>
            {errSede && (
              <div style={{ fontSize: "12px", color: "#dc2626", marginTop: "-6px", marginBottom: "8px", fontWeight: 500 }}>
                ⚠ {errSede}
              </div>
            )}

            {/* 2. Fecha efecto (automática · informativa, no editable) */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Fecha efecto (automática)
              </label>
              <input type="text" value={`${fechaEfectoStr} · día 1 del mes siguiente`}
                disabled
                style={{ ...STYLE_INPUT_OK, background: "#f3f4f6", color: "#6b7280" }} />
              <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>
                No editable · regla Ann · no mid-month, no retroactivo
              </div>
            </div>

            {/* 3. Cuota/membresía nueva */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Cuota / membresía
              </label>
              <select
                value={cambioSedeCFForm.nuevo_cuota_id}
                onChange={(e) => {
                  const newId = e.target.value;
                  const newCuota = cuotas.find(c => c.id === newId);
                  const newHoras = newCuota?.horas_semana || 0;
                  // Si cambia cuota y cambian horas, resetear turnos a array vacío
                  // del tamaño correcto (el admin elegirá día/hora de cada uno).
                  const horasActuales = cuotaActualObj?.horas_semana || 0;
                  let turnosNuevos = cambioSedeCFForm.nuevo_turno_fijo;
                  if (newId && newId !== cuotaActualId && newHoras !== horasActuales) {
                    turnosNuevos = Array.from({ length: newHoras },
                      () => ({ dia: "", hora: "" }));
                  } else if (newId === "" || newId === cuotaActualId) {
                    // Volver a heredar
                    turnosNuevos = [];
                  }
                  setCambioSedeCFForm({
                    ...cambioSedeCFForm,
                    nuevo_cuota_id: newId,
                    nuevo_turno_fijo: turnosNuevos,
                  });
                }}
                disabled={cambioSedeCFProcessing}
                style={STYLE_INPUT_OK}>
                <option value="">
                  {cuotaActualObj
                    ? `Heredar actual · ${cuotaActualObj.nombre} (${cuotaActualObj.horas_semana}h/sem · ${cuotaActualObj.precio_final || cuotaActualObj.precio}€)`
                    : "Heredar cuota actual"}
                </option>
                {cuotas
                  .filter(c => c.activo !== false && c.tipo !== "becado" && c.exenta !== true)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} · {c.horas_semana}h/sem · {c.precio_final || c.precio}€
                    </option>
                  ))}
              </select>
              <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>
                Deja "Heredar actual" si solo cambias la sede sin tocar la cuota.
              </div>
            </div>

            {/* 4. Turnos nuevos · solo si se cambia cuota con horas distintas
                 o si el admin quiere reasignar manualmente. */}
            {(necesitaTurnosExplicitos || turnosModal.length > 0) && (
              <div style={{ marginBottom: "12px", padding: "10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "4px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "8px" }}>
                  Turnos nuevos {necesitaTurnosExplicitos && <span style={{ color: "#dc2626" }}>*</span>}
                  <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: "6px" }}>
                    ({numTurnosUI || horasDestino} {(numTurnosUI || horasDestino) === 1 ? "turno" : "turnos"} según cuota)
                  </span>
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {Array.from({ length: numTurnosUI }).map((_, idx) => {
                    const t = turnosModal[idx] || { dia: "", hora: "" };
                    return (
                      <div key={idx} style={{ display: "grid", gridTemplateColumns: "30px 1fr 1fr", gap: "6px", alignItems: "center" }}>
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}>{idx + 1}.</span>
                        <select
                          value={t.dia}
                          onChange={(e) => updateTurnoModal(idx, "dia", e.target.value)}
                          disabled={cambioSedeCFProcessing}
                          style={{ ...STYLE_INPUT_OK, padding: "4px 6px", fontSize: "12px" }}>
                          <option value="">Día...</option>
                          {DIAS.map((d) => (<option key={d} value={d}>{d}</option>))}
                        </select>
                        <select
                          value={t.hora}
                          onChange={(e) => updateTurnoModal(idx, "hora", e.target.value)}
                          disabled={cambioSedeCFProcessing || !t.dia}
                          style={{ ...STYLE_INPUT_OK, padding: "4px 6px", fontSize: "12px" }}>
                          <option value="">Hora...</option>
                          {HORARIOS_BASE.map((h) => (<option key={h} value={h}>{h}</option>))}
                        </select>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "6px", fontStyle: "italic" }}>
                  El backend validará aforo de la sede destino antes de aplicar el cambio.
                  Si no hay plaza en algún slot, el cambio se aborta sin tocar nada.
                </div>
              </div>
            )}
            {errTurnos && (
              <div style={{ fontSize: "12px", color: "#dc2626", marginTop: "-6px", marginBottom: "8px", fontWeight: 500 }}>
                ⚠ {errTurnos}
              </div>
            )}

            {/* 5. Motivo (opcional) */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Motivo (opcional)
              </label>
              <input
                type="text"
                value={cambioSedeCFForm.motivo}
                onChange={(e) => setCambioSedeCFForm({ ...cambioSedeCFForm, motivo: e.target.value })}
                placeholder="p.ej. cambio de domicilio, preferencia horaria..."
                disabled={cambioSedeCFProcessing}
                maxLength={200}
                style={STYLE_INPUT_OK}
              />
            </div>

            {/* 6. Comentario (opcional) */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Comentario (opcional)
              </label>
              <textarea
                value={cambioSedeCFForm.comentario}
                onChange={(e) => setCambioSedeCFForm({ ...cambioSedeCFForm, comentario: e.target.value })}
                placeholder="Detalle adicional para trazabilidad..."
                rows={3}
                disabled={cambioSedeCFProcessing}
                maxLength={2000}
                style={STYLE_TA_OK}
              />
            </div>

            {cambioSedeCFError && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: "4px", padding: "8px 12px", marginBottom: "12px",
                fontSize: "12px", color: "#991b1b"
              }}>
                {cambioSedeCFError}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                type="button"
                onClick={() => { setMostrarModalCambioSedeCF(false); setCambioSedeCFError(null); }}
                disabled={cambioSedeCFProcessing}
                style={{ padding: "8px 16px", fontSize: "13px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={
                  cambioSedeCFProcessing
                  || motivosBloqueoCSede.length > 0
                  || !client?.id
                  || String(client.id) !== String(id)
                }
                title={
                  cambioSedeCFProcessing ? "Procesando…"
                  : (!client?.id || String(client.id) !== String(id))
                    ? "Ficha no sincronizada · recarga la página"
                    : (motivosBloqueoCSede.length ? motivosBloqueoCSede.join("\n") : "Confirmar cambio de sede")
                }
                onClick={async () => {
                  // Construir nombre completo del cliente (confirmacion_cliente_nombre)
                  const nombreCompletoCliente = [
                    client?.nombre,
                    client?.primer_apellido,
                    client?.segundo_apellido,
                  ].filter(Boolean).join(" ").trim();

                  // Defensa anti cross-cliente (Ann 2026-05-20 · post-incidente Madeleine):
                  // Verificar que client.id coincide con el id del searchParams
                  // antes de enviar nada al backend (estado stale durante navegación).
                  if (!client?.id || String(client.id) !== String(id)) {
                    setCambioSedeCFError(
                      "🛑 Ficha no sincronizada · client.id no coincide con la URL · " +
                      "cierra el modal y recarga la página antes de cambiar la sede."
                    );
                    return;
                  }
                  if (!nombreCompletoCliente) {
                    setCambioSedeCFError(
                      "🛑 No se puede construir el nombre del cliente · recarga la ficha."
                    );
                    return;
                  }

                  // Construir payload (solo enviar cuota/turno si son explícitos)
                  const turnoLimpio = (cambioSedeCFForm.nuevo_turno_fijo || [])
                    .filter(t => t.dia && t.hora)
                    .map(t => ({ dia: t.dia, hora: t.hora }));
                  const payload = {
                    nueva_sede: cambioSedeCFForm.nueva_sede,
                    motivo: cambioSedeCFForm.motivo || null,
                    comentario: cambioSedeCFForm.comentario || null,
                    // Salvaguarda anti cross-cliente · backend valida coincidencia
                    confirmacion_cliente_nombre: nombreCompletoCliente,
                  };
                  if (cambioSedeCFForm.nuevo_cuota_id) {
                    payload.nuevo_cuota_id = cambioSedeCFForm.nuevo_cuota_id;
                  }
                  if (turnoLimpio.length > 0) {
                    payload.nuevo_turno_fijo = turnoLimpio;
                  }
                  // Resumen para la confirmación · incluye nombre + id explícitos
                  const cambios = [`sede ${sedeActualCF || "(sin sede)"} → ${cambioSedeCFForm.nueva_sede}`];
                  if (cuotaCambia) {
                    cambios.push(`cuota → ${cuotaDestinoObj?.nombre || cambioSedeCFForm.nuevo_cuota_id}`);
                  }
                  if (turnoLimpio.length > 0) {
                    cambios.push(`turnos → ${turnoLimpio.map(t => `${t.dia} ${t.hora}`).join(", ")}`);
                  }
                  if (!window.confirm(
                    `¿Confirmar cambio de sede para:\n\n` +
                    `   ${nombreCompletoCliente.toUpperCase()}\n` +
                    `   (id: ${client.id})\n\n` +
                    `${cambios.map(c => "• " + c).join("\n")}\n\n` +
                    `Etapa actual se cerrará con motivo "cambio_sede" y se abrirá nueva etapa el día 1 del mes siguiente.\n` +
                    `Cuota de inscripción EXENTA. NO es una baja.\n\n` +
                    `⚠️ Verifica que el nombre arriba sea el cliente que querías modificar.`
                  )) return;
                  setCambioSedeCFProcessing(true);
                  setCambioSedeCFError(null);
                  try {
                    const resp = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${client.id}/clases-fijas/cambiar-sede`,
                      payload,
                      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
                    );
                    const data = resp.data || {};
                    const en = data.etapa_nueva || {};
                    const extras = [];
                    if (en.cuota_cambia) extras.push("cuota actualizada");
                    if (en.turno_cambia) extras.push("turnos actualizados");
                    toast.success(
                      `✅ Cambio aplicado · etapa nueva inicia ${en.fecha_inicio} en ${en.sede_snapshot_nombre} · CI exenta${extras.length ? ` · ${extras.join(" + ")}` : ""}`,
                      { duration: 10000 },
                    );
                    setMostrarModalCambioSedeCF(false);
                    setCambioSedeCFForm({ nueva_sede: "", nuevo_cuota_id: "", nuevo_turno_fijo: [], motivo: "", comentario: "" });
                    window.location.reload();
                  } catch (err) {
                    const detail = err.response?.data?.detail;
                    const code = detail?.code || err.response?.data?.code;
                    let msg = "Error al cambiar la sede.";
                    if (code === "CLIENTE_NOMBRE_NO_COINCIDE") {
                      msg = "🛑 INCIDENTE DETECTADO POR BACKEND · el nombre del cliente enviado NO coincide con el cliente al que apunta el id. Operación abortada · NO se ha tocado nada. Cierra el modal y RECARGA LA PÁGINA antes de reintentar.";
                    } else if (code === "AFORO_EXCEDIDO") {
                      msg = "🛑 La sede destino no tiene plaza en alguno de los slots. Cambio abortado (atomicidad). Detalle: " + JSON.stringify(detail?.detail || {});
                    } else if (code === "CLIENTE_EN_IS_ACTIVA") {
                      // Código LEGACY · solo aparece si una versión vieja del
                      // backend está corriendo (pre-PR-CSDIS-1)
                      const detalleEC = detail?.detail || {};
                      const inconsistente = detalleEC?.inconsistencia_estructural === true;
                      msg = inconsistente
                        ? "🛑 Cliente con IS estructural INCONSISTENTE detectada (caso tipo Josefina · incidencia_activa poblada pero sus.estado=activa). Operación abortada. Requiere saneamiento estructural antes de cambiar sede."
                        : "🛑 Cliente con IS activa · cierra la incidencia antes de cambiar sede.";
                    } else if (code === "CLIENTE_EN_IS_SIN_REINCORPORACION_PROGRAMADA") {
                      // PR-CSDIS-1 (Ann 2026-05-26) · regla 1
                      msg = "🛑 Para cambiar de sede primero debe programarse la fecha de reincorporación tras la IS.";
                    } else if (code === "CLIENTE_EN_IS_CON_REINCORPORACION_PROGRAMADA") {
                      // PR-CSDIS-1 (Ann 2026-05-26) · regla 2 · ofrecer programar diferido
                      const detalleEC = detail?.detail || {};
                      const fechaReinc = detalleEC?.fecha_reincorporacion || "(fecha pendiente)";
                      const confirmar = window.confirm(
                        `El cambio de sede se aplicará desde la fecha de reincorporación tras la IS: ${fechaReinc}.\n\n` +
                        `¿Programar ahora el cambio de sede diferido a esa fecha?\n\n` +
                        `Durante la IS NO se cambiará la sede actual, ni cobros, ni turnos.`
                      );
                      if (confirmar) {
                        try {
                          const programPayload = {
                            sede_destino: cambioSedeCFForm.nueva_sede,
                          };
                          if (cambioSedeCFForm.nuevo_cuota_id) {
                            programPayload.cuota_destino_id = cambioSedeCFForm.nuevo_cuota_id;
                          }
                          if (turnoLimpio.length > 0) {
                            programPayload.turno_fijo_destino = turnoLimpio;
                          }
                          const respProg = await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${client.id}/clases-fijas/programar-cambio-sede`,
                            programPayload,
                            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
                          );
                          const dataProg = respProg.data || {};
                          const csp = dataProg.cambio_sede_programado || {};
                          toast.success(
                            `✅ Cambio de sede programado para ${csp.fecha_efecto} · destino ${csp.sede_destino}`,
                            { duration: 10000 },
                          );
                          setMostrarModalCambioSedeCF(false);
                          setCambioSedeCFForm({ nueva_sede: "", nuevo_cuota_id: "", nuevo_turno_fijo: [], motivo: "", comentario: "" });
                          window.location.reload();
                          return;
                        } catch (errProg) {
                          const detProg = errProg.response?.data?.detail;
                          const codeProg = detProg?.code || errProg.response?.data?.code;
                          if (codeProg === "CAMBIO_SEDE_YA_PROGRAMADO") {
                            msg = "ℹ️ Ya hay un cambio de sede programado para esta IS. Cancélalo primero si quieres crear uno nuevo.";
                          } else if (codeProg === "FECHA_REINCORPORACION_INCONSISTENTE") {
                            msg = "🛑 incidencia_activa.fecha_regreso y cierre_programado.fecha_fin+1d NO coinciden. Revisa los datos del cierre IS antes de programar el cambio de sede.";
                          } else if (codeProg === "FECHA_REINCORPORACION_AUSENTE") {
                            msg = "🛑 No se puede determinar la fecha de reincorporación. Programa primero el cierre de la IS.";
                          } else {
                            msg = `🛑 No se pudo programar el cambio diferido · ${codeProg || "Error"}: ${JSON.stringify(detProg?.detail || detProg || {})}`;
                          }
                        }
                      } else {
                        msg = `ℹ️ Cambio de sede no programado. Se aplicará automáticamente en ${fechaReinc} si lo programas más adelante.`;
                      }
                    } else if (code === "SIN_CAMBIO_REAL") {
                      msg = "ℹ️ La sede destino coincide con la actual.";
                    } else if (code === "FECHA_EFECTO_FUERA_DE_ALCANCE") {
                      msg = "🛑 Solo se acepta fecha efecto = día 1 del mes siguiente.";
                    } else if (code === "TURNO_FIJO_REQUERIDO") {
                      msg = "🛑 La cuota nueva tiene horas/semana distintas · debes indicar los turnos nuevos.";
                    } else if (code === "TURNO_NO_COINCIDE_CUOTA") {
                      msg = `🛑 El número de turnos no coincide con las horas/semana de la cuota seleccionada.`;
                    } else if (code === "CUOTA_INVALIDA" || code === "CUOTA_INACTIVA") {
                      msg = "🛑 La cuota seleccionada no es válida o está inactiva.";
                    } else if (code === "BECADO_FUERA_ALCANCE") {
                      msg = "🛑 No se puede cambiar a una cuota becada/exenta desde este flujo.";
                    } else if (typeof detail === "string") {
                      msg = detail;
                    } else if (detail) {
                      msg = `${code || "Error"}: ${JSON.stringify(detail?.detail || detail)}`;
                    }
                    setCambioSedeCFError(msg);
                  } finally {
                    setCambioSedeCFProcessing(false);
                  }
                }}
                style={{ padding: "8px 16px", fontSize: "13px", background: "#1d4ed8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", opacity: (cambioSedeCFProcessing || motivosBloqueoCSede.length > 0) ? 0.5 : 1 }}
              >
                {cambioSedeCFProcessing ? "Procesando..." : "Confirmar cambio de sede"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ═══ MODAL: Reinscribir cliente (solo a nivel cliente, NO toca servicios) ═══ */}
      {showReinscribirModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => !reinscribirProcessing && setShowReinscribirModal(false)}
        >
          <div
            style={{
              background: "white", borderRadius: "8px", padding: "24px",
              maxWidth: "560px", width: "90%", boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 12px 0", color: "#16a34a", fontSize: "18px" }}>
              ✅ Reinscribir cliente
            </h2>
            <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#6b7280" }}>
              Pasa al cliente de <strong>baja a alta</strong> únicamente a nivel cliente:
              <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: "3px", fontSize: "12px" }}>estado_cliente=activo</code>,
              registra <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: "3px", fontSize: "12px" }}>fecha_reactivacion</code>
              y limpia las banderas de baja.<br/>
              <strong>Los servicios NO se reactivan automáticamente.</strong> Tras reinscribir, ve a la
              pestaña <strong>Inscripciones</strong> y gestiona cada servicio (Clases Fijas, Plataforma,
              Clínica, mandato SEPA, etc.) por separado.
            </p>

            {/* Alerta si tenía deuda al darse de baja */}
            {client.baja_con_deuda && (
              <div style={{
                marginBottom: "16px", padding: "12px", border: "2px solid #f59e0b",
                background: "#fef3c7", borderRadius: "6px",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <span style={{ fontSize: "20px" }}>⚠️</span>
                  <div style={{ flex: 1, fontSize: "13px" }}>
                    <div style={{ fontWeight: 700, color: "#92400e", marginBottom: "4px" }}>
                      CLIENTE DADO DE BAJA CON DEUDA PENDIENTE
                    </div>
                    {client.mensaje_baja_deuda && (
                      <div style={{
                        marginTop: "6px", padding: "8px", background: "white",
                        borderRadius: "4px", border: "1px solid #fcd34d",
                        whiteSpace: "pre-wrap", color: "#374151",
                      }}>
                        {client.mensaje_baja_deuda}
                      </div>
                    )}
                    <div style={{ marginTop: "6px", color: "#92400e", fontSize: "12px" }}>
                      Verifica el estado de la deuda antes de continuar. (Por ahora no bloquea, solo informa.)
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                type="button"
                onClick={() => setShowReinscribirModal(false)}
                disabled={reinscribirProcessing}
                style={{ padding: "8px 16px", fontSize: "13px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={reinscribirProcessing}
                onClick={async () => {
                  setReinscribirProcessing(true);
                  try {
                    const res = await axios.post(`${API}/admin/clients/${id}/reinscribir-cliente`);
                    const aviso = res.data?.tenia_deuda
                      ? " ⚠️ Recuerda revisar la deuda pendiente."
                      : "";
                    toast.success(`Cliente reinscrito. Ahora gestiona los servicios desde Inscripciones.${aviso}`, { duration: 6000 });
                    setShowReinscribirModal(false);
                    window.location.reload();
                  } catch (err) {
                    const detail = err.response?.data?.detail || err.message || "Error al reinscribir";
                    toast.error(`Error: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`, { duration: 8000 });
                  } finally {
                    setReinscribirProcessing(false);
                  }
                }}
                style={{ padding: "8px 16px", fontSize: "13px", background: "#16a34a", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", opacity: reinscribirProcessing ? 0.5 : 1 }}
              >
                {reinscribirProcessing ? "Procesando..." : "Confirmar reinscripción"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bloque 1 — Modal de Inscripción a Clases Fijas (autopista nueva) */}
      <ModalInscripcionClasesFijas
        open={showInscripcionModal}
        onClose={() => setShowInscripcionModal(false)}
        onSuccess={async () => {
          setShowInscripcionModal(false);
          try {
            const fichaRes = await axios.get(`${API}/admin/clients/${id}/ficha`);
            setClient(fichaRes.data);
          } catch (e) {
            toast.error("Inscripción registrada, pero hubo un error refrescando la ficha. Recarga la página manualmente.");
          }
          try {
            await fetchCobrosIndicador();
          } catch (e) { /* no crítico */ }
        }}
        clientId={id}
        clientNombre={`${client.nombre || ""} ${client.primer_apellido || ""}`.trim()}
        clientSede={client.sede}
        clientFormaPago={client.forma_pago}
        clientSepa={{
          iban: client.sepa_iban,
          titular_cuenta: client.sepa_titular_cuenta,
          titular_dni: client.sepa_titular_dni,
          // Ann 2026-05-26 · autopista única SEPA: el modal calcula los
          // "faltantes" con la misma lógica que el backend canónico
          // (cambiar_forma_cobro_clases_fijas._validar_datos_bancarios).
          // 3 ramas: pagador alternativo, legacy sepa_titular_*, derivar del
          // cliente. Sin estos extras el modal pedía a ciegas titular_cuenta
          // y titular_dni aunque el titular fuera el propio cliente.
          tiene_pagador_alternativo: client.tiene_pagador_alternativo,
          pagador_nombre: client.pagador_nombre,
          pagador_apellidos: client.pagador_apellidos,
          pagador_dni: client.pagador_dni,
          pagador_relacion: client.pagador_relacion,
          cliente_nombre: client.nombre,
          cliente_primer_apellido: client.primer_apellido,
          cliente_dni: client.dni,
        }}
        clientCuotaId={client.cuota_id}
        clientTurnos={client.turno_fijo}
        cuotas={cuotas}
        horariosDisponibles={horariosDisponibles}
        isSlotFull={isSlotFull}
        getSlotInfo={getSlotInfo}
        getHorarioIntensidad={getHorarioIntensidad}
        modo="alta"
      />

      {/* PR-IS-EDIT-3 · Modal "Editar fechas IS" */}
      <ModalEditarFechasIS
        open={showEditarFechasIS}
        onClose={() => setShowEditarFechasIS(false)}
        suscripcionId={suscripcionActiva?.id || suscripcionActiva?._id}
        incidenciaActiva={suscripcionActiva?.incidencia_activa}
        onAplicado={async () => {
          try {
            await fetchIncidenciaData();
            await fetchCobrosIndicador();
          } catch (e) { /* no crítico */ }
        }}
      />
    </div>
  );
};

// ═══ Panel de Comunicación (historial real + envíos) ═══
const ComunicacionPanel = ({ clientId, clientEmail, clientName }) => {
  const [historial, setHistorial] = React.useState([]);
  const [plantillas, setPlantillas] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [plantillaId, setPlantillaId] = React.useState('');

  React.useEffect(() => {
    const load = async () => {
      try {
        const [histRes, plantRes] = await Promise.all([
          axios.get(`${API}/email/historial-cliente/${clientId}`).catch(() => ({ data: { envios: [] } })),
          axios.get(`${API}/email/plantillas-ficha`).catch(() => ({ data: [] })),
        ]);
        setHistorial(histRes.data?.envios || []);
        setPlantillas(plantRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [clientId]);

  const handleEnviar = async () => {
    if (!plantillaId) return;
    setSending(true);
    try {
      await axios.post(`${API}/email/envio-directo-cliente`, { cliente_id: clientId, plantilla_id: plantillaId });
      toast.success('Email enviado correctamente');
      // Recargar historial
      const res = await axios.get(`${API}/email/historial-cliente/${clientId}`);
      setHistorial(res.data?.envios || []);
      setPlantillaId('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar email');
    } finally { setSending(false); }
  };

  const handleEnviarCredenciales = async (plataforma) => {
    setSending(true);
    try {
      await axios.post(`${API}/email/enviar-credenciales`, { cliente_id: clientId, plataforma });
      toast.success(`Credenciales de ${plataforma} enviadas`);
      const res = await axios.get(`${API}/email/historial-cliente/${clientId}`);
      setHistorial(res.data?.envios || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar credenciales');
    } finally { setSending(false); }
  };

  const fmtFecha = (f) => f ? new Date(f).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const estadoBadge = (envio) => {
    if (envio.abierto) return { label: 'Leído', color: '#059669', bg: 'rgba(5,150,105,0.08)' };
    if (envio.entregado) return { label: 'Entregado', color: '#2563eb', bg: 'rgba(37,99,235,0.08)' };
    return { label: 'Enviado', color: '#d97706', bg: 'rgba(217,119,6,0.08)' };
  };

  const S = {
    TH: { textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  };

  return (
    <div style={{ maxWidth: 900, fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Envío rápido */}
      <div style={{ background: '#fff', border: '1px solid #e8ecf1', borderRadius: 4, padding: '16px 20px', marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Enviar comunicación</h3>
        <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 12px' }}>
          Destino: <strong>{clientEmail || 'Sin email'}</strong>
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={plantillaId} onChange={e => setPlantillaId(e.target.value)}
            style={{ flex: 1, minWidth: 200, padding: '7px 10px', fontSize: 12, border: '1px solid #e8ecf1', borderRadius: 3 }}>
            <option value="">Seleccionar plantilla...</option>
            {plantillas.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.nombre}</option>)}
          </select>
          <button onClick={handleEnviar} disabled={!plantillaId || sending || !clientEmail}
            style={{ fontSize: 11, fontWeight: 600, padding: '7px 16px', borderRadius: 3, border: 'none', background: plantillaId && clientEmail ? '#1e293b' : '#e2e8f0', color: plantillaId && clientEmail ? '#fff' : '#94a3b8', cursor: plantillaId && clientEmail ? 'pointer' : 'not-allowed' }}>
            {sending ? 'Enviando...' : 'Enviar email'}
          </button>
          <span style={{ width: 1, height: 24, background: '#e8ecf1' }} />
          <button onClick={() => handleEnviarCredenciales('gestor_cambios')} disabled={sending}
            style={{ fontSize: 10, fontWeight: 500, padding: '6px 10px', borderRadius: 3, border: '1px solid #e8ecf1', background: '#fff', color: '#64748b', cursor: 'pointer' }}>
            Credenciales Gestor
          </button>
          <button onClick={() => handleEnviarCredenciales('canal_videos')} disabled={sending}
            style={{ fontSize: 10, fontWeight: 500, padding: '6px 10px', borderRadius: 3, border: '1px solid #e8ecf1', background: '#fff', color: '#64748b', cursor: 'pointer' }}>
            Credenciales Canal
          </button>
          <button onClick={() => handleEnviarCredenciales('plataforma_paciente')} disabled={sending}
            style={{ fontSize: 10, fontWeight: 500, padding: '6px 10px', borderRadius: 3, border: '1px solid #e8ecf1', background: '#fff', color: '#64748b', cursor: 'pointer' }}>
            Credenciales Paciente
          </button>
        </div>
      </div>

      {/* Historial */}
      <div style={{ background: '#fff', border: '1px solid #e8ecf1', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf1' }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: 0 }}>Historial de comunicaciones</h3>
          <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0' }}>{historial.length} envíos registrados</p>
        </div>
        {loading ? (
          <p style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Cargando...</p>
        ) : historial.length === 0 ? (
          <p style={{ padding: 30, textAlign: 'center', color: '#cbd5e1', fontSize: 12 }}>No hay comunicaciones registradas con este cliente.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e8ecf1' }}>
                <th style={S.TH}>Fecha</th>
                <th style={S.TH}>Asunto</th>
                <th style={S.TH}>Tipo</th>
                <th style={S.TH}>Canal</th>
                <th style={S.TH}>Estado</th>
                <th style={S.TH}>Enviado por</th>
              </tr>
            </thead>
            <tbody>
              {historial.map((envio, idx) => {
                const estado = estadoBadge(envio);
                return (
                  <tr key={envio.id || idx} style={{ borderBottom: idx < historial.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <td style={{ padding: '8px 14px', color: '#64748b', fontSize: 11, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {fmtFecha(envio.fecha)}
                    </td>
                    <td style={{ padding: '8px 14px', fontWeight: 500, color: '#1e293b' }}>{envio.asunto || '—'}</td>
                    <td style={{ padding: '8px 14px' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b', padding: '2px 6px', background: '#f1f5f9', borderRadius: 2 }}>
                        {envio.tipo}
                      </span>
                    </td>
                    <td style={{ padding: '8px 14px', color: '#64748b', fontSize: 11 }}>{envio.canal}</td>
                    <td style={{ padding: '8px 14px' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: estado.color, background: estado.bg, padding: '2px 8px', borderRadius: 2 }}>
                        {estado.label}
                      </span>
                      {envio.fecha_apertura && (
                        <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 6 }}>
                          {fmtFecha(envio.fecha_apertura)}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: 11 }}>{envio.enviado_por || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ═══ Panel de Documentos Legales (dinámico desde legal_templates) ═══
const LegalDocsPanel = ({ clientId, clientData, suscripcionActiva }) => {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(new Set());
  const [generando, setGenerando] = React.useState(false);

  const TIPOS = {
    'PRIVACIDAD': { label: 'RGPD', color: '#2563eb' },
    'IMAGEN': { label: 'Imagen', color: '#059669' },
    'NORMATIVA': { label: 'Normativa', color: '#7c3aed' },
    'SEPA': { label: 'SEPA', color: '#d97706' },
    'ANEXO': { label: 'Anexo', color: '#64748b' },
    'TAQUILLA': { label: 'Taquilla', color: '#64748b' },
    'IS': { label: 'Inc. Salud', color: '#c2410c' },
  };

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/settings/legal-templates`);
        const tieneISActiva = !!(suscripcionActiva?.incidencia_activa);
        setTemplates((res.data || []).filter(t => {
          if (t.status !== 'PUBLISHED') return false;
          if (t.type === 'IS' && !tieneISActiva) return false;
          return true;
        }));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleViewPdf = async (templateId) => {
    try {
      const res = await axios.get(`${API}/settings/legal-templates/${templateId}/pdf/${clientId}`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch (e) { console.error('Error generating PDF:', e); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === templates.length) setSelected(new Set());
    else setSelected(new Set(templates.map(t => t.id)));
  };

  const handleConjunto = async () => {
    if (selected.size === 0) return;
    setGenerando(true);
    try {
      const res = await axios.post(`${API}/settings/legal-templates/pdf-conjunto/${clientId}`,
        { template_ids: [...selected] }, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch (e) { console.error('Error generating combined PDF:', e); }
    finally { setGenerando(false); }
  };

  const TH = { textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const chkStyle = { width: 16, height: 16, cursor: 'pointer', accentColor: '#1e293b' };

  if (loading) return <p style={{ padding: 20, color: '#94a3b8', fontSize: 12 }}>Cargando documentos...</p>;

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ marginBottom: 16 }}>
        <h3 className="cn-panel-title">Documentos Legales</h3>
        <p className="cn-panel-subtitle">Selecciona documentos para generar un PDF conjunto o haz click en "Ver PDF" para ver uno individual.</p>
      </div>

      <div style={{ border: '1px solid #e8ecf1', borderRadius: 4, overflow: 'hidden', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: "'Inter', system-ui, sans-serif" }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e8ecf1' }}>
              <th style={{ ...TH, width: 40, textAlign: 'center' }}>
                <input type="checkbox" checked={selected.size === templates.length && templates.length > 0}
                  onChange={selectAll} style={chkStyle} title="Seleccionar todos" />
              </th>
              <th style={TH}>Tipo</th>
              <th style={TH}>Documento</th>
              <th style={TH}>Versión</th>
              <th style={TH}>Firma</th>
              <th style={{ ...TH, textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tpl, idx) => {
              const tipo = TIPOS[tpl.type] || { label: tpl.type, color: '#64748b' };
              const isChecked = selected.has(tpl.id);
              return (
                <tr key={tpl.id} style={{
                  borderBottom: idx < templates.length - 1 ? '1px solid #f1f5f9' : 'none',
                  background: isChecked ? 'rgba(30,58,95,0.03)' : 'transparent',
                }}>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <input type="checkbox" checked={isChecked} onChange={() => toggleSelect(tpl.id)} style={chkStyle} />
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: tipo.color, padding: '4px 10px', border: `1px solid ${tipo.color}30`, borderRadius: 3 }}>
                      {tipo.label}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', fontWeight: 500, color: '#1e293b' }}>{tpl.name}</td>
                  <td style={{ padding: '10px 14px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>v{tpl.version}</td>
                  <td style={{ padding: '10px 14px', fontSize: 11 }}>
                    <span style={{ color: '#d97706', fontWeight: 500 }}>Pendiente</span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    <button onClick={() => handleViewPdf(tpl.id)} style={{
                      fontSize: 11, fontWeight: 600, padding: '5px 14px', borderRadius: 3,
                      border: '1px solid #e8ecf1', background: '#fff', color: '#1e293b', cursor: 'pointer',
                    }}>
                      Ver PDF
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Botón documento conjunto */}
        <div style={{
          padding: '12px 14px', borderTop: '1px solid #e8ecf1', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: selected.size > 0 ? 'rgba(30,58,95,0.03)' : '#fafbfc',
        }}>
          <span style={{ fontSize: 11, color: '#64748b' }}>
            {selected.size > 0 ? `${selected.size} documento${selected.size > 1 ? 's' : ''} seleccionado${selected.size > 1 ? 's' : ''}` : 'Selecciona documentos para combinar'}
          </span>
          <button
            onClick={handleConjunto}
            disabled={selected.size === 0 || generando}
            style={{
              fontSize: 12, fontWeight: 700, padding: '8px 20px', borderRadius: 3,
              border: 'none', cursor: selected.size > 0 ? 'pointer' : 'not-allowed',
              background: selected.size > 0 ? '#1e293b' : '#e2e8f0',
              color: selected.size > 0 ? '#fff' : '#94a3b8',
              opacity: generando ? 0.6 : 1, transition: 'all 0.15s',
            }}
          >
            {generando ? 'Generando...' : 'Crear documento conjunto'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmpleadoFicha;
